import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setSelectedProject } from "../../store/userSlice";
import store from "../../store/store";
// import { useTheme } from "../../ThemeContext";
// ⬆️ In your project, delete the mock block below and un-comment this import instead.

import { apiFetch } from "../apiClient";
// ⬆️ Central authenticated API client. Adjust the relative path if this
// dashboard file isn't in the same folder as apiClient.js.

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

// ✅ NEW: PDF export & chart capture libraries.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import ProjectOverview from "../ProjectOverview";
import ProjectOverviewKpi from "../ProjectOverviewKpi";
import {
  getUnitStageRoleSummary,
  fetchTowersByProject,
  getStageDetailsByProjectId,
  getLevelsWithFlatsByBuilding,
  getUnitWorkInProgressBreakdown,
  getUnitChecklistReport,
  getSnagStats,
  getMyAssignedMIRs,
  listMIRs,
  getProjectsForCurrentUser,
  getNCRList,
  getNCRSummary,
  fetchQCObservations,
  fetchQCObservationItems,
  getFloorTypeDetails,
} from "../../api";
import axios from "axios";
import { projectInstance, checklistInstance } from "../../api/axiosInstance";

/* ------------------------------------------------------------------ */
/*  🧪 DEMO-ONLY theme mock — remove when you drop this into your app  */
/* ------------------------------------------------------------------ */
function useTheme() {
  const [theme, setTheme] = useState("light");
  return { theme, toggleTheme: () => setTheme((t) => (t === "light" ? "dark" : "light")) };
}
/* ------------------------------------------------------------------ */

const ORANGE = "#ff9f43";
const PALETTE = [
  "#ff9f43", // Bright Amber
  "#00cfe8", // Electric Cyan
  "#10b981", // Bright Emerald
  "#ff4d6d", // Vibrant Crimson
  "#7367f0", // Bright Indigo/Purple
  "#feca57", // Golden Yellow
  "#00d2d3", // Vivid Teal
  "#54a0ff", // Sky Blue
  "#5f27cd", // Deep Violet
  "#ff6b6b", // Coral
  "#e056fd", // Neon Pink
  "#686de0"  // Royal Slate
];

// Status → color mapping used consistently across every MIR chart/badge.
const STATUS_COLORS = {
  APPROVED: "#10b981",    // Bright Emerald Green
  REJECTED: "#ef4444",    // Bright Crimson Red
  PENDING: "#ff9f43",     // Bright Amber Orange
  UNDER_REVIEW: "#00cfe8" // Bright Electric Cyan
};
const STATUS_LABELS = {
  APPROVED: "Approved",
  REJECTED: "Rejected",
  PENDING: "Pending",
  UNDER_REVIEW: "Under Review",
};
// Fixed, intentional order used for every legend/table/summary in this dashboard.
const STATUS_ORDER = ["APPROVED", "REJECTED", "PENDING", "UNDER_REVIEW"];

/* ------------------------------------------------------------------ */
/*  Material Inspection Requests (MIR) Configuration & Helpers        */
/*  MIR records are fetched dynamically from the backend API          */
/* ------------------------------------------------------------------ */

const MONTH_MAP = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

// "25 Aug 2026" -> Date. Used only for sorting/grouping, never displayed
// in place of the original "Created Date" string.
function parseCreatedDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string" || dateStr === "—") return new Date(0);
  const parts = dateStr.trim().split(" ");
  if (parts.length >= 3) {
    const [day, mon, year] = parts;
    const monthIdx = MONTH_MAP[mon];
    if (monthIdx !== undefined && !isNaN(Number(year)) && !isNaN(Number(day))) {
      return new Date(Number(year), monthIdx, Number(day));
    }
  }
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? new Date(0) : parsed;
}

function monthKeyOf(dateStr) {
  if (!dateStr || typeof dateStr !== "string" || dateStr === "—") return "Unknown";
  const parts = dateStr.trim().split(" ");
  if (parts.length >= 3) {
    const [, mon, year] = parts;
    if (mon && year) return `${mon} ${year}`;
  }
  const d = new Date(dateStr);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  }
  return "Unknown";
}

/* ------------------------------------------------------------------ */
/*  Metric 3-dot sequential wave loading animation                     */
/* ------------------------------------------------------------------ */
export function MetricWaveLoading({ color = "#ff9f43" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 h-8 select-none py-1"
      aria-label="Loading metric"
      style={{ verticalAlign: "middle" }}
    >
      <style>{`
        @keyframes metricDotWave {
          0%, 100% {
            transform: translateY(0);
            opacity: 0.35;
          }
          50% {
            transform: translateY(-5px);
            opacity: 1;
          }
        }
      `}</style>
      <span
        className="w-2 h-2 rounded-full inline-block"
        style={{
          backgroundColor: color,
          animation: "metricDotWave 1.2s ease-in-out infinite",
          animationDelay: "0s",
        }}
      />
      <span
        className="w-2 h-2 rounded-full inline-block"
        style={{
          backgroundColor: color,
          animation: "metricDotWave 1.2s ease-in-out infinite",
          animationDelay: "0.18s",
        }}
      />
      <span
        className="w-2 h-2 rounded-full inline-block"
        style={{
          backgroundColor: color,
          animation: "metricDotWave 1.2s ease-in-out infinite",
          animationDelay: "0.36s",
        }}
      />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Count-up animation helper for metrics cards                        */
/* ------------------------------------------------------------------ */
export function getStartValue(finalVal) {
  const num = typeof finalVal === "number" ? finalVal : Number(String(finalVal).replace(/,/g, "").trim());
  if (isNaN(num) || num <= 0) return 0;
  if (num <= 3) return 0;
  if (num < 100) return 1;
  if (num <= 200) return 50;
  if (num <= 500) return 100;
  return Math.min(500, Math.floor(num * 0.4));
}

function CountUpInner({ targetNumber, duration = 1250, triggerKey = "" }) {
  const startNumber = getStartValue(targetNumber);

  // Initialize state with startNumber so final number is NEVER rendered instantly
  const [displayValue, setDisplayValue] = useState(() => {
    if (targetNumber <= startNumber) return targetNumber.toLocaleString("en-US");
    return startNumber.toLocaleString("en-US");
  });

  useEffect(() => {
    if (targetNumber <= startNumber) {
      setDisplayValue(targetNumber.toLocaleString("en-US"));
      return;
    }

    let animationFrameId;
    let startTime = null;

    // Immediately display the starting number before animation frame runs
    setDisplayValue(startNumber.toLocaleString("en-US"));

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smooth ease-out (cubic): starts relatively fast and gradually slows down
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startNumber + (targetNumber - startNumber) * easeOut);

      setDisplayValue(current.toLocaleString("en-US"));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      } else {
        setDisplayValue(targetNumber.toLocaleString("en-US"));
      }
    };

    animationFrameId = requestAnimationFrame(step);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [targetNumber, startNumber, duration, triggerKey]);

  return <>{displayValue}</>;
}

export function CountUpNumber({ value, loading = false, color = "#ff9f43", duration = 1250, triggerKey = "" }) {
  // 1. While API/data is loading, render the animated 3-dot wave
  if (loading || value === "…" || value === null || value === undefined || value === "") {
    return <MetricWaveLoading color={color} />;
  }

  const rawNum = typeof value === "number" ? value : Number(String(value).replace(/,/g, "").trim());
  if (isNaN(rawNum)) {
    return <MetricWaveLoading color={color} />;
  }

  return (
    <CountUpInner
      key={`${triggerKey}_${rawNum}`}
      targetNumber={rawNum}
      duration={duration}
      triggerKey={triggerKey}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Shared Project Resolution Helpers                                 */
/* ------------------------------------------------------------------ */

function getActiveDashboardProjectId() {
  try {
    const qp = new URLSearchParams(window.location.search).get("project_id");
    if (qp && Number(qp)) return String(qp);

    const ls =
      localStorage.getItem("ACTIVE_PROJECT_ID") ||
      localStorage.getItem("SELECTED_PROJECT_ID") ||
      localStorage.getItem("PROJECT_ID") ||
      localStorage.getItem("project_id") ||
      localStorage.getItem("active_project_id");
    if (ls && Number(ls)) return String(ls);

    const spRaw = localStorage.getItem("SELECTED_PROJECT") || localStorage.getItem("selected_project");
    if (spRaw) {
      const sp = JSON.parse(spRaw);
      const spId = sp?.id || sp?.project_id;
      if (spId) return String(spId);
    }
  } catch { }

  return "";
}

function getAccessibleProjects() {
  const userProjects = [];
  try {
    const accessesRaw = localStorage.getItem("ACCESSES");
    if (accessesRaw && accessesRaw !== "undefined") {
      const parsed = JSON.parse(accessesRaw);
      if (Array.isArray(parsed)) {
        parsed.forEach((a) => {
          const pId = a.project_id || a.projectId || a.id;
          const pName = a.project_name || a.projectName || a.name || a.title;
          if (pId && pName) userProjects.push({ id: String(pId), name: String(pName) });
        });
      }
    }

    const udRaw = localStorage.getItem("USER_DATA");
    if (udRaw && udRaw !== "undefined") {
      const ud = JSON.parse(udRaw);
      if (Array.isArray(ud?.accesses)) {
        ud.accesses.forEach((a) => {
          const pId = a.project_id || a.projectId || a.id;
          const pName = a.project_name || a.projectName || a.name || a.title;
          if (pId && pName) userProjects.push({ id: String(pId), name: String(pName) });
        });
      }
      if (Array.isArray(ud?.projects)) {
        ud.projects.forEach((a) => {
          const pId = a.project_id || a.projectId || a.id;
          const pName = a.project_name || a.projectName || a.name || a.title;
          if (pId && pName) userProjects.push({ id: String(pId), name: String(pName) });
        });
      }
    }

    ["PROJECTS", "projects", "PROJECT_LIST", "project_list"].forEach((key) => {
      const raw = localStorage.getItem(key);
      if (raw && raw !== "undefined") {
        try {
          const parsed = JSON.parse(raw);
          const list = Array.isArray(parsed) ? parsed : parsed?.projects || parsed?.data || [];
          if (Array.isArray(list)) {
            list.forEach((a) => {
              const pId = a.project_id || a.projectId || a.id;
              const pName = a.project_name || a.projectName || a.name || a.title;
              if (pId && pName) userProjects.push({ id: String(pId), name: String(pName) });
            });
          }
        } catch (_) {}
      }
    });

    const spRaw = localStorage.getItem("SELECTED_PROJECT") || localStorage.getItem("selected_project");
    if (spRaw && spRaw !== "undefined") {
      try {
        const sp = JSON.parse(spRaw);
        const pId = sp?.id || sp?.project_id;
        const pName = sp?.name || sp?.project_name;
        if (pId && pName) userProjects.push({ id: String(pId), name: String(pName) });
      } catch (_) {}
    }
  } catch (e) {
    console.warn("getAccessibleProjects: failed to resolve from localStorage", e);
  }

  const dedupMap = new Map();
  userProjects.forEach((p) => {
    const rawId = p?.id;
    if (!rawId) return;
    const strId = String(rawId).trim();
    if (strId.includes("/")) return; // Filter malformed project IDs like 1/1
    const numId = Number(strId);
    if (isNaN(numId) || !Number.isInteger(numId) || numId <= 0) return;

    const rawName = p?.name ? String(p.name).trim() : "";
    const isSynthetic = !rawName || /^project\s*#?\s*\d+$/i.test(rawName);
    const finalName = isSynthetic ? "Unnamed Project" : rawName;

    dedupMap.set(String(numId), { id: String(numId), name: finalName });
  });
  return Array.from(dedupMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function getActiveDashboardProjectName(activeProjectId) {
  if (!activeProjectId) return null;
  try {
    const spRaw = localStorage.getItem("SELECTED_PROJECT") || localStorage.getItem("selected_project");
    if (spRaw) {
      const sp = JSON.parse(spRaw);
      const spId = sp?.id || sp?.project_id;
      const spName = sp?.name || sp?.project_name;
      if (spName && (!spId || String(spId) === String(activeProjectId))) {
        const clean = String(spName).trim();
        if (clean && !/^project\s*#?\s*\d+$/i.test(clean)) return clean;
      }
    }

    const accessible = getAccessibleProjects();
    const match = accessible.find((p) => String(p.id) === String(activeProjectId));
    if (match && match.name && !/^project\s*#?\s*\d+$/i.test(match.name)) return match.name;
  } catch (e) {
    console.warn("getActiveDashboardProjectName: failed to resolve project name", e);
  }
  return null;
}

function normalizeProjectName(name) {
  if (!name) return "";
  return String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .trim();
}

function isProjectMatch(record, filterProjectId, filterProjectName) {
  if (!filterProjectId && (!filterProjectName || filterProjectName === "ALL")) return true;

  const targetNorm = normalizeProjectName(filterProjectName);
  const targetId = filterProjectId ? String(filterProjectId) : null;

  // 1. Direct project id match
  const rawId =
    record?.projectId ??
    record?.project_id ??
    record?.projectIdProp ??
    (typeof record?.project === "object" ? record?.project?.id || record?.project?.project_id : record?.project);
  if (targetId && rawId !== undefined && rawId !== null && String(rawId) === targetId) {
    return true;
  }

  // 2. Project name match (exact normalized match)
  const rawName =
    (typeof record?.project === "string" ? record.project : record?.project?.name) ??
    record?.project_name ??
    record?.projectName ??
    record?.project_title;
  if (rawName && typeof rawName === "string") {
    const rNorm = normalizeProjectName(rawName);
    if (targetNorm && rNorm && rNorm === targetNorm) {
      return true;
    }
  }

  // If a target project ID was specified and the record has a conflicting project ID, do not match
  if (targetId && rawId !== undefined && rawId !== null && String(rawId) !== targetId) {
    return false;
  }

  // 3. Location text as fallback only when record has no project ID
  const rLoc = record?.location ?? record?.location_text ?? record?.full_location;
  if (!rawId && rLoc && typeof rLoc === "string" && targetNorm && targetNorm.length >= 5) {
    const locNorm = normalizeProjectName(rLoc);
    if (locNorm === targetNorm || locNorm.startsWith(targetNorm)) return true;
  }

  return false;
}

/* ------------------------------------------------------------------ */
/*  Shared small UI atoms                                             */
/* ------------------------------------------------------------------ */
function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || "#999";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: `${color}22`, color }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color, display: "inline-block" }} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function MirSummaryCards({ records, cardColor, textColor, onSelectStatus }) {
  const counts = useMemo(() => {
    const base = { APPROVED: 0, REJECTED: 0, PENDING: 0, UNDER_REVIEW: 0 };
    records.forEach((r) => {
      if (base[r.status] !== undefined) base[r.status] += 1;
    });
    return base;
  }, [records]);

  const cards = [
    { key: "TOTAL", label: "Total MIRs", value: records.length, color: textColor, clickable: false },
    ...STATUS_ORDER.map((s) => ({
      key: s,
      label: STATUS_LABELS[s],
      value: counts[s],
      color: STATUS_COLORS[s],
      clickable: true,
    })),
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5 sm:gap-4">
      {cards.map((c) => (
        <button
          key={c.key}
          disabled={!c.clickable}
          onClick={() => c.clickable && onSelectStatus && onSelectStatus(c.key)}
          className="text-left rounded-xl p-3 sm:p-4 shadow-sm border transition-all hover:scale-[1.02]"
          style={{
            background: cardColor,
            borderColor: c.color,
            borderWidth: 2,
            cursor: c.clickable ? "pointer" : "default",
          }}
        >
          <p className="text-xs font-semibold uppercase tracking-wide opacity-60">{c.label}</p>
          <p className="text-3xl font-bold mt-1" style={{ color: c.color }}>
            <CountUpNumber value={c.value} color={c.color} triggerKey={`mir_${c.key}_${c.value}`} />
          </p>
        </button>
      ))}
    </div>
  );
}

function MirStatusChart({ records, cardColor, textColor, theme, onDrill }) {
  const data = useMemo(
    () =>
      STATUS_ORDER.map((s) => ({
        name: STATUS_LABELS[s],
        status: s,
        value: records.filter((r) => r.status === s).length,
      })).filter((d) => d.value > 0),
    [records]
  );

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No MIRs match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={3}
          cursor="pointer"
          onClick={(d) => onDrill && onDrill("status", d.status)}
        >
          {data.map((d) => (
            <Cell key={d.status} fill={STATUS_COLORS[d.status]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 12, color: textColor }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function MirProjectChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      map[r.project] = (map[r.project] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [records]);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No MIRs match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={12} width={110} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar
          dataKey="value"
          radius={[0, 6, 6, 0]}
          cursor="pointer"
          onClick={(d) => onDrill && onDrill("project", d.name)}
        >
          {data.map((d, i) => (
            <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function MirProjectStatusChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      if (!map[r.project]) {
        map[r.project] = { name: r.project, APPROVED: 0, REJECTED: 0, PENDING: 0, UNDER_REVIEW: 0 };
      }
      map[r.project][r.status] += 1;
    });
    return Object.values(map).sort(
      (a, b) =>
        b.APPROVED + b.REJECTED + b.PENDING + b.UNDER_REVIEW -
        (a.APPROVED + a.REJECTED + a.PENDING + a.UNDER_REVIEW)
    );
  }, [records]);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No MIRs match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" stroke={axisColor} fontSize={11} interval={0} angle={-15} textAnchor="end" height={50} />
        <YAxis stroke={axisColor} fontSize={12} allowDecimals={false} />
        <Tooltip shared={false} contentStyle={tooltipStyle} />
        <Legend
          wrapperStyle={{ fontSize: 12, color: textColor }}
          formatter={(value) => STATUS_LABELS[value] || value}
        />
        {STATUS_ORDER.map((s) => (
          <Bar
            key={s}
            dataKey={s}
            stackId="a"
            fill={STATUS_COLORS[s]}
            name={s}
            cursor="pointer"
            onClick={(d) => onDrill && onDrill("project-status", { project: d.name, status: s })}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function MirTrendChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const key = monthKeyOf(r.createdDate);
      if (!map[key]) map[key] = { key, name: key, value: 0, sortDate: parseCreatedDate(`01 ${key}`) };
      map[key].value += 1;
    });
    return Object.values(map).sort((a, b) => a.sortDate - b.sortDate);
  }, [records]);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No MIRs match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" stroke={axisColor} fontSize={12} />
        <YAxis stroke={axisColor} fontSize={12} allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} />
        <Line
          type="monotone"
          dataKey="value"
          name="MIRs created"
          stroke={ORANGE}
          strokeWidth={3}
          dot={{ r: 4, cursor: "pointer" }}
          activeDot={{ r: 6, onClick: (e, payload) => onDrill && onDrill("month", payload?.payload?.key) }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function MirCreatedByChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      map[r.createdBy] = (map[r.createdBy] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [records]);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No MIRs match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={11} width={110} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar
          dataKey="value"
          fill={ORANGE}
          radius={[0, 6, 6, 0]}
          name="MIRs created"
          cursor="pointer"
          onClick={(d) => onDrill && onDrill("creator", d.name)}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function MirFilters({ filters, setFilters, options, cardColor, textColor, theme, onProjectChange }) {
  const selectStyle = {
    background: theme === "dark" ? "#17171d" : "#fff",
    color: textColor,
    border: `1px solid ${ORANGE}88`,
  };

  const update = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const reset = () => {
    setFilters({ status: "ALL", project: "ALL", createdBy: "ALL", month: "ALL", search: "" });
    if (onProjectChange) onProjectChange("ALL");
  };

  const hasActiveFilters =
    filters.status !== "ALL" ||
    filters.project !== "ALL" ||
    filters.createdBy !== "ALL" ||
    filters.month !== "ALL" ||
    filters.search !== "";

  return (
    <div
      className="rounded-lg p-3 sm:p-4 shadow flex flex-wrap items-end gap-2.5 sm:gap-3"
      style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}
    >
      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Status</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.status} onChange={update("status")}>
          <option value="ALL">All statuses</option>
          {options.statuses.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s] || s}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Project</label>
        <select
          className="text-xs sm:text-sm rounded px-2 py-1.5 w-full"
          style={selectStyle}
          value={filters.project}
          onChange={(e) => {
            const val = e.target.value;
            setFilters((f) => ({ ...f, project: val }));
            if (onProjectChange) onProjectChange(val);
          }}
        >
          <option value="ALL">All projects</option>
          {options.projects.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Created By</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.createdBy} onChange={update("createdBy")}>
          <option value="ALL">All creators</option>
          {options.creators.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Created Date</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.month} onChange={update("month")}>
          <option value="ALL">All dates</option>
          {options.months.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 min-w-[140px] w-full sm:w-auto">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Search</label>
        <input
          type="text"
          placeholder="MIR #, location, project…"
          className="text-xs sm:text-sm rounded px-2 py-1.5 w-full"
          style={selectStyle}
          value={filters.search}
          onChange={update("search")}
        />
      </div>

      {hasActiveFilters && (
        <button
          onClick={reset}
          className="text-xs px-3 py-1.5 rounded-full border opacity-80 hover:opacity-100 font-semibold shrink-0"
          style={{ borderColor: ORANGE, color: ORANGE }}
        >
          ✕ Clear filters
        </button>
      )}
    </div>
  );
}

const PAGE_SIZE = 15;

function MirTable({ records, cardColor, textColor, theme, onOpenMir }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(records.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = records.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";

  React.useEffect(() => {
    setPage(0);
  }, [records.length]);

  const actionBtnStyle = (color) => ({
    color,
    border: `1px solid ${color}55`,
    background: `${color}14`,
  });

  return (
    <div className="rounded-lg shadow overflow-hidden" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
      <div className="overflow-x-auto touch-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
              {["MIR Number", "Project", "Location", "Status", "Created By", "Created Date", "Created Time", "Actions"].map(
                (h) => (
                  <th key={h} className="text-left px-3 py-2 text-[11px] uppercase tracking-wide opacity-60 whitespace-nowrap">
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.mirNumber} style={{ borderBottom: `1px solid ${borderColor}` }}>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">{r.mirNumber}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.project}</td>
                <td className="px-3 py-2 max-w-[260px] truncate" title={r.location}>
                  {r.location}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <StatusBadge status={r.status} />
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{r.createdBy}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.createdDate}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.createdTime}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => onOpenMir && onOpenMir(r.mirNumber)}
                      className="text-[11px] px-2 py-0.5 rounded"
                      style={actionBtnStyle(ORANGE)}
                    >
                      View Details
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm opacity-50">
                  No MIRs match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 px-3 py-2.5 text-xs opacity-75" style={{ borderTop: `1px solid ${borderColor}` }}>
        <span className="text-center sm:text-left">
          Showing {records.length === 0 ? 0 : safePage * PAGE_SIZE + 1}–
          {Math.min(records.length, safePage * PAGE_SIZE + PAGE_SIZE)} of {records.length} MIRs
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: ORANGE }}
          >
            ← Prev
          </button>
          <span>
            Page {safePage + 1} / {pageCount}
          </span>
          <button
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: ORANGE }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

function buildMirFrame(kind, key, recordsSource) {
  let records = recordsSource;
  let title = String(key);

  if (kind === "status") {
    records = recordsSource.filter((r) => r.status === key);
    title = `Status: ${STATUS_LABELS[key] || key}`;
  } else if (kind === "project") {
    records = recordsSource.filter((r) => r.project === key);
    title = `Project: ${key}`;
  } else if (kind === "creator") {
    records = recordsSource.filter((r) => r.createdBy === key);
    title = `Creator: ${key}`;
  } else if (kind === "month") {
    records = recordsSource.filter((r) => monthKeyOf(r.createdDate) === key);
    title = `Month: ${key}`;
  } else if (kind === "project-status") {
    records = recordsSource.filter((r) => r.project === key.project && r.status === key.status);
    title = `${key.project} — ${STATUS_LABELS[key.status] || key.status}`;
  } else if (kind === "mir") {
    records = recordsSource.filter((r) => r.mirNumber === key);
    title = key;
  }

  return { kind, key, title, records };
}

function MirDrillSummary({ frame, onExplore, onDrillFurther }) {
  const { records, title, kind } = frame;

  const counts = {
    APPROVED: records.filter((r) => r.status === "APPROVED").length,
    REJECTED: records.filter((r) => r.status === "REJECTED").length,
    PENDING: records.filter((r) => r.status === "PENDING").length,
    UNDER_REVIEW: records.filter((r) => r.status === "UNDER_REVIEW").length,
  };

  const projectMap = {};
  records.forEach((r) => { projectMap[r.project] = (projectMap[r.project] || 0) + 1; });
  const topProjects = Object.entries(projectMap).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const creatorMap = {};
  records.forEach((r) => { creatorMap[r.createdBy] = (creatorMap[r.createdBy] || 0) + 1; });
  const topCreators = Object.entries(creatorMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base sm:text-lg font-bold" style={{ color: ORANGE }}>{title}</h3>
        <p className="text-xs opacity-70 mt-1">
          {records.length} MIR record{records.length === 1 ? "" : "s"} in this selection
        </p>
      </div>

      {kind !== "status" && (
        <div>
          <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Status Breakdown (Click to Drill)</p>
          <div className="grid grid-cols-2 gap-2">
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                onClick={() => onDrillFurther && onDrillFurther("status", s)}
                className="text-left rounded-lg p-2.5 border hover:opacity-90 hover:scale-[1.01] transition-all"
                style={{ borderColor: STATUS_COLORS[s], background: `${STATUS_COLORS[s]}12` }}
              >
                <p className="text-[10px] uppercase font-semibold opacity-70">{STATUS_LABELS[s]}</p>
                <p className="text-lg font-bold" style={{ color: STATUS_COLORS[s] }}>{counts[s]}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {kind !== "project" && topProjects.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Projects Breakdown (Click to Drill)</p>
          <div className="flex flex-wrap gap-1.5">
            {topProjects.map(([p, c]) => (
              <button
                key={p}
                onClick={() => onDrillFurther && onDrillFurther("project", p)}
                className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                style={{ borderColor: `${ORANGE}77`, background: `${ORANGE}12`, color: ORANGE }}
              >
                <span className="font-medium">{p}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-amber-500/20">{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {kind !== "creator" && topCreators.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Creators Breakdown (Click to Drill)</p>
          <div className="flex flex-wrap gap-1.5">
            {topCreators.map(([cr, c]) => (
              <button
                key={cr}
                onClick={() => onDrillFurther && onDrillFurther("creator", cr)}
                className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                style={{ borderColor: "#6c9bff77", background: "#6c9bff12", color: "#6c9bff" }}
              >
                <span className="font-medium">{cr}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-blue-500/20">{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <p className="text-sm opacity-50 py-6 text-center">No MIR records match this selection.</p>
      ) : (
        <button
          onClick={onExplore}
          className="w-full text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-lg shadow hover:opacity-90 transition-all flex items-center justify-center gap-2"
          style={{ background: ORANGE, color: "#1a1a1a" }}
        >
          <span>Explore All {records.length} Record{records.length === 1 ? "" : "s"}</span>
          <span>→</span>
        </button>
      )}
    </div>
  );
}

function MirDrillRecordsList({ frame, cardColor, theme, onOpenMir }) {
  const [search, setSearch] = useState("");
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const records = useMemo(() => {
    if (!search.trim()) return frame.records;
    const s = search.toLowerCase();
    return frame.records.filter(
      (r) =>
        (r.mirNumber || "").toLowerCase().includes(s) ||
        (r.project || "").toLowerCase().includes(s) ||
        (r.location || "").toLowerCase().includes(s) ||
        (r.status || "").toLowerCase().includes(s)
    );
  }, [frame.records, search]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold" style={{ color: ORANGE }}>{frame.title} — Records ({records.length})</h3>
        <input
          type="text"
          placeholder="Filter records..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-xs rounded px-2.5 py-1 w-full sm:w-40 border"
          style={{
            background: theme === "dark" ? "#17171d" : "#fff",
            borderColor: `${ORANGE}66`,
            color: "inherit",
          }}
        />
      </div>
      <div className="rounded-lg overflow-hidden border" style={{ borderColor: `${ORANGE}55` }}>
        <div className="max-h-[460px] overflow-y-auto">
          {/* Desktop view */}
          <div className="hidden sm:block">
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ background: cardColor }}>
                <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                  {["MIR #", "Project", "Location", "Status", "Date"].map((h) => (
                    <th key={h} className="text-left px-2.5 py-2 text-[10px] uppercase tracking-wide opacity-60 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr
                    key={r.mirNumber}
                    onClick={() => onOpenMir(r.mirNumber)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ borderBottom: `1px solid ${borderColor}` }}
                  >
                    <td className="px-2.5 py-2 font-semibold whitespace-nowrap" style={{ color: ORANGE }}>{r.mirNumber}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.project}</td>
                    <td className="px-2.5 py-2 max-w-[140px] truncate" title={r.location}>{r.location}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap"><StatusBadge status={r.status} /></td>
                    <td className="px-2.5 py-2 whitespace-nowrap text-xs">{r.createdDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card list */}
          <div className="sm:hidden divide-y" style={{ borderColor }}>
            {records.map((r) => (
              <div
                key={r.mirNumber}
                onClick={() => onOpenMir(r.mirNumber)}
                className="p-3 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono font-bold text-xs" style={{ color: ORANGE }}>{r.mirNumber}</span>
                  <StatusBadge status={r.status} />
                </div>
                <p className="text-xs font-medium truncate">{r.project} • {r.location}</p>
                <p className="text-[10px] opacity-60 mt-0.5">{r.createdDate}</p>
              </div>
            ))}
          </div>

          {records.length === 0 && (
            <p className="text-xs opacity-50 py-8 text-center">No matching records found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MirDrillItem({ mir, theme }) {
  if (!mir) return <p className="text-sm opacity-50 py-10 text-center">Record not found.</p>;
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const fields = [
    ["MIR Number", mir.mirNumber],
    ["Project", mir.project],
    ["Location", mir.location],
    ["Status", null],
    ["Created By", mir.createdBy],
    ["Created Date", mir.createdDate],
    ["Created Time", mir.createdTime],
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold" style={{ color: ORANGE }}>{mir.mirNumber}</h3>
      <div className="rounded-lg p-4 space-y-3" style={{ border: `1px solid ${ORANGE}66` }}>
        {fields.map(([label, value]) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4"
            style={{ borderBottom: `1px solid ${borderColor}`, paddingBottom: 8 }}
          >
            <span className="text-xs uppercase tracking-wide opacity-60 shrink-0">{label}</span>
            {label === "Status" ? <StatusBadge status={mir.status} /> : <span className="text-sm text-right font-medium">{value}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function MirDrillDrawer({ stack, onBack, onClose, onPush, onOpenMir, onSlice, cardColor, textColor, theme }) {
  if (stack.length === 0) return null;
  const top = stack[stack.length - 1];
  const isItemFrame = top.kind === "mir";
  const isRecordsFrame = top.kind === "records";

  const handleDrillFurther = (kind, key) => onPush(buildMirFrame(kind, key, top.records));
  const handleExplore = () => onPush({ kind: "records", key: top.key, records: top.records, title: top.title });

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-full sm:max-w-md overflow-y-auto shadow-2xl p-4 sm:p-5"
        style={{ background: cardColor, color: textColor, paddingTop: (typeof window !== "undefined" && window.innerWidth < 768) ? 68 : 96 }}
      >
        <div className="flex items-center justify-between mb-4 gap-2">
          {/* Clickable Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            {stack.map((f, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="opacity-40">/</span>}
                <button
                  type="button"
                  onClick={() => onSlice && onSlice(i)}
                  disabled={i === stack.length - 1}
                  className={`font-semibold transition-all ${
                    i === stack.length - 1
                      ? "cursor-default"
                      : "opacity-60 hover:opacity-100 hover:underline"
                  }`}
                  style={{ color: i === stack.length - 1 ? ORANGE : undefined }}
                >
                  {f.kind === "mir" ? (f.key?.mirNumber || f.title) : f.title}
                </button>
              </React.Fragment>
            ))}
          </div>
          <button onClick={onClose} className="text-lg opacity-60 hover:opacity-100 p-1 shrink-0" title="Close">✕</button>
        </div>

        {stack.length > 1 && (
          <button
            onClick={onBack}
            className="text-xs mb-4 px-3 py-1 rounded-full border hover:opacity-100 transition-opacity flex items-center gap-1"
            style={{ borderColor: ORANGE, color: ORANGE, background: `${ORANGE}10` }}
          >
            <span>←</span>
            <span>Back</span>
          </button>
        )}

        {isItemFrame && <MirDrillItem mir={top.key} theme={theme} />}
        {isRecordsFrame && (
          <MirDrillRecordsList frame={top} cardColor={cardColor} theme={theme} onOpenMir={onOpenMir} />
        )}
        {!isItemFrame && !isRecordsFrame && (
          <MirDrillSummary frame={top} onExplore={handleExplore} onDrillFurther={handleDrillFurther} />
        )}
      </div>
    </div>
  );
}

function MirDashboard({
  theme,
  cardColor,
  textColor,
  onExportContext,
  selectedProjectId,
  selectedProjectName,
  onSelectProject,
}) {
  const [accessibleProjects, setAccessibleProjects] = useState(() => getAccessibleProjects());
  const [liveMirRecords, setLiveMirRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [filters, setFilters] = useState({
    status: "ALL",
    project: selectedProjectName || "ALL",
    createdBy: "ALL",
    month: "ALL",
    search: "",
  });
  const [drillStack, setDrillStack] = useState([]);

  // Sync internal project filter whenever parent selected project changes
  useEffect(() => {
    if (selectedProjectName) {
      setFilters((prev) => ({ ...prev, project: selectedProjectName }));
    } else if (!selectedProjectId) {
      setFilters((prev) => ({ ...prev, project: "ALL" }));
    }
  }, [selectedProjectId, selectedProjectName]);

  const handleProjectFilterChange = (newProjName) => {
    setFilters((prev) => ({ ...prev, project: newProjName }));
    if (onSelectProject) {
      if (!newProjName || newProjName === "ALL") {
        onSelectProject("", "ALL");
      } else {
        const found = accessibleProjects.find(
          (p) =>
            p.name.trim().toLowerCase() === newProjName.trim().toLowerCase() ||
            normalizeProjectName(p.name) === normalizeProjectName(newProjName)
        );
        onSelectProject(found ? found.id : null, newProjName);
      }
    }
  };

  // Load all accessible projects for current user via API as well, guaranteeing full dropdown options
  useEffect(() => {
    let isMounted = true;
    async function loadUserProjects() {
      try {
        const res = await getProjectsForCurrentUser();
        const rows = res?.data?.projects || res?.data || [];
        if (Array.isArray(rows) && rows.length > 0 && isMounted) {
          const mapped = rows
            .map((p) => {
              const rawId = p?.id || p?.project_id || p?.uuid;
              if (rawId == null) return null;
              const strId = String(rawId).trim();
              if (strId.includes("/")) return null;
              const numId = Number(strId);
              if (isNaN(numId) || !Number.isInteger(numId) || numId <= 0) return null;

              const rawName = String(p?.name || p?.project_name || p?.title || "").trim();
              const isSynthetic = !rawName || /^project\s*#?\s*\d+$/i.test(rawName);
              return {
                id: String(numId),
                name: isSynthetic ? "Unnamed Project" : rawName,
              };
            })
            .filter(Boolean);
          if (mapped.length > 0) {
            setAccessibleProjects((prev) => {
              const map = new Map(prev.map((item) => [String(item.id), item]));
              mapped.forEach((item) => map.set(String(item.id), item));
              return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
            });
          }
        }
      } catch (err) {
        console.warn("MirDashboard: could not fetch projects for user", err);
      }
    }
    loadUserProjects();
    return () => {
      isMounted = false;
    };
  }, []);

  // Quick lookup map: project id -> project name
  const projectMap = useMemo(() => {
    const map = new Map();
    accessibleProjects.forEach((p) => {
      if (p.id && p.name && !/^project\s*#?\s*\d+$/i.test(p.name)) {
        map.set(String(p.id), p.name);
      }
    });
    return map;
  }, [accessibleProjects]);

  // Fetch real MIR data from backend API
  const loadLiveMIRs = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      let list = [];
      let fetchError = null;
      try {
        const res = await listMIRs(selectedProjectId ? { project_id: selectedProjectId } : {});
        const data = Array.isArray(res?.data) ? res.data : (res?.data?.results || res?.data?.data || []);
        if (Array.isArray(data) && data.length > 0) {
          list = data;
        }
      } catch (err) {
        fetchError = err;
      }

      if (!list.length) {
        try {
          const res = await getMyAssignedMIRs(selectedProjectId ? { project_id: selectedProjectId } : {});
          const data = Array.isArray(res?.data) ? res.data : (res?.data?.results || res?.data?.data || []);
          if (Array.isArray(data) && data.length > 0) {
            list = data;
            fetchError = null;
          }
        } catch (_) {}
      }

      if (fetchError && !list.length) {
        console.warn("Failed to load live MIRs:", fetchError);
        setLiveMirRecords([]);
        setErrorMessage(fetchError?.response?.data?.message || fetchError?.message || "Failed to load Material Inspection Requests from server.");
        return;
      }

      if (Array.isArray(list) && list.length > 0) {
        const parsed = list.map((r, idx) => {
          const rawStatus = (r.status || "PENDING").toUpperCase().replace(/\s+/g, "_");
          const status = STATUS_ORDER.includes(rawStatus) ? rawStatus : "PENDING";
          const dateObj = r.created_at ? new Date(r.created_at) : null;
          const createdDate = r.created_date || (dateObj && !isNaN(dateObj.getTime()) ? dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
          const createdTime = r.created_time || (dateObj && !isNaN(dateObj.getTime()) ? dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");

          const rawProjectId =
            r.project_id !== undefined && r.project_id !== null
              ? String(r.project_id)
              : r.projectId !== undefined && r.projectId !== null
              ? String(r.projectId)
              : typeof r.project === "number" || (typeof r.project === "string" && !isNaN(Number(r.project)) && r.project.trim() !== "")
              ? String(r.project)
              : r.project?.id || r.project?.project_id || null;

          const rawProjectName =
            r.project_name ||
            r.projectName ||
            r.project_title ||
            (typeof r.project === "object" ? (r.project?.name || r.project?.project_name) : null) ||
            (typeof r.project === "string" && isNaN(Number(r.project)) && !/^project\s*#?\s*\d+$/i.test(r.project) && !r.project.includes("/") ? r.project : null) ||
            (rawProjectId && projectMap.get(String(rawProjectId))) ||
            r.tower?.project_name ||
            r.unit?.project_name ||
            "";

          const project = rawProjectName && rawProjectName.trim() && !/^project\s*#?\s*\d+$/i.test(rawProjectName.trim()) && !rawProjectName.includes("/")
            ? rawProjectName.trim()
            : rawProjectId
            ? (projectMap.get(String(rawProjectId)) || "Unnamed Project")
            : "Unnamed Project";

          return {
            mirNumber: r.mir_number || r.mir_no || r.number || `#MIR-${r.id || idx + 1}`,
            project,
            projectId: rawProjectId,
            location: r.location || r.tower_name || r.building_name || "—",
            status,
            createdBy: r.created_by_name || r.created_by?.name || r.user_name || "Admin",
            createdDate,
            createdTime,
            id: r.id,
          };
        });
        setLiveMirRecords(parsed);
      } else {
        setLiveMirRecords([]);
      }
    } catch (err) {
      console.warn("Failed to load live MIRs:", err);
      setLiveMirRecords([]);
      setErrorMessage(err?.response?.data?.message || err?.message || "Failed to load Material Inspection Requests from server.");
    } finally {
      setIsLoading(false);
    }
  }, [projectMap, selectedProjectId]);

  useEffect(() => {
    loadLiveMIRs();
  }, [loadLiveMIRs]);

  const options = useMemo(() => {
    const statuses = Array.from(new Set(liveMirRecords.map((r) => r.status))).sort(
      (a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b)
    );

    // Ensure all accessible projects appear in the Project dropdown
    const projectNamesSet = new Set();
    accessibleProjects.forEach((p) => {
      if (p?.name && p.name.trim() && !/^project\s*#?\s*\d+$/i.test(p.name.trim()) && !p.name.includes("/")) {
        projectNamesSet.add(p.name.trim());
      }
    });
    liveMirRecords.forEach((r) => {
      if (
        r?.project &&
        r.project !== "Unknown Project" &&
        r.project !== "Unnamed Project" &&
        r.project !== "—" &&
        r.project.trim() &&
        !/^project\s*#?\s*\d+$/i.test(r.project.trim()) &&
        !r.project.includes("/")
      ) {
        projectNamesSet.add(r.project.trim());
      }
    });
    if (
      filters.project &&
      filters.project !== "ALL" &&
      filters.project.trim() &&
      !/^project\s*#?\s*\d+$/i.test(filters.project.trim()) &&
      !filters.project.includes("/")
    ) {
      projectNamesSet.add(filters.project.trim());
    }

    const projects = Array.from(projectNamesSet).sort((a, b) => a.localeCompare(b));
    const creators = Array.from(new Set(liveMirRecords.map((r) => r.createdBy))).sort();
    const months = Array.from(new Set(liveMirRecords.map((r) => monthKeyOf(r.createdDate)))).sort(
      (a, b) => parseCreatedDate(`01 ${a}`) - parseCreatedDate(`01 ${b}`)
    );
    return { statuses, projects, creators, months };
  }, [liveMirRecords, accessibleProjects, filters.project]);

  const filteredRecords = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return liveMirRecords.filter((r) => {
      if (filters.status !== "ALL" && r.status !== filters.status) return false;
      if (filters.project !== "ALL") {
        if (!isProjectMatch(r, selectedProjectId, filters.project)) return false;
      }
      if (filters.createdBy !== "ALL" && r.createdBy !== filters.createdBy) return false;
      if (filters.month !== "ALL" && monthKeyOf(r.createdDate) !== filters.month) return false;
      if (q) {
        const haystack = `${r.mirNumber} ${r.project} ${r.location} ${r.createdBy}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [liveMirRecords, filters, selectedProjectId]);

  useEffect(() => {
    if (onExportContext) {
      onExportContext({
        filters,
        selectedProject: filters.project,
        liveMirRecords,
        filteredRecords,
      });
    }
  }, [filters, liveMirRecords, filteredRecords, onExportContext]);

  const openDrill = (kind, key) => setDrillStack([buildMirFrame(kind, key, filteredRecords)]);
  const pushDrill = (frame) => setDrillStack((s) => [...s, frame]);
  const popDrill = () => setDrillStack((s) => s.slice(0, -1));
  const closeDrill = () => setDrillStack([]);

  const openMir = (mirNo) => {
    const mir = liveMirRecords.find((r) => r.mirNumber === mirNo);
    if (mir) pushDrill({ kind: "mir", key: mir, records: [mir], title: mir.mirNumber });
  };

  return (
    <div className="space-y-4" id="active-dashboard-charts-container">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wider opacity-70">
          Material Inspection Requests (MIR)
        </h2>
        <div className="flex items-center gap-2">
          {isLoading && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-500 animate-pulse font-mono">
              🔄 Loading live MIR data...
            </span>
          )}
          <button
            onClick={loadLiveMIRs}
            disabled={isLoading}
            className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-amber-500/40 hover:border-amber-500 text-amber-500 transition-colors disabled:opacity-50"
            title="Refresh MIR data from server"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {errorMessage && !isLoading && (
        <div className="rounded-xl p-5 text-center border shadow-sm bg-red-500/10 border-red-500/30">
          <div className="text-2xl mb-1">⚠️</div>
          <p className="text-sm font-bold text-red-500">Failed to load Material Inspection Requests</p>
          <p className="text-xs opacity-70 mt-1 mb-3">{errorMessage}</p>
          <button
            onClick={loadLiveMIRs}
            className="text-xs font-semibold px-4 py-1.5 rounded-full border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      <MirFilters
        filters={filters}
        setFilters={setFilters}
        options={options}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
        onProjectChange={handleProjectFilterChange}
      />

      <MirSummaryCards
        records={filteredRecords}
        cardColor={cardColor}
        textColor={textColor}
        onSelectStatus={(s) => openDrill("status", s)}
      />

      {isLoading ? (
        <div className="rounded-xl p-12 text-center border shadow-sm" style={{ background: cardColor, borderColor: `${ORANGE}44` }}>
          <div className="text-3xl mb-2 animate-spin inline-block">⏳</div>
          <p className="text-sm font-bold opacity-80">Loading Material Inspection Requests...</p>
          <p className="text-xs opacity-50 mt-1 font-mono">Fetching live MIR data from backend API...</p>
        </div>
      ) : filteredRecords.length === 0 && !errorMessage ? (
        <div className="rounded-xl p-10 text-center border shadow-sm" style={{ background: cardColor, borderColor: `${ORANGE}44` }}>
          <div className="text-3xl mb-2">📦</div>
          <p className="text-sm font-bold opacity-80">No Material Inspection Requests (MIR) Found</p>
          <p className="text-xs opacity-50 mt-1">
            {liveMirRecords.length === 0
              ? "There are currently 0 MIR records found in the backend database for this project scope."
              : "No MIR records match the currently applied filters."}
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Status distribution</p>
              <p className="text-[11px] opacity-50 mb-2">Share of MIRs by approval status — Approved, Rejected, Pending, Under Review.</p>
              <MirStatusChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">MIRs by project</p>
              <p className="text-[11px] opacity-50 mb-2">Total MIR count grouped by project — highlights which projects raise the most material inspection requests.</p>
              <MirProjectChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
          </div>

          <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
            <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Project × status breakdown</p>
            <p className="text-[11px] opacity-50 mb-2">How each project's MIRs split across statuses — spot projects with a high pending or rejected share.</p>
            <MirProjectStatusChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">MIRs created over time</p>
              <p className="text-[11px] opacity-50 mb-2">Monthly trend of new MIR submissions — useful for spotting spikes or slowdowns in inspection requests.</p>
              <MirTrendChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Top creators</p>
              <p className="text-[11px] opacity-50 mb-2">Users who raised the most MIRs, most active first.</p>
              <MirCreatedByChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
          </div>

          <MirTable records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onOpenMir={openMir} />
        </>
      )}

      <MirDrillDrawer
        stack={drillStack}
        onBack={popDrill}
        onClose={closeDrill}
        onPush={pushDrill}
        onOpenMir={openMir}
        onSlice={(idx) => setDrillStack((s) => s.slice(0, idx + 1))}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
      />
    </div>
  );
}

/* ==================================================================== */
/*  ✅ REAL QC OBSERVATION DATASET                                       */
/*  Source of truth: the actual QC Observation export (66 records).      */
/*  Every card, chart, filter option and table row in the QC Observation */
/*  dashboard is derived from this single array — nothing fabricated.    */
/*  Shape per row: [id, project, location, items, status, assignee,      */
/*                  createdDate]                                         */
/* ==================================================================== */
/* ==================================================================== */
/*  ✅ REAL QC OBSERVATION DATASET                                       */
/*  Source of truth: the actual QC Observation export (66 records).      */
/*  Every card, chart, filter option and table row in the QC Observation */
/*  dashboard is derived from this single array — nothing fabricated.    */
/*  Shape per row: [id, project, location, items, status, assignee,      */
/*                  createdDate]                                         */
/* ==================================================================== */
/* ==================================================================== */
/*  ✅ REAL QC OBSERVATION DATASET                                       */
/*  This QC_TUPLES / QC_RECORDS mock array is NOT what powers the live   */
/*  dashboard below — QcDashboard fetches real data from                 */
/*  GET /checklists/observations/. It's kept here only because the       */
/*  "full multi-domain PDF export" branch elsewhere in the file still    */
/*  references QC_RECORDS.length / QC_RECORDS as a fallback sample.      */
/* ==================================================================== */
const QC_STATUS_COLORS = {
  Pending: "#ff9f43",     // Bright Amber
  "In Progress": "#00cfe8", // Electric Cyan
  Resolved: "#10b981",    // Bright Emerald
  Closed: "#64748b",      // Modern Slate Blue
};
const QC_STATUS_ORDER = ["Pending", "In Progress", "Resolved", "Closed"];

const QC_TUPLES = [
  ["OBS-0066", "Arkade - Nest", "Tower E · P4", 1, "Closed", "Xyz Maker", "26/8/2026"],
  ["OBS-0065", "Arkade - Nest", "Tower A · P4", 1, "Closed", "Xyz Maker", "25/8/2026"],
  ["OBS-0064", "KT-3", "Tower 1 · Floor 1", 1, "Closed", "KTMaker KTMaker", "25/8/2026"],
  ["OBS-0063", "KT-3", "Tower 1 · Floor 1", 1, "Closed", "KTMaker KTMaker", "25/8/2026"],
  ["OBS-0062", "Vistas & View", "- · -", 1, "Pending", "Birendra Kumar Chaubey", "25/8/2026"],
  ["OBS-0061", "Vistas & View", "- · -", 1, "Resolved", "Birendra Kumar Chaubey", "25/8/2026"],
  ["OBS-0060", "KT-3", "Tower 1 · Floor 1", 1, "Closed", "KTMaker KTMaker", "25/8/2026"],
  ["OBS-0059", "KT-3", "Tower 1 · Floor 1", 1, "In Progress", "KTMaker KTMaker", "25/8/2026"],
  ["OBS-0058", "KT-3", "Tower 1 · Floor 1", 1, "Closed", "KTMaker", "25/8/2026"],
  ["OBS-0057", "KT-3", "Tower 1 · Floor 1", 1, "Closed", "KTMaker KTMaker", "25/8/2026"],
  ["OBS-0056", "KT-3", "Tower 1 · Floor 1", 1, "Resolved", "KTMaker KTMaker", "24/8/2026"],
  ["OBS-0055", "Arkade - Rare", "Tower B · Floor 9", 1, "Pending", "Rajbhar Jitendra, Akhilesh Rajbhar, Anand Patel, Amol Bhagyawant", "24/8/2026"],
  ["OBS-0054", "Arkade - Nest", "Tower A · P4", 1, "Pending", "Vijay patil", "24/8/2026"],
  ["OBS-0053", "Arkade - Nest", "TEST · P3", 1, "Closed", "Nest Maker", "22/8/2026"],
  ["OBS-0052", "Arkade Rare", "Tower G · P2", 1, "Pending", "Rare Maker", "22/8/2026"],
  ["OBS-0051", "Arkade Rare", "Tower A · P3", 1, "Resolved", "Rare Maker", "22/8/2026"],
  ["OBS-0050", "Arkade - Rare", "Test · P-2", 1, "Closed", "Rare Maker", "22/8/2026"],
  ["OBS-0049", "Arkade - Rare", "Test · P-1", 1, "Resolved", "Rare Maker", "22/8/2026"],
  ["OBS-0048", "KT-3", "Tower 1 · Floor 1", 1, "Pending", "KTMaker KTMaker", "22/8/2026"],
];

const QC_RECORDS = QC_TUPLES.map((t) => ({
  id: t[0],
  project: t[1],
  location: t[2],
  items: t[3],
  status: t[4],
  assignee: t[5],
  createdDate: t[6],
}));

const QC_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "24/6/2026" -> Date. Used only for sorting/grouping, never displayed
// in place of the original Created Date string.
function parseQcDate(dateStr) {
  const [d, m, y] = dateStr.split("/").map(Number);
  return new Date(y, m - 1, d);
}

function qcMonthKeyOf(dateStr) {
  const [, m, y] = dateStr.split("/").map(Number);
  return `${QC_MONTH_NAMES[m - 1]} ${y}`;
}

/* ------------------------------------------------------------------ */
/*  ✅ Confirmed against the real GET /checklists/observations/         */
/*  response: status lives at `overall_status`, lowercase, one of       */
/*  "pending" | "in_progress" | "resolved" | "closed". Normalizing      */
/*  "in_progress" -> "in progress" makes it match "In Progress"         */
/*  case-insensitively, so this maps every real value correctly.        */
/* ------------------------------------------------------------------ */
function normalizeQcStatus(raw) {
  if (raw === null || raw === undefined || raw === "") return "Pending";
  const cleaned = String(raw).trim().toLowerCase().replace(/[\s_-]+/g, " ");
  const exact = QC_STATUS_ORDER.find((s) => s.toLowerCase() === cleaned);
  if (exact) return exact;
  const synonyms = {
    open: "Pending",
    "yet to start": "Pending",
    new: "Pending",
    wip: "In Progress",
    "in review": "In Progress",
    fixed: "Resolved",
    complete: "Closed",
    completed: "Closed",
    done: "Closed",
  };
  return synonyms[cleaned] || "Pending";
}

/* ------------------------------------------------------------------ */
/*  ✅ NEW — matches the real payload's nested shape. There is no flat  */
/*  "assignee" field anywhere on the response; each observation has an  */
/*  `items[]` array, and each item carries `current_actor_name` (who    */
/*  needs to act right now) plus an `assignments[]` history             */
/*  ({ user_name, is_active, is_skipped, ... }).                        */
/*                                                                        */
/*  Tier 1: unique current_actor_name across items — "who's on it now". */
/*  Tier 2: unique active (is_active) assignment user_name — covers     */
/*          items the API hasn't set a current_actor on yet.            */
/*  Tier 3: every assignment on record regardless of is_active — covers */
/*          fully closed/resolved items where nothing is "active"       */
/*          anymore but the people who worked it are still meaningful.  */
/*  Falls back to "Unassigned" only if none of the above yield a name.  */
/* ------------------------------------------------------------------ */
function resolveQcAssigneeFromItems(items, obsStatus) {
  if (!Array.isArray(items) || items.length === 0) return "Unassigned";

  const uniquePush = (arr, name) => {
    const trimmed = name && String(name).trim();
    if (trimmed && !arr.includes(trimmed)) arr.push(trimmed);
  };

  const currentActors = [];
  items.forEach((it) => uniquePush(currentActors, it?.current_actor_name));
  if (currentActors.length > 0) return currentActors.join(", ");

  const activeAssignees = [];
  items.forEach((it) => {
    (it?.assignments || []).forEach((a) => {
      if (a?.is_active) uniquePush(activeAssignees, a?.user_name);
    });
  });
  if (activeAssignees.length > 0) return activeAssignees.join(", ");

  // Safeguard: For open or pending items/observations, never fall back to inactive historical assignees
  const isOpen =
    ["pending", "in progress", "open"].includes(String(obsStatus || "").toLowerCase()) ||
    items.some((it) => ["pending", "in_progress"].includes(String(it?.status || "").toLowerCase()));
  if (isOpen) {
    return "Unassigned";
  }

  const allAssignees = [];
  items.forEach((it) => {
    (it?.assignments || []).forEach((a) => uniquePush(allAssignees, a?.user_name));
  });
  if (allAssignees.length > 0) return allAssignees.join(", ");

  return "Unassigned";
}

/* ------------------------------------------------------------------ */
/*  ✅ NEW — sequential, human-readable item names for an observation.  */
/*  There is no single confirmed field name for an item's "name" on     */
/*  the real payload yet, so this tries the most likely candidates in   */
/*  priority order and falls back to a positional "Item N" label so the */
/*  list is never blank. Order is preserved exactly as returned by the  */
/*  API (i.e. the array index order of `items[]`), which is what makes  */
/*  this a "proper sequence" rather than a re-sorted/alphabetized list. */
/*                                                                        */
/*  ⚠️ If your real item objects use a different key (check the network  */
/*  tab response for GET /checklists/observations/), add it to the      */
/*  candidate list below in priority order.                             */
/* ------------------------------------------------------------------ */
function resolveQcItemNames(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  return items.map((it, idx) => {
    const candidate =
      it?.name ??
      it?.item_name ??
      it?.title ??
      it?.checklist_item_name ??
      it?.description ??
      it?.label;
    const trimmed = candidate && String(candidate).trim();
    return trimmed ? trimmed : `Item ${idx + 1}`;
  });
}

function QcStatusBadge({ status }) {
  const color = QC_STATUS_COLORS[status] || "#999";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: `${color}22`, color }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color, display: "inline-block" }} />
      {status}
    </span>
  );
}

function QcSummaryCards({ records, cardColor, textColor, onSelectStatus }) {
  const counts = useMemo(() => {
    const base = { Pending: 0, "In Progress": 0, Resolved: 0, Closed: 0 };
    records.forEach((r) => {
      if (base[r.status] !== undefined) base[r.status] += 1;
    });
    return base;
  }, [records]);

  const cards = [
    { key: "TOTAL", label: "Total", value: records.length, color: textColor, clickable: false },
    ...QC_STATUS_ORDER.map((s) => ({ key: s, label: s, value: counts[s], color: QC_STATUS_COLORS[s], clickable: true })),
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5 sm:gap-4">
      {cards.map((c) => (
        <button
          key={c.key}
          disabled={!c.clickable}
          onClick={() => c.clickable && onSelectStatus(c.key)}
          className="text-left rounded-lg p-3 sm:p-4 shadow"
          style={{ background: cardColor, border: `2px solid ${c.color}`, cursor: c.clickable ? "pointer" : "default" }}
        >
          <p className="text-xs font-semibold uppercase tracking-wide opacity-60">{c.label}</p>
          <p className="text-2xl sm:text-3xl font-bold mt-1" style={{ color: c.color }}>
            <CountUpNumber value={c.value} color={c.color} triggerKey={`qc_${c.key}_${c.value}`} />
          </p>
        </button>
      ))}
    </div>
  );
}

function QcStatusChart({ records, cardColor, textColor, onDrill }) {
  const data = useMemo(
    () => QC_STATUS_ORDER.map((s) => ({ name: s, value: records.filter((r) => r.status === s).length })).filter((d) => d.value > 0),
    [records]
  );

  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No observations match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={3}
          cursor="pointer" onClick={(d) => onDrill("status", d.name)}
        >
          {data.map((d) => (
            <Cell key={d.name} fill={QC_STATUS_COLORS[d.name]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} observations`, n]} />
        <Legend wrapperStyle={{ fontSize: 12, color: textColor }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function QcProjectChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      map[r.project] = (map[r.project] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [records]);

  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No observations match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={11} width={120} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} observations`, "Count"]} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d) => onDrill("project", d.name)}>
          {data.map((d, i) => (
            <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function QcProjectStatusChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      if (!map[r.project]) {
        map[r.project] = { name: r.project, Pending: 0, "In Progress": 0, Resolved: 0, Closed: 0 };
      }
      map[r.project][r.status] += 1;
    });
    return Object.values(map).sort(
      (a, b) =>
        b.Pending + b["In Progress"] + b.Resolved + b.Closed -
        (a.Pending + a["In Progress"] + a.Resolved + a.Closed)
    );
  }, [records]);

  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No observations match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" stroke={axisColor} fontSize={11} interval={0} angle={-15} textAnchor="end" height={60} />
        <YAxis stroke={axisColor} fontSize={12} allowDecimals={false} />
        <Tooltip shared={false} contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 12, color: textColor }} />
        {QC_STATUS_ORDER.map((s) => (
          <Bar
            key={s} dataKey={s} stackId="a" fill={QC_STATUS_COLORS[s]} name={s} cursor="pointer"
            onClick={(d) => onDrill("project-status", { project: d.name, status: s })}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Filter a raw QC observations list down to just the active project's
 *  records. Shared by the top-level overview cards in ProjectHeadDashboard
 *  and (optionally) QcDashboard, so scoping logic lives in one place.
 *  Same resolution order used to fix QcDashboard: prefer an id on the
 *  record, fall back to matching project_name against the active
 *  project's name, exclude anything that matches neither. */
function scopeQcRecordsToProject(list, activeProjectId, activeProjectName) {
  if (!activeProjectId) return list;
  return list.filter((r) => {
    const projectId =
      r.project !== undefined && r.project !== null
        ? String(r.project)
        : r.project_id !== undefined && r.project_id !== null
        ? String(r.project_id)
        : r.projectId !== undefined && r.projectId !== null
        ? String(r.projectId)
        : r.tower?.project !== undefined && r.tower?.project !== null
        ? String(r.tower.project)
        : r.tower?.project_id !== undefined && r.tower?.project_id !== null
        ? String(r.tower.project_id)
        : null;
    if (projectId !== null) return projectId === String(activeProjectId);
    if (activeProjectName && r.project_name) {
      return String(r.project_name).trim().toLowerCase() === activeProjectName.trim().toLowerCase();
    }
    return false;
  });
}

function QcLocationChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      map[r.location] = (map[r.location] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
  }, [records]);

  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No observations match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={10} width={140} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} observations`, "Count"]} />
        <Bar dataKey="value" fill={ORANGE} radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d) => onDrill("location", d.name)} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function QcAssigneeChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const name = (r.assignee && String(r.assignee).trim()) || "Unassigned";
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [records]);

  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No observations match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis
          type="category"
          dataKey="name"
          stroke={axisColor}
          fontSize={9}
          width={190}
          interval={0}
          tickFormatter={(val) => (val && val.length > 26 ? `${val.slice(0, 24)}…` : val)}
        />
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n, item) => [`${v} observations`, item.payload.name]} />
        <Bar dataKey="value" fill="#6c9bff" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d) => onDrill("assignee", d.name)} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function QcTrendChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const key = qcMonthKeyOf(r.createdDate);
      if (!map[key]) map[key] = { key, name: key, value: 0, sortDate: parseQcDate(`1/${r.createdDate.split("/")[1]}/${r.createdDate.split("/")[2]}`) };
      map[key].value += 1;
    });
    return Object.values(map).sort((a, b) => a.sortDate - b.sortDate);
  }, [records]);

  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No observations match the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" stroke={axisColor} fontSize={12} />
        <YAxis stroke={axisColor} fontSize={12} allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} observations`, "Count"]} />
        <Line
          type="monotone" dataKey="value" name="Observations" stroke={ORANGE} strokeWidth={3}
          dot={{ r: 5, cursor: "pointer" }}
          activeDot={{ r: 7, cursor: "pointer", onClick: (_, p) => onDrill("month", p.payload.name) }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function QcFilters({ filters, setFilters, options, cardColor, textColor, theme, onProjectChange }) {
  const selectStyle = { background: theme === "dark" ? "#17171d" : "#fff", color: textColor, border: `1px solid ${ORANGE}88` };
  const update = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const reset = () => {
    setFilters({ status: "ALL", project: "ALL", location: "ALL", assignee: "ALL", month: "ALL", search: "" });
    if (onProjectChange) onProjectChange("ALL");
  };

  const hasActiveFilters =
    filters.status !== "ALL" ||
    filters.project !== "ALL" ||
    filters.location !== "ALL" ||
    filters.assignee !== "ALL" ||
    filters.month !== "ALL" ||
    filters.search !== "";

  return (
    <div
      className="rounded-lg p-3 sm:p-4 shadow flex flex-wrap items-end gap-2.5 sm:gap-3"
      style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}
    >
      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Status</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.status} onChange={update("status")}>
          <option value="ALL">All statuses</option>
          {options.statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Project</label>
        <select
          className="text-xs sm:text-sm rounded px-2 py-1.5 w-full"
          style={selectStyle}
          value={filters.project}
          onChange={(e) => {
            const val = e.target.value;
            setFilters((f) => ({ ...f, project: val }));
            if (onProjectChange) onProjectChange(val);
          }}
        >
          <option value="ALL">All projects</option>
          {options.projects.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Location</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.location} onChange={update("location")}>
          <option value="ALL">All locations</option>
          {options.locations.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Assignee</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.assignee} onChange={update("assignee")}>
          <option value="ALL">All assignees</option>
          {options.assignees.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Created Date</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.month} onChange={update("month")}>
          <option value="ALL">All dates</option>
          {options.months.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 min-w-[140px] w-full sm:w-auto">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Search</label>
        <input
          type="text"
          placeholder="OBS #, location, project…"
          className="text-xs sm:text-sm rounded px-2 py-1.5 w-full"
          style={selectStyle}
          value={filters.search}
          onChange={update("search")}
        />
      </div>

      {hasActiveFilters && (
        <button
          onClick={reset}
          className="text-xs px-3 py-1.5 rounded-full border opacity-80 hover:opacity-100 shrink-0"
          style={{ borderColor: ORANGE, color: ORANGE }}
        >
          ✕ Clear filters
        </button>
      )}
    </div>
  );
}

const QC_PAGE_SIZE = 12;

/* ------------------------------------------------------------------ */
/*  ✅ UPDATED — header alignment fix: every header cell used to be     */
/*  text-left regardless of what the column actually contains, which   */
/*  is why the "Items" number (rendered with text-center on the <td>)  */
/*  looked like it had drifted off to the right of its own header.     */
/*  Now the "Items" header is text-center too, so header and value     */
/*  line up. Also added a `title` tooltip with the full ordered item   */
/*  name list so a quick hover in the table gives a preview without    */
/*  having to open the drawer.                                         */
/* ------------------------------------------------------------------ */
function QcTable({ records, cardColor, textColor, theme, onOpenObs }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(records.length / QC_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = records.slice(safePage * QC_PAGE_SIZE, safePage * QC_PAGE_SIZE + QC_PAGE_SIZE);

  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";

  React.useEffect(() => {
    setPage(0);
  }, [records.length]);

  return (
    <div className="rounded-lg shadow overflow-hidden" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
      {/* Header strip: All Records & count */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between"
        style={{ borderColor, background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }}
      >
        <h2 className="text-sm font-bold flex items-center gap-2" style={{ color: textColor }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: ORANGE }} />
          All Records
        </h2>
        <div className="text-xs font-medium opacity-60">
          {records.length} {records.length === 1 ? "entry" : "entries"} found
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block overflow-x-auto touch-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
              {["ID", "Project", "Location", "Items", "Status", "Assignee", "Created Date", "Action"].map((h) => (
                <th
                  key={h}
                  className={`px-3 py-2 text-[11px] uppercase tracking-wide opacity-60 whitespace-nowrap ${
                    h === "Items" ? "text-center" : "text-left"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">{r.id}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.project}</td>
                <td className="px-3 py-2 max-w-[320px] whitespace-normal break-words" title={r.location}>{r.location}</td>
                <td
                  className="px-3 py-2 text-center"
                  title={Array.isArray(r.itemNames) && r.itemNames.length > 0 ? r.itemNames.join(", ") : ""}
                >
                  {r.items}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <QcStatusBadge status={r.status} />
                </td>
                <td className="px-3 py-2 max-w-[220px] truncate" title={r.assignee}>{r.assignee}</td>
                <td className="px-3 py-2 whitespace-nowrap">{r.createdDate}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button
                    onClick={() => onOpenObs(r.id)}
                    className="text-[11px] px-2 py-0.5 rounded font-semibold"
                    style={{ color: ORANGE, border: `1px solid ${ORANGE}55`, background: `${ORANGE}14` }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm opacity-50">
                  No observations match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Stacked Card View (Block layout mirroring NCR Dashboard) */}
      <div className="sm:hidden flex flex-col divide-y" style={{ borderColor }}>
        {pageRows.map((r) => {
          const normStatus = String(r.status || "").toLowerCase();
          const borderClass =
            normStatus.includes("pending")
              ? "border-l-rose-500"
              : normStatus.includes("progress")
              ? "border-l-amber-500"
              : normStatus.includes("resolved")
              ? "border-l-emerald-500"
              : "border-l-slate-400";

          return (
            <div
              key={r.id}
              onClick={() => onOpenObs(r.id)}
              className={`p-4 cursor-pointer transition-colors duration-150 border-l-4 ${borderClass}`}
              style={{
                background: cardColor,
                borderBottom: `1px solid ${borderColor}`,
              }}
            >
              <div className="flex justify-between items-start mb-2 gap-2">
                <span className="font-mono font-bold text-base" style={{ color: ORANGE }}>
                  {r.id}
                </span>
                <QcStatusBadge status={r.status} />
              </div>

              <div className="text-sm font-medium mb-1.5 flex items-center gap-1.5 flex-wrap" style={{ color: textColor }}>
                <span className="font-bold">{r.project}</span>
                <span className="opacity-40">/</span>
                <span className="opacity-80">{r.location}</span>
              </div>

              <div className="text-xs mb-2 flex items-start gap-1.5" style={{ color: textColor }}>
                <span className="opacity-50 shrink-0">Assignee:</span>
                <span className="font-semibold opacity-90 truncate">{r.assignee || "—"}</span>
              </div>

              <div className="flex justify-between items-center text-xs opacity-70">
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: `${ORANGE}18`, color: ORANGE, border: `1px solid ${ORANGE}44` }}
                >
                  {r.items} {r.items === 1 ? "Item" : "Items"}
                </span>
                <span>Created: {r.createdDate}</span>
              </div>

              <div
                className="mt-3 pt-2.5 flex items-center justify-end"
                style={{ borderTop: `1px solid ${borderColor}` }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => onOpenObs(r.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition-transform active:scale-95"
                  style={{
                    background: `${ORANGE}1a`,
                    color: ORANGE,
                    border: `1px solid ${ORANGE}55`,
                  }}
                >
                  <span>👁</span>
                  <span>View Report</span>
                </button>
              </div>
            </div>
          );
        })}

        {pageRows.length === 0 && (
          <div className="p-8 text-center text-sm opacity-50">
            No observations match the current filters.
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 px-3 py-2.5 text-xs opacity-75" style={{ borderTop: `1px solid ${borderColor}` }}>
        <span className="text-center sm:text-left">
          Showing {records.length === 0 ? 0 : safePage * QC_PAGE_SIZE + 1}–
          {Math.min(records.length, safePage * QC_PAGE_SIZE + QC_PAGE_SIZE)} of {records.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: ORANGE }}
          >
            ← Prev
          </button>
          <span>
            Page {safePage + 1} / {pageCount}
          </span>
          <button
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: ORANGE }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

function qcStatusBreakdown(records) {
  const base = { Pending: 0, "In Progress": 0, Resolved: 0, Closed: 0 };
  records.forEach((r) => { if (base[r.status] !== undefined) base[r.status] += 1; });
  return base;
}
function qcCountBy(records, keyFn) {
  const map = {};
  records.forEach((r) => {
    const k = keyFn(r);
    map[k] = (map[k] || 0) + 1;
  });
  return map;
}
function qcDateRange(records) {
  if (records.length === 0) return null;
  const sorted = [...records].sort((a, b) => parseQcDate(a.createdDate) - parseQcDate(b.createdDate));
  return { oldest: sorted[0], newest: sorted[sorted.length - 1] };
}
function qcTopEntries(map, n = 5) {
  return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, n);
}

function buildQcFrame(kind, key, baseRecords) {
  let records = baseRecords;
  let title = "";

  if (kind === "status") {
    records = baseRecords.filter((r) => r.status === key);
    title = `Status: ${key}`;
  } else if (kind === "project") {
    records = baseRecords.filter((r) => r.project === key);
    title = `Project: ${key}`;
  } else if (kind === "location") {
    records = baseRecords.filter((r) => r.location === key);
    title = `Location: ${key}`;
  } else if (kind === "assignee") {
    records = baseRecords.filter((r) => r.assignee === key);
    title = `Assignee: ${key}`;
  } else if (kind === "month") {
    records = baseRecords.filter((r) => qcMonthKeyOf(r.createdDate) === key);
    title = `Month: ${key}`;
  } else if (kind === "project-status") {
    records = baseRecords.filter((r) => r.project === key.project && r.status === key.status);
    title = `${key.project} — ${key.status}`;
  } else if (kind === "observation") {
    records = baseRecords.filter((r) => r.id === key);
    title = String(key);
  }

  return { kind, key, records, title };
}

function QcDrillSummary({ frame, onExplore, onDrillFurther }) {
  const { records, title, kind } = frame;
  const byStatus = qcStatusBreakdown(records);
  const byProject = qcCountBy(records, (r) => r.project);
  const byLocation = qcCountBy(records, (r) => r.location);
  const byAssignee = qcCountBy(records, (r) => r.assignee);
  const range = qcDateRange(records);
  const topProjects = qcTopEntries(byProject, 6);
  const topLocations = qcTopEntries(byLocation, 4);
  const topAssignees = qcTopEntries(byAssignee, 4);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base sm:text-lg font-bold" style={{ color: ORANGE }}>{title}</h3>
        <p className="text-xs opacity-70 mt-1">
          {records.length} observation{records.length === 1 ? "" : "s"} in this selection
        </p>
      </div>

      {records.length === 0 ? (
        <p className="text-sm opacity-50 py-10 text-center">No observations match this selection.</p>
      ) : (
        <>
          {kind !== "status" && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Status Breakdown (Click to Drill)</p>
              <div className="grid grid-cols-2 gap-2">
                {QC_STATUS_ORDER.map((s) => (
                  <button
                    key={s}
                    onClick={() => onDrillFurther("status", s)}
                    className="text-left rounded-lg p-2.5 border hover:opacity-90 hover:scale-[1.01] transition-all"
                    style={{ border: `1px solid ${QC_STATUS_COLORS[s]}55`, background: `${QC_STATUS_COLORS[s]}12` }}
                  >
                    <p className="text-[10px] uppercase opacity-60 font-semibold">{s}</p>
                    <p className="text-lg font-bold" style={{ color: QC_STATUS_COLORS[s] }}>{byStatus[s]}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {kind !== "project" && topProjects.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Projects Breakdown (Click to Drill)</p>
              <div className="flex flex-wrap gap-1.5">
                {topProjects.map(([p, c]) => (
                  <button
                    key={p}
                    onClick={() => onDrillFurther("project", p)}
                    className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                    style={{ borderColor: `${ORANGE}77`, background: `${ORANGE}12`, color: ORANGE }}
                  >
                    <span className="font-medium">{p}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-amber-500/20">{c}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {kind !== "location" && topLocations.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Top Locations (Click to Drill)</p>
              <div className="flex flex-wrap gap-1.5">
                {topLocations.map(([loc, c]) => (
                  <button
                    key={loc}
                    onClick={() => onDrillFurther("location", loc)}
                    className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                    style={{ borderColor: "#00cfe877", background: "#00cfe812", color: "#00cfe8" }}
                  >
                    <span className="font-medium truncate max-w-[140px]">{loc}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-cyan-500/20">{c}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {kind !== "assignee" && topAssignees.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Top Assignees (Click to Drill)</p>
              <div className="flex flex-wrap gap-1.5">
                {topAssignees.map(([a, c]) => (
                  <button
                    key={a}
                    onClick={() => onDrillFurther("assignee", a)}
                    className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                    style={{ borderColor: "#6c9bff77", background: "#6c9bff12", color: "#6c9bff" }}
                  >
                    <span className="font-medium truncate max-w-[130px]">{a}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-blue-500/20">{c}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {range && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1">Created Date Range</p>
              <p className="text-xs opacity-80">{range.oldest.createdDate} – {range.newest.createdDate}</p>
            </div>
          )}

          <button
            onClick={onExplore}
            className="w-full text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-lg shadow hover:opacity-90 transition-all flex items-center justify-center gap-2"
            style={{ background: ORANGE, color: "#1a1a1a" }}
          >
            <span>Explore All {records.length} Observation{records.length === 1 ? "" : "s"}</span>
            <span>→</span>
          </button>
        </>
      )}
    </div>
  );
}

function QcDrillRecordsList({ frame, cardColor, theme, onOpenObs }) {
  const [search, setSearch] = useState("");
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const records = useMemo(() => {
    if (!search.trim()) return frame.records;
    const s = search.toLowerCase();
    return frame.records.filter(
      (r) =>
        (r.id || "").toLowerCase().includes(s) ||
        (r.project || "").toLowerCase().includes(s) ||
        (r.location || "").toLowerCase().includes(s) ||
        (r.assignee || "").toLowerCase().includes(s) ||
        (r.status || "").toLowerCase().includes(s)
    );
  }, [frame.records, search]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold" style={{ color: ORANGE }}>{frame.title} — Records ({records.length})</h3>
        <input
          type="text"
          placeholder="Filter observations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-xs rounded px-2.5 py-1 w-full sm:w-44 border"
          style={{
            background: theme === "dark" ? "#17171d" : "#fff",
            borderColor: `${ORANGE}66`,
            color: "inherit",
          }}
        />
      </div>
      <div className="rounded-lg overflow-hidden border" style={{ borderColor: `${ORANGE}55` }}>
        <div className="max-h-[460px] overflow-y-auto">
          {/* Desktop Table */}
          <div className="hidden sm:block">
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ background: cardColor }}>
                <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                  {["ID", "Project", "Location", "Items", "Status", "Assignee", "Created"].map((h) => (
                    <th
                      key={h}
                      className={`px-2 py-2 text-[10px] uppercase tracking-wide opacity-60 whitespace-nowrap ${
                        h === "Items" ? "text-center" : "text-left"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => onOpenObs(r.id)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ borderBottom: `1px solid ${borderColor}` }}
                  >
                    <td className="px-2 py-2 font-semibold whitespace-nowrap" style={{ color: ORANGE }}>{r.id}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{r.project}</td>
                    <td className="px-2 py-2 max-w-[200px] truncate" title={r.location}>{r.location}</td>
                    <td className="px-2 py-2 text-center font-bold text-xs">{r.items}</td>
                    <td className="px-2 py-2 whitespace-nowrap"><QcStatusBadge status={r.status} /></td>
                    <td className="px-2 py-2 max-w-[140px] truncate" title={r.assignee}>{r.assignee}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs">{r.createdDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card List */}
          <div className="sm:hidden divide-y" style={{ borderColor }}>
            {records.map((r) => (
              <div
                key={r.id}
                onClick={() => onOpenObs(r.id)}
                className="p-3 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono font-bold text-xs" style={{ color: ORANGE }}>{r.id}</span>
                  <QcStatusBadge status={r.status} />
                </div>
                <p className="text-xs font-medium truncate">{r.project} • {r.location}</p>
                <div className="flex items-center justify-between text-[11px] opacity-60 mt-1">
                  <span>Assignee: {r.assignee || "—"}</span>
                  <span>{r.items} Items</span>
                </div>
              </div>
            ))}
          </div>

          {records.length === 0 && (
            <p className="text-xs opacity-50 py-8 text-center">No matching observations found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function QcDrillObservation({ obs, theme }) {
  if (!obs) return <p className="text-sm opacity-50 py-10 text-center">No observations match the current filters.</p>;
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const fields = [
    ["Project", obs.project],
    ["Location", obs.location],
    ["Status", null],
    ["Assignee", obs.assignee],
    ["Created", obs.createdDate],
  ];
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold" style={{ color: ORANGE }}>{obs.id}</h3>
      <div className="rounded-lg p-4 space-y-3" style={{ border: `1px solid ${ORANGE}66` }}>
        {fields.map(([label, value]) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4"
            style={{ borderBottom: `1px solid ${borderColor}`, paddingBottom: 8 }}
          >
            <span className="text-xs uppercase tracking-wide opacity-60 shrink-0">{label}</span>
            {label === "Status" ? <QcStatusBadge status={obs.status} /> : <span className="text-sm text-right">{value}</span>}
          </div>
        ))}
      </div>

      <div className="rounded-lg p-4" style={{ border: `1px solid ${ORANGE}66` }}>
        <p className="text-xs uppercase tracking-wide opacity-60 mb-2 text-center">
          Items ({obs.items})
        </p>
        {Array.isArray(obs.itemNames) && obs.itemNames.length > 0 ? (
          <ol className="space-y-2 text-sm">
            {obs.itemNames.map((name, idx) => (
              <li key={idx} className="flex items-start gap-2 text-left">
                <span className="text-xs opacity-50 shrink-0 w-4 text-right">{idx + 1}.</span>
                <span className="leading-snug">{name}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-center opacity-50">No item details available.</p>
        )}
      </div>
    </div>
  );
}

function QcDrillDrawer({ stack, onBack, onClose, onPush, onOpenObs, onSlice, cardColor, textColor, theme }) {
  if (stack.length === 0) return null;
  const top = stack[stack.length - 1];
  const isObsFrame = top.kind === "observation";
  const isRecordsFrame = top.kind === "records";

  const handleDrillFurther = (kind, key) => onPush(buildQcFrame(kind, key, top.records));
  const handleExplore = () => onPush({ kind: "records", key: top.key, records: top.records, title: top.title });

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-full sm:max-w-md overflow-y-auto shadow-2xl p-4 sm:p-5"
        style={{ background: cardColor, color: textColor, paddingTop: (typeof window !== "undefined" && window.innerWidth < 768) ? 68 : 96 }}
      >
        <div className="flex items-center justify-between mb-4 gap-2">
          {/* Clickable Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            {stack.map((f, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="opacity-40">/</span>}
                <button
                  type="button"
                  onClick={() => onSlice && onSlice(i)}
                  disabled={i === stack.length - 1}
                  className={`font-semibold transition-all ${
                    i === stack.length - 1
                      ? "cursor-default"
                      : "opacity-60 hover:opacity-100 hover:underline"
                  }`}
                  style={{ color: i === stack.length - 1 ? ORANGE : undefined }}
                >
                  {f.kind === "observation" ? f.key.id : f.title}
                </button>
              </React.Fragment>
            ))}
          </div>
          <button onClick={onClose} className="text-lg opacity-60 hover:opacity-100 p-1 shrink-0" title="Close">✕</button>
        </div>

        {stack.length > 1 && (
          <button
            onClick={onBack}
            className="text-xs mb-4 px-3 py-1 rounded-full border hover:opacity-100 transition-opacity flex items-center gap-1"
            style={{ borderColor: ORANGE, color: ORANGE, background: `${ORANGE}10` }}
          >
            <span>←</span>
            <span>Back</span>
          </button>
        )}

        {isObsFrame && <QcDrillObservation obs={top.key} theme={theme} />}

        {isRecordsFrame && (
          <QcDrillRecordsList frame={top} cardColor={cardColor} theme={theme} onOpenObs={onOpenObs} />
        )}

        {!isObsFrame && !isRecordsFrame && (
          <QcDrillSummary frame={top} onExplore={handleExplore} onDrillFurther={handleDrillFurther} />
        )}
      </div>
    </div>
  );
}

// Note: getActiveDashboardProjectName is defined at the top of this module for shared usage.

/* ------------------------------------------------------------------ */
/*  Full QC Observation dashboard.                                     */
/*                                                                       */
/*  ✅ FIX — data ingestion now matches the CONFIRMED real API shape    */
/*  from GET /checklists/observations/:                                 */
/*    - status:   r.overall_status ("pending"/"in_progress"/            */
/*                "resolved"/"closed") -> normalizeQcStatus()           */
/*    - assignee: derived from r.items[].current_actor_name / .assignments[] */
/*                via resolveQcAssigneeFromItems() — there is no flat    */
/*                assignee field anywhere on the real payload            */
/*    - itemNames: ✅ NEW — derived from r.items[] via                   */
/*                resolveQcItemNames(), preserving array order so the    */
/*                Items drawer/tooltip shows a proper sequence           */
/*    - location: r.tower_name + r.floor_name, falling back to           */
/*                r.custom_location                                      */
/*    - items:    r.item_count                                           */
/*    - id:       OBS-#### built from r.id                               */
/*    - createdDate: parsed from r.created_at (ISO datetime)             */
/*                                                                        */
/*  ✅ FIX — project scoping. The old filter let ANY record with an      */
/*  unresolved projectId through (`rec.projectId === null || ...`).      */
/*  Since the API never actually populates a flat r.project field, that  */
/*  matched literally every record, which is why every project's QC     */
/*  data leaked into every other project's dashboard. Now: match by id   */
/*  when one is resolvable, fall back to matching project_name against   */
/*  the active project's name (reliable — it's what exposed the leak in  */
/*  the first place), and exclude anything that matches neither instead  */
/*  of showing it.                                                       */
/* ------------------------------------------------------------------ */
function QcDashboard({
  theme,
  cardColor,
  textColor,
  onExportContext,
  selectedProjectId,
  selectedProjectName,
  onSelectProject,
}) {
  const activeProjectId = selectedProjectId || (selectedProjectName === "ALL" ? "" : getActiveDashboardProjectId());
  const activeProjectName = selectedProjectName || (activeProjectId ? getActiveDashboardProjectName(activeProjectId) : "ALL");
  const [liveQcRecords, setLiveQcRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    status: "ALL",
    project: selectedProjectName || "ALL",
    location: "ALL",
    assignee: "ALL",
    month: "ALL",
    search: "",
  });
  const [drillStack, setDrillStack] = useState([]);

  // Sync internal project filter whenever parent selected project changes
  useEffect(() => {
    if (selectedProjectName) {
      setFilters((prev) => ({ ...prev, project: selectedProjectName }));
    } else if (!selectedProjectId) {
      setFilters((prev) => ({ ...prev, project: "ALL" }));
    }
  }, [selectedProjectId, selectedProjectName]);

  const handleProjectFilterChange = (newProjName) => {
    setFilters((prev) => ({ ...prev, project: newProjName }));
    if (onSelectProject) {
      onSelectProject(null, newProjName);
    }
  };

  useEffect(() => {
    let isMounted = true;
    async function loadLiveQCObservations() {
      setIsLoading(true);
      try {
        const query = selectedProjectId ? { project_id: selectedProjectId, project: selectedProjectId } : {};
        const res = await fetchQCObservations(query);
        // GET /checklists/observations/ returns a flat array of
        // observations directly — handle both an axios-style {data: [...]}
        // wrapper and a raw array, in case the client changes shape later.
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : (res?.data?.results || res?.data?.data || []);

        if (Array.isArray(list) && list.length > 0) {
          const parsed = list.map((r) => {
            const status = normalizeQcStatus(r.overall_status);

            const dateObj = r.created_at ? new Date(r.created_at) : null;
            const createdDate = dateObj
              ? `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`
              : "—";

            const towerPart = r.tower_name || "";
            const floorPart = r.floor_name || "";
            const location =
              (towerPart && floorPart ? `${towerPart} · ${floorPart}` : towerPart || floorPart) ||
              r.custom_location ||
              "—";

            const items = Array.isArray(r.items) ? r.items : [];
            const assignee = resolveQcAssigneeFromItems(items, status);
            const itemNames = resolveQcItemNames(items); // ordered item names

            // ✅ FIX — try every plausible field name for the observation's
            // owning project, the same defensive way MIR resolves its
            // project id, instead of assuming a flat `r.project` exists.
            const projectId =
              r.project !== undefined && r.project !== null
                ? String(r.project)
                : r.project_id !== undefined && r.project_id !== null
                ? String(r.project_id)
                : r.projectId !== undefined && r.projectId !== null
                ? String(r.projectId)
                : r.tower?.project !== undefined && r.tower?.project !== null
                ? String(r.tower.project)
                : r.tower?.project_id !== undefined && r.tower?.project_id !== null
                ? String(r.tower.project_id)
                : null;

            const rawPname =
              r.project_name ||
              r.projectName ||
              r.project_title ||
              (typeof r.project === "string" && isNaN(Number(r.project)) && !/^project\s*#?\s*\d+$/i.test(r.project) && !r.project.includes("/") ? r.project : null);
            const resolvedPname =
              (rawPname && !/^project\s*#?\s*\d+$/i.test(rawPname) && !rawPname.includes("/"))
                ? rawPname
                : (projectId && getActiveDashboardProjectName(projectId)) || "Unnamed Project";

            return {
              id: `OBS-${String(r.id).padStart(4, "0")}`,
              project: resolvedPname,
              projectId,
              location,
              items: Number(r.item_count ?? items.length ?? 1),
              itemNames, // ✅ NEW — sequential item names, used by table tooltip + drawer
              status,
              assignee,
              createdDate,
              rawId: r.id,
            };
          });

          if (isMounted) setLiveQcRecords(parsed);
        } else {
          if (isMounted) setLiveQcRecords([]);
        }
      } catch (err) {
        console.warn("Failed to load live QC observations:", err);
        if (isMounted) setLiveQcRecords([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadLiveQCObservations();
    return () => {
      isMounted = false;
    };
  }, [selectedProjectId, activeProjectId, activeProjectName]);

  const options = useMemo(() => {
    const statuses = QC_STATUS_ORDER.filter((s) => liveQcRecords.some((r) => r.status === s));
    const projectsSet = new Set(
      liveQcRecords
        .map((r) => r.project)
        .filter((name) => name && name !== "Unnamed Project" && !/^project\s*#?\s*\d+$/i.test(name) && !name.includes("/"))
    );
    const accessible = getAccessibleProjects();
    accessible.forEach((p) => {
      if (p.name && !/^project\s*#?\s*\d+$/i.test(p.name) && !p.name.includes("/")) {
        projectsSet.add(p.name);
      }
    });
    if (
      filters.project &&
      filters.project !== "ALL" &&
      filters.project.trim() &&
      !/^project\s*#?\s*\d+$/i.test(filters.project.trim()) &&
      !filters.project.includes("/")
    ) {
      projectsSet.add(filters.project.trim());
    }
    const projects = Array.from(projectsSet).filter(Boolean).sort();
    const locations = Array.from(new Set(liveQcRecords.map((r) => r.location))).sort();
    const assignees = Array.from(new Set(liveQcRecords.map((r) => r.assignee))).sort();
    const months = Array.from(new Set(liveQcRecords.map((r) => qcMonthKeyOf(r.createdDate)))).sort((a, b) => {
      const recA = liveQcRecords.find((r) => qcMonthKeyOf(r.createdDate) === a);
      const recB = liveQcRecords.find((r) => qcMonthKeyOf(r.createdDate) === b);
      if (!recA || !recB) return 0;
      const da = (recA.createdDate || "").split("/");
      const db = (recB.createdDate || "").split("/");
      return parseQcDate(`1/${da[1] || 1}/${da[2] || 2026}`) - parseQcDate(`1/${db[1] || 1}/${db[2] || 2026}`);
    });
    return { statuses, projects, locations, assignees, months };
  }, [liveQcRecords, filters.project]);

  const filteredRecords = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return liveQcRecords.filter((r) => {
      if (filters.status !== "ALL" && r.status !== filters.status) return false;
      if (filters.project !== "ALL") {
        if (!isProjectMatch(r, selectedProjectId, filters.project)) return false;
      }
      if (filters.location !== "ALL" && r.location !== filters.location) return false;
      if (filters.assignee !== "ALL" && r.assignee !== filters.assignee) return false;
      if (filters.month !== "ALL" && qcMonthKeyOf(r.createdDate) !== filters.month) return false;
      if (q) {
        const haystack = `${r.id} ${r.project} ${r.location} ${r.assignee}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [liveQcRecords, filters, selectedProjectId]);

  useEffect(() => {
    if (onExportContext) {
      onExportContext({
        filters,
        selectedProject: filters.project,
        liveQcRecords,
        filteredRecords,
        activeProjectId: selectedProjectId || activeProjectId,
        activeProjectName: selectedProjectName || activeProjectName,
      });
    }
  }, [filters, liveQcRecords, filteredRecords, selectedProjectId, selectedProjectName, activeProjectId, activeProjectName, onExportContext]);

  const openDrill = (kind, key) => setDrillStack([buildQcFrame(kind, key, filteredRecords)]);
  const pushDrill = (frame) => setDrillStack((s) => [...s, frame]);
  const popDrill = () => setDrillStack((s) => s.slice(0, -1));
  const closeDrill = () => setDrillStack([]);

  const openObservation = (obsId) => {
    const obs = liveQcRecords.find((r) => r.id === obsId);
    if (obs) pushDrill({ kind: "observation", key: obs, records: [obs], title: obs.id });
  };

  return (
    <div className="space-y-4" id="active-dashboard-charts-container">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wider opacity-70">
          QC Observations Dashboard
        </h2>
        {isLoading && (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-500 animate-pulse font-mono">
            🔄 Loading live QC Observations...
          </span>
        )}
      </div>

      <QcFilters
        filters={filters}
        setFilters={setFilters}
        options={options}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
        onProjectChange={handleProjectFilterChange}
      />

      <QcSummaryCards
        records={filteredRecords}
        cardColor={cardColor}
        textColor={textColor}
        onSelectStatus={(s) => openDrill("status", s)}
      />

      {filteredRecords.length === 0 && !isLoading ? (
        <div className="rounded-xl p-10 text-center border shadow-sm" style={{ background: cardColor, borderColor: `${ORANGE}44` }}>
          <div className="text-3xl mb-2">🔍</div>
          <p className="text-sm font-bold opacity-80">No QC Observations Found</p>
          <p className="text-xs opacity-50 mt-1">There are currently 0 QC observation records found for this project scope.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Status distribution</p>
              <p className="text-[11px] opacity-50 mb-2">Share of observations by workflow status — Pending, In Progress, Resolved, Closed.</p>
              <QcStatusChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onDrill={openDrill} />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Observations by project</p>
              <p className="text-[11px] opacity-50 mb-2">Total QC observations logged per project.</p>
              <QcProjectChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
          </div>

          <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
            <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Project × status breakdown</p>
            <p className="text-[11px] opacity-50 mb-2">How each project's observations split across statuses.</p>
            <QcProjectStatusChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Observations by location</p>
              <p className="text-[11px] opacity-50 mb-2">Where observations are concentrated on site — top 12 locations shown.</p>
              <QcLocationChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Observations by assignee</p>
              <p className="text-[11px] opacity-50 mb-2">Workload distribution across the top 10 assignees.</p>
              <QcAssigneeChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
          </div>

          <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
            <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Observations created over time</p>
            <p className="text-[11px] opacity-50 mb-2">Monthly trend of newly logged QC observations.</p>
            <QcTrendChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
          </div>

          <QcTable records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onOpenObs={openObservation} />
        </>
      )}

      <QcDrillDrawer
        stack={drillStack}
        onBack={popDrill}
        onClose={closeDrill}
        onPush={pushDrill}
        onOpenObs={openObservation}
        onSlice={(idx) => setDrillStack((s) => s.slice(0, idx + 1))}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
      />
    </div>
  );
}

/* ==================================================================== */
/*  ✅ REAL NCR (NON-CONFORMITY REPORT) DATASET                          */
/*  Source of truth: the actual NCR export (22 records). Every card,     */
/*  chart, filter option, table row and drill-down in the NCR dashboard  */
/*  is derived from this single array — nothing fabricated.              */
/*  Shape per row: [ncrNo, location, cls, targetDate, status, createdDate]*/
/*  `location` is the raw "Project / Tower / Location" string; it is     */
/*  parsed into project / tower / place below and never overwritten.     */
/* ==================================================================== */
/* ==================================================================== */
/*  ✅ REAL NCR (NON-CONFORMITY REPORT) DATASET                          */
/*  Source of truth: the actual NCR export (22 records). Every card,     */
/*  chart, filter option, table row and drill-down in the NCR dashboard  */
/*  is derived from this single array — nothing fabricated.              */
/*  Shape per row: [ncrNo, location, cls, targetDate, status, createdDate]*/
/*  `location` is the raw "Project / Tower / Location" string; it is     */
/*  parsed into project / tower / place below and never overwritten.     */
/* ==================================================================== */
/* ==================================================================== */
/*  ✅ REAL NCR (NON-CONFORMITY REPORT) DATASET                          */
/*  Source of truth: the actual NCR export (22 records). Every card,     */
/*  chart, filter option, table row and drill-down in the NCR dashboard  */
/*  is derived from this single array — nothing fabricated.              */
/*  Shape per row: [ncrNo, location, cls, targetDate, status, createdDate]*/
/*  `location` is the raw "Project / Tower / Location" string; it is     */
/*  parsed into project / tower / place below and never overwritten.     */
/* ==================================================================== */
  const NCR_STATUS_COLORS = {
  assigned_to_maker: "#00cfe8",      // Electric Cyan
  "Assigned to Maker": "#00cfe8",
  resubmission_required: "#ff4d6d",  // Vibrant Crimson
  "Resubmission Required": "#ff4d6d",
  maker_submitted: "#ff9f43",        // Bright Amber
  "Maker Submitted": "#ff9f43",
  Closed: "#10b981",                 // Bright Emerald
  closed: "#10b981",
};
const NCR_STATUS_ORDER = ["assigned_to_maker", "resubmission_required", "maker_submitted", "Closed"];
const NCR_CLASS_COLORS = { major: "#ef4444", minor: "#00cfe8" };
const NCR_CLASS_ORDER = ["major", "minor"];

function formatNcrText(val) {
  if (!val || typeof val !== "string") return val || "";
  if (!val.includes("_")) return val;
  return val
    .replace(/_/g, " ")
    .split(/\s+/)
    .map((word, idx) => {
      const wLower = word.toLowerCase();
      if (wLower === "ph" || wLower === "qc" || wLower === "ncr") {
        return wLower.toUpperCase();
      }
      if (idx > 0 && ["to", "of", "in", "for", "and", "the", "by", "on", "at"].includes(wLower)) {
        return wLower;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

function getNcrStatusColor(status) {
  if (!status) return "#999";
  const s = String(status).trim();
  return (
    NCR_STATUS_COLORS[s] ||
    NCR_STATUS_COLORS[s.toLowerCase()] ||
    NCR_STATUS_COLORS[s.toLowerCase().replace(/\s+/g, "_")] ||
    NCR_STATUS_COLORS[formatNcrText(s)] ||
    "#999"
  );
}

// Dashboard-level workflow buckets (separate taxonomy from the raw status
// field above — see rule in NcrSummaryCards). Colors only; counts computed.
const NCR_METRIC_COLORS = {
  Draft: "#94a3b8",
  "Pending PH Approval": "#a855f7",
  "In Progress": "#00cfe8",
  "Pending Verification": "#ff9f43",
  Closed: "#10b981",
  Rejected: "#ef4444",
  Overdue: "#ff4d6d",
};

// ✅ FIX — the live API returns status as lowercase snake_case, including
// "closed" (lowercase), while every constant in this file — NCR_STATUS_COLORS,
// NCR_STATUS_ORDER, and the "Closed" checks in ncrIsOverdue / buildNcrFrame /
// NcrSummaryCards — key off the capitalized "Closed". Left unmapped, closed
// NCRs rendered with no color (fell back to "#999"), vanished from the
// status pie chart's Closed slice, and — worse — ncrIsOverdue's
// `r.status === "Closed"` check silently failed, so closed NCRs could still
// get counted as overdue. This normalizes once, at ingest, so every
// downstream comparison in the file keeps working unchanged.
function normalizeNcrStatus(rawStatus) {
  const s = String(rawStatus || "").trim();
  return s.toLowerCase() === "closed" ? "Closed" : s || "assigned_to_maker";
}

const NCR_TUPLES = [
  ["123456", "KT-3 / Tower 1 / fhxyf yrhf", "major", "24/08/2026", "assigned_to_maker", "24/08/2026"],
  ["098098", "KT-3 / Tower 1 / fyx", "major", "22/08/2026", "resubmission_required", "22/08/2026"],
  ["123120", "KT-3 / Snagging Test / hsudye hejs", "major", "21/08/2026", "Closed", "21/08/2026"],
  ["556677", "project 1 / Tower 2 / andheri west", "major", "21/08/2026", "assigned_to_maker", "21/08/2026"],
  ["123123", "project 1 / Tower 1 / andheri", "major", "21/08/2026", "Closed", "21/08/2026"],
  ["88793", "project 1 / Tower 1 / andheri", "major", "21/08/2026", "assigned_to_maker", "20/08/2026"],
  ["vtcucuf", "project 1 / Tower 2 / ucvicjccuc", "major", "20/08/2026", "assigned_to_maker", "20/08/2026"],
  ["1234", "project 1 / Tower 1 / andheri", "major", "21/08/2026", "assigned_to_maker", "20/08/2026"],
  ["tcyctxxx", "project 1 / Tower 1 / vgfgfhfh", "major", "20/08/2026", "assigned_to_maker", "20/08/2026"],
  ["ncrno0501", "Test Project / Tower A / andheri", "major", "30/07/2026", "Closed", "29/07/2026"],
  ["2027", "KT-3 / Tower 1 / 3 floor", "major", "06/08/2026", "Closed", "29/07/2026"],
  ["ncr2026", "Test Project / Tower A / andheri", "major", "30/07/2026", "Closed", "29/07/2026"],
  ["tgy433", "Test Project / Tower A / ggy", "minor", "30/07/2026", "maker_submitted", "29/07/2026"],
  ["ncr/qc/19", "KT-3 / Tower 1 / test location 11", "major", "04/08/2026", "maker_submitted", "28/07/2026"],
  ["NCR/QC/9", "project 1 / Tower 1", "major", "20/07/2026", "assigned_to_maker", "18/07/2026"],
  ["NCR/QC/8", "Test Project / Tower A", "major", "28/07/2026", "maker_submitted", "18/07/2026"],
  ["NCR/QC/6", "project 1 / Tower 1", "minor", "19/07/2026", "assigned_to_maker", "18/07/2026"],
  ["NCR/QC/5", "KT-3 / Tower 1", "major", "24/07/2026", "Closed", "17/07/2026"],
  ["NCR/QC/4", "Test Project / Tower A", "minor", "17/07/2026", "maker_submitted", "16/07/2026"],
  ["NCR/QC/3", "Test Project / Tower A", "minor", "24/07/2026", "assigned_to_maker", "16/07/2026"],
  ["NCR/QC/2", "KT-3 / Tower 1", "minor", "21/07/2026", "Closed", "14/07/2026"],
  ["NCR/QC/1", "KT-3 / Tower 1", "major", "21/07/2026", "assigned_to_maker", "14/07/2026"],
];

// "KT-3 / Tower 1 / fhxyf yrhf" -> { project, tower, place }. Records with
// only 2 parts (no specific location) get place = "—". Never invented.
function parseNcrLocation(loc) {
  const parts = (loc || "").split("/").map((p) => p.trim()).filter(Boolean);
  return {
    project: parts[0] || "—",
    tower: parts[1] || "—",
    place: parts.slice(2).join(" / ") || "—",
  };
}

// "24/08/2026" -> Date, or ISO strings like "2026-08-24" / "2026-08-24T10:00:00Z"
// -> Date. Returns null (never an Invalid Date) when the value is missing,
// a placeholder ("—" / "--" / ""), or genuinely unparseable — so every
// caller can just check truthiness instead of guessing at NaN semantics.
function parseNcrDate(dateStr) {
  if (!dateStr || dateStr === "—" || dateStr === "--") return null;

  if (dateStr.includes("/")) {
    const [d, m, y] = dateStr.split("/").map(Number);
    if (!d || !m || !y) return null;
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  }

  // Fall back to whatever the JS Date constructor can parse — covers
  // "2026-08-24" and full ISO datetimes coming straight from the API.
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

// Derives the "Aug 2026"-style month key straight from parseNcrDate, so it
// inherits the same DD/MM/YYYY + ISO support. Returns null (not a garbage
// "undefined NaN"-style string) when the date can't be parsed — that's
// what lets the Target Date / Created Date filter dropdowns correctly
// drop unparseable records via `.filter(Boolean)` instead of showing a
// bogus "undefined" option built from a failed split.
function ncrMonthKeyOf(dateStr) {
  const date = parseNcrDate(dateStr);
  if (!date) return null;
  return `${QC_MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

// Same idea, but also returns a real Date for the 1st of that month so
// trend charts can sort chronologically without re-splitting the raw
// string a second time.
function ncrMonthInfo(dateStr) {
  const date = parseNcrDate(dateStr);
  if (!date) return null;
  return {
    key: `${QC_MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`,
    sortDate: new Date(date.getFullYear(), date.getMonth(), 1),
  };
}

// Reference anchor for the bundled NCR_TUPLES demo rows only, so their
// overdue math stays reproducible in isolation. Live records never use
// this — see ncrIsOverdue below, which prefers the API's own is_overdue
// flag (computed server-side against the real current date) and otherwise
// falls back to comparing against an actual `new Date()`, not this frozen
// anchor.
const NCR_CURRENT_DATE = new Date(2026, 7, 26);

const NCR_RECORDS = NCR_TUPLES.map((t) => {
  const [ncrNo, location, cls, targetDate, status, createdDate] = t;
  const { project, tower, place } = parseNcrLocation(location);
  return { ncrNo, location, project, tower, place, cls, targetDate, status, createdDate };
});

// ✅ FIX — live records carry `isOverdueFromApi` (mapped from the API's
// `is_overdue`), which is computed server-side against the real current
// date, so that's trusted first. Only the bundled demo records (which have
// no isOverdueFromApi) fall back to date math, and that math now compares
// against real "now" instead of the previous hardcoded NCR_CURRENT_DATE
// anchor, so overdue status doesn't drift out of sync with today.
function ncrIsOverdue(r) {
  if (typeof r.isOverdueFromApi === "boolean") return r.isOverdueFromApi;
  if (r.status === "Closed") return false;
  const targetDate = parseNcrDate(r.targetDate);
  if (!targetDate) return false; // no usable target date -> can't be overdue
  return targetDate < new Date();
}

function NcrStatusBadge({ status }) {
  const color = getNcrStatusColor(status);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: `${color}22`, color }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color, display: "inline-block" }} />
      {formatNcrText(status)}
    </span>
  );
}

function NcrClassBadge({ cls }) {
  const rawCls = String(cls || "").trim().toLowerCase();
  const color = NCR_CLASS_COLORS[rawCls] || NCR_CLASS_COLORS[cls] || "#999";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase"
      style={{ background: `${color}22`, color }}
    >
      {formatNcrText(cls)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Summary cards — Draft / Pending PH Approval / In Progress /         */
/*  Pending Verification / Closed / Rejected / Overdue, all counted     */
/*  live from the (filtered) dataset. These are dashboard-level         */
/*  workflow buckets, a separate taxonomy from the raw `status` field   */
/*  (assigned_to_maker / resubmission_required / maker_submitted /      */
/*  Closed) — never renamed into each other. Closed maps to raw         */
/*  "Closed"; Pending Verification maps to raw "maker_submitted";       */
/*  Overdue is computed from real Target Date + current status; Draft,  */
/*  Pending PH Approval, In Progress and Rejected have no matching raw  */
/*  status in this dataset and are correctly 0 rather than invented.    */
/*  Clicking a card opens its drill-down.                               */
/* ------------------------------------------------------------------ */
function NcrSummaryCards({ records, cardColor, textColor, onSelectMetric }) {
  const counts = useMemo(
    () => ({
      "Pending PH Approval": 0,
      "In Progress": 0,
      "Pending Verification": records.filter((r) => r.status === "maker_submitted").length,
      Closed: records.filter((r) => r.status === "Closed").length,
      Rejected: 0,
      Overdue: records.filter(ncrIsOverdue).length,
    }),
    [records]
  );

  const metricOrder = ["Pending PH Approval", "In Progress", "Pending Verification", "Closed", "Rejected", "Overdue"];

  const cards = [
    { key: "Total", label: "Total NCR", value: records.length, color: textColor, clickable: false },
    ...metricOrder.map((m) => ({ key: m, label: m, value: counts[m], color: NCR_METRIC_COLORS[m], clickable: true })),
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4">
      {cards.map((c, idx) => (
        <button
          key={c.key}
          disabled={!c.clickable}
          onClick={() => c.clickable && onSelectMetric(c.key)}
          className={`text-left rounded-lg p-3 sm:p-4 shadow transition-all ${idx === cards.length - 1 ? "col-span-2 sm:col-span-1" : ""}`}
          style={{ background: cardColor, border: `2px solid ${c.color}`, cursor: c.clickable ? "pointer" : "default" }}
        >
          <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide opacity-60 truncate">{c.label}</p>
          <p className="text-2xl sm:text-3xl font-bold mt-1" style={{ color: c.color }}>
            <CountUpNumber value={c.value} color={c.color} triggerKey={`ncr_${c.key}_${c.value}`} />
          </p>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Status distribution donut — actual raw statuses. Click drills down. */
/* ------------------------------------------------------------------ */
function NcrStatusChart({ records, cardColor, textColor, onDrill }) {
  const data = useMemo(
    () =>
      NCR_STATUS_ORDER.map((s) => ({
        rawStatus: s,
        name: formatNcrText(s),
        value: records.filter((r) => r.status === s).length,
      })).filter((d) => d.value > 0),
    [records]
  );
  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };
  if (data.length === 0) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={3}
          cursor="pointer"
          onClick={(d) => onDrill("status", d.rawStatus || d.name)}
        >
          {data.map((d) => (
            <Cell key={d.name} fill={getNcrStatusColor(d.rawStatus || d.name)} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} NCRs`, n]} />
        <Legend wrapperStyle={{ fontSize: 12, color: textColor }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  Class distribution donut — Major / Minor. Click drills down.        */
/* ------------------------------------------------------------------ */
function NcrClassChart({ records, cardColor, textColor, onDrill }) {
  const data = useMemo(
    () => NCR_CLASS_ORDER.map((c) => ({ name: c, value: records.filter((r) => r.cls === c).length })).filter((d) => d.value > 0),
    [records]
  );
  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };
  if (data.length === 0) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={3}
          cursor="pointer" onClick={(d) => onDrill("class", d.name)}
        >
          {data.map((d) => (<Cell key={d.name} fill={NCR_CLASS_COLORS[d.name]} />))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} NCRs`, n]} />
        <Legend wrapperStyle={{ fontSize: 12, color: textColor }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  Project-wise NCR count. Click drills down.                          */
/* ------------------------------------------------------------------ */
function NcrProjectChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => { map[r.project] = (map[r.project] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [records]);
  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };
  if (data.length === 0) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={12} width={110} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} NCRs`, "Count"]} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d) => onDrill("project", d.name)}>
          {data.map((d, i) => (<Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  Tower-wise NCR count. Click drills down.                            */
/* ------------------------------------------------------------------ */
function NcrTowerChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => { map[r.tower] = (map[r.tower] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [records]);
  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };
  if (data.length === 0) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={12} width={110} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} NCRs`, "Count"]} />
        <Bar dataKey="value" fill="#6c9bff" radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d) => onDrill("tower", d.name)} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  Specific-location-wise NCR count (top 12). Click drills down.       */
/* ------------------------------------------------------------------ */
function NcrLocationChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => { map[r.place] = (map[r.place] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 12);
  }, [records]);
  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };
  if (data.length === 0) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={10} width={140} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} NCRs`, "Count"]} />
        <Bar dataKey="value" fill={ORANGE} radius={[0, 6, 6, 0]} cursor="pointer" onClick={(d) => onDrill("location", d.name)} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  NCRs created over time, grouped by month. Point click drills down.  */
/* ------------------------------------------------------------------ */
function NcrCreatedTrendChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const info = ncrMonthInfo(r.createdDate);
      if (!info) return; // skip records with no usable created date
      if (!map[info.key]) map[info.key] = { key: info.key, name: info.key, value: 0, sortDate: info.sortDate };
      map[info.key].value += 1;
    });
    return Object.values(map).sort((a, b) => a.sortDate - b.sortDate);
  }, [records]);
  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };
  if (data.length === 0) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" stroke={axisColor} fontSize={12} />
        <YAxis stroke={axisColor} fontSize={12} allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} NCRs`, "Created"]} />
        <Line
          type="monotone" dataKey="value" name="NCRs created" stroke={ORANGE} strokeWidth={3}
          dot={{ r: 5, cursor: "pointer" }}
          activeDot={{ r: 7, cursor: "pointer", onClick: (_, p) => onDrill("created-month", p.payload.name) }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  NCRs due, grouped by target-date month. Point click drills down.    */
/* ------------------------------------------------------------------ */
function NcrTargetDateChart({ records, cardColor, textColor, theme, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const info = ncrMonthInfo(r.targetDate);
      if (!info) return; // skip records with no usable target date
      if (!map[info.key]) map[info.key] = { key: info.key, name: info.key, value: 0, sortDate: info.sortDate };
      map[info.key].value += 1;
    });
    return Object.values(map).sort((a, b) => a.sortDate - b.sortDate);
  }, [records]);
  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };
  if (data.length === 0) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="name" stroke={axisColor} fontSize={12} />
        <YAxis stroke={axisColor} fontSize={12} allowDecimals={false} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} NCRs`, "Due"]} />
        <Line
          type="monotone" dataKey="value" name="NCRs due" stroke="#6c9bff" strokeWidth={3}
          dot={{ r: 5, cursor: "pointer" }}
          activeDot={{ r: 7, cursor: "pointer", onClick: (_, p) => onDrill("target-month", p.payload.name) }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  Overdue vs on-track donut, computed from real Target Date + status. */
/*  Clicking the Overdue slice opens the Overdue drill-down.            */
/* ------------------------------------------------------------------ */
function NcrOverdueChart({ records, cardColor, textColor, onDrill }) {
  const data = useMemo(() => {
    const overdue = records.filter(ncrIsOverdue).length;
    return [
      { name: "Overdue", value: overdue },
      { name: "On Track", value: records.length - overdue },
    ].filter((d) => d.value > 0);
  }, [records]);
  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };
  if (data.length === 0) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  const colors = { Overdue: "#ff8fa3", "On Track": "#7fd8a0" };
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={3}
          cursor="pointer" onClick={(d) => d.name === "Overdue" && onDrill("metric", "Overdue")}
        >
          {data.map((d) => (<Cell key={d.name} fill={colors[d.name]} />))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} NCRs`, n]} />
        <Legend wrapperStyle={{ fontSize: 12, color: textColor }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------------------------------------------ */
/*  Filters — options populated from the ACTUAL values present in the  */
/*  NCR dataset (no hardcoded lists). Changing any filter re-derives   */
/*  every card, chart, and table row (and any open drill-down) from    */
/*  the same filtered dataset.                                         */
/* ------------------------------------------------------------------ */
function NcrFilters({ filters, setFilters, options, cardColor, textColor, theme, onProjectChange }) {
  const selectStyle = { background: theme === "dark" ? "#17171d" : "#fff", color: textColor, border: `1px solid ${ORANGE}88` };
  const update = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const reset = () => {
    setFilters({ status: "ALL", cls: "ALL", project: "ALL", tower: "ALL", location: "ALL", checker: "ALL", targetMonth: "ALL", createdMonth: "ALL", search: "" });
    if (onProjectChange) onProjectChange("ALL");
  };

  const hasActiveFilters =
    filters.status !== "ALL" ||
    filters.cls !== "ALL" ||
    filters.project !== "ALL" ||
    filters.tower !== "ALL" ||
    filters.location !== "ALL" ||
    filters.checker !== "ALL" ||
    filters.targetMonth !== "ALL" ||
    filters.createdMonth !== "ALL" ||
    filters.search !== "";

  return (
    <div
      className="rounded-lg p-3 sm:p-4 shadow flex flex-wrap items-end gap-2.5 sm:gap-3"
      style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}
    >
      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Status</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.status} onChange={update("status")}>
          <option value="ALL">All statuses</option>
          {options.statuses.map((s) => (<option key={s} value={s}>{formatNcrText(s)}</option>))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Class</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.cls} onChange={update("cls")}>
          <option value="ALL">All classes</option>
          {options.classes.map((c) => (<option key={c} value={c}>{formatNcrText(c)}</option>))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Project</label>
        <select
          className="text-xs sm:text-sm rounded px-2 py-1.5 w-full"
          style={selectStyle}
          value={filters.project}
          onChange={(e) => {
            const val = e.target.value;
            setFilters((f) => ({ ...f, project: val }));
            if (onProjectChange) onProjectChange(val);
          }}
        >
          <option value="ALL">All projects</option>
          {options.projects.map((p) => (<option key={p} value={p}>{p}</option>))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Tower</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.tower} onChange={update("tower")}>
          <option value="ALL">All towers</option>
          {options.towers.map((t) => (<option key={t} value={t}>{t}</option>))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Location</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.location} onChange={update("location")}>
          <option value="ALL">All locations</option>
          {options.locations.map((l) => (<option key={l} value={l}>{l}</option>))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Checker</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.checker} onChange={update("checker")}>
          <option value="ALL">All checkers</option>
          {(options.checkers || []).map((c) => {
            const val = typeof c === "object" ? c.value : c;
            const lbl = typeof c === "object" ? c.label : c;
            return (
              <option key={val} value={val}>
                {lbl}
              </option>
            );
          })}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Target Date</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.targetMonth} onChange={update("targetMonth")}>
          <option value="ALL">All dates</option>
          {options.targetMonths.map((m) => (<option key={m} value={m}>{m}</option>))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 sm:flex-initial min-w-[130px]">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Created Date</label>
        <select className="text-xs sm:text-sm rounded px-2 py-1.5 w-full" style={selectStyle} value={filters.createdMonth} onChange={update("createdMonth")}>
          <option value="ALL">All dates</option>
          {options.createdMonths.map((m) => (<option key={m} value={m}>{m}</option>))}
        </select>
      </div>

      <div className="flex flex-col gap-1 flex-1 min-w-[140px] sm:min-w-[160px] w-full sm:w-auto">
        <label className="text-[11px] uppercase tracking-wide opacity-60">Search</label>
        <input
          type="text"
          placeholder="NCR #, checker, location…"
          className="text-xs sm:text-sm rounded px-2 py-1.5 w-full"
          style={selectStyle}
          value={filters.search}
          onChange={update("search")}
        />
      </div>

      {hasActiveFilters && (
        <button
          onClick={reset}
          className="text-xs px-3 py-1.5 rounded-full border opacity-80 hover:opacity-100 whitespace-nowrap self-center sm:self-end mt-2 sm:mt-0"
          style={{ borderColor: ORANGE, color: ORANGE }}
        >
          ✕ Clear filters
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  NCR table — actual records, paginated client-side.                  */
/* ------------------------------------------------------------------ */
const NCR_PAGE_SIZE = 12;

function NcrTable({ records, cardColor, textColor, theme, onOpenNcr }) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(records.length / NCR_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = records.slice(safePage * NCR_PAGE_SIZE, safePage * NCR_PAGE_SIZE + NCR_PAGE_SIZE);
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";

  React.useEffect(() => { setPage(0); }, [records.length]);

  return (
    <div className="rounded-lg shadow overflow-hidden" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
      {/* Header strip: All Records & count */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between"
        style={{ borderColor, background: theme === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" }}
      >
        <h2 className="text-sm font-bold flex items-center gap-2" style={{ color: textColor }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: ORANGE }} />
          All Records
        </h2>
        <div className="text-xs font-medium opacity-60">
          {records.length} {records.length === 1 ? "entry" : "entries"} found
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block overflow-x-auto touch-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
              {["NCR No", "Raised By", "Location", "Class", "Target Date", "Status", "Created Date", "Action"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[11px] uppercase tracking-wide opacity-60 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.ncrNo} style={{ borderBottom: `1px solid ${borderColor}` }}>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">{r.ncrNo}</td>
                <td className="px-3 py-2 whitespace-nowrap max-w-[160px] truncate" title={r.raisedBy}>{r.raisedBy || "—"}</td>
                <td className="px-3 py-2 max-w-[260px] truncate" title={r.location}>{r.location}</td>
                <td className="px-3 py-2 whitespace-nowrap"><NcrClassBadge cls={r.cls} /></td>
                <td className="px-3 py-2 whitespace-nowrap">{r.targetDate}</td>
                <td className="px-3 py-2 whitespace-nowrap"><NcrStatusBadge status={r.status} /></td>
                <td className="px-3 py-2 whitespace-nowrap">{r.createdDate}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button
                    onClick={() => onOpenNcr(r.ncrNo)}
                    className="text-[11px] px-2 py-0.5 rounded font-semibold"
                    style={{ color: ORANGE, border: `1px solid ${ORANGE}55`, background: `${ORANGE}14` }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-sm opacity-50">No NCRs match the current filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Stacked Card View (Block layout) */}
      <div className="sm:hidden flex flex-col divide-y" style={{ borderColor }}>
        {pageRows.map((r) => {
          const normStatus = String(r.status || "").toLowerCase();
          const borderClass =
            normStatus.includes("closed")
              ? "border-l-emerald-500"
              : normStatus.includes("maker_submitted")
              ? "border-l-blue-500"
              : normStatus.includes("resubmission")
              ? "border-l-amber-500"
              : "border-l-rose-500";

          return (
            <div
              key={r.ncrNo}
              onClick={() => onOpenNcr(r.ncrNo)}
              className={`p-4 cursor-pointer transition-colors duration-150 border-l-4 ${borderClass}`}
              style={{
                background: cardColor,
                borderBottom: `1px solid ${borderColor}`,
              }}
            >
              <div className="flex justify-between items-start mb-2 gap-2">
                <span className="font-mono font-bold text-base" style={{ color: ORANGE }}>
                  {r.ncrNo}
                </span>
                <NcrStatusBadge status={r.status} />
              </div>

              <div className="text-sm font-medium mb-1" style={{ color: textColor }}>
                {r.location}
              </div>

              <div className="text-xs mb-1.5 opacity-75 truncate" title={r.raisedBy}>
                <span className="opacity-60">Raised By: </span>
                <span className="font-semibold" style={{ color: textColor }}>{r.raisedBy || "—"}</span>
              </div>

              <div className="flex justify-between items-center text-xs opacity-70 mb-1">
                <NcrClassBadge cls={r.cls} />
                <span>Target: {r.targetDate}</span>
              </div>

              <div
                className="mt-3 pt-2.5 flex items-center justify-between text-xs"
                style={{ borderTop: `1px solid ${borderColor}` }}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="opacity-50">Created: {r.createdDate}</span>
                <button
                  type="button"
                  onClick={() => onOpenNcr(r.ncrNo)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition-transform active:scale-95"
                  style={{
                    background: `${ORANGE}1a`,
                    color: ORANGE,
                    border: `1px solid ${ORANGE}55`,
                  }}
                >
                  <span>👁</span>
                  <span>View Report</span>
                </button>
              </div>
            </div>
          );
        })}

        {pageRows.length === 0 && (
          <div className="p-8 text-center text-sm opacity-50">
            No NCRs match the current filters.
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 px-3 py-2.5 text-xs opacity-75" style={{ borderTop: `1px solid ${borderColor}` }}>
        <span className="text-center sm:text-left">
          Showing {records.length === 0 ? 0 : safePage * NCR_PAGE_SIZE + 1}–
          {Math.min(records.length, safePage * NCR_PAGE_SIZE + NCR_PAGE_SIZE)} of {records.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: ORANGE }}
          >
            ← Prev
          </button>
          <span>Page {safePage + 1} / {pageCount}</span>
          <button
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: ORANGE }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== */
/*  NCR DRILL-DOWN SYSTEM                                                */
/*                                                                        */
/*  drillStack: array of frames. Each frame: { kind, key, records, title}*/
/*  kind ∈ status | class | project | tower | location | created-month | */
/*         target-month | metric | records | record                     */
/*                                                                        */
/*  Navigating deeper pushes a frame; "← Back" pops the last one.        */
/*  Every frame's records is derived from the same filtered dataset the  */
/*  charts/cards are showing — the drill-down inherits active dashboard  */
/*  filters instead of resetting them (see NcrDashboard.openDrill).      */
/* ==================================================================== */
function ncrStatusBreakdown(records) {
  const base = { assigned_to_maker: 0, resubmission_required: 0, maker_submitted: 0, Closed: 0 };
  records.forEach((r) => { if (base[r.status] !== undefined) base[r.status] += 1; });
  return base;
}
function ncrClassBreakdown(records) {
  const base = { major: 0, minor: 0 };
  records.forEach((r) => { if (base[r.cls] !== undefined) base[r.cls] += 1; });
  return base;
}
function ncrCountBy(records, keyFn) {
  const map = {};
  records.forEach((r) => { const k = keyFn(r); map[k] = (map[k] || 0) + 1; });
  return map;
}
function ncrTargetRange(records) {
  if (records.length === 0) return null;
  // Records with no usable target date (missing, placeholder, or genuinely
  // unparseable) are simply excluded — parseNcrDate returns null for all
  // three cases, so this is a single clean filter instead of a
  // string-equality check against specific placeholder values.
  const validRecords = records.filter((r) => parseNcrDate(r.targetDate));
  if (validRecords.length === 0) return null;
  const sorted = [...validRecords].sort(
    (a, b) => parseNcrDate(a.targetDate) - parseNcrDate(b.targetDate)
  );
  return { earliest: sorted[0], latest: sorted[sorted.length - 1] };
}
function ncrTopEntries(map, n = 5) {
  return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, n);
}

function buildNcrFrame(kind, key, baseRecords) {
  let records = baseRecords;
  let title = "";

  if (kind === "status") {
    records = baseRecords.filter((r) => r.status === key);
    title = `Status: ${formatNcrText(key)}`;
  } else if (kind === "class") {
    records = baseRecords.filter((r) => r.cls === key);
    title = key === "major" ? "Major NCRs" : "Minor NCRs";
  } else if (kind === "project") {
    records = baseRecords.filter((r) => r.project === key);
    title = `Project: ${key}`;
  } else if (kind === "tower") {
    records = baseRecords.filter((r) => r.tower === key);
    title = `Tower: ${key}`;
  } else if (kind === "location") {
    records = baseRecords.filter((r) => r.place === key);
    title = `Location: ${key}`;
  } else if (kind === "created-month") {
    records = baseRecords.filter((r) => ncrMonthKeyOf(r.createdDate) === key);
    title = `Created: ${key}`;
  } else if (kind === "target-month") {
    records = baseRecords.filter((r) => ncrMonthKeyOf(r.targetDate) === key);
    title = `Due: ${key}`;
  } else if (kind === "project-status") {
    records = baseRecords.filter((r) => r.project === key.project && r.status === key.status);
    title = `${key.project} — ${formatNcrText(key.status)}`;
  } else if (kind === "record") {
    records = baseRecords.filter((r) => r.ncrNo === key);
    title = String(key);
  } else if (kind === "metric") {
    if (key === "Overdue") { records = baseRecords.filter(ncrIsOverdue); title = "Overdue NCRs"; }
    else if (key === "Closed") { records = baseRecords.filter((r) => r.status === "Closed"); title = "Closed NCRs"; }
    else if (key === "Pending Verification") { records = baseRecords.filter((r) => r.status === "maker_submitted"); title = "Pending Verification"; }
    else { records = []; title = key; }
  }

  return { kind, key, records, title };
}

function NcrDrillSummary({ frame, onExplore, onDrillFurther }) {
  const { records, title, kind } = frame;
  const byStatus = ncrStatusBreakdown(records);
  const byClass = ncrClassBreakdown(records);
  const byProject = ncrCountBy(records, (r) => r.project);
  const byTower = ncrCountBy(records, (r) => r.tower);
  const byLocation = ncrCountBy(records, (r) => r.place);
  const topProjects = ncrTopEntries(byProject, 6);
  const topTowers = ncrTopEntries(byTower, 5);
  const topLocations = ncrTopEntries(byLocation, 4);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base sm:text-lg font-bold" style={{ color: ORANGE }}>{title}</h3>
        <p className="text-xs opacity-70 mt-1">
          {records.length} NCR record{records.length === 1 ? "" : "s"} in this selection
        </p>
      </div>

      {records.length === 0 ? (
        <p className="text-sm opacity-50 py-10 text-center">
          {kind === "metric" ? "No NCRs currently fall in this workflow bucket." : "No NCRs match this selection."}
        </p>
      ) : (
        <>
          {/* Class Breakdown */}
          {kind !== "class" && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Class Breakdown (Click to Drill)</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onDrillFurther("class", "major")}
                  className="rounded-lg p-2.5 border text-left hover:opacity-90 hover:scale-[1.01] transition-all"
                  style={{ border: `1px solid ${NCR_CLASS_COLORS.major}55`, background: `${NCR_CLASS_COLORS.major}12` }}
                >
                  <p className="text-[10px] uppercase font-semibold opacity-70">Major</p>
                  <p className="text-lg font-bold" style={{ color: NCR_CLASS_COLORS.major }}>{byClass.major}</p>
                </button>
                <button
                  type="button"
                  onClick={() => onDrillFurther("class", "minor")}
                  className="rounded-lg p-2.5 border text-left hover:opacity-90 hover:scale-[1.01] transition-all"
                  style={{ border: `1px solid ${NCR_CLASS_COLORS.minor}55`, background: `${NCR_CLASS_COLORS.minor}12` }}
                >
                  <p className="text-[10px] uppercase font-semibold opacity-70">Minor</p>
                  <p className="text-lg font-bold" style={{ color: NCR_CLASS_COLORS.minor }}>{byClass.minor}</p>
                </button>
              </div>
            </div>
          )}

          {/* Status Breakdown */}
          {kind !== "status" && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Status Breakdown (Click to Drill)</p>
              <div className="flex flex-wrap gap-1.5">
                {NCR_STATUS_ORDER.filter((s) => byStatus[s] > 0).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onDrillFurther("status", s)}
                    className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                    style={{ borderColor: getNcrStatusColor(s), background: `${getNcrStatusColor(s)}12`, color: getNcrStatusColor(s) }}
                  >
                    <span className="font-medium">{formatNcrText(s)}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-amber-500/20">{byStatus[s]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Project Breakdown */}
          {kind !== "project" && topProjects.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Projects Breakdown (Click to Drill)</p>
              <div className="flex flex-wrap gap-1.5">
                {topProjects.map(([p, c]) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onDrillFurther("project", p)}
                    className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                    style={{ borderColor: `${ORANGE}77`, background: `${ORANGE}12`, color: ORANGE }}
                  >
                    <span className="font-medium">{p}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-amber-500/20">{c}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tower Breakdown */}
          {kind !== "tower" && topTowers.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Towers Breakdown (Click to Drill)</p>
              <div className="flex flex-wrap gap-1.5">
                {topTowers.map(([t, c]) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => onDrillFurther("tower", t)}
                    className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                    style={{ borderColor: "#6c9bff77", background: "#6c9bff12", color: "#6c9bff" }}
                  >
                    <span className="font-medium">{t}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-blue-500/20">{c}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Location Breakdown */}
          {kind !== "location" && topLocations.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase opacity-60 mb-1.5">Locations Breakdown (Click to Drill)</p>
              <div className="flex flex-wrap gap-1.5">
                {topLocations.map(([loc, c]) => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => onDrillFurther("location", loc)}
                    className="text-xs px-2.5 py-1 rounded-lg border hover:opacity-100 opacity-85 transition-all text-left flex items-center gap-1.5"
                    style={{ borderColor: "#00cfe877", background: "#00cfe812", color: "#00cfe8" }}
                  >
                    <span className="font-medium truncate max-w-[140px]">{loc}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-bold bg-cyan-500/20">{c}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={onExplore}
            className="w-full text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-lg shadow hover:opacity-90 transition-all flex items-center justify-center gap-2"
            style={{ background: ORANGE, color: "#1a1a1a" }}
          >
            <span>Explore All {records.length} NCR{records.length === 1 ? "" : "s"}</span>
            <span>→</span>
          </button>
        </>
      )}
    </div>
  );
}

function NcrDrillRecordsList({ frame, cardColor, theme, onOpenNcr }) {
  const [search, setSearch] = useState("");
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const records = useMemo(() => {
    if (!search.trim()) return frame.records;
    const s = search.toLowerCase();
    return frame.records.filter(
      (r) =>
        (r.ncrNo || "").toLowerCase().includes(s) ||
        (r.project || "").toLowerCase().includes(s) ||
        (r.place || "").toLowerCase().includes(s) ||
        (r.cls || "").toLowerCase().includes(s) ||
        formatNcrText(r.cls || "").toLowerCase().includes(s) ||
        (r.status || "").toLowerCase().includes(s) ||
        formatNcrText(r.status || "").toLowerCase().includes(s)
    );
  }, [frame.records, search]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-bold" style={{ color: ORANGE }}>{frame.title} — Records ({records.length})</h3>
        <input
          type="text"
          placeholder="Filter NCRs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-xs rounded px-2.5 py-1 w-full sm:w-44 border"
          style={{
            background: theme === "dark" ? "#17171d" : "#fff",
            borderColor: `${ORANGE}66`,
            color: "inherit",
          }}
        />
      </div>
      <div className="rounded-lg overflow-hidden border" style={{ borderColor: `${ORANGE}55` }}>
        <div className="max-h-[460px] overflow-y-auto">
          {/* Desktop view */}
          <div className="hidden sm:block">
            <table className="w-full text-sm">
              <thead className="sticky top-0" style={{ background: cardColor }}>
                <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                  {["NCR No", "Location", "Class", "Target Date", "Status", "Created"].map((h) => (
                    <th key={h} className="text-left px-2 py-2 text-[10px] uppercase tracking-wide opacity-60 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr
                    key={r.ncrNo}
                    onClick={() => onOpenNcr(r.ncrNo)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ borderBottom: `1px solid ${borderColor}` }}
                  >
                    <td className="px-2 py-2 font-semibold whitespace-nowrap" style={{ color: ORANGE }}>{r.ncrNo}</td>
                    <td className="px-2 py-2 max-w-[160px] truncate" title={r.location}>{r.location}</td>
                    <td className="px-2 py-2 whitespace-nowrap"><NcrClassBadge cls={r.cls} /></td>
                    <td className="px-2 py-2 whitespace-nowrap">{r.targetDate}</td>
                    <td className="px-2 py-2 whitespace-nowrap"><NcrStatusBadge status={r.status} /></td>
                    <td className="px-2 py-2 whitespace-nowrap text-xs">{r.createdDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card List */}
          <div className="sm:hidden divide-y" style={{ borderColor }}>
            {records.map((r) => (
              <div
                key={r.ncrNo}
                onClick={() => onOpenNcr(r.ncrNo)}
                className="p-3 cursor-pointer hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-mono font-bold text-xs" style={{ color: ORANGE }}>{r.ncrNo}</span>
                  <NcrStatusBadge status={r.status} />
                </div>
                <p className="text-xs font-medium truncate">{r.location || r.place}</p>
                <div className="flex items-center justify-between text-[11px] opacity-60 mt-1">
                  <NcrClassBadge cls={r.cls} />
                  <span>Target: {r.targetDate}</span>
                </div>
              </div>
            ))}
          </div>

          {records.length === 0 && (
            <p className="text-xs opacity-50 py-8 text-center">No matching records found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function NcrDrillRecord({ ncr, theme }) {
  if (!ncr) return <p className="text-sm opacity-50 py-10 text-center">No NCRs match the current filters.</p>;
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const fields = [
    ["Project", ncr.project],
    ["Tower", ncr.tower],
    ["Location", ncr.place],
    ["Raised By", ncr.raisedBy || "—"],
    ["Class", null],
    ["Target Date", ncr.targetDate],
    ["Status", null],
    ["Created Date", ncr.createdDate],
    ["Overdue", ncrIsOverdue(ncr) ? "Yes" : "No"],
  ];
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold" style={{ color: ORANGE }}>{ncr.ncrNo}</h3>
      <div className="rounded-lg p-4 space-y-3" style={{ border: `1px solid ${ORANGE}66` }}>
        {fields.map(([label, value]) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4"
            style={{ borderBottom: `1px solid ${borderColor}`, paddingBottom: 8 }}
          >
            <span className="text-xs uppercase tracking-wide opacity-60 shrink-0">{label}</span>
            {label === "Class" ? (
              <NcrClassBadge cls={ncr.cls} />
            ) : label === "Status" ? (
              <NcrStatusBadge status={ncr.status} />
            ) : (
              <span className="text-sm text-right font-medium">{value}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function NcrDrillDrawer({ stack, onBack, onClose, onPush, onOpenNcr, onSlice, cardColor, textColor, theme, allRecords }) {
  if (stack.length === 0) return null;
  const top = stack[stack.length - 1];
  const isRecordFrame = top.kind === "record";
  const isRecordsFrame = top.kind === "records";

  const handleDrillFurther = (kind, key) => onPush(buildNcrFrame(kind, key, top.records));
  const handleExplore = () => onPush({ kind: "records", key: top.key, records: top.records, title: top.title });

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-full sm:max-w-md overflow-y-auto shadow-2xl p-4 sm:p-5"
        style={{ background: cardColor, color: textColor, paddingTop: (typeof window !== "undefined" && window.innerWidth < 768) ? 68 : 96 }}
      >
        <div className="flex items-center justify-between mb-4 gap-2">
          {/* Clickable Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            {stack.map((f, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="opacity-40">/</span>}
                <button
                  type="button"
                  onClick={() => onSlice && onSlice(i)}
                  disabled={i === stack.length - 1}
                  className={`font-semibold transition-all ${
                    i === stack.length - 1
                      ? "cursor-default"
                      : "opacity-60 hover:opacity-100 hover:underline"
                  }`}
                  style={{ color: i === stack.length - 1 ? ORANGE : undefined }}
                >
                  {f.kind === "record" ? (f.key?.ncrNo || f.title) : formatNcrText(f.title)}
                </button>
              </React.Fragment>
            ))}
          </div>
          <button onClick={onClose} className="text-lg opacity-60 hover:opacity-100 p-1 shrink-0" title="Close">✕</button>
        </div>

        {stack.length > 1 && (
          <button
            onClick={onBack}
            className="text-xs mb-4 px-3 py-1 rounded-full border hover:opacity-100 transition-opacity flex items-center gap-1"
            style={{ borderColor: ORANGE, color: ORANGE, background: `${ORANGE}10` }}
          >
            <span>←</span>
            <span>Back</span>
          </button>
        )}

        {isRecordFrame && <NcrDrillRecord ncr={top.key} theme={theme} />}
        {isRecordsFrame && (
          <NcrDrillRecordsList frame={top} cardColor={cardColor} theme={theme} onOpenNcr={onOpenNcr} />
        )}
        {!isRecordFrame && !isRecordsFrame && (
          <NcrDrillSummary frame={top} allRecords={allRecords} onExplore={handleExplore} onDrillFurther={handleDrillFurther} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Full NCR dashboard — this is what opens when the "NCR" card is      */
/*  clicked. One central dataset feeds the summary cards, every chart,  */
/*  the filters, the table, and the drill-down system.                  */
/* ------------------------------------------------------------------ */
function NcrDashboard({
  theme,
  cardColor,
  textColor,
  onExportContext,
  selectedProjectId,
  selectedProjectName,
  onSelectProject,
}) {
  const activeProjectId = selectedProjectId || (selectedProjectName === "ALL" ? "" : getActiveDashboardProjectId());
  const [liveNcrRecords, setLiveNcrRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    status: "ALL",
    cls: "ALL",
    project: selectedProjectName || "ALL",
    tower: "ALL",
    location: "ALL",
    checker: "ALL",
    targetMonth: "ALL",
    createdMonth: "ALL",
    search: "",
  });
  const [drillStack, setDrillStack] = useState([]);

  // Sync internal project filter whenever parent selected project changes
  useEffect(() => {
    if (selectedProjectName) {
      setFilters((prev) => ({ ...prev, project: selectedProjectName }));
    } else if (!selectedProjectId) {
      setFilters((prev) => ({ ...prev, project: "ALL" }));
    }
  }, [selectedProjectId, selectedProjectName]);

  const handleProjectFilterChange = (newProjName) => {
    setFilters((prev) => ({ ...prev, project: newProjName }));
    if (onSelectProject) {
      onSelectProject(null, newProjName);
    }
  };

  // Fetch real NCR data from backend API
  useEffect(() => {
    let isMounted = true;
    async function loadLiveNCRs() {
      setIsLoading(true);
      try {
        const query = selectedProjectId
          ? { project_id: selectedProjectId, project: selectedProjectId, paginate: "false" }
          : { paginate: "false" };
        const res = await getNCRList(query);
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.results || res?.data?.data || []);

        if (Array.isArray(list) && list.length > 0) {
          const parsed = list.map((r, idx) => {
            const raisedDate = r.raised_at ? new Date(r.raised_at) : null;
            const createdDate =
              raisedDate && !isNaN(raisedDate.getTime())
                ? `${String(raisedDate.getDate()).padStart(2, "0")}/${String(raisedDate.getMonth() + 1).padStart(2, "0")}/${raisedDate.getFullYear()}`
                : "—";

            const targetDate = r.target_date_of_compliance || "—";

            const raisedBy =
              (r.raised_by_name || r.raised_by || r.checker_name || r.created_by_name || "").trim() || "—";
            const raisedById = r.raised_by_id || null;

            const projectId =
              r.project_id !== undefined && r.project_id !== null
                ? String(r.project_id)
                : r.projectId !== undefined && r.projectId !== null
                ? String(r.projectId)
                : typeof r.project === "number" || (typeof r.project === "string" && !isNaN(Number(r.project)) && r.project.trim() !== "")
                ? String(r.project)
                : null;

            const rawPname =
              r.project_name ||
              r.projectName ||
              (typeof r.project === "string" && isNaN(Number(r.project)) && !/^project\s*#?\s*\d+$/i.test(r.project) && !r.project.includes("/") ? r.project : null);
            const resolvedPname =
              (rawPname && !/^project\s*#?\s*\d+$/i.test(rawPname) && !rawPname.includes("/"))
                ? rawPname
                : (projectId && getActiveDashboardProjectName(projectId)) || "Unnamed Project";

            return {
              ncrNo: r.ncr_no || `NCR-${r.id ?? idx + 1}`,
              location: r.full_location || [resolvedPname !== "Unnamed Project" ? resolvedPname : "", r.tower_name, r.location_text].filter(Boolean).join(" / ") || "—",
              place: r.location_text || "—",
              project: resolvedPname,
              projectId,
              tower: r.tower_name || "—",
              cls: (r.classification || "minor").toLowerCase(),
              targetDate,
              status: normalizeNcrStatus(r.status),
              createdDate,
              raisedBy,
              raisedById,
              isOverdueFromApi: typeof r.is_overdue === "boolean" ? r.is_overdue : null,
              id: r.id,
            };
          });
          if (isMounted) setLiveNcrRecords(parsed);
        } else {
          if (isMounted) setLiveNcrRecords([]);
        }
      } catch (err) {
        console.warn("Failed to load live NCRs:", err);
        if (isMounted) setLiveNcrRecords([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadLiveNCRs();
    return () => {
      isMounted = false;
    };
  }, [selectedProjectId]);

  const [projectCheckers, setProjectCheckers] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadProjectCheckers() {
      if (!selectedProjectId) {
        setProjectCheckers([]);
        return;
      }
      try {
        const res = await checklistInstance.get(`/safety/observations/checkers/`, {
          params: { project_id: selectedProjectId },
        });
        const list = res?.data?.checkers || res?.data?.results || [];
        if (isMounted) {
          setProjectCheckers(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        if (isMounted) setProjectCheckers([]);
      }
    }
    loadProjectCheckers();
    return () => {
      isMounted = false;
    };
  }, [selectedProjectId]);

  // Filter options derived dynamically from live dataset
  const options = useMemo(() => {
    const statuses = NCR_STATUS_ORDER.filter((s) => liveNcrRecords.some((r) => r.status === s));
    const classes = NCR_CLASS_ORDER.filter((c) => liveNcrRecords.some((r) => r.cls === c));
    const projectsSet = new Set(
      liveNcrRecords
        .map((r) => r.project)
        .filter((name) => name && name !== "Unnamed Project" && name !== "—" && !/^project\s*#?\s*\d+$/i.test(name) && !name.includes("/"))
    );
    const accessible = getAccessibleProjects();
    accessible.forEach((p) => {
      if (p.name && !/^project\s*#?\s*\d+$/i.test(p.name) && !p.name.includes("/")) {
        projectsSet.add(p.name);
      }
    });
    if (
      filters.project &&
      filters.project !== "ALL" &&
      filters.project.trim() &&
      !/^project\s*#?\s*\d+$/i.test(filters.project.trim()) &&
      !filters.project.includes("/")
    ) {
      projectsSet.add(filters.project.trim());
    }
    const projects = Array.from(projectsSet).filter(Boolean).sort();
    const towers = Array.from(new Set(liveNcrRecords.map((r) => r.tower))).sort();
    const locations = Array.from(new Set(liveNcrRecords.map((r) => r.place))).sort();

    const activeCheckerNames = new Set(
      projectCheckers
        .filter((c) => c.is_active !== false && !c.historical_only)
        .map((c) => (c.raw_name || c.name || "").trim().toLowerCase())
    );

    const rawCheckers = Array.from(
      new Set(
        liveNcrRecords
          .map((r) => r.raisedBy)
          .filter((name) => name && name !== "—")
      )
    );

    const checkers = rawCheckers.map((name) => {
      const lower = name.trim().toLowerCase();
      const isActive = projectCheckers.length === 0 || activeCheckerNames.has(lower);
      return {
        value: name,
        label: isActive ? name : `${name} (Former Member)`,
        isActive,
      };
    }).sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      return a.value.localeCompare(b.value);
    });

    const monthSort = (a, b) => {
      const [ma, ya] = a.split(" ");
      const [mb, yb] = b.split(" ");
      return new Date(Number(ya || 2026), QC_MONTH_NAMES.indexOf(ma)) - new Date(Number(yb || 2026), QC_MONTH_NAMES.indexOf(mb));
    };
    const targetMonths = Array.from(new Set(liveNcrRecords.map((r) => ncrMonthKeyOf(r.targetDate)).filter(Boolean))).sort(monthSort);
    const createdMonths = Array.from(new Set(liveNcrRecords.map((r) => ncrMonthKeyOf(r.createdDate)).filter(Boolean))).sort(monthSort);
    return { statuses, classes, projects, towers, locations, checkers, targetMonths, createdMonths };
  }, [liveNcrRecords, filters.project, projectCheckers]);

  // The single derived dataset every card/chart/table/drill-down reads from.
  const filteredRecords = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return liveNcrRecords.filter((r) => {
      if (filters.status !== "ALL" && r.status !== filters.status) return false;
      if (filters.cls !== "ALL" && r.cls !== filters.cls) return false;
      if (filters.project !== "ALL") {
        if (!isProjectMatch(r, selectedProjectId, filters.project)) return false;
      }
      if (filters.tower !== "ALL" && r.tower !== filters.tower) return false;
      if (filters.location !== "ALL" && r.place !== filters.location) return false;
      if (filters.checker !== "ALL" && r.raisedBy !== filters.checker) return false;
      if (filters.targetMonth !== "ALL" && ncrMonthKeyOf(r.targetDate) !== filters.targetMonth) return false;
      if (filters.createdMonth !== "ALL" && ncrMonthKeyOf(r.createdDate) !== filters.createdMonth) return false;
      if (q) {
        const haystack = `${r.ncrNo} ${r.project} ${r.tower} ${r.place} ${r.raisedBy} ${r.status} ${formatNcrText(r.status)} ${r.cls} ${formatNcrText(r.cls)}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [liveNcrRecords, filters, selectedProjectId]);

  useEffect(() => {
    if (onExportContext) {
      onExportContext({
        filters,
        selectedProject: filters.project,
        liveNcrRecords,
        filteredRecords,
      });
    }
  }, [filters, liveNcrRecords, filteredRecords, onExportContext]);

  // Drill-down entry point from any chart/card — inherits current filters.
  const openDrill = (kind, key) => setDrillStack([buildNcrFrame(kind, key, filteredRecords)]);
  const pushDrill = (frame) => setDrillStack((s) => [...s, frame]);
  const popDrill = () => setDrillStack((s) => s.slice(0, -1));
  const closeDrill = () => setDrillStack([]);

  const openNcr = (ncrNo) => {
    const ncr = liveNcrRecords.find((r) => r.ncrNo === ncrNo);
    if (ncr) pushDrill({ kind: "record", key: ncr, records: [ncr], title: ncr.ncrNo });
  };

  return (
    <div className="space-y-4" id="active-dashboard-charts-container">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wider opacity-70">
          Non-Conformity Reports (NCR)
        </h2>
        {isLoading && (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-500 animate-pulse font-mono">
            🔄 Loading live NCR data...
          </span>
        )}
      </div>

      <NcrFilters
        filters={filters}
        setFilters={setFilters}
        options={options}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
        onProjectChange={handleProjectFilterChange}
      />

      <NcrSummaryCards
        records={filteredRecords}
        cardColor={cardColor}
        textColor={textColor}
        onSelectMetric={(m) => openDrill("metric", m)}
      />

      {filteredRecords.length === 0 && !isLoading ? (
        <div className="rounded-xl p-10 text-center border shadow-sm" style={{ background: cardColor, borderColor: `${ORANGE}44` }}>
          <div className="text-3xl mb-2">⚠️</div>
          <p className="text-sm font-bold opacity-80">No Non-Conformity Reports (NCR) Found</p>
          <p className="text-xs opacity-50 mt-1">There are currently 0 NCR records found for this project scope.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Status distribution</p>
              <p className="text-[11px] opacity-50 mb-2">Share of NCRs by raw workflow status — assigned to maker, resubmission required, maker submitted, closed.</p>
              <NcrStatusChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onDrill={openDrill} />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Class distribution</p>
              <p className="text-[11px] opacity-50 mb-2">Split between Major and Minor non-conformities.</p>
              <NcrClassChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onDrill={openDrill} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">NCRs by project</p>
              <p className="text-[11px] opacity-50 mb-2">Total NCRs raised per project.</p>
              <NcrProjectChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">NCRs by tower</p>
              <p className="text-[11px] opacity-50 mb-2">NCR distribution across towers.</p>
              <NcrTowerChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
          </div>

          <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
            <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">NCRs by location</p>
            <p className="text-[11px] opacity-50 mb-2">Specific in-tower locations with the most non-conformities — top 12 shown.</p>
            <NcrLocationChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">NCRs created over time</p>
              <p className="text-[11px] opacity-50 mb-2">Monthly trend of new NCRs raised.</p>
              <NcrCreatedTrendChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Target date trend</p>
              <p className="text-[11px] opacity-50 mb-2">NCRs grouped by the month they're due for resolution.</p>
              <NcrTargetDateChart records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onDrill={openDrill} />
            </div>
          </div>

          <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
            <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-1">Overdue vs on track</p>
            <p className="text-[11px] opacity-50 mb-2">Share of open NCRs past their target date versus still on schedule.</p>
            <NcrOverdueChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onDrill={openDrill} />
          </div>

          <NcrTable records={filteredRecords} cardColor={cardColor} textColor={textColor} theme={theme} onOpenNcr={openNcr} />
        </>
      )}

      <NcrDrillDrawer
        stack={drillStack}
        onBack={popDrill}
        onClose={closeDrill}
        onPush={pushDrill}
        onOpenNcr={openNcr}
        onSlice={(idx) => setDrillStack((s) => s.slice(0, idx + 1))}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
        allRecords={filteredRecords}
      />
    </div>
  );
}
/* ==================================================================== */
/*  ✅ REAL FORMS / CHECKLISTS DASHBOARD (LIVE API — FULLY DYNAMIC)       */
/*                                                                        */
/*  Source of truth: the live Forms/Checklists dashboard API. NOTHING   */
/*  here is mock/hardcoded — every card, chart, filter option and table  */
/*  row is derived directly from whatever JSON the API returns for the   */
/*  currently-applied filters. Filters start with NO project/tower/floor */
/*  baked in, so the dashboard isn't scoped to one project by default —  */
/*  it shows whatever the API actually gives back, which (per the        */
/*  sample response) can span multiple projects/towers/floors/templates  */
/*  at once. Tower/Floor/Template/Role dropdown options are populated    */
/*  FROM the API's own breakdown arrays for the currently-loaded         */
/*  response, so the filter UI adapts to real data instead of assuming   */
/*  a fixed project's IDs.                                               */
/* ==================================================================== */
/* ==================================================================== */
/*  ✅ REAL FORMS / CHECKLISTS DASHBOARD (LIVE API — FULLY DYNAMIC)       */
/*                                                                        */
/*  Source of truth: the live Forms/Checklists dashboard API. NOTHING   */
/*  here is mock/hardcoded — every card, chart, filter option and table  */
/*  row is derived directly from whatever JSON the API returns for the   */
/*  currently-applied filters. Filters start with NO project/tower/floor */
/*  baked in, so the dashboard isn't scoped to one project by default —  */
/*  it shows whatever the API actually gives back, which (per the        */
/*  sample response) can span multiple projects/towers/floors/templates  */
/*  at once. Tower/Floor/Template/Role dropdown options are populated    */
/*  FROM the API's own breakdown arrays for the currently-loaded         */
/*  response, so the filter UI adapts to real data instead of assuming   */
/*  a fixed project's IDs.                                               */
/* ==================================================================== */
const FORMS_STATUS_COLORS = {
  open: "#6c9bff",
  pending: ORANGE,
  in_progress: "#c9a3ff",
  completed: "#7fd8a0",
};
const FORMS_STATUS_LABELS = {
  open: "Open",
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
};

function formsStatusColor(status) {
  return FORMS_STATUS_COLORS[status] || "#9a9aa5";
}
function formsStatusLabel(status) {
  return FORMS_STATUS_LABELS[status] || status;
}

// ISO datetime -> readable string. Never replaces the raw API value elsewhere.
function formatIsoDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

// The ONLY place that calls the Forms API. Every filter is forwarded exactly
// as the API defines it — start_date, end_date, project_id, tower_id,
// floor_id, template_id, role. Empty/omitted filters are left OUT of the
// query string entirely (not sent as blank params), matching:
// GET /checklists/manual-checklists/dashboard/?start_date&end_date&project_id&tower_id&floor_id&template_id&role=CHECKER
// Auth (Bearer attach + 401 refresh-and-retry) is handled inside apiFetch.
async function fetchFormsDashboardData(filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) params.append(k, v);
  });
  const qs = params.toString();
  return apiFetch(`/checklists/manual-checklists/dashboard/${qs ? `?${qs}` : ""}`);
}

async function fetchManualChecklistsList(filters = {}) {
  const params = new URLSearchParams();
  if (filters.project_id) params.append("project_id", filters.project_id);
  if (filters.status) params.append("status", filters.status);
  if (filters.template_id) params.append("template_id", filters.template_id);
  if (filters.role) params.append("role", filters.role);
  const qs = params.toString();
  try {
    const raw = await apiFetch(`/checklists/manual-checklists/${qs ? `?${qs}` : ""}`);
    const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.results) ? raw.results : (Array.isArray(raw?.data) ? raw.data : []));
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.warn("[FormsDashboard] fetchManualChecklistsList failed, falling back to recent_checklists:", err);
    return [];
  }
}

/* ------------------------------------------------------------------ */
/*  Forms Filter Bar — Interconnected & Cascading                      */
/* ------------------------------------------------------------------ */
function FormsFilters({
  filters,
  onFilterChange,
  onClearFilters,
  onRemoveFilter,
  options,
  cardColor,
  textColor,
  theme,
  loading,
}) {
  const inputStyle = {
    background: theme === "dark" ? "#1b1b22" : "#fcfaf7",
    color: textColor,
    border: `1px solid ${ORANGE}88`,
  };

  const activeFilterEntries = useMemo(() => {
    const list = [];
    if (filters.project_id) {
      const p = options.projects.find((x) => String(x.id) === String(filters.project_id));
      list.push({ key: "project_id", label: "Project", value: p?.name || "Unnamed Project" });
    }
    if (filters.tower_id) {
      const t = options.towers.find((x) => String(x.id) === String(filters.tower_id));
      list.push({ key: "tower_id", label: "Tower", value: t?.name || `Tower #${filters.tower_id}` });
    }
    if (filters.floor_id) {
      const f = options.floors.find((x) => String(x.id) === String(filters.floor_id));
      list.push({ key: "floor_id", label: "Floor", value: f?.name || `Floor #${filters.floor_id}` });
    }
    if (filters.template_id) {
      const tm = options.templates.find((x) => String(x.id) === String(filters.template_id));
      list.push({ key: "template_id", label: "Template", value: tm?.name || `Template #${filters.template_id}` });
    }
    if (filters.status) {
      list.push({ key: "status", label: "Status", value: formsStatusLabel(filters.status) });
    }
    if (filters.role) {
      list.push({ key: "role", label: "Role", value: filters.role });
    }
    if (filters.start_date || filters.end_date) {
      const range = `${filters.start_date || "Any"} → ${filters.end_date || "Any"}`;
      list.push({ key: "date_range", label: "Date Range", value: range });
    }
    return list;
  }, [filters, options]);

  return (
    <div
      className="rounded-lg p-3 sm:p-4 shadow space-y-3"
      style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b" style={{ borderColor: `${ORANGE}33` }}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: ORANGE }}>
            Filter Dashboard
          </span>
          {loading && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 animate-pulse font-medium">
              Updating data…
            </span>
          )}
        </div>
        {activeFilterEntries.length > 0 && (
          <button
            onClick={onClearFilters}
            className="text-xs font-semibold px-3 py-1 rounded-full border transition-all hover:opacity-100 flex items-center gap-1.5"
            style={{ borderColor: ORANGE, color: ORANGE, background: `${ORANGE}15` }}
          >
            <span>✕ Clear All Filters</span>
            <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-amber-500 text-black font-bold">
              {activeFilterEntries.length}
            </span>
          </button>
        )}
      </div>

      {/* Filter Controls Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5 sm:gap-3">
        {/* Project Selector */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Project</label>
          <select
            className="text-xs rounded px-2.5 py-2 font-medium focus:outline-none focus:ring-1 transition-all"
            style={inputStyle}
            value={filters.project_id}
            onChange={(e) => onFilterChange("project_id", e.target.value)}
          >
            <option value="">All Projects</option>
            {options.projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || "Unnamed Project"}
              </option>
            ))}
          </select>
        </div>

        {/* Tower Selector (Cascading from Project) */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
            Tower {filters.project_id ? "(Filtered)" : ""}
          </label>
          <select
            className="text-xs rounded px-2.5 py-2 font-medium focus:outline-none focus:ring-1 transition-all"
            style={inputStyle}
            value={filters.tower_id}
            onChange={(e) => onFilterChange("tower_id", e.target.value)}
          >
            <option value="">
              {filters.project_id ? "All Towers in Project" : "All Towers"}
            </option>
            {options.towers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.displayName || t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Floor Selector (Cascading from Tower/Project) */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
            Floor {filters.tower_id ? "(Filtered)" : ""}
          </label>
          <select
            className="text-xs rounded px-2.5 py-2 font-medium focus:outline-none focus:ring-1 transition-all"
            style={inputStyle}
            value={filters.floor_id}
            onChange={(e) => onFilterChange("floor_id", e.target.value)}
          >
            <option value="">
              {filters.tower_id ? "All Floors in Tower" : "All Floors"}
            </option>
            {options.floors.map((f) => (
              <option key={f.id} value={f.id}>
                {f.displayName || f.name}
              </option>
            ))}
          </select>
        </div>

        {/* Template Selector */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Template</label>
          <select
            className="text-xs rounded px-2.5 py-2 font-medium focus:outline-none focus:ring-1 transition-all"
            style={inputStyle}
            value={filters.template_id}
            onChange={(e) => onFilterChange("template_id", e.target.value)}
          >
            <option value="">All Templates</option>
            {options.templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Status Selector */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Status</label>
          <select
            className="text-xs rounded px-2.5 py-2 font-medium focus:outline-none focus:ring-1 transition-all"
            style={inputStyle}
            value={filters.status}
            onChange={(e) => onFilterChange("status", e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="open">Open</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {/* Role Selector */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Role</label>
          <select
            className="text-xs rounded px-2.5 py-2 font-medium focus:outline-none focus:ring-1 transition-all"
            style={inputStyle}
            value={filters.role}
            onChange={(e) => onFilterChange("role", e.target.value)}
          >
            <option value="">All Roles</option>
            {options.roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range Inputs */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Start Date</label>
          <input
            type="date"
            className="text-xs rounded px-2.5 py-2 font-medium focus:outline-none focus:ring-1 transition-all"
            style={inputStyle}
            value={filters.start_date}
            onChange={(e) => onFilterChange("start_date", e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wide opacity-60 font-semibold">End Date:</span>
          <input
            type="date"
            className="text-xs rounded px-2.5 py-1 font-medium focus:outline-none focus:ring-1 transition-all w-36"
            style={inputStyle}
            value={filters.end_date}
            onChange={(e) => onFilterChange("end_date", e.target.value)}
          />
        </div>

        {/* Active Filter Pills */}
        <div className="flex items-center gap-1.5 flex-wrap flex-1 justify-start sm:justify-end">
          {activeFilterEntries.length === 0 ? (
            <span className="text-[11px] opacity-50 italic">
              Showing all checklists across all projects. Select filters to narrow scope.
            </span>
          ) : (
            activeFilterEntries.map((item) => (
              <span
                key={item.key}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all shadow-sm"
                style={{
                  background: `${ORANGE}22`,
                  color: textColor,
                  border: `1px solid ${ORANGE}66`,
                }}
              >
                <span className="opacity-60 text-[10px] uppercase font-bold">{item.label}:</span>
                <span className="font-semibold max-w-[140px] truncate" title={item.value}>
                  {item.value}
                </span>
                <button
                  type="button"
                  onClick={() => onRemoveFilter(item.key)}
                  className="hover:scale-125 transition-transform opacity-70 hover:opacity-100 font-bold ml-0.5"
                  title={`Remove ${item.label} filter`}
                >
                  ✕
                </button>
              </span>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* Checklist status cards — Total / Pending / In Progress / Completed,   */
/* taken straight from `widgets`. Clicking a status sets the status filter. */
function FormsSummaryCards({ widgets, activeStatus, cardColor, textColor, onSelectStatus }) {
  if (!widgets) return null;
  const cards = [
    { key: "total", label: "Total Checklists", value: widgets.total_checklists, color: textColor, filterKey: "", subtitle: "All checklists in scope" },
    { key: "pending", label: "Pending", value: widgets.pending_checklists, color: formsStatusColor("pending"), filterKey: "pending", subtitle: "Awaiting completion" },
    { key: "in_progress", label: "In Progress", value: widgets.in_progress_checklists, color: formsStatusColor("in_progress"), filterKey: "in_progress", subtitle: "Active & currently being filled" },
    { key: "completed", label: "Completed", value: widgets.completed_checklists, color: formsStatusColor("completed"), filterKey: "completed", subtitle: "Finished & closed out" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4">
      {cards.map((c) => {
        const isSelected = activeStatus === c.filterKey && c.filterKey !== "";
        return (
          <button
            key={c.key}
            onClick={() => onSelectStatus(c.filterKey)}
            className="text-left rounded-lg p-3 sm:p-4 shadow transition-all hover:scale-[1.01]"
            style={{
              background: cardColor,
              border: isSelected ? `2px solid ${ORANGE}` : `1px solid ${c.color}66`,
              boxShadow: isSelected ? `0 0 0 2px ${ORANGE}44` : undefined,
              cursor: "pointer",
            }}
          >
            <div className="flex items-center justify-between">
              <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide opacity-60 truncate">{c.label}</p>
              {isSelected && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 shrink-0 ml-1">
                  Active
                </span>
              )}
            </div>
            <p className="text-2xl sm:text-3xl font-bold mt-1" style={{ color: c.color }}>
              <CountUpNumber value={c.value ?? 0} color={c.color} triggerKey={`forms_${c.key}_${c.value}`} />
            </p>
            {c.subtitle && (
              <p className="text-[10px] sm:text-[11px] opacity-60 mt-1 font-medium leading-tight truncate">
                {c.subtitle}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* Questions + completion — straight from `widgets.total_questions` /    */
/* pending_questions / completed_questions / completion_percentage.     */
function FormsQuestionsCard({ widgets, cardColor, textColor }) {
  if (!widgets) return null;
  const rows = [
    { label: "Total Questions", value: widgets.total_questions ?? 0, color: textColor },
    { label: "Completed", value: widgets.completed_questions ?? 0, color: "#10b981" },
    { label: "Pending", value: widgets.pending_questions ?? 0, color: "#f59e0b" },
  ];
  const pct = Math.min(100, Math.max(0, Math.round(widgets.completion_percentage ?? 0)));
  const ringColor = pct >= 80 ? "#10b981" : pct >= 50 ? ORANGE : "#f59e0b";
  const strokeDashoffset = 226.2 - (226.2 * pct) / 100;

  return (
    <div className="rounded-lg p-4 shadow h-full flex flex-col justify-between" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-60">Inspection Questions Progress</p>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: `${ringColor}22`, color: ringColor }}
          >
            {pct}% Answered
          </span>
        </div>

        <div className="flex items-center gap-4 mb-3">
          {/* Circular Progress Gauge */}
          <div className="relative flex items-center justify-center shrink-0 w-20 h-20 sm:w-24 sm:h-24">
            <svg className="w-20 h-20 sm:w-24 sm:h-24 transform -rotate-90" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r="36"
                stroke="currentColor"
                strokeWidth="7"
                className="opacity-15"
                fill="transparent"
              />
              <circle
                cx="40"
                cy="40"
                r="36"
                stroke={ringColor}
                strokeWidth="7"
                strokeDasharray="226.2"
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                fill="transparent"
                style={{ transition: "stroke-dashoffset 0.8s ease-in-out" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="text-base sm:text-lg font-extrabold leading-none" style={{ color: ringColor }}>{pct}%</span>
              <span className="text-[9px] font-medium opacity-60 uppercase tracking-tighter mt-0.5">Done</span>
            </div>
          </div>

          {/* Quick Metrics Breakdown */}
          <div className="flex-1 grid grid-cols-1 gap-1.5">
            {rows.map((r) => (
              <div
                key={r.label}
                className="flex items-center justify-between px-2.5 py-1 rounded-md border"
                style={{ borderColor: `${ORANGE}22`, background: `${ORANGE}06` }}
              >
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.color }} />
                  <span className="text-[11px] opacity-75 font-medium">{r.label}</span>
                </div>
                <span className="text-xs font-bold" style={{ color: r.color }}>
                  <CountUpNumber value={r.value ?? 0} color={r.color} triggerKey={`forms_q_${r.label}_${r.value}`} />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-[11px] opacity-60 mb-1 font-medium">
          <span>Overall Field Completion</span>
          <span>{widgets.completed_questions ?? 0} of {widgets.total_questions ?? 0}</span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: `${ORANGE}22` }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: ringColor }}
          />
        </div>
      </div>
    </div>
  );
}

/* Positive / negative responses donut — straight from `widgets`.       */
/* Positive / negative responses donut — straight from `widgets`.       */
function FormsResponsesChart({ widgets, cardColor, textColor }) {
  if (!widgets) return null;
  const positive = widgets.positive_responses ?? 0;
  const negative = widgets.negative_responses ?? 0;
  const total = widgets.total_responses ?? (positive + negative);
  const passRate = total > 0 ? Math.round((positive / total) * 100) : 0;

  const data = [
    { name: "Positive (Pass)", value: positive, color: "#10b981", rawName: "Positive" },
    { name: "Negative (Flagged)", value: negative, color: "#ef4444", rawName: "Negative" },
  ].filter((d) => d.value > 0);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
  };

  return (
    <div className="rounded-lg p-4 shadow h-full flex flex-col justify-between" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold uppercase tracking-wide opacity-60">
            Inspection Quality Compliance
          </p>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{
              background: passRate >= 80 ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
              color: passRate >= 80 ? "#10b981" : "#ef4444",
            }}
          >
            {passRate}% Pass Rate
          </span>
        </div>
        <p className="text-[11px] opacity-50 mb-1">
          Questions passing inspection standards ({total} responses).
        </p>
      </div>

      {data.length === 0 ? (
        <p className="text-sm opacity-50 py-10 text-center">No response data for the selected filters.</p>
      ) : (
        <div className="flex-1 flex flex-col justify-center my-1">
          <div className="relative my-auto" style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={4}
                  stroke="none"
                >
                  {data.map((d) => (
                    <Cell key={d.name} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value, name) => [`${value} items (${total > 0 ? Math.round((value / total) * 100) : 0}%)`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Centered Pass Rate Metric inside Donut */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-extrabold" style={{ color: passRate >= 80 ? "#10b981" : "#ef4444" }}>
                {passRate}%
              </span>
              <span className="text-[10px] font-medium opacity-60 uppercase tracking-wide">Compliance</span>
            </div>
          </div>

          {/* User-Friendly Legend Pills */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="flex items-center justify-between px-3 py-1.5 rounded-lg border" style={{ borderColor: "rgba(16, 185, 129, 0.3)", background: "rgba(16, 185, 129, 0.08)" }}>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                <span className="text-xs font-medium">Positive</span>
              </div>
              <span className="text-xs font-bold text-emerald-500">
                {positive} <span className="text-[10px] opacity-70">({total > 0 ? Math.round((positive / total) * 100) : 0}%)</span>
              </span>
            </div>
            <div className="flex items-center justify-between px-3 py-1.5 rounded-lg border" style={{ borderColor: "rgba(239, 68, 68, 0.3)", background: "rgba(239, 68, 68, 0.08)" }}>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                <span className="text-xs font-medium">Negative</span>
              </div>
              <span className="text-xs font-bold text-red-500">
                {negative} <span className="text-[10px] opacity-70">({total > 0 ? Math.round((negative / total) * 100) : 0}%)</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Checklist status donut — clicking a slice filters or opens drill-down. */
function FormsStatusChart({ widgets, activeStatus, cardColor, textColor, onSelectStatus, onDrill }) {
  if (!widgets) return null;
  const inProgress = widgets.in_progress_checklists ?? 0;
  const open = widgets.open_checklists ?? 0;
  const completed = widgets.completed_checklists ?? 0;
  const total = widgets.total_checklists ?? (inProgress + open + completed);
  const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Mutually exclusive status categories
  const data = [
    { key: "in_progress", name: "In Progress", value: inProgress, color: formsStatusColor("in_progress") },
    { key: "open", name: "Open", value: open, color: formsStatusColor("open") },
    { key: "completed", name: "Completed", value: completed, color: formsStatusColor("completed") },
  ].filter((d) => d.value > 0);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
  };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No checklists match the current filters.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="relative my-auto" style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={55}
              outerRadius={80}
              paddingAngle={4}
              cursor="pointer"
              stroke="none"
              onClick={(d) => {
                if (onDrill) onDrill("status", d.key, formsStatusLabel(d.key));
                else if (onSelectStatus) onSelectStatus(d.key);
              }}
            >
              {data.map((d) => (
                <Cell
                  key={d.key}
                  fill={d.color}
                  stroke={activeStatus === d.key ? "#fff" : "none"}
                  strokeWidth={activeStatus === d.key ? 2 : 0}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value, name) => [`${value} checklists (${total > 0 ? Math.round((value / total) * 100) : 0}%)`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Centered Total & Completion Metric inside Donut */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-extrabold leading-none">{total}</span>
          <span className="text-[10px] font-medium opacity-60 uppercase tracking-wide mt-0.5">Checklists</span>
          <span className="text-[11px] font-bold text-emerald-500 mt-0.5">{completionPct}% Done</span>
        </div>
      </div>

      {/* Interactive Status Pills under Donut */}
      <div className="grid grid-cols-3 gap-2">
        {data.map((d) => {
          const isSelected = activeStatus === d.key;
          const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
          return (
            <button
              key={d.key}
              type="button"
              onClick={() => {
                if (onDrill) onDrill("status", d.key, formsStatusLabel(d.key));
                else if (onSelectStatus) onSelectStatus(isSelected ? "" : d.key);
              }}
              className={`p-2 rounded-lg border text-left transition-all hover:scale-[1.02] ${
                isSelected ? "ring-2 ring-amber-500" : ""
              }`}
              style={{ borderColor: `${d.color}44`, background: `${d.color}10` }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                <span className="text-[11px] font-semibold truncate">{d.name}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-bold" style={{ color: d.color }}>{d.value}</span>
                <span className="text-[10px] opacity-70 font-semibold">{pct}%</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Helper: Resolve Default Permitted Project for Project Head         */
/* ------------------------------------------------------------------ */
function getInitialDefaultProject() {
  try {
    const direct =
      localStorage.getItem("ACTIVE_PROJECT_ID") ||
      localStorage.getItem("PROJECT_ID") ||
      localStorage.getItem("project_id") ||
      localStorage.getItem("active_project_id");
    if (direct && direct !== "undefined" && direct !== "null") return String(direct);

    const accessesRaw = localStorage.getItem("ACCESSES");
    if (accessesRaw && accessesRaw !== "undefined") {
      const parsed = JSON.parse(accessesRaw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const pId = parsed[0].project_id || parsed[0].projectId || parsed[0].id;
        if (pId) return String(pId);
      }
    }

    const userDataRaw = localStorage.getItem("USER_DATA");
    if (userDataRaw && userDataRaw !== "undefined") {
      const userData = JSON.parse(userDataRaw);
      if (userData?.active_project_id || userData?.project_id) {
        return String(userData.active_project_id || userData.project_id);
      }
      if (Array.isArray(userData?.accesses) && userData.accesses.length > 0) {
        const pId = userData.accesses[0].project_id || userData.accesses[0].projectId || userData.accesses[0].id;
        if (pId) return String(pId);
      }
    }
  } catch (e) {
    console.warn("[Forms] Error resolving default project:", e);
  }
  return "";
}

/* ------------------------------------------------------------------ */
/*  Checklists by template — Top 10 (scroll-free) / 20 / 50 / All      */
/*  Supports Multiple Intuitive Graph Types: Stacked Status / Volume / %*/
/* ------------------------------------------------------------------ */
function FormsTemplateChart({ charts, activeTemplateId, cardColor, textColor, theme, onSelectTemplate, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const [viewLimit, setViewLimit] = useState("all");
  const [chartMode, setChartMode] = useState("stacked"); // "stacked" | "volume" | "completion"

  const rawData = useMemo(() => {
    return (charts?.template_breakdown || [])
      .map((t) => {
        const total = t.total ?? 0;
        const completed = t.completed ?? 0;
        const in_progress = t.in_progress ?? 0;
        const open = t.open ?? 0;
        const completion_pct = total > 0 ? Math.round((completed / total) * 100) : 0;
        return {
          name: t.template__name || `Template #${t.template_id}`,
          template_id: String(t.template_id),
          total,
          open,
          in_progress,
          completed,
          pending: t.pending ?? 0,
          completion_pct,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [charts]);

  const displayCount = viewLimit === "all" ? rawData.length : Math.min(parseInt(viewLimit, 10), rawData.length);
  const data = rawData.slice(0, displayCount);
  const isTop10 = viewLimit === "10";

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
  };

  if (rawData.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No template data for the current filters.</p>;
  }

  // Generous row height; when Top 10 is clicked, fits comfortably without any scrollbar
  const rowHeight = 36;
  const chartHeight = isTop10
    ? Math.max(260, data.length * 36 + 16)
    : Math.max(260, data.length * rowHeight);

  // Vibrant palette for bright and attractive total volume visualization
  const BRIGHT_PALETTE = [
    "#ff9f43", "#00cfe8", "#28c76f", "#ea5455", "#7367f0",
    "#feca57", "#ff6b6b", "#48dbfb", "#1dd1a1", "#9b5de5",
    "#f368e0", "#ff9ff3", "#54a0ff", "#5f27cd", "#00d2d3"
  ];

  return (
    <div className="space-y-2.5">
      {/* Controls Row: Chart Type & Record Limit Selectors */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        {/* Intuitive Chart Type Toggle */}
        <div className="inline-flex rounded-lg p-0.5 border" style={{ borderColor: `${ORANGE}44`, background: `${ORANGE}0a` }}>
          {[
            { key: "stacked", label: "📊 Status Breakdown" },
            { key: "volume", label: "📈 Total Volume" },
            { key: "completion", label: "🎯 Completion %" },
          ].map((item) => {
            const isSelected = chartMode === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setChartMode(item.key)}
                className={`text-[11px] px-2 py-0.5 rounded font-semibold transition-all ${
                  isSelected ? "shadow" : "opacity-70 hover:opacity-100"
                }`}
                style={{
                  background: isSelected ? ORANGE : "transparent",
                  color: isSelected ? "#1a1a1a" : textColor,
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Top 10 / 20 / 50 / All Selector */}
        <div className="inline-flex rounded-lg p-0.5 border" style={{ borderColor: `${ORANGE}44`, background: `${ORANGE}0c` }}>
          {[
            { key: "10", label: "Top 10" },
            { key: "20", label: "Top 20" },
            { key: "50", label: "Top 50" },
            { key: "all", label: `All (${rawData.length})` },
          ].map((item) => {
            const isSelected = viewLimit === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setViewLimit(item.key)}
                className={`text-[11px] px-2 py-0.5 rounded font-semibold transition-all ${
                  isSelected ? "shadow" : "opacity-70 hover:opacity-100"
                }`}
                style={{
                  background: isSelected ? ORANGE : "transparent",
                  color: isSelected ? "#1a1a1a" : textColor,
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend & Hint Bar */}
      <div className="flex items-center justify-between text-[11px] px-1 opacity-75 flex-wrap gap-1">
        <span>Showing {data.length} of {rawData.length} templates</span>
        {chartMode === "stacked" && (
          <div className="flex items-center gap-3 font-semibold">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Completed</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> In Progress</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: ORANGE }} /> Open</span>
          </div>
        )}
        {chartMode === "completion" && (
          <span className="text-emerald-500 font-semibold">Higher % = better inspection progress</span>
        )}
      </div>

      {/* Chart Canvas: Scrollbar COMPLETELY removed when isTop10 is true */}
      <div
        className="rounded-md border p-1"
        style={{
          maxHeight: isTop10 ? "none" : 480,
          overflowY: isTop10 ? "visible" : (data.length > 8 ? "auto" : "visible"),
          borderColor: `${ORANGE}22`,
        }}
      >
        <ResponsiveContainer width="100%" height={chartHeight}>
          {chartMode === "stacked" ? (
            /* Mode 1: Stacked Status Breakdown Bar Chart */
            <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                stroke={axisColor}
                fontSize={11}
                width={190}
                interval={0}
                tickFormatter={(val) => (val && val.length > 26 ? `${val.slice(0, 24)}…` : val)}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value, name, item) => {
                  const p = item.payload;
                  return [
                    `${value} checklists (${p.total > 0 ? Math.round((value / p.total) * 100) : 0}%)`,
                    name,
                  ];
                }}
              />
              <Bar
                dataKey="completed"
                name="Completed"
                stackId="statusStack"
                fill="#10b981"
                cursor="pointer"
                onClick={(d) => {
                  if (onDrill) onDrill("template", d.template_id, d.name);
                  else if (onSelectTemplate) onSelectTemplate(d.template_id);
                }}
              />
              <Bar
                dataKey="in_progress"
                name="In Progress"
                stackId="statusStack"
                fill="#f59e0b"
                cursor="pointer"
                onClick={(d) => {
                  if (onDrill) onDrill("template", d.template_id, d.name);
                  else if (onSelectTemplate) onSelectTemplate(d.template_id);
                }}
              />
              <Bar
                dataKey="open"
                name="Open"
                stackId="statusStack"
                fill={ORANGE}
                radius={[0, 5, 5, 0]}
                cursor="pointer"
                onClick={(d) => {
                  if (onDrill) onDrill("template", d.template_id, d.name);
                  else if (onSelectTemplate) onSelectTemplate(d.template_id);
                }}
              />
            </BarChart>
          ) : chartMode === "completion" ? (
            /* Mode 2: Completion Rate Percentage (0 - 100%) */
            <BarChart data={data} layout="vertical" margin={{ left: 10, right: 35, top: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis type="number" domain={[0, 100]} unit="%" stroke={axisColor} fontSize={12} />
              <YAxis
                type="category"
                dataKey="name"
                stroke={axisColor}
                fontSize={11}
                width={190}
                interval={0}
                tickFormatter={(val) => (val && val.length > 26 ? `${val.slice(0, 24)}…` : val)}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(val, name, item) => [
                  `${val}% completed (${item.payload.completed} of ${item.payload.total} checklists)`,
                  item.payload.name,
                ]}
              />
              <Bar
                dataKey="completion_pct"
                name="Completion Rate"
                radius={[0, 6, 6, 0]}
                cursor="pointer"
                onClick={(d) => {
                  if (onDrill) onDrill("template", d.template_id, d.name);
                  else if (onSelectTemplate) onSelectTemplate(d.template_id);
                }}
              >
                {data.map((d) => {
                  const fillColor = d.completion_pct >= 80 ? "#10b981" : d.completion_pct >= 40 ? "#f59e0b" : ORANGE;
                  return <Cell key={d.template_id} fill={fillColor} />;
                })}
              </Bar>
            </BarChart>
          ) : (
            /* Mode 3: Total Volume Bar Chart */
            <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                stroke={axisColor}
                fontSize={11}
                width={190}
                interval={0}
                tickFormatter={(val) => (val && val.length > 26 ? `${val.slice(0, 24)}…` : val)}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value, name, item) => [`${value} total checklists`, item.payload.name]}
              />
              <Bar
                dataKey="total"
                radius={[0, 6, 6, 0]}
                cursor="pointer"
                onClick={(d) => {
                  if (onDrill) onDrill("template", d.template_id, d.name);
                  else if (onSelectTemplate) onSelectTemplate(d.template_id);
                }}
              >
                {data.map((d, i) => {
                  const isSelected = activeTemplateId && String(activeTemplateId) === String(d.template_id);
                  return (
                    <Cell
                      key={d.template_id}
                      fill={isSelected ? "#ffffff" : BRIGHT_PALETTE[i % BRIGHT_PALETTE.length]}
                      stroke={isSelected ? ORANGE : "none"}
                      strokeWidth={isSelected ? 3 : 0}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* Checklists by tower — Top 10 (scroll-free) / 20 / All (scoped to project) */
function FormsTowerChart({ charts, selectedProjectId, activeTowerId, cardColor, textColor, theme, onSelectTower, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const [viewLimit, setViewLimit] = useState("all");

  const rawData = useMemo(() => {
    return (charts?.tower_breakdown || [])
      .map((t) => ({
        name: t.tower_name || `Tower #${t.tower_id}`,
        tower_id: String(t.tower_id),
        total: t.total ?? 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [charts]);

  const displayCount = viewLimit === "all" ? rawData.length : Math.min(parseInt(viewLimit, 10), rawData.length);
  const data = rawData.slice(0, displayCount);
  const isTop10 = viewLimit === "10";

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
  };

  if (rawData.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No tower data for the current filters.</p>;
  }

  const rowHeight = 36;
  const chartHeight = isTop10 ? Math.max(240, data.length * 34 + 14) : Math.max(260, data.length * rowHeight);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-[11px] font-semibold opacity-70">
          Showing {data.length} of {rawData.length} towers
        </span>
        {rawData.length > 5 && (
          <div className="inline-flex rounded-lg p-0.5 border" style={{ borderColor: `${ORANGE}44`, background: `${ORANGE}0c` }}>
            {[
              { key: "10", label: "Top 10" },
              { key: "20", label: "Top 20" },
              { key: "all", label: `All (${rawData.length})` },
            ].map((item) => {
              const isSelected = viewLimit === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setViewLimit(item.key)}
                  className={`text-[11px] px-2 py-0.5 rounded font-semibold transition-all ${
                    isSelected ? "shadow" : "opacity-70 hover:opacity-100"
                  }`}
                  style={{
                    background: isSelected ? ORANGE : "transparent",
                    color: isSelected ? "#1a1a1a" : textColor,
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* When Top 10 is selected, scrollbar is removed completely */}
      <div
        className="rounded-md border p-1"
        style={{
          maxHeight: isTop10 ? "none" : 480,
          overflowY: isTop10 ? "visible" : (data.length > 8 ? "auto" : "visible"),
          borderColor: `${ORANGE}22`,
        }}
      >
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              stroke={axisColor}
              fontSize={11}
              width={140}
              interval={0}
              tickFormatter={(val) => (val && val.length > 18 ? `${val.slice(0, 16)}…` : val)}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value, name, item) => [`${value} checklists`, item.payload.name]}
            />
            <Bar
              dataKey="total"
              radius={[0, 6, 6, 0]}
              cursor="pointer"
              onClick={(d) => {
                if (onDrill) onDrill("tower", d.tower_id, d.name);
                else if (onSelectTower) onSelectTower(d.tower_id);
              }}
            >
              {data.map((d) => {
                const isSelected = activeTowerId && String(activeTowerId) === String(d.tower_id);
                return (
                  <Cell
                    key={d.tower_id}
                    fill={isSelected ? "#10b981" : "#ff9f43"}
                    stroke={isSelected ? "#fff" : "none"}
                    strokeWidth={isSelected ? 2 : 0}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* Checklists by floor — Top 10 (scroll-free) / Top 20 / All           */
function FormsFloorChart({ charts, activeFloorId, cardColor, textColor, theme, onSelectFloor, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const [viewLimit, setViewLimit] = useState("all");

  const all = useMemo(() => {
    return (charts?.floor_breakdown || [])
      .map((f) => ({
        name: f.floor_name || `Floor #${f.floor_id}`,
        floor_id: String(f.floor_id),
        total: f.total ?? 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [charts]);

  const displayCount = viewLimit === "all" ? all.length : Math.min(parseInt(viewLimit, 10), all.length);
  const data = all.slice(0, displayCount);
  const isTop10 = viewLimit === "10";

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  if (all.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No floor data for the current filters.</p>;
  }

  const rowHeight = 32;
  const chartHeight = isTop10 ? Math.max(240, data.length * 32 + 14) : Math.max(240, data.length * rowHeight);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-[11px] font-semibold opacity-70">
          Showing {data.length} of {all.length} floors
        </span>
        {all.length > 5 && (
          <div className="inline-flex rounded-lg p-0.5 border" style={{ borderColor: `${ORANGE}44`, background: `${ORANGE}0c` }}>
            {[
              { key: "10", label: "Top 10" },
              { key: "20", label: "Top 20" },
              { key: "all", label: `All (${all.length})` },
            ].map((item) => {
              const isSelected = viewLimit === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setViewLimit(item.key)}
                  className={`text-[11px] px-2 py-0.5 rounded font-semibold transition-all ${
                    isSelected ? "shadow" : "opacity-70 hover:opacity-100"
                  }`}
                  style={{
                    background: isSelected ? ORANGE : "transparent",
                    color: isSelected ? "#1a1a1a" : textColor,
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* When Top 10 is selected, scrollbar is removed completely */}
      <div
        className="rounded-md border p-1"
        style={{
          maxHeight: isTop10 ? "none" : 480,
          overflowY: isTop10 ? "visible" : (data.length > 8 ? "auto" : "visible"),
          borderColor: `${ORANGE}22`,
        }}
      >
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30, top: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              stroke={axisColor}
              fontSize={11}
              width={140}
              interval={0}
              tickFormatter={(val) => (val && val.length > 18 ? `${val.slice(0, 16)}…` : val)}
            />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar
              dataKey="total"
              radius={[0, 6, 6, 0]}
              cursor="pointer"
              onClick={(d) => {
                if (onDrill) onDrill("floor", d.floor_id, d.name);
                else if (onSelectFloor) onSelectFloor(d.floor_id);
              }}
            >
              {data.map((d) => {
                const isSelected = activeFloorId && String(activeFloorId) === String(d.floor_id);
                return (
                  <Cell
                    key={d.floor_id}
                    fill={isSelected ? "#f59e0b" : "#3b82f6"}
                    stroke={isSelected ? "#fff" : "none"}
                    strokeWidth={isSelected ? 2 : 0}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  NEW: Checklist Activity & Completion Trend (Smooth Area Chart)     */
/* ------------------------------------------------------------------ */
function FormsActivityTimelineChart({ checklists, cardColor, textColor, theme }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";

  const timelineData = useMemo(() => {
    if (!Array.isArray(checklists) || checklists.length === 0) return [];

    const map = {};
    checklists.forEach((c) => {
      if (c.created_at) {
        const d = new Date(c.created_at);
        if (!isNaN(d.getTime())) {
          const key = d.toISOString().slice(0, 10);
          if (!map[key]) {
            map[key] = {
              dateKey: key,
              displayDate: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
              created: 0,
              completed: 0,
            };
          }
          map[key].created += 1;
        }
      }
      if (c.completed_at) {
        const d = new Date(c.completed_at);
        if (!isNaN(d.getTime())) {
          const key = d.toISOString().slice(0, 10);
          if (!map[key]) {
            map[key] = {
              dateKey: key,
              displayDate: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
              created: 0,
              completed: 0,
            };
          }
          map[key].completed += 1;
        }
      }
    });

    return Object.values(map)
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey))
      .slice(-14);
  }, [checklists]);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${ORANGE}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
  };

  return (
    <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-60">
            Checklist Activity & Submission Trend
          </p>
          <p className="text-[11px] opacity-50">
            Pace of checklist creation vs completion over recent activity.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs font-medium">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: ORANGE }} />
            <span>Created</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span>Completed</span>
          </span>
        </div>
      </div>

      {timelineData.length < 2 ? (
        <div className="py-10 text-center opacity-50 text-xs">
          Activity timeline will track trends as multiple days of checklist submissions are logged.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={timelineData} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
            <defs>
              <linearGradient id="formsColorCreated" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={ORANGE} stopOpacity={0.4} />
                <stop offset="95%" stopColor={ORANGE} stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="formsColorCompleted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} opacity={0.6} />
            <XAxis dataKey="displayDate" stroke={axisColor} fontSize={11} tickLine={false} />
            <YAxis stroke={axisColor} fontSize={11} allowDecimals={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area
              type="monotone"
              dataKey="created"
              name="Created Checklists"
              stroke={ORANGE}
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#formsColorCreated)"
            />
            <Area
              type="monotone"
              dataKey="completed"
              name="Completed Checklists"
              stroke="#10b981"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#formsColorCompleted)"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/* Pending questions by role — from `charts.role_breakdown`.             */
function FormsRoleChart({ charts, activeRole, cardColor, textColor, theme, onSelectRole, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const data = (charts?.role_breakdown || []).map((r) => ({
    name: r.role,
    pending_questions: r.pending_questions ?? 0,
  }));

  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No role data for the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={12} width={110} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar
          dataKey="pending_questions"
          radius={[0, 6, 6, 0]}
          name="Pending questions"
          cursor="pointer"
          onClick={(d) => {
            if (onDrill) onDrill("role", d.name, `Role: ${d.name}`);
            else if (onSelectRole) onSelectRole(d.name);
          }}
        >
          {data.map((d) => {
            const isSelected = activeRole && activeRole.toLowerCase() === d.name.toLowerCase();
            return (
              <Cell
                key={d.name}
                fill={isSelected ? ORANGE : "#8b5cf6"}
                stroke={isSelected ? "#fff" : "none"}
                strokeWidth={isSelected ? 2 : 0}
              />
            );
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* Checklists by project — from `recent_checklists`.                    */
function FormsRecentProjectChart({ checklists, activeProjectId, cardColor, textColor, theme, onSelectProject, onDrill }) {
  const gridColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const axisColor = theme === "dark" ? "#9a9aa5" : "#7a7a7a";
  const data = useMemo(() => {
    const map = {};
    (checklists || []).forEach((c) => {
      const rawPname = c.project_name && !/^project\s*#?\s*\d+$/i.test(c.project_name) ? c.project_name : null;
      const pName = rawPname || (c.project_id && getActiveDashboardProjectName(c.project_id)) || "Unnamed Project";
      if (!map[pName]) {
        map[pName] = { name: pName, project_id: String(c.project_id), value: 0 };
      }
      map[pName].value += 1;
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [checklists]);

  const tooltipStyle = { background: cardColor, border: `1px solid ${ORANGE}`, borderRadius: 8, color: textColor, fontSize: 12 };

  if (data.length === 0) {
    return <p className="text-sm opacity-50 py-10 text-center">No recent checklists for the current filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} layout="vertical" margin={{ left: 24, right: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis type="number" stroke={axisColor} fontSize={12} allowDecimals={false} />
        <YAxis type="category" dataKey="name" stroke={axisColor} fontSize={12} width={120} />
        <Tooltip contentStyle={tooltipStyle} />
        <Bar
          dataKey="value"
          radius={[0, 6, 6, 0]}
          cursor="pointer"
          onClick={(d) => {
            if (onDrill) onDrill("project", d.project_id, d.name);
            else if (onSelectProject) onSelectProject(d.project_id);
          }}
        >
          {data.map((d) => {
            const isSelected = activeProjectId && String(activeProjectId) === String(d.project_id);
            return (
              <Cell
                key={d.name}
                fill={isSelected ? ORANGE : "#14b8a6"}
                stroke={isSelected ? "#fff" : "none"}
                strokeWidth={isSelected ? 2 : 0}
              />
            );
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function FormsChecklistBadge({ status }) {
  const color = formsStatusColor(status);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: `${color}22`, color }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: color, display: "inline-block" }} />
      {formsStatusLabel(status)}
    </span>
  );
}

/* Checklists table — supports full checklist dataset with pagination and page sizing */
function FormsTable({ checklists, cardColor, textColor, theme, onOpenChecklist }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const pageCount = Math.max(1, Math.ceil(checklists.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = checklists.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";

  React.useEffect(() => { setPage(0); }, [checklists.length, pageSize]);

  return (
    <div className="rounded-lg shadow overflow-hidden" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
      <div className="overflow-x-auto touch-scroll">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
              {["ID", "Template", "Project", "Tower", "Floor", "Status", "Created At", "Completed At", "Action"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[11px] uppercase tracking-wide opacity-60 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((c) => (
              <tr
                key={c.id}
                className="transition-colors hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer"
                style={{ borderBottom: `1px solid ${borderColor}` }}
                onClick={() => onOpenChecklist && onOpenChecklist(c.id)}
              >
                <td className="px-3 py-2 font-mono text-xs font-semibold whitespace-nowrap">{c.id}</td>
                <td className="px-3 py-2 font-medium max-w-[240px] truncate">
                  {c.template_name || c.template__name || c.template?.name || (c.template_id ? `Template #${c.template_id}` : "—")}
                </td>
                <td className="px-3 py-2 whitespace-nowrap opacity-80">{c.project_name || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap opacity-80">{c.tower_name || (c.tower_id ? `Tower #${c.tower_id}` : "—")}</td>
                <td className="px-3 py-2 whitespace-nowrap opacity-80">{c.floor_name || (c.floor_id ? `Floor #${c.floor_id}` : "—")}</td>
                <td className="px-3 py-2 whitespace-nowrap"><FormsChecklistBadge status={c.status} /></td>
                <td className="px-3 py-2 whitespace-nowrap opacity-70 text-xs">{formatIsoDate(c.created_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap opacity-70 text-xs">{formatIsoDate(c.completed_at)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenChecklist && onOpenChecklist(c.id);
                    }}
                    className="text-[11px] px-2 py-0.5 rounded font-semibold"
                    style={{ color: ORANGE, border: `1px solid ${ORANGE}55`, background: `${ORANGE}14` }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-sm opacity-50">
                  No checklists match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 px-3 py-2.5 text-xs opacity-75" style={{ borderTop: `1px solid ${borderColor}` }}>
        <span className="text-center sm:text-left">
          Showing {checklists.length === 0 ? 0 : safePage * pageSize + 1}–
          {Math.min(checklists.length, safePage * pageSize + pageSize)} of {checklists.length} checklists
        </span>
        <div className="flex flex-wrap items-center justify-center sm:justify-end gap-3">
          <div className="flex items-center gap-1.5">
            <span className="opacity-60 text-[11px]">Rows:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              className="px-1.5 py-0.5 rounded border text-xs outline-none cursor-pointer"
              style={{ background: cardColor, color: textColor, borderColor: `${ORANGE}55` }}
            >
              {[10, 25, 50, 100].map((sz) => (
                <option key={sz} value={sz}>{sz}</option>
              ))}
            </select>
          </div>
          <button
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: ORANGE }}
          >
            ← Prev
          </button>
          <span>Page {safePage + 1} / {pageCount}</span>
          <button
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: ORANGE }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Forms drill-down system — drawer/back-stack UX                     */
/* ------------------------------------------------------------------ */
function buildFormsFrame(kind, key, label, apiData, fullChecklists = [], recordsSource = null) {
  const all = recordsSource || ((Array.isArray(fullChecklists) && fullChecklists.length > 0) ? fullChecklists : (apiData?.recent_checklists || []));
  let records = all;
  let title = label || String(key);
  let stat = null;

  if (kind === "status") {
    records = key === "pending"
      ? all.filter((c) => c.status === "open" || c.status === "in_progress")
      : all.filter((c) => c.status === key);
    title = label || formsStatusLabel(key);
  } else if (kind === "template") {
    records = all.filter((c) => String(c.template_id ?? c.template?.id ?? c.template) === String(key));
    stat = (apiData?.charts?.template_breakdown || []).find((t) => String(t.template_id) === String(key));
    if (!label && stat?.template__name) {
      title = stat.template__name;
    }
  } else if (kind === "tower") {
    records = all.filter((c) => String(c.tower_id) === String(key) || c.tower_name === key);
    stat = (apiData?.charts?.tower_breakdown || []).find((t) => String(t.tower_id) === String(key));
  } else if (kind === "floor") {
    records = all.filter((c) => String(c.floor_id) === String(key) || c.floor_name === key);
    stat = (apiData?.charts?.floor_breakdown || []).find((f) => String(f.floor_id) === String(key));
  } else if (kind === "role") {
    stat = (apiData?.charts?.role_breakdown || []).find((r) => r.role === key);
    records = all.filter((c) => c.pending_role === key || c.role === key);
  } else if (kind === "project") {
    records = all.filter((c) => {
      const cPid = c.project_id ?? c.projectId ?? c.project;
      return (cPid !== undefined && String(cPid) === String(key)) || c.project_name === key;
    });
    const rawPname = records[0]?.project_name && !/^project\s*#?\s*\d+$/i.test(records[0].project_name) ? records[0].project_name : null;
    title = label || rawPname || (key && getActiveDashboardProjectName(key)) || "Unnamed Project";
  } else if (kind === "checklist") {
    records = all.filter((c) => c.id === key);
    title = `Checklist #${key}`;
  }

  return { kind, key, title, records, stat };
}

function FormsDrillSummary({ frame, onExplore, onDrillFurther }) {
  const { records, title, stat, kind } = frame;

  // Aggregate sub-dimensions for interactive drill-further
  const statusCounts = useMemo(() => {
    const counts = { open: 0, in_progress: 0, completed: 0 };
    records.forEach((c) => {
      if (counts[c.status] !== undefined) counts[c.status] += 1;
    });
    return counts;
  }, [records]);

  const templateBreakdown = useMemo(() => {
    const map = {};
    records.forEach((c) => {
      const tid = c.template_id ?? c.template?.id ?? c.template ?? "Unknown";
      const tname = c.template__name || c.template_name || `Template #${tid}`;
      if (!map[tid]) map[tid] = { id: String(tid), name: tname, count: 0 };
      map[tid].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [records]);

  const towerBreakdown = useMemo(() => {
    const map = {};
    records.forEach((c) => {
      const tid = c.tower_id || c.tower_name || "Unknown";
      const tname = c.tower_name || `Tower #${tid}`;
      if (!map[tid]) map[tid] = { id: String(tid), name: tname, count: 0 };
      map[tid].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [records]);

  const floorBreakdown = useMemo(() => {
    const map = {};
    records.forEach((c) => {
      const fid = c.floor_id || c.floor_name || "Unknown";
      const fname = c.floor_name || `Floor #${fid}`;
      if (!map[fid]) map[fid] = { id: String(fid), name: fname, count: 0 };
      map[fid].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [records]);

  const projectBreakdown = useMemo(() => {
    const map = {};
    records.forEach((c) => {
      const pid = c.project_id ?? c.projectId ?? c.project ?? "Unknown";
      const rawPname = c.project_name && !/^project\s*#?\s*\d+$/i.test(c.project_name) ? c.project_name : null;
      const pname = rawPname || (pid !== "Unknown" && getActiveDashboardProjectName(pid)) || "Unnamed Project";
      if (!map[pid]) map[pid] = { id: String(pid), name: pname, count: 0 };
      map[pid].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [records]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold" style={{ color: ORANGE }}>{title}</h3>
          <p className="text-xs opacity-70 mt-0.5">
            {records.length} matching checklist{records.length === 1 ? "" : "s"}
          </p>
        </div>
        <span
          className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0"
          style={{ background: `${ORANGE}22`, color: ORANGE, border: `1px solid ${ORANGE}55` }}
        >
          {records.length} Checklists
        </span>
      </div>

      {stat && (
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(stat)
            .filter(([k]) => !k.endsWith("_id") && !k.endsWith("__name") && k !== "role" && typeof stat[k] === "number")
            .map(([k, v]) => (
              <div key={k} className="rounded-lg p-2.5 border" style={{ borderColor: `${ORANGE}44`, background: `${ORANGE}0c` }}>
                <p className="text-[10px] uppercase opacity-60 font-semibold">{k.replace(/_/g, " ")}</p>
                <p className="text-base font-extrabold" style={{ color: ORANGE }}>{v}</p>
              </div>
            ))}
        </div>
      )}

      {/* Sub-group 1: Status Distribution Pills */}
      {kind !== "status" && (
        <div className="rounded-xl p-3 border" style={{ borderColor: `${ORANGE}33`, background: `${ORANGE}08` }}>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-2">Drill by Status</p>
          <div className="flex flex-wrap gap-1.5">
            {["open", "in_progress", "completed"].map((st) => {
              const count = statusCounts[st] || 0;
              const color = formsStatusColor(st);
              return (
                <button
                  key={st}
                  type="button"
                  onClick={() => onDrillFurther && onDrillFurther("status", st, formsStatusLabel(st))}
                  disabled={count === 0}
                  className="text-xs px-2.5 py-1 rounded-lg border font-medium flex items-center gap-1.5 transition-all hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
                  style={{ borderColor: `${color}66`, color }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                  <span>{formsStatusLabel(st)}</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded font-bold" style={{ background: `${color}25` }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sub-group 2: Template Breakdown */}
      {kind !== "template" && templateBreakdown.length > 0 && (
        <div className="rounded-xl p-3 border" style={{ borderColor: `${ORANGE}33`, background: `${ORANGE}08` }}>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-2">Drill by Template</p>
          <div className="flex flex-wrap gap-1.5">
            {templateBreakdown.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onDrillFurther && onDrillFurther("template", t.id, t.name)}
                className="text-xs px-2.5 py-1 rounded-lg border font-medium flex items-center gap-1.5 transition-all hover:scale-105"
                style={{ borderColor: `${ORANGE}44`, color: "inherit" }}
              >
                <span className="truncate max-w-[140px]">{t.name}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded font-bold" style={{ background: `${ORANGE}25`, color: ORANGE }}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sub-group 3: Tower Breakdown */}
      {kind !== "tower" && towerBreakdown.length > 0 && (
        <div className="rounded-xl p-3 border" style={{ borderColor: `${ORANGE}33`, background: `${ORANGE}08` }}>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-2">Drill by Tower</p>
          <div className="flex flex-wrap gap-1.5">
            {towerBreakdown.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onDrillFurther && onDrillFurther("tower", t.id, t.name)}
                className="text-xs px-2.5 py-1 rounded-lg border font-medium flex items-center gap-1.5 transition-all hover:scale-105"
                style={{ borderColor: `${ORANGE}44`, color: "inherit" }}
              >
                <span>{t.name}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded font-bold" style={{ background: `${ORANGE}25`, color: ORANGE }}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sub-group 4: Floor Breakdown */}
      {kind !== "floor" && floorBreakdown.length > 0 && (
        <div className="rounded-xl p-3 border" style={{ borderColor: `${ORANGE}33`, background: `${ORANGE}08` }}>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-2">Drill by Floor</p>
          <div className="flex flex-wrap gap-1.5">
            {floorBreakdown.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => onDrillFurther && onDrillFurther("floor", f.id, f.name)}
                className="text-xs px-2.5 py-1 rounded-lg border font-medium flex items-center gap-1.5 transition-all hover:scale-105"
                style={{ borderColor: `${ORANGE}44`, color: "inherit" }}
              >
                <span>{f.name}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded font-bold" style={{ background: `${ORANGE}25`, color: ORANGE }}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sub-group 5: Project Breakdown */}
      {kind !== "project" && projectBreakdown.length > 1 && (
        <div className="rounded-xl p-3 border" style={{ borderColor: `${ORANGE}33`, background: `${ORANGE}08` }}>
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-2">Drill by Project</p>
          <div className="flex flex-wrap gap-1.5">
            {projectBreakdown.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onDrillFurther && onDrillFurther("project", p.id, p.name)}
                className="text-xs px-2.5 py-1 rounded-lg border font-medium flex items-center gap-1.5 transition-all hover:scale-105"
                style={{ borderColor: `${ORANGE}44`, color: "inherit" }}
              >
                <span className="truncate max-w-[130px]">{p.name}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded font-bold" style={{ background: `${ORANGE}25`, color: ORANGE }}>
                  {p.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {kind === "role" && records.length === 0 ? (
        <p className="text-xs opacity-60 p-2">
          The API reports aggregate pending questions for this role across checklists.
        </p>
      ) : records.length === 0 ? (
        <p className="text-sm opacity-50 py-6 text-center">No checklists match this selection.</p>
      ) : (
        <button
          type="button"
          onClick={onExplore}
          className="w-full text-xs sm:text-sm font-bold px-4 py-2.5 rounded-xl shadow transition-all hover:scale-[1.01]"
          style={{ background: ORANGE, color: "#1a1a1a" }}
        >
          View {records.length} Checklist{records.length === 1 ? "" : "s"} Table &amp; Cards →
        </button>
      )}
    </div>
  );
}

function FormsDrillRecordsList({ frame, cardColor, theme, onOpenChecklist }) {
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return frame.records;
    const q = searchTerm.toLowerCase();
    return frame.records.filter((c) => {
      const idStr = String(c.id || "").toLowerCase();
      const tmpl = String(c.template__name || c.template_name || "").toLowerCase();
      const proj = String(c.project_name || "").toLowerCase();
      const tower = String(c.tower_name || "").toLowerCase();
      const floor = String(c.floor_name || "").toLowerCase();
      const st = String(c.status || "").toLowerCase();
      return (
        idStr.includes(q) ||
        tmpl.includes(q) ||
        proj.includes(q) ||
        tower.includes(q) ||
        floor.includes(q) ||
        st.includes(q)
      );
    });
  }, [frame.records, searchTerm]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-bold truncate" style={{ color: ORANGE }}>{frame.title}</h3>
        <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-amber-500/10 text-amber-500 shrink-0">
          {filtered.length} / {frame.records.length}
        </span>
      </div>

      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search template, tower, floor, status…"
          className="w-full text-xs px-3 py-2 rounded-lg border outline-none transition-all"
          style={{
            borderColor: `${ORANGE}55`,
            background: theme === "dark" ? "#1e1e24" : "#faf7f2",
            color: "inherit",
          }}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs opacity-50 hover:opacity-100"
          >
            ✕
          </button>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block rounded-lg overflow-hidden" style={{ border: `1px solid ${ORANGE}44` }}>
        <div className="max-h-[440px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0" style={{ background: cardColor }}>
              <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                {["ID", "Template", "Project", "Tower", "Floor", "Status", "Created"].map((h) => (
                  <th key={h} className="text-left px-2.5 py-2 text-[10px] uppercase tracking-wide opacity-60 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => onOpenChecklist && onOpenChecklist(c)}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  style={{ borderBottom: `1px solid ${borderColor}` }}
                >
                  <td className="px-2.5 py-2 font-bold whitespace-nowrap" style={{ color: ORANGE }}>#{c.id}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap max-w-[130px] truncate">{c.template__name || c.template_name || "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap max-w-[110px] truncate">{c.project_name || "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{c.tower_name || "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap">{c.floor_name || "—"}</td>
                  <td className="px-2.5 py-2 whitespace-nowrap"><FormsChecklistBadge status={c.status} /></td>
                  <td className="px-2.5 py-2 whitespace-nowrap opacity-70">{formatIsoDate(c.created_at)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center opacity-50 text-xs">No checklists match the search query.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card List */}
      <div className="block sm:hidden space-y-2 max-h-[440px] overflow-y-auto pr-0.5">
        {filtered.map((c) => (
          <div
            key={c.id}
            onClick={() => onOpenChecklist && onOpenChecklist(c)}
            className="p-3 rounded-xl border cursor-pointer hover:border-amber-500 transition-all space-y-1.5"
            style={{ borderColor: `${ORANGE}33`, background: `${ORANGE}06` }}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="font-bold text-xs" style={{ color: ORANGE }}>#{c.id}</span>
                <p className="font-semibold text-xs leading-snug line-clamp-1">{c.template__name || c.template_name || "Checklist"}</p>
              </div>
              <FormsChecklistBadge status={c.status} />
            </div>
            <div className="grid grid-cols-2 gap-1 text-[11px] opacity-75">
              <div><span className="opacity-50">Tower: </span>{c.tower_name || "—"}</div>
              <div><span className="opacity-50">Floor: </span>{c.floor_name || "—"}</div>
              <div className="col-span-2 truncate"><span className="opacity-50">Project: </span>{c.project_name || "—"}</div>
            </div>
            <div className="flex items-center justify-between pt-1 border-t text-[10px] opacity-60" style={{ borderColor: `${borderColor}` }}>
              <span>{formatIsoDate(c.created_at)}</span>
              <span style={{ color: ORANGE }} className="font-semibold">View Details →</span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="p-8 text-center opacity-50 text-xs">No checklists match your search.</div>
        )}
      </div>
    </div>
  );
}

function FormsDrillChecklist({ checklist, theme }) {
  if (!checklist) return <p className="text-sm opacity-50 py-10 text-center">Checklist not found in recent activity.</p>;
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const fields = [
    ["Checklist ID", `#${checklist.id}`],
    ["Template", checklist.template__name || checklist.template_name],
    ["Project", checklist.project_name],
    ["Tower", checklist.tower_name],
    ["Floor", checklist.floor_name],
    ["Status", null],
    ["Pending From", checklist.pending_role || checklist.pending_from || "—"],
    ["Created At", formatIsoDate(checklist.created_at)],
    ["Completed At", formatIsoDate(checklist.completed_at) || "Not Completed"],
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-bold" style={{ color: ORANGE }}>Checklist #{checklist.id}</h3>
        <FormsChecklistBadge status={checklist.status} />
      </div>
      <div className="rounded-xl p-4 space-y-3 border" style={{ borderColor: `${ORANGE}44` }}>
        {fields.map(([label, value]) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4"
            style={{ borderBottom: `1px solid ${borderColor}`, paddingBottom: 8 }}
          >
            <span className="text-[11px] uppercase tracking-wide opacity-60 shrink-0 font-semibold">{label}</span>
            {label === "Status" ? (
              <FormsChecklistBadge status={checklist.status} />
            ) : (
              <span className="text-xs text-right font-medium">{value || "—"}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FormsDrillDrawer({ stack, onBack, onClose, onPush, onOpenChecklist, onSlice, cardColor, textColor, theme, apiData, fullChecklists }) {
  if (stack.length === 0) return null;
  const top = stack[stack.length - 1];
  const isChecklistFrame = top.kind === "checklist";
  const isRecordsFrame = top.kind === "records";

  const handleDrillFurther = (kind, key, label) => onPush(buildFormsFrame(kind, key, label, apiData, fullChecklists, top.records));
  const handleExplore = () => onPush({ kind: "records", key: top.key, title: top.title, records: top.records });

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end" style={{ background: "rgba(0,0,0,0.4)" }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-full sm:max-w-md overflow-y-auto shadow-2xl p-4 sm:p-5"
        style={{ background: cardColor, color: textColor, paddingTop: (typeof window !== "undefined" && window.innerWidth < 768) ? 68 : 96 }}
      >
        <div className="flex items-center justify-between mb-4 gap-2">
          {/* Clickable Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            {stack.map((f, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="opacity-40">/</span>}
                <button
                  type="button"
                  onClick={() => onSlice && onSlice(i)}
                  disabled={i === stack.length - 1}
                  className={`font-semibold transition-all ${
                    i === stack.length - 1
                      ? "cursor-default"
                      : "opacity-60 hover:opacity-100 hover:underline"
                  }`}
                  style={{ color: i === stack.length - 1 ? ORANGE : undefined }}
                >
                  {f.kind === "checklist" ? (f.key?.id ? `Checklist #${f.key.id}` : f.title) : f.title}
                </button>
              </React.Fragment>
            ))}
          </div>
          <button onClick={onClose} className="text-lg opacity-60 hover:opacity-100 p-1">✕</button>
        </div>

        {stack.length > 1 && (
          <button
            type="button"
            onClick={onBack}
            className="text-xs mb-4 px-3 py-1 rounded-full border font-semibold transition-all hover:scale-105"
            style={{ borderColor: ORANGE, color: ORANGE }}
          >
            ← Back
          </button>
        )}

        {isChecklistFrame && <FormsDrillChecklist checklist={top.key} theme={theme} />}
        {isRecordsFrame && (
          <FormsDrillRecordsList frame={top} cardColor={cardColor} theme={theme} onOpenChecklist={onOpenChecklist} />
        )}
        {!isChecklistFrame && !isRecordsFrame && (
          <FormsDrillSummary frame={top} onExplore={handleExplore} onDrillFurther={handleDrillFurther} />
        )}
      </div>
    </div>
  );
}

/* ==================================================================== */
/*  ✅ FIX for the top-level "SNAGGING" overview card in                 */
/*  ProjectHeadDashboard. Two bugs fixed:                                */
/*                                                                        */
/*  1. It called getUnitStageRoleSummary(activeProjectId ? {...} : {})   */
/*     — when there's no active project it sent {} with no project_id,   */
/*     but that endpoint requires project_id per call, so it silently    */
/*     returned nothing → card showed 0 / 0 pending.                     */
/*                                                                        */
/*  2. It read `counts.pending_units`, which doesn't exist on the        */
/*     response — the real field is `pending_yet_to_start`.              */
/*                                                                        */
/*  This adds a small helper to resolve every project the user has       */
/*  access to (same logic already used inside SnaggingDashboard), then   */
/*  aggregates snagging totals across all of them when no single         */
/*  project is active — mirroring how MIR/QC/NCR already behave.         */
/* ==================================================================== */

// Note: getActiveDashboardProjectId and getAccessibleProjects are defined at the top of this module for shared usage.

/* ------------------------------------------------------------------ */
/*  Inside ProjectHeadDashboard's `loadOverviewStats` function,         */
/*  REPLACE the "5. Snagging Stats" block with this:                    */
/* ------------------------------------------------------------------ */

/*
    // 5. Snagging Stats — aggregate across all accessible projects when
    // no single project is active (mirrors MIR/QC/NCR behavior); use a
    // single scoped call when one is active (unchanged from before).
    let snaggingTotal = 0;
    let snaggingPending = 0;

    if (activeProjectId) {
      if (snagRes.status === "fulfilled" && snagRes.value?.data?.counts) {
        const c = snagRes.value.data.counts;
        snaggingTotal = c.total_units ?? 0;
        snaggingPending = c.pending_yet_to_start ?? 0; // ✅ correct field name
      }
    } else {
      const accessibleProjects = getAccessibleProjects();
      if (accessibleProjects.length > 0) {
        const snagResults = await Promise.allSettled(
          accessibleProjects.map((p) => getUnitStageRoleSummary({ project_id: p.id }))
        );
        snagResults.forEach((r) => {
          if (r.status === "fulfilled" && r.value?.data?.counts) {
            const c = r.value.data.counts;
            snaggingTotal += Number(c.total_units || 0);
            snaggingPending += Number(c.pending_yet_to_start || 0); // ✅ correct field name
          }
        });
      }
    }
*/

/* ------------------------------------------------------------------ */
/*  Also update the Promise.allSettled call list at the top of          */
/*  loadOverviewStats: since Snagging is now handled separately above,  */
/*  you can DROP `getUnitStageRoleSummary(...)` from that original      */
/*  destructured array (the `snagRes` slot) when activeProjectId is     */
/*  falsy — or simplest, just keep it in the array (harmless, unused    */
/*  when aggregating) and only branch on it inside the block above.     */
/*  No change needed there if you'd rather keep the diff minimal.       */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Full Forms dashboard — Centralized State & Cascading Engine        */
/* ------------------------------------------------------------------ */
function FormsDashboard({
  theme,
  cardColor,
  textColor,
  onExportContext,
  selectedProjectId,
  selectedProjectName,
  onSelectProject,
}) {
  // Resolve default project for the Project Head
  const defaultProjectId = useMemo(
    () => selectedProjectId || (selectedProjectName === "ALL" ? "" : getActiveDashboardProjectId()),
    [selectedProjectId, selectedProjectName]
  );

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    project_id: selectedProjectId || (selectedProjectName === "ALL" ? "" : defaultProjectId) || "",
    tower_id: "",
    floor_id: "",
    template_id: "",
    status: "",
    role: "",
  });

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      project_id: selectedProjectId || "",
      tower_id: "",
      floor_id: "",
    }));
  }, [selectedProjectId]);

  const [apiData, setApiData] = useState(null);
  const [fullChecklists, setFullChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drillStack, setDrillStack] = useState([]);

  // Master knowledge stores populated across requests & storage
  const [masterProjects, setMasterProjects] = useState([]);
  const [masterTowers, setMasterTowers] = useState({});
  const [masterFloors, setMasterFloors] = useState({});

  // 1. Initial discovery of accessible projects from localStorage & API
  React.useEffect(() => {
    let isMounted = true;
    async function loadFormsProjects() {
      try {
        const accessible = getAccessibleProjects();
        if (accessible.length > 0 && isMounted) {
          setMasterProjects(accessible);
        }

        const res = await getProjectsForCurrentUser();
        const rows = res?.data?.projects || res?.data || [];
        if (Array.isArray(rows) && rows.length > 0 && isMounted) {
          const mapped = rows
            .map((p) => {
              const rawId = p?.id || p?.project_id || p?.uuid;
              if (rawId == null) return null;
              const strId = String(rawId).trim();
              if (strId.includes("/")) return null;
              const numId = Number(strId);
              if (isNaN(numId) || !Number.isInteger(numId) || numId <= 0) return null;

              const rawName = String(p?.name || p?.project_name || p?.title || "").trim();
              const isSynthetic = !rawName || /^project\s*#?\s*\d+$/i.test(rawName);
              return {
                id: String(numId),
                name: isSynthetic ? "Unnamed Project" : rawName,
              };
            })
            .filter(Boolean);

          if (mapped.length > 0 && isMounted) {
            setMasterProjects((prev) => {
              const map = new Map(prev.map((p) => [String(p.id), p]));
              mapped.forEach((p) => map.set(String(p.id), p));
              return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
            });
          }
        }
      } catch (e) {
        console.warn("[FormsDashboard] Error loading initial project accesses:", e);
      }
    }
    loadFormsProjects();
    return () => {
      isMounted = false;
    };
  }, []);

  // 2. Fetch dashboard data & full checklists whenever any filter changes
  React.useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);

      Promise.allSettled([
        fetchFormsDashboardData(filters),
        fetchManualChecklistsList(filters),
      ])
        .then(([dashSettled, listSettled]) => {
          if (cancelled) return;
          const data = dashSettled.status === "fulfilled" ? dashSettled.value : null;
          setApiData(data);

          const list = listSettled.status === "fulfilled" && Array.isArray(listSettled.value) ? listSettled.value : [];
          setFullChecklists(list);

          // Ingest new metadata from this response into master caches
          if (data || list.length > 0) {
            const allItems = [...(list || []), ...((data && data.recent_checklists) || [])];
            // Update projects
            const newProjects = [];
            allItems.forEach((c) => {
              if (c.project_id && c.project_name) {
                newProjects.push({ id: String(c.project_id), name: String(c.project_name) });
              }
            });
            if (newProjects.length > 0) {
              setMasterProjects((prev) => {
                const map = new Map(prev.map((p) => [String(p.id), p]));
                newProjects.forEach((p) => map.set(String(p.id), p));
                return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
              });
            }

            // Update towers (bound to project_id)
            setMasterTowers((prev) => {
              const updated = { ...prev };
              (data?.charts?.tower_breakdown || []).forEach((t) => {
                const tid = String(t.tower_id);
                updated[tid] = {
                  id: tid,
                  name: t.tower_name || `Tower #${t.tower_id}`,
                  project_id: filters.project_id || updated[tid]?.project_id || "",
                  project_name: updated[tid]?.project_name || "",
                };
              });
              allItems.forEach((c) => {
                if (c.tower_id) {
                  const tid = String(c.tower_id);
                  updated[tid] = {
                    id: tid,
                    name: c.tower_name || `Tower #${c.tower_id}`,
                    project_id: c.project_id ? String(c.project_id) : updated[tid]?.project_id || "",
                    project_name: c.project_name || updated[tid]?.project_name || "",
                  };
                }
              });
              return updated;
            });

            // Update floors (bound to tower_id & project_id)
            setMasterFloors((prev) => {
              const updated = { ...prev };
              (data?.charts?.floor_breakdown || []).forEach((f) => {
                const fid = String(f.floor_id);
                updated[fid] = {
                  id: fid,
                  name: f.floor_name || `Floor #${f.floor_id}`,
                  tower_id: filters.tower_id || updated[fid]?.tower_id || "",
                  project_id: filters.project_id || updated[fid]?.project_id || "",
                };
              });
              allItems.forEach((c) => {
                if (c.floor_id) {
                  const fid = String(c.floor_id);
                  updated[fid] = {
                    id: fid,
                    name: c.floor_name || `Floor #${c.floor_id}`,
                    tower_id: c.tower_id ? String(c.tower_id) : updated[fid]?.tower_id || "",
                    tower_name: c.tower_name || updated[fid]?.tower_name || "",
                    project_id: c.project_id ? String(c.project_id) : updated[fid]?.project_id || "",
                  };
                }
              });
              return updated;
            });
          }
        })
        .catch((err) => {
          if (!cancelled) setError(err.message || "Failed to load Forms dashboard data.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters]);

  // 3. Cascading Filter Change Handler with validation and dependent reset
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };

      if (key === "project_id") {
        // If switching project, verify if selected tower still belongs to this project
        if (next.tower_id) {
          const tower = masterTowers[next.tower_id];
          if (tower && value && tower.project_id && String(tower.project_id) !== String(value)) {
            next.tower_id = "";
            next.floor_id = "";
          }
        }
        // Verify if selected floor still belongs to this project
        if (next.floor_id) {
          const floor = masterFloors[next.floor_id];
          if (floor && value && floor.project_id && String(floor.project_id) !== String(value)) {
            next.floor_id = "";
          }
        }
      }

      if (key === "tower_id") {
        // If switching tower, verify if selected floor still belongs to this tower
        if (next.floor_id) {
          const floor = masterFloors[next.floor_id];
          if (floor && value && floor.tower_id && String(floor.tower_id) !== String(value)) {
            next.floor_id = "";
          }
        }
        // If user picked a tower and project was empty, auto-scope project if known
        if (value && !next.project_id && masterTowers[value]?.project_id) {
          next.project_id = String(masterTowers[value].project_id);
        }
      }

      if (key === "project_id" && onSelectProject) {
        onSelectProject(value || "", null);
      }

      return next;
    });
  };

  const handleClearFilters = () => {
    if (onSelectProject) onSelectProject("", "ALL");
    setFilters({
      start_date: "",
      end_date: "",
      project_id: "",
      tower_id: "",
      floor_id: "",
      template_id: "",
      status: "",
      role: "",
    });
  };

  const handleRemoveFilter = (key) => {
    if (key === "date_range") {
      setFilters((f) => ({ ...f, start_date: "", end_date: "" }));
    } else {
      handleFilterChange(key, "");
    }
  };

  // 4. Compute cascading dropdown options
  const options = useMemo(() => {
    // Projects
    const projects = [...masterProjects];

    // Towers: If project_id is set, only show towers belonging to that project
    const allTowerList = Object.values(masterTowers);
    let towers = [];
    if (filters.project_id) {
      towers = allTowerList.filter((t) => !t.project_id || String(t.project_id) === String(filters.project_id));
      if (towers.length === 0 && apiData?.charts?.tower_breakdown) {
        towers = (apiData.charts.tower_breakdown || []).map((t) => ({
          id: String(t.tower_id),
          name: t.tower_name || `Tower #${t.tower_id}`,
        }));
      }
    } else {
      towers = (apiData?.charts?.tower_breakdown || []).map((t) => {
        const tid = String(t.tower_id);
        const known = masterTowers[tid];
        return {
          id: tid,
          name: t.tower_name || `Tower #${tid}`,
          displayName: known?.project_name ? `${t.tower_name} (${known.project_name})` : t.tower_name,
        };
      });
      if (towers.length === 0) {
        towers = allTowerList.map((t) => ({
          id: t.id,
          name: t.name,
          displayName: t.project_name ? `${t.name} (${t.project_name})` : t.name,
        }));
      }
    }
    towers = towers.sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name));

    // Floors: If tower_id is set, only show floors in that tower.
    // If project_id is set, show floors in that project.
    const allFloorList = Object.values(masterFloors);
    let floors = [];
    if (filters.tower_id) {
      floors = allFloorList.filter((f) => !f.tower_id || String(f.tower_id) === String(filters.tower_id));
      if (floors.length === 0 && apiData?.charts?.floor_breakdown) {
        floors = (apiData.charts.floor_breakdown || []).map((f) => ({
          id: String(f.floor_id),
          name: f.floor_name || `Floor #${f.floor_id}`,
        }));
      }
    } else if (filters.project_id) {
      floors = allFloorList.filter((f) => !f.project_id || String(f.project_id) === String(filters.project_id));
    } else {
      floors = (apiData?.charts?.floor_breakdown || []).map((f) => {
        const fid = String(f.floor_id);
        const known = masterFloors[fid];
        return {
          id: fid,
          name: f.floor_name || `Floor #${fid}`,
          displayName: known?.tower_name ? `${f.floor_name} (${known.tower_name})` : f.floor_name,
        };
      });
      if (floors.length === 0) {
        floors = allFloorList.map((f) => ({
          id: f.id,
          name: f.name,
          displayName: f.tower_name ? `${f.name} (${f.tower_name})` : f.name,
        }));
      }
    }
    floors = floors.sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name));

    // Templates
    const templates = (apiData?.charts?.template_breakdown || [])
      .map((t) => ({ id: String(t.template_id), name: t.template__name || `Template #${t.template_id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Roles
    const rolesFromApi = (apiData?.charts?.role_breakdown || []).map((r) => r.role);
    const standardRoles = ["CHECKER", "MAKER", "APPROVER", "REVIEWER", "INSPECTOR"];
    const roles = Array.from(new Set([...rolesFromApi, ...standardRoles, filters.role].filter(Boolean))).sort();

    return { projects, towers, floors, templates, roles };
  }, [masterProjects, masterTowers, masterFloors, apiData, filters.project_id, filters.tower_id, filters.role]);

  useEffect(() => {
    if (onExportContext) {
      const selectedProjectObj = options.projects.find((p) => String(p.id) === String(filters.project_id));
      const selectedProjectName = selectedProjectObj?.name || (filters.project_id ? (getActiveDashboardProjectName(filters.project_id) || "Unnamed Project") : "");
      onExportContext({
        filters,
        selectedProjectId: filters.project_id,
        selectedProjectName,
        options,
        apiData,
        checklists: fullChecklists,
      });
    }
  }, [filters, options, apiData, fullChecklists, onExportContext]);

  const openDrill = (kind, key, label) => setDrillStack([buildFormsFrame(kind, key, label, apiData, fullChecklists)]);
  const pushDrill = (frame) => setDrillStack((s) => [...s, frame]);
  const popDrill = () => setDrillStack((s) => s.slice(0, -1));
  const closeDrill = () => setDrillStack([]);

  const openChecklist = (itemOrId) => {
    const checklist =
      typeof itemOrId === "object" && itemOrId !== null
        ? itemOrId
        : (fullChecklists || []).find((c) => c.id === itemOrId) ||
          (apiData?.recent_checklists || []).find((c) => c.id === itemOrId);
    if (checklist) pushDrill({ kind: "checklist", key: checklist, title: `Checklist #${checklist.id || itemOrId}`, records: [checklist] });
  };

  const hasZeroRecords =
    apiData &&
    (apiData.widgets?.total_checklists === 0 || apiData.widgets?.total_checklists === undefined) &&
    (apiData.recent_checklists?.length === 0 || apiData.recent_checklists === undefined) &&
    fullChecklists.length === 0;

  return (
    <div className="space-y-4" id="active-dashboard-charts-container">
      {/* 1. Universal Cascading Filter Bar */}
      <FormsFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        onRemoveFilter={handleRemoveFilter}
        options={options}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
        loading={loading}
      />

      {error && (
        <div className="rounded-lg p-4 flex items-center justify-between" style={{ background: cardColor, border: `1px solid #e0605e` }}>
          <p className="text-sm" style={{ color: "#e0605e" }}>{error}</p>
          <button
            onClick={() => handleClearFilters()}
            className="text-xs px-3 py-1 rounded border border-red-400 text-red-400 hover:bg-red-500/10"
          >
            Reset Filters
          </button>
        </div>
      )}

      {loading && !apiData && (
        <div className="rounded-lg p-12 text-center" style={{ background: cardColor, border: `1px solid ${ORANGE}44` }}>
          <div className="inline-block w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm font-semibold opacity-70">Fetching filtered checklist analytics…</p>
        </div>
      )}

      {apiData && hasZeroRecords && !loading && (
        <div className="rounded-lg p-12 text-center shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}44` }}>
          <div className="text-3xl mb-2">📋</div>
          <h3 className="text-base font-bold mb-1" style={{ color: ORANGE }}>
            No checklist data available for the selected filters.
          </h3>
          <p className="text-xs opacity-60 max-w-md mx-auto mb-4">
            Try adjusting your project, tower, floor, template, status, or date range filters to view checklist metrics.
          </p>
          <button
            onClick={handleClearFilters}
            className="text-xs font-semibold px-4 py-2 rounded-md"
            style={{ background: ORANGE, color: "#1a1a1a" }}
          >
            Clear Filters & View All Data
          </button>
        </div>
      )}

      {apiData && !hasZeroRecords && (
        <div className="space-y-4 relative">
          {loading && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-lg backdrop-blur-[1px]"
              style={{ background: theme === "dark" ? "rgba(23, 23, 29, 0.4)" : "rgba(255, 255, 255, 0.4)" }}
            >
              <div className="px-4 py-2 rounded-full shadow-lg border flex items-center gap-2 bg-amber-500 text-black text-xs font-bold animate-pulse">
                <span>Updating data…</span>
              </div>
            </div>
          )}

          {/* Summary KPI Counters */}
          <FormsSummaryCards
            widgets={apiData.widgets}
            activeStatus={filters.status}
            cardColor={cardColor}
            textColor={textColor}
            onSelectStatus={(status) => handleFilterChange("status", status)}
          />

          {/* Row 1: Checklist Status, Questions Progress & Quality Compliance (3 Balanced Cards) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow h-full flex flex-col justify-between" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-60">Status Distribution</p>
                  {filters.status && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                      Filtered: {formsStatusLabel(filters.status)}
                    </span>
                  )}
                </div>
                <p className="text-[11px] opacity-50 mb-2">
                  Click any slice to filter by status.
                </p>
              </div>
              <FormsStatusChart
                widgets={apiData.widgets}
                activeStatus={filters.status}
                cardColor={cardColor}
                textColor={textColor}
                onSelectStatus={(status) => handleFilterChange("status", status)}
                onDrill={openDrill}
              />
            </div>
            <div data-pdf-chart="true">
              <FormsQuestionsCard widgets={apiData.widgets} cardColor={cardColor} textColor={textColor} />
            </div>
            <div data-pdf-chart="true">
              <FormsResponsesChart widgets={apiData.widgets} cardColor={cardColor} textColor={textColor} />
            </div>
          </div>

          {/* Row 2: Checklists by Template (Full Width for optimal bar visibility & zero wasted space) */}
          <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold uppercase tracking-wide opacity-60">Checklists by Template</p>
              {filters.template_id && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                  Filtered
                </span>
              )}
            </div>
            <p className="text-[11px] opacity-50 mb-2">
              Checklist volume per template for active filters. Click a bar to filter by template.
            </p>
            <FormsTemplateChart
              charts={apiData.charts}
              activeTemplateId={filters.template_id}
              cardColor={cardColor}
              textColor={textColor}
              theme={theme}
              onSelectTemplate={(tid) => handleFilterChange("template_id", tid === filters.template_id ? "" : tid)}
              onDrill={openDrill}
            />
          </div>

          {/* Row 3: Checklist Activity & Completion Trend (Area Chart) */}
          <FormsActivityTimelineChart
            checklists={fullChecklists.length > 0 ? fullChecklists : (apiData.recent_checklists || [])}
            cardColor={cardColor}
            textColor={textColor}
            theme={theme}
          />

          {/* Row 4: Checklists by Tower & Checklists by Floor */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-60">Checklists by Tower</p>
                {filters.tower_id && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                    Filtered
                  </span>
                )}
              </div>
              <p className="text-[11px] opacity-50 mb-2">
                {filters.project_id
                  ? "Tower checklist volume within the selected project. Click a bar to filter by tower."
                  : "Tower checklist volume across current scope. Click a bar to filter by tower."}
              </p>
              <FormsTowerChart
                charts={apiData.charts}
                selectedProjectId={filters.project_id}
                activeTowerId={filters.tower_id}
                cardColor={cardColor}
                textColor={textColor}
                theme={theme}
                onSelectTower={(tid) => handleFilterChange("tower_id", tid === filters.tower_id ? "" : tid)}
                onDrill={openDrill}
              />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-60">Checklists by Floor</p>
                {filters.floor_id && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                    Filtered
                  </span>
                )}
              </div>
              <p className="text-[11px] opacity-50 mb-2">
                {filters.tower_id
                  ? "Floor volume for the selected tower. Click a bar to filter by floor."
                  : "Floor volume for the current filters. Click a bar to filter by floor."}
              </p>
              <FormsFloorChart
                charts={apiData.charts}
                activeFloorId={filters.floor_id}
                cardColor={cardColor}
                textColor={textColor}
                theme={theme}
                onSelectFloor={(fid) => handleFilterChange("floor_id", fid === filters.floor_id ? "" : fid)}
                onDrill={openDrill}
              />
            </div>
          </div>

          {/* Row 4: Questions by Role & Recent Activity by Project */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-60">Pending Questions by Role</p>
                {filters.role && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                    Filtered: {filters.role}
                  </span>
                )}
              </div>
              <p className="text-[11px] opacity-50 mb-2">
                Roles with unanswered questions for the selected filters. Click a bar to filter by role.
              </p>
              <FormsRoleChart
                charts={apiData.charts}
                activeRole={filters.role}
                cardColor={cardColor}
                textColor={textColor}
                theme={theme}
                onSelectRole={(role) => handleFilterChange("role", role === filters.role ? "" : role)}
                onDrill={openDrill}
              />
            </div>
            <div data-pdf-chart="true" className="rounded-lg p-4 shadow" style={{ background: cardColor, border: `1px solid ${ORANGE}66` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-60">Recent Checklists by Project</p>
                {filters.project_id && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500">
                    Filtered
                  </span>
                )}
              </div>
              <p className="text-[11px] opacity-50 mb-2">
                Project volume in recent activity. Click a bar to filter by project.
              </p>
              <FormsRecentProjectChart
                checklists={fullChecklists.length > 0 ? fullChecklists : (apiData.recent_checklists || [])}
                activeProjectId={filters.project_id}
                cardColor={cardColor}
                textColor={textColor}
                theme={theme}
                onSelectProject={(pid) => handleFilterChange("project_id", pid === filters.project_id ? "" : pid)}
                onDrill={openDrill}
              />
            </div>
          </div>

          {/* Checklists Table */}
          {(() => {
            let list = (Array.isArray(fullChecklists) && fullChecklists.length > 0)
              ? fullChecklists
              : (apiData.recent_checklists || []);
            if (filters.project_id) {
              const selectedProj = options.projects.find((p) => String(p.id) === String(filters.project_id));
              list = list.filter((c) => {
                const cPid = c.project_id ?? c.projectId ?? c.project;
                if (cPid !== undefined && cPid !== null && String(cPid).trim() !== "") {
                  return String(cPid) === String(filters.project_id);
                }
                if (selectedProj?.name && c.project_name) {
                  return c.project_name.trim().toLowerCase() === selectedProj.name.trim().toLowerCase();
                }
                return true;
              });
            }
            if (filters.tower_id) {
              list = list.filter((c) => String(c.tower_id) === String(filters.tower_id));
            }
            if (filters.floor_id) {
              list = list.filter((c) => String(c.floor_id) === String(filters.floor_id));
            }
            if (filters.template_id) {
              list = list.filter((c) => String(c.template_id ?? c.template?.id ?? c.template) === String(filters.template_id));
            }
            const filtered = filters.status
              ? filters.status === "pending"
                ? list.filter((c) => c.status === "open" || c.status === "in_progress")
                : list.filter((c) => c.status === filters.status)
              : list;
            const selectedProjName = options.projects.find((p) => String(p.id) === String(filters.project_id))?.name;
            const isFullList = Array.isArray(fullChecklists) && fullChecklists.length > 0;
            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-60">
                    {isFullList ? "Checklists" : "Recent Checklists"} ({filtered.length}
                    {filters.project_id ? ` — Project: ${selectedProjName || filters.project_id}` : ""}
                    {filters.status ? ` — ${formsStatusLabel(filters.status)}` : ""})
                  </p>
                </div>
                <FormsTable
                  checklists={filtered}
                  cardColor={cardColor}
                  textColor={textColor}
                  theme={theme}
                  onOpenChecklist={openChecklist}
                />
              </div>
            );
          })()}
        </div>
      )}

      {/* Drill-down Drawer */}
      <FormsDrillDrawer
        stack={drillStack}
        onBack={popDrill}
        onClose={closeDrill}
        onPush={pushDrill}
        onOpenChecklist={openChecklist}
        onSlice={(idx) => setDrillStack((s) => s.slice(0, idx + 1))}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
        apiData={apiData}
        fullChecklists={fullChecklists}
      />
    </div>
  );
}

/* Helper to normalize API list responses */
const normalizeList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.results)) return res.results;
  if (Array.isArray(res.data?.data)) return res.data.data;
  if (Array.isArray(res.data?.results)) return res.data.results;
  return [];
};

/* Format last activity timestamp into user-friendly date */
const formatActivityDate = (dateVal) => {
  if (!dateVal || dateVal === "—" || dateVal === "-" || dateVal === "Not Ended") return "—";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return String(dateVal);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return String(dateVal);
  }
};

/* Natural ascending sort helper for units and flats (e.g. Flat 101, 102, 201, 301, 601) */
function sortSnaggingRecords(records) {
  if (!Array.isArray(records)) return [];
  return [...records].sort((a, b) => {
    // 1. Tower (Tower A before Tower B)
    const towerA = String(a.tower || "");
    const towerB = String(b.tower || "");
    if (towerA !== towerB) {
      const tDiff = towerA.localeCompare(towerB, undefined, { numeric: true, sensitivity: "base" });
      if (tDiff !== 0) return tDiff;
    }

    // 2. Flat Number (natural numeric: 101, 102, 201, 301, 601)
    const flatA = String(a.flatNumber || a.unitNo || "");
    const flatB = String(b.flatNumber || b.unitNo || "");
    if (flatA && flatB && flatA !== flatB) {
      const fDiff = flatA.localeCompare(flatB, undefined, { numeric: true, sensitivity: "base" });
      if (fDiff !== 0) return fDiff;
    }

    // 3. Floor (Floor 1, Floor 2, etc.)
    const floorA = String(a.floor || "");
    const floorB = String(b.floor || "");
    if (floorA !== floorB) {
      const flDiff = floorA.localeCompare(floorB, undefined, { numeric: true, sensitivity: "base" });
      if (flDiff !== 0) return flDiff;
    }

    // 4. Fallback to Unit ID
    return String(a.unitId || a.id || "").localeCompare(String(b.unitId || b.id || ""), undefined, { numeric: true, sensitivity: "base" });
  });
}

/**
 * Generates and downloads a branded, executive-ready PDF report for an individual unit.
 * Captures Flat Identification, Execution KPIs, Maker/Checker metrics, Room-level breakdown,
 * and individual open snag checkpoints.
 */
async function downloadUnitSnagReport(unit, existingRoomData = null) {
  if (!unit) return;

  // 1. Fetch room level breakdown if not already supplied
  let rooms = existingRoomData;
  const fetchedSnagQuestions = [];
  if (!rooms || rooms.length === 0) {
    const flatId = unit.flatId || unit.unitId || unit.id;
    const projectId = unit.projectId;
    if (flatId && projectId) {
      try {
        const API_BASE = "https://konstruct.world";
        const token =
          localStorage.getItem("ACCESS_TOKEN") ||
          localStorage.getItem("TOKEN") ||
          localStorage.getItem("token") ||
          localStorage.getItem("access") ||
          "";
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios
          .get(`${API_BASE}/checklists/stats/flat-room/`, {
            params: { project_id: projectId, flat_id: flatId, include_questions: "true" },
            headers: authHeader,
            timeout: 6500,
          })
          .catch(() => null);

        if (res?.data && Array.isArray(res.data.rooms)) {
          rooms = res.data.rooms;
        }
      } catch (err) {
        console.warn("Could not fetch room details for unit report:", err);
      }
    }
  }

  // Parse rooms & open snags if present
  const parsedRooms = [];
  if (Array.isArray(rooms)) {
    rooms.forEach((r, idx) => {
      const rId = String(r.id || r.room_id || idx + 1);
      const rName = r.name || r.room_label || r.rooms || `Room #${rId}`;
      const openCount = Number(r.snagCount ?? r.open ?? 0);
      const closedCount = Number(r.completedCount ?? r.closed ?? 0);
      const totalCount = Number(r.totalQuestions ?? r.total ?? (openCount + closedCount));

      parsedRooms.push({
        name: rName,
        total: totalCount,
        open: openCount,
        closed: closedCount,
        pct: totalCount > 0 ? Math.round((closedCount / totalCount) * 100) : (openCount === 0 ? 100 : 0),
      });

      // Collect snags from r.snagQuestions or r.by_category
      if (Array.isArray(r.snagQuestions)) {
        r.snagQuestions.forEach((sq) => {
          fetchedSnagQuestions.push({
            room: rName,
            category: sq.category || "General",
            question: sq.question || "Snag checkpoint item",
            attempt: sq.attempt || "1",
          });
        });
      } else if (Array.isArray(r.by_category)) {
        r.by_category.forEach((cat) => {
          const catName = cat.category_label || `Category #${cat.category_id || ""}`;
          if (Array.isArray(cat.questions)) {
            cat.questions.forEach((q) => {
              const subStatus = String(q.latest_submission_status || q.item_status || "").toLowerCase();
              const isSnag =
                subStatus.includes("reject") ||
                subStatus.includes("fail") ||
                subStatus === "n" ||
                q.item_status === "in_progress" ||
                q.item_status === "open";
              if (isSnag) {
                fetchedSnagQuestions.push({
                  room: rName,
                  category: catName,
                  question: q.item_title || q.title || q.question || "Snag checkpoint item",
                  attempt: q.latest_attempt || "1",
                });
              }
            });
          }
        });
      }
    });
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 36;
  const contentWidth = pageWidth - marginX * 2;
  let cursorY = 36;

  // 1. Header Banner
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(marginX, cursorY, contentWidth, 54, "F");

  // Emerald brand line
  doc.setFillColor(16, 185, 129); // emerald-500
  doc.rect(marginX, cursorY + 50, contentWidth, 4, "F");

  // Title left
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text("KONSTRUCT", marginX + 16, cursorY + 23);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(52, 211, 153); // emerald-400
  doc.text("QUALITY ASSURANCE & SNAGGING MANAGEMENT SYSTEM", marginX + 16, cursorY + 38);

  // Title right
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(255, 255, 255);
  doc.text("UNIT INSPECTION REPORT", marginX + contentWidth - 16, cursorY + 22, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(203, 213, 225); // slate-300
  doc.text(`Generated: ${new Date().toLocaleString()}`, marginX + contentWidth - 16, cursorY + 36, { align: "right" });

  cursorY += 66;

  // 2. Unit Information Card
  const flatDisplay = unit.flatNumber
    ? `Flat ${unit.flatNumber} (Unit #${unit.unitNo || unit.unitId})`
    : `Unit ${unit.unitNo || unit.unitId || "—"}`;

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginX, right: marginX },
    theme: "grid",
    styles: {
      fontSize: 8.5,
      cellPadding: 4.5,
      lineColor: [226, 232, 240],
      lineWidth: 0.5,
      textColor: [30, 41, 59],
    },
    columnStyles: {
      0: { fontStyle: "bold", fillColor: [248, 250, 252], textColor: [71, 85, 105], cellWidth: 95 },
      1: { fontStyle: "bold", textColor: [15, 23, 42], cellWidth: 165 },
      2: { fontStyle: "bold", fillColor: [248, 250, 252], textColor: [71, 85, 105], cellWidth: 95 },
      3: { fontStyle: "bold", textColor: [15, 23, 42], cellWidth: 168 },
    },
    body: [
      ["Project Name", unit.projectName || "Konstruct Project", "Unit / Flat", flatDisplay],
      ["Tower & Floor", `${unit.tower || "—"} • ${unit.floor || "—"}`, "Category / Typology", unit.category || "Standard"],
      ["Construction Stage", unit.stage || "Snagging & Handover", "Execution Status", String(unit.status || "IN_PROGRESS").toUpperCase()],
      ["Pending Action With", unit.pendingRole || "None", "Last Activity", unit.lastActivity || "—"],
    ],
  });

  cursorY = doc.lastAutoTable.finalY + 12;

  // 3. KPI Statistics Grid
  const totalQ = Number(unit.totalQuestions || 0);
  const compQ = Number(unit.completedQuestions || 0);
  const openS = Number(unit.openSnags || 0);
  const raisedC = Number(unit.raisedByChecker || 0);
  const doneM = Number(unit.completedByMaker || 0);
  const pctProgress = totalQ > 0 ? Math.round((compQ / totalQ) * 100) : (openS === 0 ? 100 : 0);

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 8, cellPadding: 5, halign: "center", fontStyle: "bold" },
    headStyles: {
      fillColor: [16, 185, 129],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8.5,
    },
    bodyStyles: { fontSize: 10, textColor: [15, 23, 42] },
    head: [["Total Checkpoints", "Passed / Signed-off", "Sign-off Progress", "Open Snags", "Raised (Checker)", "Resolved (Maker)"]],
    body: [
      [
        String(totalQ),
        String(compQ),
        `${pctProgress}%`,
        { content: String(openS), styles: { textColor: openS > 0 ? [225, 29, 72] : [16, 185, 129], fontStyle: "bold" } },
        String(raisedC),
        String(doneM),
      ],
    ],
  });

  cursorY = doc.lastAutoTable.finalY + 14;

  // 4. Room-wise Breakdown Table (if rooms exist)
  if (parsedRooms.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text("Room-Wise Snag & Inspection Summary", marginX, cursorY);
    cursorY += 6;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 4, textColor: [51, 65, 85] },
      headStyles: { fillColor: [51, 65, 85], textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 160 },
        1: { halign: "center", cellWidth: 70 },
        2: { halign: "center", cellWidth: 70 },
        3: { halign: "center", cellWidth: 70 },
        4: { halign: "center", cellWidth: 75 },
        5: { halign: "center", cellWidth: 78 },
      },
      head: [["Room Name", "Total Items", "Passed", "Open Snags", "Completion", "Room Status"]],
      body: parsedRooms.map((rm) => [
        rm.name,
        String(rm.total),
        String(rm.closed),
        {
          content: String(rm.open),
          styles: { textColor: rm.open > 0 ? [225, 29, 72] : [16, 185, 129], fontStyle: "bold" },
        },
        `${rm.pct}%`,
        rm.open > 0 ? "Snags Pending" : "Clean / Passed",
      ]),
    });

    cursorY = doc.lastAutoTable.finalY + 14;
  }

  // 5. Open Snags Detailed Table (if specific open snags are found)
  if (fetchedSnagQuestions.length > 0) {
    if (cursorY > pageHeight - 160) {
      doc.addPage();
      cursorY = 40;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(225, 29, 72); // rose-600
    doc.text(`Open Non-Conformances & Snag Items (${fetchedSnagQuestions.length})`, marginX, cursorY);
    cursorY += 6;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [51, 65, 85] },
      headStyles: { fillColor: [225, 29, 72], textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: {
        0: { halign: "center", cellWidth: 28 },
        1: { fontStyle: "bold", cellWidth: 100 },
        2: { cellWidth: 95 },
        3: { cellWidth: 240 },
        4: { halign: "center", cellWidth: 60 },
      },
      head: [["#", "Room Location", "Category", "Inspection Checkpoint / Defect", "Status"]],
      body: fetchedSnagQuestions.slice(0, 50).map((sq, i) => [
        String(i + 1),
        sq.room,
        sq.category,
        sq.question,
        "OPEN SNAG",
      ]),
    });

    if (fetchedSnagQuestions.length > 50) {
      cursorY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 116, 139);
      doc.text(`* Showing first 50 snags of ${fetchedSnagQuestions.length} total. View dashboard for complete interactive list.`, marginX, cursorY);
      cursorY += 10;
    } else {
      cursorY = doc.lastAutoTable.finalY + 14;
    }
  } else if (openS > 0 && parsedRooms.length === 0) {
    if (cursorY > pageHeight - 120) {
      doc.addPage();
      cursorY = 40;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(15, 23, 42);
    doc.text("Snagging Observations & Execution Details", marginX, cursorY);
    cursorY += 6;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 5 },
      headStyles: { fillColor: [71, 85, 105], textColor: [255, 255, 255] },
      head: [["Metric / Log", "Record Detail", "Observation"]],
      body: [
        ["Open Snags", `${openS} unresolved items`, "Pending rectification by assigned Maker team."],
        ["Raised by Checker", `${raisedC} snags recorded`, "Quality verification initiated by Checker."],
        ["Completed by Maker", `${doneM} snags resolved`, "Maker action completed and logged."],
        ["Pending Role", unit.pendingRole || "Maker", `Active responsibility assigned to ${unit.pendingRole || "Maker"}.`],
      ],
    });
    cursorY = doc.lastAutoTable.finalY + 14;
  }

  // 6. Signature & Certification Box
  if (cursorY > pageHeight - 110) {
    doc.addPage();
    cursorY = 40;
  }

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 7.5, cellPadding: 6, textColor: [71, 85, 105], lineColor: [203, 213, 225], lineWidth: 0.5 },
    columnStyles: {
      0: { cellWidth: 174 },
      1: { cellWidth: 174 },
      2: { cellWidth: 175 },
    },
    body: [
      [
        "INSPECTED BY (CHECKER)\n\nName: ______________________\nSign: ______________________\nDate: ______________________",
        "RECTIFIED BY (MAKER)\n\nName: ______________________\nSign: ______________________\nDate: ______________________",
        "VERIFIED & APPROVED (PROJECT HEAD)\n\nName: ______________________\nSign: ______________________\nDate: ______________________",
      ],
    ],
  });

  // 7. Footer on all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text(
      "Konstruct Quality Assurance Platform • Confidential QA/QC Inspection Document",
      marginX,
      pageHeight - 20
    );
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - marginX,
      pageHeight - 20,
      { align: "right" }
    );
  }

  // 8. Download PDF
  const safeFlat = (unit.flatNumber || unit.unitNo || unit.unitId || "Unit").toString().replace(/[^a-zA-Z0-9_-]/g, "_");
  const safeTower = (unit.tower || "Tower").toString().replace(/[^a-zA-Z0-9_-]/g, "_");
  doc.save(`Konstruct_Report_Flat_${safeFlat}_${safeTower}.pdf`);
}

/* Paginated snagging units table — with rich interactive Multi-Filter Tab toolbar on top */
function SnaggingTable({ records, cardColor, textColor: propTextColor, theme, onOpenUnit }) {
  const textColor = propTextColor || (theme === "dark" ? "#fff" : "#222");
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState("flat");
  const [sortDir, setSortDir] = useState("asc");
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadReport = async (unitRow) => {
    try {
      setDownloadingId(unitRow.id);
      await downloadUnitSnagReport(unitRow);
    } catch (err) {
      console.error("Failed to generate unit report:", err);
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Multi-Filter State on Top of Table ──
  const [flatSearch, setFlatSearch] = useState("");
  const [towerFilter, setTowerFilter] = useState("");
  const [openSnagsFilter, setOpenSnagsFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [progressFilter, setProgressFilter] = useState("");
  const [roomSnagsFilter, setRoomSnagsFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Unique options dynamically derived from current records
  const uniqueTowers = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r.tower) set.add(r.tower); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [records]);

  const uniqueCategories = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r.category) set.add(r.category); });
    return Array.from(set).sort();
  }, [records]);

  const uniqueStages = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r.stage) set.add(r.stage); });
    return Array.from(set).sort();
  }, [records]);

  const uniqueRoles = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r.pendingRole) set.add(r.pendingRole); });
    return Array.from(set).sort();
  }, [records]);

  const uniqueStatuses = useMemo(() => {
    const set = new Set();
    records.forEach((r) => { if (r.status) set.add(r.status); });
    return Array.from(set).sort();
  }, [records]);

  // Apply all 10 filters
  const filteredByTable = useMemo(() => {
    return records.filter((r) => {
      // 1. Flat / Unit Search
      if (flatSearch.trim()) {
        const q = flatSearch.trim().toLowerCase();
        const flatStr = String(r.flatNumber || "").toLowerCase();
        const unitStr = String(r.unitNo || "").toLowerCase();
        const idStr = String(r.unitId || r.id || "").toLowerCase();
        if (!flatStr.includes(q) && !unitStr.includes(q) && !idStr.includes(q)) {
          return false;
        }
      }

      // 2. Tower Filter
      if (towerFilter && r.tower !== towerFilter) {
        return false;
      }

      // 3. Open Snags Filter
      if (openSnagsFilter) {
        const snags = Number(r.openSnags || 0);
        if (openSnagsFilter === "has_snags" && snags <= 0) return false;
        if (openSnagsFilter === "zero_snags" && snags > 0) return false;
        if (openSnagsFilter === "high_50" && snags < 50) return false;
        if (openSnagsFilter === "critical_100" && snags < 100) return false;
      }

      // 4. Category Filter
      if (categoryFilter && r.category !== categoryFilter) {
        return false;
      }

      // 5. Stage Filter
      if (stageFilter && r.stage !== stageFilter) {
        return false;
      }

      // 6. Pending Role Filter
      if (roleFilter && r.pendingRole !== roleFilter) {
        return false;
      }

      // 7. Questions Progress Filter
      if (progressFilter) {
        const total = Number(r.totalQuestions || 0);
        const comp = Number(r.completedQuestions || 0);
        if (progressFilter === "completed" && (r.status !== "COMPLETED" && (total === 0 || comp < total))) return false;
        if (progressFilter === "in_progress" && (comp === 0 || (total > 0 && comp >= total))) return false;
        if (progressFilter === "not_started" && comp > 0) return false;
      }

      // 8. Room Snags Filter
      if (roomSnagsFilter) {
        const snags = Number(r.openSnags || 0);
        if (roomSnagsFilter === "has_room_snags" && snags <= 0) return false;
        if (roomSnagsFilter === "clean_rooms" && snags > 0) return false;
      }

      // 9. Last Activity Filter
      if (activityFilter) {
        const hasDate = r.lastActivity && r.lastActivity !== "—" && r.lastActivity !== "-";
        if (activityFilter === "has_activity" && !hasDate) return false;
        if (activityFilter === "no_activity" && hasDate) return false;
      }

      // 10. Status Filter
      if (statusFilter && r.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [
    records,
    flatSearch,
    towerFilter,
    openSnagsFilter,
    categoryFilter,
    stageFilter,
    roleFilter,
    progressFilter,
    roomSnagsFilter,
    activityFilter,
    statusFilter,
  ]);

  const activeFilterCount = [
    flatSearch.trim(),
    towerFilter,
    openSnagsFilter,
    categoryFilter,
    stageFilter,
    roleFilter,
    progressFilter,
    roomSnagsFilter,
    activityFilter,
    statusFilter,
  ].filter(Boolean).length;

  const handleClearAllTableFilters = () => {
    setFlatSearch("");
    setTowerFilter("");
    setOpenSnagsFilter("");
    setCategoryFilter("");
    setStageFilter("");
    setRoleFilter("");
    setProgressFilter("");
    setRoomSnagsFilter("");
    setActivityFilter("");
    setStatusFilter("");
  };

  const sortedRecords = useMemo(() => {
    const list = [...filteredByTable];
    list.sort((a, b) => {
      let diff = 0;
      if (sortKey === "flat") {
        const towerA = String(a.tower || "");
        const towerB = String(b.tower || "");
        if (towerA !== towerB) {
          diff = towerA.localeCompare(towerB, undefined, { numeric: true, sensitivity: "base" });
        }
        if (diff === 0) {
          const flatA = String(a.flatNumber || a.unitNo || "");
          const flatB = String(b.flatNumber || b.unitNo || "");
          diff = flatA.localeCompare(flatB, undefined, { numeric: true, sensitivity: "base" });
        }
      } else if (sortKey === "tower") {
        diff = String(a.tower || "").localeCompare(String(b.tower || ""), undefined, { numeric: true, sensitivity: "base" });
        if (diff === 0) {
          diff = String(a.floor || "").localeCompare(String(b.floor || ""), undefined, { numeric: true, sensitivity: "base" });
        }
        if (diff === 0) {
          diff = String(a.flatNumber || a.unitNo || "").localeCompare(String(b.flatNumber || b.unitNo || ""), undefined, { numeric: true, sensitivity: "base" });
        }
      } else if (sortKey === "category") {
        diff = String(a.category || "").localeCompare(String(b.category || ""));
      } else if (sortKey === "stage") {
        diff = String(a.stage || "").localeCompare(String(b.stage || ""));
      } else if (sortKey === "status") {
        diff = String(a.status || "").localeCompare(String(b.status || ""));
      } else if (sortKey === "openSnags") {
        diff = Number(a.openSnags || 0) - Number(b.openSnags || 0);
      } else if (sortKey === "raisedByChecker") {
        diff = Number(a.raisedByChecker || 0) - Number(b.raisedByChecker || 0);
      } else if (sortKey === "completedByMaker") {
        diff = Number(a.completedByMaker || 0) - Number(b.completedByMaker || 0);
      } else {
        const flatA = String(a.flatNumber || a.unitNo || "");
        const flatB = String(b.flatNumber || b.unitNo || "");
        diff = flatA.localeCompare(flatB, undefined, { numeric: true, sensitivity: "base" });
      }
      return sortDir === "asc" ? diff : -diff;
    });
    return list;
  }, [filteredByTable, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sortedRecords.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sortedRecords.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  React.useEffect(() => {
    setPage(0);
  }, [
    filteredByTable.length,
    sortKey,
    sortDir,
    flatSearch,
    towerFilter,
    openSnagsFilter,
    categoryFilter,
    stageFilter,
    roleFilter,
    progressFilter,
    roomSnagsFilter,
    activityFilter,
    statusFilter,
  ]);

  return (
    <div className="rounded-2xl shadow-sm border overflow-hidden" style={{ background: cardColor, borderColor: `${EMERALD}44` }}>
      {/* ── Multi-Filter Control Tab / Panel on Top of Table ── */}
      <div
        className="p-4 sm:p-5 border-b space-y-3.5"
        style={{
          background: theme === "dark" ? "#191922" : "#fbfcfe",
          borderColor: `${EMERALD}25`,
        }}
      >
        {/* Row 1: Header + Active Count + Reset Button */}
        <div className="flex items-center justify-between flex-wrap gap-2.5 pb-2.5 border-b" style={{ borderColor: `${EMERALD}15` }}>
          <div className="flex items-center gap-2.5">
            <span className="text-xl">🎛️</span>
            <div>
              <h3 className="text-sm font-black uppercase tracking-wider flex items-center gap-2" style={{ color: EMERALD }}>
                <span>Unit &amp; Snagging Filter Toolbar</span>
                {activeFilterCount > 0 && (
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-black">
                    {activeFilterCount} Active
                  </span>
                )}
              </h3>
              <p className="text-xs opacity-65 mt-0.5">
                Filter by Flat, Tower, Open Snags, Category, Stage, Pending Role, Questions Progress, Room Snags, Last Activity &amp; Status
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-xs font-bold opacity-80 font-mono">
              Showing <strong className="text-emerald-600 dark:text-emerald-400 font-extrabold">{sortedRecords.length}</strong> of {records.length} units
            </span>
            {activeFilterCount > 0 && (
              <button
                onClick={handleClearAllTableFilters}
                className="px-3 py-1 rounded-lg text-xs font-extrabold transition-all border flex items-center gap-1.5 hover:bg-rose-500/15 text-rose-600 dark:text-rose-400 border-rose-500/35 shadow-2xs"
                title="Reset all table filters"
              >
                <span>✕</span>
                <span>Reset All Filters</span>
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Quick Preset Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin text-xs">
          <span className="text-[11px] font-bold uppercase tracking-wider opacity-60 mr-1 shrink-0">
            Quick Tabs:
          </span>
          <button
            onClick={handleClearAllTableFilters}
            className={`px-3 py-1.5 rounded-lg font-bold shrink-0 transition-all border ${
              activeFilterCount === 0
                ? "bg-emerald-600 text-white shadow-xs border-emerald-600"
                : "opacity-70 hover:opacity-100 border-slate-300 dark:border-slate-700"
            }`}
          >
            🏢 All Units ({records.length})
          </button>
          <button
            onClick={() => {
              handleClearAllTableFilters();
              setOpenSnagsFilter("has_snags");
            }}
            className={`px-3 py-1.5 rounded-lg font-bold shrink-0 transition-all border flex items-center gap-1.5 ${
              openSnagsFilter === "has_snags"
                ? "bg-rose-600 text-white shadow-xs border-rose-600"
                : "opacity-70 hover:opacity-100 border-slate-300 dark:border-slate-700"
            }`}
          >
            <span>⚠️ Open Snags (&gt;0)</span>
          </button>
          <button
            onClick={() => {
              handleClearAllTableFilters();
              setStatusFilter("IN_PROGRESS");
            }}
            className={`px-3 py-1.5 rounded-lg font-bold shrink-0 transition-all border flex items-center gap-1.5 ${
              statusFilter === "IN_PROGRESS"
                ? "bg-amber-600 text-white shadow-xs border-amber-600"
                : "opacity-70 hover:opacity-100 border-slate-300 dark:border-slate-700"
            }`}
          >
            <span>⚡ In Progress</span>
          </button>
          <button
            onClick={() => {
              handleClearAllTableFilters();
              setRoleFilter("MAKER");
            }}
            className={`px-3 py-1.5 rounded-lg font-bold shrink-0 transition-all border flex items-center gap-1.5 ${
              roleFilter === "MAKER"
                ? "bg-amber-600 text-white shadow-xs border-amber-600"
                : "opacity-70 hover:opacity-100 border-slate-300 dark:border-slate-700"
            }`}
          >
            <span>🛠️ Pending Maker</span>
          </button>
          <button
            onClick={() => {
              handleClearAllTableFilters();
              setRoleFilter("CHECKER");
            }}
            className={`px-3 py-1.5 rounded-lg font-bold shrink-0 transition-all border flex items-center gap-1.5 ${
              roleFilter === "CHECKER"
                ? "bg-purple-600 text-white shadow-xs border-purple-600"
                : "opacity-70 hover:opacity-100 border-slate-300 dark:border-slate-700"
            }`}
          >
            <span>🔍 Pending Checker</span>
          </button>
          <button
            onClick={() => {
              handleClearAllTableFilters();
              setStatusFilter("COMPLETED");
            }}
            className={`px-3 py-1.5 rounded-lg font-bold shrink-0 transition-all border flex items-center gap-1.5 ${
              statusFilter === "COMPLETED"
                ? "bg-emerald-600 text-white shadow-xs border-emerald-600"
                : "opacity-70 hover:opacity-100 border-slate-300 dark:border-slate-700"
            }`}
          >
            <span>✅ Completed</span>
          </button>
        </div>

        {/* Row 3: 10 Dedicated Filter Inputs / Dropdowns */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-10 gap-2 text-xs">
          {/* 1. Flat / Unit # Search */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Unit / Flat
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="🔍 Flat #..."
                value={flatSearch}
                onChange={(e) => setFlatSearch(e.target.value)}
                className="w-full px-2.5 py-1.5 rounded-lg border text-xs focus:outline-none"
                style={{
                  background: theme === "dark" ? "#22222d" : "#ffffff",
                  borderColor: flatSearch ? EMERALD : `${EMERALD}33`,
                  color: textColor,
                }}
              />
              {flatSearch && (
                <button
                  onClick={() => setFlatSearch("")}
                  className="absolute right-1.5 top-1.5 text-[10px] opacity-50 hover:opacity-100"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 2. Tower */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Tower
            </label>
            <select
              value={towerFilter}
              onChange={(e) => setTowerFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: towerFilter ? EMERALD : `${EMERALD}33`,
                color: textColor,
              }}
            >
              <option value="">All Towers</option>
              {uniqueTowers.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* 3. Open Snags */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Open Snags
            </label>
            <select
              value={openSnagsFilter}
              onChange={(e) => setOpenSnagsFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: openSnagsFilter ? ROSE : `${EMERALD}33`,
                color: openSnagsFilter ? ROSE : textColor,
              }}
            >
              <option value="">All Snags</option>
              <option value="has_snags">⚠️ Has Snags (&gt;0)</option>
              <option value="zero_snags">✅ Zero Snags (0)</option>
              <option value="high_50">🔥 High (≥50)</option>
              <option value="critical_100">⚡ Critical (≥100)</option>
            </select>
          </div>

          {/* 4. Category */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: categoryFilter ? EMERALD : `${EMERALD}33`,
                color: textColor,
              }}
            >
              <option value="">All Categories</option>
              {uniqueCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* 5. Stage */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Stage
            </label>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: stageFilter ? EMERALD : `${EMERALD}33`,
                color: textColor,
              }}
            >
              <option value="">All Stages</option>
              {uniqueStages.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* 6. Status */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: statusFilter ? EMERALD : `${EMERALD}33`,
                color: textColor,
              }}
            >
              <option value="">All Statuses</option>
              {uniqueStatuses.map((st) => (
                <option key={st} value={st}>
                  {SNAG_STATUS_CONFIG[st]?.label || st}
                </option>
              ))}
            </select>
          </div>

          {/* 7. Pending Role */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Pending Role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: roleFilter ? AMBER : `${EMERALD}33`,
                color: roleFilter ? AMBER : textColor,
              }}
            >
              <option value="">All Roles</option>
              {uniqueRoles.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* 8. Questions Progress */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Progress
            </label>
            <select
              value={progressFilter}
              onChange={(e) => setProgressFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: progressFilter ? CYAN : `${EMERALD}33`,
                color: textColor,
              }}
            >
              <option value="">All Progress</option>
              <option value="completed">✅ 100% Completed</option>
              <option value="in_progress">⚡ In Progress (1–99%)</option>
              <option value="not_started">⏳ Not Started (0%)</option>
            </select>
          </div>

          {/* 9. Room Snags */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Room Snags
            </label>
            <select
              value={roomSnagsFilter}
              onChange={(e) => setRoomSnagsFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: roomSnagsFilter ? ROSE : `${EMERALD}33`,
                color: textColor,
              }}
            >
              <option value="">All Rooms</option>
              <option value="has_room_snags">⚠️ Has Room Snags</option>
              <option value="clean_rooms">✅ All Rooms Clean</option>
            </select>
          </div>

          {/* 10. Last Activity */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">
              Last Activity
            </label>
            <select
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border text-xs font-medium focus:outline-none"
              style={{
                background: theme === "dark" ? "#22222d" : "#ffffff",
                borderColor: activityFilter ? EMERALD : `${EMERALD}33`,
                color: textColor,
              }}
            >
              <option value="">All Activity</option>
              <option value="has_activity">📅 Has Activity Date</option>
              <option value="no_activity">— No Activity Logged</option>
            </select>
          </div>
        </div>

        {/* Row 4: Active Filter Chips */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1 text-xs">
            <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Active:</span>
            {flatSearch && (
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                Flat: &ldquo;{flatSearch}&rdquo;
                <button onClick={() => setFlatSearch("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {towerFilter && (
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                Tower: {towerFilter}
                <button onClick={() => setTowerFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {openSnagsFilter && (
              <span className="px-2 py-0.5 rounded-md bg-rose-500/15 text-rose-600 dark:text-rose-400 font-semibold flex items-center gap-1">
                Snags: {openSnagsFilter === "has_snags" ? "With Snags" : openSnagsFilter}
                <button onClick={() => setOpenSnagsFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {categoryFilter && (
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                Category: {categoryFilter}
                <button onClick={() => setCategoryFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {stageFilter && (
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                Stage: {stageFilter}
                <button onClick={() => setStageFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {statusFilter && (
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                Status: {SNAG_STATUS_CONFIG[statusFilter]?.label || statusFilter}
                <button onClick={() => setStatusFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {roleFilter && (
              <span className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-1">
                Role: {roleFilter}
                <button onClick={() => setRoleFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {progressFilter && (
              <span className="px-2 py-0.5 rounded-md bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 font-semibold flex items-center gap-1">
                Progress: {progressFilter}
                <button onClick={() => setProgressFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {roomSnagsFilter && (
              <span className="px-2 py-0.5 rounded-md bg-rose-500/15 text-rose-600 dark:text-rose-400 font-semibold flex items-center gap-1">
                Room Snags: {roomSnagsFilter}
                <button onClick={() => setRoomSnagsFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
            {activityFilter && (
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                Activity: {activityFilter}
                <button onClick={() => setActivityFilter("")} className="hover:opacity-75">✕</button>
              </span>
            )}
          </div>
        )}
      </div>

      <div className="overflow-x-auto touch-scroll">
        <table className="w-full text-xs text-left">
          <thead className="opacity-80 border-b uppercase text-[10px] tracking-wider" style={{ borderColor: `${EMERALD}22` }}>
            <tr>
              <th
                onClick={() => handleSort("flat")}
                className="px-4 py-3 whitespace-nowrap cursor-pointer hover:text-emerald-500 transition-colors select-none"
                title="Click to sort by Flat Number"
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-extrabold">Unit / Flat</span>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                    {sortKey === "flat" ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </div>
              </th>
              <th
                onClick={() => handleSort("tower")}
                className="px-4 py-3 whitespace-nowrap cursor-pointer hover:text-emerald-500 transition-colors select-none"
                title="Click to sort by Tower & Floor"
              >
                <div className="flex items-center gap-1.5">
                  <span>Tower &amp; Floor</span>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                    {sortKey === "tower" ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </div>
              </th>
              <th className="px-4 py-3 whitespace-nowrap">Category</th>
              <th className="px-4 py-3 whitespace-nowrap">Stage</th>
              <th
                onClick={() => handleSort("status")}
                className="px-4 py-3 whitespace-nowrap cursor-pointer hover:text-emerald-500 transition-colors select-none"
              >
                <div className="flex items-center gap-1.5">
                  <span>Status</span>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                    {sortKey === "status" ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </div>
              </th>
              <th className="px-4 py-3 whitespace-nowrap">Pending Role</th>
              <th className="px-4 py-3 whitespace-nowrap">Questions Progress</th>
              <th
                onClick={() => handleSort("openSnags")}
                className="px-4 py-3 whitespace-nowrap cursor-pointer hover:text-rose-500 transition-colors select-none"
              >
                <div className="flex items-center gap-1.5">
                  <span>Open Snags</span>
                  <span className="text-[11px] font-bold text-rose-500">
                    {sortKey === "openSnags" ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </div>
              </th>
              <th
                onClick={() => handleSort("raisedByChecker")}
                className="px-4 py-3 whitespace-nowrap cursor-pointer hover:text-amber-500 transition-colors select-none"
              >
                <div className="flex items-center gap-1.5">
                  <span>Raised (Checker)</span>
                  <span className="text-[11px] font-bold text-amber-500">
                    {sortKey === "raisedByChecker" ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </div>
              </th>
              <th
                onClick={() => handleSort("completedByMaker")}
                className="px-4 py-3 whitespace-nowrap cursor-pointer hover:text-emerald-500 transition-colors select-none"
              >
                <div className="flex items-center gap-1.5">
                  <span>Completed (Maker)</span>
                  <span className="text-[11px] font-bold text-emerald-500">
                    {sortKey === "completedByMaker" ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                  </span>
                </div>
              </th>
              <th className="px-4 py-3 whitespace-nowrap">Room Snags</th>
              <th className="px-4 py-3 whitespace-nowrap">Last Activity</th>
              <th className="px-4 py-3 whitespace-nowrap text-right">Actions / Report</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: `${EMERALD}15` }}>
            {pageRows.map((r) => {
              const cfg = SNAG_STATUS_CONFIG[r.status] || { label: r.status, color: "#888", bg: "#8882" };
              const raisedCount = Number(r.raisedByChecker || 0);
              const doneCount = Number(r.completedByMaker || 0);
              return (
                <tr key={r.id} className="hover:bg-emerald-500/5 transition-colors cursor-pointer" onClick={() => onOpenUnit(r)}>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-extrabold text-sm tracking-tight" style={{ color: "#000000" }}>
                        {r.flatNumber ? `Flat ${r.flatNumber}` : (r.unitNo ? `Unit ${r.unitNo}` : "—")}
                      </span>
                      {r.flatNumber && r.unitNo && String(r.flatNumber) !== String(r.unitNo) && (
                        <span className="text-[10px] font-semibold font-mono" style={{ color: "#475569" }}>
                          Unit #{r.unitNo}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium" style={{ color: "#1e293b" }}>{r.tower} • {r.floor}</td>
                  <td className="px-4 py-3 font-medium" style={{ color: "#334155" }}>{r.category}</td>
                  <td className="px-4 py-3 font-semibold" style={{ color: "#0f172a" }}>{r.stage}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full font-bold text-[10px]" style={{ color: cfg.color, background: cfg.bg }}>
                      {cfg.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-semibold ${r.pendingRole === "None" ? "opacity-40" : "text-amber-500 font-bold"}`}>
                      {r.pendingRole}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${r.totalQuestions > 0 ? Math.round((r.completedQuestions / r.totalQuestions) * 100) : 0}%` }} />
                      </div>
                      <span className="font-medium text-[10px] font-mono" style={{ color: "#334155" }}>{r.completedQuestions}/{r.totalQuestions}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.openSnags > 0 ? (
                      <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-500 font-bold text-[10px]">{r.openSnags} snags</span>
                    ) : (
                      <span className="text-emerald-500 font-bold text-[10px]">✓ Zero</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium">
                    <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-bold text-[10px] font-mono">
                      {raisedCount} snags
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">
                    <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold text-[10px] font-mono">
                      {doneCount} snags
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenUnit(r); }}
                      className={`px-2 py-1 rounded text-[10px] font-bold border transition-all hover:scale-105 flex items-center gap-1 ${
                        r.openSnags > 0
                          ? "border-rose-500/40 text-rose-600 bg-rose-500/10 hover:bg-rose-500/20"
                          : "border-emerald-500/40 text-emerald-600 bg-emerald-500/10 hover:bg-emerald-500/20"
                      }`}
                      title="Click to view room-wise snag details"
                    >
                      <span>{r.openSnags > 0 ? "⚠️ Room Snags" : "✅ Rooms Clean"}</span>
                      <span className="opacity-70 text-[9px]">🔍</span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-[11px] font-mono whitespace-nowrap font-medium" style={{ color: r.lastActivity && r.lastActivity !== "—" ? "#0f172a" : "#94a3b8" }}>
                    {r.lastActivity || "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadReport(r);
                        }}
                        disabled={downloadingId === r.id}
                        className="px-2.5 py-1 rounded-md text-[11px] font-bold border transition-all hover:scale-105 flex items-center gap-1 shadow-2xs disabled:opacity-50"
                        style={{
                          borderColor: `${EMERALD}55`,
                          color: EMERALD,
                          background: `${EMERALD}15`,
                        }}
                        title={`Download PDF Inspection Report for Flat ${r.flatNumber || r.unitNo}`}
                      >
                        {downloadingId === r.id ? (
                          <>
                            <span className="animate-spin text-[10px]">⏳</span>
                            <span className="text-[10px]">PDF...</span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs">📥</span>
                            <span>Report</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onOpenUnit(r); }}
                        className="px-2.5 py-1 rounded-md text-[11px] font-bold hover:bg-emerald-500/10 transition-colors"
                        style={{ color: EMERALD }}
                      >
                        Inspect →
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={13} className="px-4 py-12 text-center text-sm opacity-60">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="text-2xl">🔍</span>
                    <span className="font-semibold">No units match the selected filters.</span>
                    {activeFilterCount > 0 && (
                      <button
                        onClick={handleClearAllTableFilters}
                        className="mt-1 px-3 py-1 text-xs font-bold text-emerald-600 underline"
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 px-4 py-2.5 text-xs opacity-75" style={{ borderTop: `1px solid ${EMERALD}22` }}>
        <span className="flex items-center justify-center sm:justify-start gap-1.5 flex-wrap text-center sm:text-left">
          <span>
            Showing {sortedRecords.length === 0 ? 0 : safePage * PAGE_SIZE + 1}–
            {Math.min(sortedRecords.length, safePage * PAGE_SIZE + PAGE_SIZE)} of {sortedRecords.length} units
          </span>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${EMERALD}15`, color: EMERALD }}>
            Sorted by {sortKey === "flat" ? "Flat Number" : sortKey} ({sortDir === "asc" ? "Ascending ↑" : "Descending ↓"})
          </span>
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: EMERALD }}
          >
            ← Prev
          </button>
          <span>Page {safePage + 1} / {pageCount}</span>
          <button
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="px-2.5 py-1 rounded border disabled:opacity-30 transition-opacity"
            style={{ borderColor: EMERALD }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

const CYAN = "#06b6d4";
const AMBER = "#f59e0b";
const PURPLE = "#8b5cf6";
const ROSE = "#f43f5e";
const EMERALD = "#10b981";

const SNAG_STATUS_CONFIG = {
  COMPLETED: { label: "Completed", color: "#10b981", bg: "rgba(16, 185, 129, 0.15)" },
  IN_PROGRESS: { label: "In Progress", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.15)" },
  PENDING: { label: "Pending (Yet to Start)", color: "#ef4444", bg: "rgba(239, 68, 68, 0.15)" },
  YET_TO_VERIFY: { label: "Yet to Verify", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.15)" },
};

function SnaggingStatusChart({ records, cardColor, textColor, onSelectStatus, onDrill }) {
  const data = useMemo(() => {
    const counts = { COMPLETED: 0, IN_PROGRESS: 0, PENDING: 0, YET_TO_VERIFY: 0 };
    records.forEach((r) => {
      if (counts[r.status] !== undefined) counts[r.status] += 1;
    });
    return Object.entries(counts).map(([k, v]) => ({
      key: k,
      name: SNAG_STATUS_CONFIG[k]?.label || k,
      value: v,
      color: SNAG_STATUS_CONFIG[k]?.color || EMERALD,
    })).filter((d) => d.value > 0);
  }, [records]);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${EMERALD}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={4}
          cursor="pointer"
          onClick={(d) => {
            if (onDrill) onDrill("status", d.key, d.name);
            else if (onSelectStatus) onSelectStatus(d.key);
          }}
        >
          {data.map((d) => (
            <Cell key={d.key} fill={d.color} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 12, color: textColor }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function SnaggingStageChart({ records, cardColor, textColor, onSelectStage, onDrill }) {
  const data = useMemo(() => {
    const stages = {};
    records.forEach((r) => {
      if (!stages[r.stage]) stages[r.stage] = { stage: r.stage, completed: 0, in_progress: 0, pending: 0 };
      if (r.status === "COMPLETED") stages[r.stage].completed += 1;
      else if (r.status === "IN_PROGRESS" || r.status === "YET_TO_VERIFY") stages[r.stage].in_progress += 1;
      else stages[r.stage].pending += 1;
    });
    return Object.values(stages);
  }, [records]);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis dataKey="stage" stroke={textColor} opacity={0.7} tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" />
        <YAxis stroke={textColor} opacity={0.7} tick={{ fontSize: 11 }} />
        <Tooltip shared={false} contentStyle={{ background: cardColor, border: `1px solid ${EMERALD}`, borderRadius: 8, color: textColor, fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11, color: textColor }} verticalAlign="top" height={30} />
        <Bar dataKey="completed" name="Completed Units" fill="#10b981" stackId="a" radius={[0, 0, 0, 0]} cursor="pointer" onClick={(d) => { if (onDrill) onDrill("stage", d.stage, `Stage: ${d.stage}`); else if (onSelectStage) onSelectStage(d.stage); }} />
        <Bar dataKey="in_progress" name="In Progress Units" fill="#f59e0b" stackId="a" radius={[0, 0, 0, 0]} cursor="pointer" onClick={(d) => { if (onDrill) onDrill("stage", d.stage, `Stage: ${d.stage}`); else if (onSelectStage) onSelectStage(d.stage); }} />
        <Bar dataKey="pending" name="Pending Units" fill="#ef4444" stackId="a" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => { if (onDrill) onDrill("stage", d.stage, `Stage: ${d.stage}`); else if (onSelectStage) onSelectStage(d.stage); }} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function SnaggingTowerChart({ records, cardColor, textColor, onSelectTower, onDrill }) {
  const data = useMemo(() => {
    const towers = {};
    records.forEach((r) => {
      if (!towers[r.tower]) towers[r.tower] = { tower: r.tower, total: 0, completed: 0, wip: 0 };
      towers[r.tower].total += 1;
      if (r.status === "COMPLETED") towers[r.tower].completed += 1;
      else towers[r.tower].wip += 1;
    });
    return Object.values(towers);
  }, [records]);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis dataKey="tower" stroke={textColor} opacity={0.7} tick={{ fontSize: 11 }} />
        <YAxis stroke={textColor} opacity={0.7} tick={{ fontSize: 11 }} />
        <Tooltip shared={false} contentStyle={{ background: cardColor, border: `1px solid ${CYAN}`, borderRadius: 8, color: textColor, fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11, color: textColor }} />
        <Bar
          dataKey="completed"
          name="Completed Units"
          fill="#10b981"
          radius={[4, 4, 0, 0]}
          cursor="pointer"
          onClick={(d) => {
            const cleanName = String(d.tower || "");
            const lbl = cleanName.toLowerCase().startsWith("tower") ? cleanName : `Tower ${cleanName}`;
            if (onDrill) onDrill("tower", d.tower, lbl);
            else if (onSelectTower) onSelectTower(d.tower);
          }}
        />
        <Bar
          dataKey="wip"
          name="Active / WIP Units"
          fill="#06b6d4"
          radius={[4, 4, 0, 0]}
          cursor="pointer"
          onClick={(d) => {
            const cleanName = String(d.tower || "");
            const lbl = cleanName.toLowerCase().startsWith("tower") ? cleanName : `Tower ${cleanName}`;
            if (onDrill) onDrill("tower", d.tower, lbl);
            else if (onSelectTower) onSelectTower(d.tower);
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function SnaggingFloorChart({ records, cardColor, textColor, onSelectFloor, onDrill }) {
  const data = useMemo(() => {
    const floors = {};
    records.forEach((r) => {
      if (!floors[r.floor]) floors[r.floor] = { floor: r.floor, total: 0, snags: 0 };
      floors[r.floor].total += 1;
      floors[r.floor].snags += r.openSnags;
    });
    return Object.values(floors);
  }, [records]);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
        <XAxis dataKey="floor" stroke={textColor} opacity={0.7} tick={{ fontSize: 11 }} />
        <YAxis stroke={textColor} opacity={0.7} tick={{ fontSize: 11 }} />
        <Tooltip shared={false} contentStyle={{ background: cardColor, border: `1px solid ${PURPLE}`, borderRadius: 8, color: textColor, fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 11, color: textColor }} />
        <Bar
          dataKey="total"
          name="Total Units"
          fill="#8b5cf6"
          radius={[4, 4, 0, 0]}
          cursor="pointer"
          onClick={(d) => {
            const cleanName = String(d.floor || "");
            const lbl = cleanName.toLowerCase().startsWith("floor") ? cleanName : `Floor ${cleanName}`;
            if (onDrill) onDrill("floor", d.floor, lbl);
            else if (onSelectFloor) onSelectFloor(d.floor);
          }}
        />
        <Bar dataKey="snags" name="Open Snags" fill="#f43f5e" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function SnaggingCategoryChart({ records, cardColor, textColor, onSelectCategory, onDrill }) {
  const data = useMemo(() => {
    const cats = {};
    records.forEach((r) => {
      if (!cats[r.category]) cats[r.category] = 0;
      cats[r.category] += 1;
    });
    const palette = ["#10b981", "#06b6d4", "#8b5cf6", "#f59e0b", "#ec4899"];
    return Object.entries(cats).map(([k, v], idx) => ({
      name: k,
      value: v,
      color: palette[idx % palette.length],
    }));
  }, [records]);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={55}
          outerRadius={95}
          paddingAngle={4}
          cursor="pointer"
          onClick={(d) => {
            if (onDrill) onDrill("category", d.name, `Category: ${d.name}`);
            else if (onSelectCategory) onSelectCategory(d.name);
          }}
        >
          {data.map((d, index) => (
            <Cell key={`cat-${index}`} fill={d.color} />
          ))}
        </Pie>
        <Tooltip contentStyle={{ background: cardColor, border: `1px solid ${EMERALD}`, borderRadius: 8, color: textColor, fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12, color: textColor }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function SnaggingCheckerChart({ records, cardColor, textColor, onDrill }) {
  const [breakdownBy, setBreakdownBy] = useState("stage"); // "stage" | "tower"

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const rawKey = breakdownBy === "stage" ? r.stage : r.tower;
      const key = rawKey && !rawKey.startsWith("Stage#") && !rawKey.startsWith("Tower#") ? rawKey : (breakdownBy === "stage" ? "Finishing & Painting" : "Tower A");
      if (!map[key]) {
        map[key] = {
          name: key,
          raised: 0,
          open: 0,
          resolved: 0,
          units: 0,
        };
      }
      const rRaised = Number(r.raisedByChecker || 0);
      const rOpen = Number(r.openSnags || 0);
      const rResolved = Number(r.completedByMaker || Math.max(0, rRaised - rOpen));

      map[key].raised += rRaised;
      map[key].open += rOpen;
      map[key].resolved += rResolved;
      map[key].units += 1;
    });

    return Object.values(map).sort((a, b) => b.raised - a.raised).slice(0, 10);
  }, [records, breakdownBy]);

  const totalRaised = useMemo(() => data.reduce((acc, d) => acc + d.raised, 0), [data]);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${AMBER}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full font-mono" style={{ background: `${AMBER}20`, color: AMBER }}>
            Total: {totalRaised.toLocaleString()} snags
          </span>
          <span className="text-[11px] opacity-50 hidden sm:inline">• Click bar to drill down</span>
        </div>
        <div className="flex items-center gap-1 p-0.5 rounded-lg border text-[10px]" style={{ borderColor: `${AMBER}44` }}>
          <button
            type="button"
            onClick={() => setBreakdownBy("stage")}
            className={`px-2 py-0.5 rounded font-bold transition-all ${
              breakdownBy === "stage" ? "bg-amber-500 text-white shadow-sm" : "opacity-60 hover:opacity-100"
            }`}
          >
            By Stage
          </button>
          <button
            type="button"
            onClick={() => setBreakdownBy("tower")}
            className={`px-2 py-0.5 rounded font-bold transition-all ${
              breakdownBy === "tower" ? "bg-amber-500 text-white shadow-sm" : "opacity-60 hover:opacity-100"
            }`}
          >
            By Tower
          </button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 25 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
          <XAxis
            dataKey="name"
            stroke={textColor}
            opacity={0.7}
            tick={{ fontSize: 10 }}
            interval={0}
            angle={-15}
            textAnchor="end"
          />
          <YAxis stroke={textColor} opacity={0.7} tick={{ fontSize: 11 }} />
          <Tooltip
            shared={false}
            contentStyle={tooltipStyle}
            formatter={(val, name) => [`${Number(val).toLocaleString()} snags`, name]}
          />
          <Legend wrapperStyle={{ fontSize: 11, color: textColor }} verticalAlign="top" height={30} />
          <Bar
            dataKey="open"
            name="Open Snags (Pending)"
            fill="#f43f5e"
            stackId="checker"
            cursor="pointer"
            onClick={(d) => {
              if (onDrill) {
                onDrill(breakdownBy === "stage" ? "checker_stage" : "checker_tower", d.name, `Checker Snags in ${d.name} (${d.raised} snags total)`);
              }
            }}
          />
          <Bar
            dataKey="resolved"
            name="Resolved Snags"
            fill="#f59e0b"
            stackId="checker"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={(d) => {
              if (onDrill) {
                onDrill(breakdownBy === "stage" ? "checker_stage" : "checker_tower", d.name, `Checker Snags in ${d.name} (${d.raised} snags total)`);
              }
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SnaggingMakerChart({ records, cardColor, textColor, onDrill }) {
  const [breakdownBy, setBreakdownBy] = useState("stage"); // "stage" | "tower"

  const data = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const rawKey = breakdownBy === "stage" ? r.stage : r.tower;
      const key = rawKey && !rawKey.startsWith("Stage#") && !rawKey.startsWith("Tower#") ? rawKey : (breakdownBy === "stage" ? "Finishing & Painting" : "Tower A");
      if (!map[key]) {
        map[key] = {
          name: key,
          completed: 0,
          pending: 0,
          total: 0,
          units: 0,
        };
      }
      const rCompleted = Number(r.completedByMaker || 0);
      const rOpen = Number(r.openSnags || 0);

      map[key].completed += rCompleted;
      map[key].pending += rOpen;
      map[key].total += (rCompleted + rOpen);
      map[key].units += 1;
    });

    return Object.values(map).sort((a, b) => b.completed - a.completed).slice(0, 10);
  }, [records, breakdownBy]);

  const totalCompleted = useMemo(() => data.reduce((acc, d) => acc + d.completed, 0), [data]);

  const tooltipStyle = {
    background: cardColor,
    border: `1px solid ${EMERALD}`,
    borderRadius: 8,
    color: textColor,
    fontSize: 12,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full font-mono" style={{ background: `${EMERALD}20`, color: EMERALD }}>
            Total: {totalCompleted.toLocaleString()} snags
          </span>
          <span className="text-[11px] opacity-50 hidden sm:inline">• Click bar to drill down</span>
        </div>
        <div className="flex items-center gap-1 p-0.5 rounded-lg border text-[10px]" style={{ borderColor: `${EMERALD}44` }}>
          <button
            type="button"
            onClick={() => setBreakdownBy("stage")}
            className={`px-2 py-0.5 rounded font-bold transition-all ${
              breakdownBy === "stage" ? "bg-emerald-600 text-white shadow-sm" : "opacity-60 hover:opacity-100"
            }`}
          >
            By Stage
          </button>
          <button
            type="button"
            onClick={() => setBreakdownBy("tower")}
            className={`px-2 py-0.5 rounded font-bold transition-all ${
              breakdownBy === "tower" ? "bg-emerald-600 text-white shadow-sm" : "opacity-60 hover:opacity-100"
            }`}
          >
            By Tower
          </button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 25 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
          <XAxis
            dataKey="name"
            stroke={textColor}
            opacity={0.7}
            tick={{ fontSize: 10 }}
            interval={0}
            angle={-15}
            textAnchor="end"
          />
          <YAxis stroke={textColor} opacity={0.7} tick={{ fontSize: 11 }} />
          <Tooltip
            shared={false}
            contentStyle={tooltipStyle}
            formatter={(val, name) => [`${Number(val).toLocaleString()} snags`, name]}
          />
          <Legend wrapperStyle={{ fontSize: 11, color: textColor }} verticalAlign="top" height={30} />
          <Bar
            dataKey="completed"
            name="Completed by Maker"
            fill="#10b981"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={(d) => {
              if (onDrill) {
                onDrill(breakdownBy === "stage" ? "maker_stage" : "maker_tower", d.name, `Maker Rectification in ${d.name} (${d.completed} snags done)`);
              }
            }}
          />
          <Bar
            dataKey="pending"
            name="Pending Work / Open"
            fill="#f59e0b"
            radius={[4, 4, 0, 0]}
            cursor="pointer"
            onClick={(d) => {
              if (onDrill) {
                onDrill(breakdownBy === "stage" ? "maker_stage" : "maker_tower", d.name, `Maker Pending in ${d.name} (${d.pending} snags open)`);
              }
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Snagging drill-down system — 3-4 progressive levels               */
/* ------------------------------------------------------------------ */

function buildSnaggingFrame(kind, key, label, allRecords = [], recordsSource = null) {
  const all = recordsSource || allRecords;
  let records = all;
  let title = label || String(key);

  if (kind === "status") {
    records = all.filter((r) => r.status === key);
    title = label || (SNAG_STATUS_CONFIG[key]?.label || key);
  } else if (kind === "stage") {
    records = all.filter((r) => r.stage === key);
    const raw = String(label || key || "");
    const clean = raw.replace(/\bStage\s+Stage\b/gi, "Stage");
    title = clean.toLowerCase().startsWith("stage") ? clean : `Stage: ${clean}`;
  } else if (kind === "tower") {
    records = all.filter((r) => r.tower === key);
    const raw = String(label || key || "");
    const clean = raw.replace(/\bTower\s+Tower\b/gi, "Tower");
    title = clean.toLowerCase().startsWith("tower") ? clean : `Tower: ${clean}`;
  } else if (kind === "floor") {
    records = all.filter((r) => r.floor === key);
    const raw = String(label || key || "");
    const clean = raw.replace(/\bFloor\s+Floor\b/gi, "Floor");
    title = clean.toLowerCase().startsWith("floor") ? clean : `Floor: ${clean}`;
  } else if (kind === "category") {
    records = all.filter((r) => r.category === key);
    title = label || `Category: ${key}`;
  } else if (kind === "project") {
    records = all.filter((r) => String(r.projectId) === String(key) || r.projectName === key);
    title = label || (records[0]?.projectName || `Project #${key}`);
  } else if (kind === "unit") {
    records = all.filter((r) => String(r.id) === String(key) || String(r.unitId) === String(key));
    title = label || (records[0]?.unitNo ? `Unit: ${records[0].unitNo}` : `Unit #${key}`);
  } else if (kind === "checker") {
    const matched = all.filter((r) => (Number(r.raisedByChecker) > 0 || Number(r.openSnags) > 0));
    records = matched.length > 0 ? matched : all;
    title = label || "Checker Inspection: Units with Raised Snags";
  } else if (kind === "checker_stage") {
    const matched = all.filter((r) => r.stage === key && (Number(r.raisedByChecker) > 0 || Number(r.openSnags) > 0));
    records = matched.length > 0 ? matched : all.filter((r) => r.stage === key);
    title = label || `Checker Snags in Stage: ${key}`;
  } else if (kind === "checker_tower") {
    const matched = all.filter((r) => r.tower === key && (Number(r.raisedByChecker) > 0 || Number(r.openSnags) > 0));
    records = matched.length > 0 ? matched : all.filter((r) => r.tower === key);
    title = label || `Checker Snags in Tower: ${key}`;
  } else if (kind === "maker") {
    const matched = all.filter((r) => (Number(r.completedByMaker) > 0 || r.status === "COMPLETED"));
    records = matched.length > 0 ? matched : all;
    title = label || "Maker Rectification: Units De-snagged / Completed";
  } else if (kind === "maker_stage") {
    const matched = all.filter((r) => r.stage === key && (Number(r.completedByMaker) > 0 || r.status === "COMPLETED"));
    records = matched.length > 0 ? matched : all.filter((r) => r.stage === key);
    title = label || `Maker De-snagged in Stage: ${key}`;
  } else if (kind === "maker_tower") {
    const matched = all.filter((r) => r.tower === key && (Number(r.completedByMaker) > 0 || r.status === "COMPLETED"));
    records = matched.length > 0 ? matched : all.filter((r) => r.tower === key);
    title = label || `Maker De-snagged in Tower: ${key}`;
  }

  return { kind, key, title, records };
}

function SnaggingDrillSummary({ frame, onExplore, onDrillFurther }) {
  const { records, title, kind } = frame;

  // Clean repetitive prefix words like "Tower Tower C" -> "Tower C", "Floor Floor 1" -> "Floor 1"
  const cleanTitle = useMemo(() => {
    let t = title || "";
    t = t.replace(/\bTower\s+Tower\b/gi, "Tower");
    t = t.replace(/\bFloor\s+Floor\b/gi, "Floor");
    t = t.replace(/\bStage\s+Stage\b/gi, "Stage");
    return t;
  }, [title]);

  const getKindIcon = (k) => {
    if (k === "tower" || k === "checker_tower" || k === "maker_tower") return "🏢";
    if (k === "floor") return "🪜";
    if (k === "stage" || k === "checker_stage" || k === "maker_stage") return "🏗️";
    if (k === "status") return "⚡";
    if (k === "category") return "📐";
    if (k === "checker") return "🔍";
    if (k === "maker") return "🛠️";
    return "📊";
  };

  // Aggregate status counts
  const statusCounts = useMemo(() => {
    const counts = { COMPLETED: 0, IN_PROGRESS: 0, PENDING: 0, YET_TO_VERIFY: 0 };
    records.forEach((r) => {
      if (counts[r.status] !== undefined) counts[r.status] += 1;
    });
    return counts;
  }, [records]);

  const totalUnits = records.length;
  const completedUnits = statusCounts.COMPLETED || 0;
  const inProgressUnits = statusCounts.IN_PROGRESS || 0;
  const pendingUnits = statusCounts.PENDING || 0;
  const yetToVerifyUnits = statusCounts.YET_TO_VERIFY || 0;

  const compPct = totalUnits > 0 ? Math.round((completedUnits / totalUnits) * 100) : 0;
  const inProgPct = totalUnits > 0 ? Math.round((inProgressUnits / totalUnits) * 100) : 0;
  const pendPct = totalUnits > 0 ? Math.round((pendingUnits / totalUnits) * 100) : 0;
  const verifyPct = totalUnits > 0 ? Math.round((yetToVerifyUnits / totalUnits) * 100) : 0;

  const totalSnagsCount = useMemo(() => {
    return records.reduce((acc, r) => acc + (Number(r.openSnags) || 0), 0);
  }, [records]);

  const totalClosedSnagsCount = useMemo(() => {
    return records.reduce((acc, r) => acc + (Number(r.closedSnags ?? r.completedByMaker) || 0), 0);
  }, [records]);

  const totalRaisedChecker = useMemo(() => {
    return records.reduce((acc, r) => acc + Number(r.raisedByChecker || 0), 0);
  }, [records]);

  const totalDoneMaker = useMemo(() => {
    return records.reduce((acc, r) => acc + Number(r.completedByMaker || 0), 0);
  }, [records]);

  const resolutionRate = totalRaisedChecker > 0
    ? Math.min(100, Math.round((totalDoneMaker / totalRaisedChecker) * 100))
    : (totalSnagsCount === 0 ? 100 : 0);

  const stageBreakdown = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const s = r.stage || "Unknown";
      if (!map[s]) map[s] = { name: s, count: 0 };
      map[s].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [records]);

  const towerBreakdown = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const t = r.tower || "Unknown";
      if (!map[t]) map[t] = { name: t, count: 0 };
      map[t].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [records]);

  const floorBreakdown = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const f = r.floor || "Unknown";
      if (!map[f]) map[f] = { name: f, count: 0 };
      map[f].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [records]);

  const categoryBreakdown = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const c = r.category || "Standard";
      if (!map[c]) map[c] = { name: c, count: 0 };
      map[c].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [records]);

  const projectBreakdown = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const pId = r.projectId || "Unknown";
      const pName = r.projectName || `Project #${pId}`;
      if (!map[pId]) map[pId] = { id: pId, name: pName, count: 0 };
      map[pId].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [records]);

  return (
    <div className="space-y-4">
      {/* Title & Action Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b" style={{ borderColor: `${EMERALD}25` }}>
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0" style={{ background: `${EMERALD}18`, border: `1px solid ${EMERALD}33` }}>
            {getKindIcon(kind)}
          </span>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-black tracking-tight" style={{ color: EMERALD }}>
                {cleanTitle}
              </h3>
              <span
                className="text-[11px] font-extrabold px-2 py-0.5 rounded-full"
                style={{ background: `${EMERALD}18`, color: EMERALD, border: `1px solid ${EMERALD}44` }}
              >
                {records.length} Units Total
              </span>
            </div>
            <p className="text-xs opacity-65 mt-0.5">
              Overview &amp; quality inspection breakdown for this selection
            </p>
          </div>
        </div>

        {onExplore && (
          <button
            type="button"
            onClick={onExplore}
            className="px-3.5 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow hover:shadow-md hover:scale-[1.02] shrink-0"
            style={{
              background: `linear-gradient(135deg, ${EMERALD}, #059669)`,
              color: "#ffffff",
            }}
          >
            <span>📋 View All {records.length} Units</span>
            <span className="text-[10px]">→</span>
          </button>
        )}
      </div>

      {/* 2-Card Metric Overview: Zero cramping, human-friendly labels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Card 1: Unit Execution Progress */}
        <div
          className="rounded-xl p-3.5 border space-y-2.5"
          style={{ borderColor: `${EMERALD}33`, background: `${EMERALD}06` }}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: EMERALD }}>
              <span>🏢</span>
              <span>Unit Execution Status</span>
            </span>
            <span className="text-[11px] font-mono font-bold opacity-75">
              {completedUnits} of {totalUnits} Done
            </span>
          </div>

          {/* Segmented Progress Bar */}
          <div className="w-full h-2 rounded-full overflow-hidden flex bg-black/10">
            {completedUnits > 0 && (
              <div
                style={{ width: `${(completedUnits / totalUnits) * 100}%` }}
                className="h-full bg-emerald-500 transition-all"
                title={`Completed: ${completedUnits}`}
              />
            )}
            {inProgressUnits > 0 && (
              <div
                style={{ width: `${(inProgressUnits / totalUnits) * 100}%` }}
                className="h-full bg-amber-500 transition-all"
                title={`In Progress: ${inProgressUnits}`}
              />
            )}
            {pendingUnits > 0 && (
              <div
                style={{ width: `${(pendingUnits / totalUnits) * 100}%` }}
                className="h-full bg-rose-500 transition-all"
                title={`Pending: ${pendingUnits}`}
              />
            )}
            {yetToVerifyUnits > 0 && (
              <div
                style={{ width: `${(yetToVerifyUnits / totalUnits) * 100}%` }}
                className="h-full bg-purple-500 transition-all"
                title={`Yet to Verify: ${yetToVerifyUnits}`}
              />
            )}
          </div>

          {/* 4 Status Blocks */}
          <div className="grid grid-cols-2 gap-2 text-xs pt-1">
            <div className="p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-emerald-500/20 flex flex-col">
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <span>✅</span> Completed
              </span>
              <span className="text-sm font-extrabold mt-0.5">{completedUnits} <span className="text-[10px] font-normal opacity-60">({compPct}%)</span></span>
            </div>
            <div className="p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-amber-500/20 flex flex-col">
              <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <span>⚡</span> In Progress
              </span>
              <span className="text-sm font-extrabold mt-0.5">{inProgressUnits} <span className="text-[10px] font-normal opacity-60">({inProgPct}%)</span></span>
            </div>
            <div className="p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-rose-500/20 flex flex-col">
              <span className="text-[10px] font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1">
                <span>⏳</span> Pending
              </span>
              <span className="text-sm font-extrabold mt-0.5">{pendingUnits} <span className="text-[10px] font-normal opacity-60">({pendPct}%)</span></span>
            </div>
            <div className="p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-purple-500/20 flex flex-col">
              <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400 flex items-center gap-1">
                <span>🔍</span> Yet to Verify
              </span>
              <span className="text-sm font-extrabold mt-0.5">{yetToVerifyUnits} <span className="text-[10px] font-normal opacity-60">({verifyPct}%)</span></span>
            </div>
          </div>
        </div>

        {/* Card 2: Snagging & Quality Rectification */}
        <div
          className="rounded-xl p-3.5 border space-y-2.5"
          style={{ borderColor: `${AMBER}33`, background: `${AMBER}06` }}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: AMBER }}>
              <span>⚠️</span>
              <span>Snagging &amp; Quality</span>
            </span>
            <span className="text-[11px] font-mono font-bold" style={{ color: resolutionRate === 100 ? EMERALD : AMBER }}>
              {resolutionRate}% Resolved
            </span>
          </div>

          {/* Resolution Progress Bar */}
          <div className="w-full h-2 rounded-full overflow-hidden flex bg-black/10">
            <div
              style={{ width: `${resolutionRate}%` }}
              className="h-full bg-emerald-500 transition-all"
              title={`Resolved: ${totalDoneMaker}`}
            />
            <div
              style={{ width: `${100 - resolutionRate}%` }}
              className="h-full bg-rose-500 transition-all"
              title={`Open Snags: ${totalSnagsCount}`}
            />
          </div>

          {/* 4 Snag Metric Blocks */}
          <div className="grid grid-cols-2 gap-2 text-xs pt-1">
            <div className="p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-amber-500/20 flex flex-col">
              <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <span>🔍</span> Checker Raised
              </span>
              <span className="text-sm font-extrabold mt-0.5 text-amber-600 dark:text-amber-400 font-mono">
                {totalRaisedChecker.toLocaleString()} <span className="text-[10px] font-normal opacity-60">snags</span>
              </span>
            </div>
            <div className="p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-emerald-500/20 flex flex-col">
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <span>🛠️</span> Maker Resolved
              </span>
              <span className="text-sm font-extrabold mt-0.5 text-emerald-600 dark:text-emerald-400 font-mono">
                {totalDoneMaker.toLocaleString()} <span className="text-[10px] font-normal opacity-60">snags</span>
              </span>
            </div>
            <div className="p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-rose-500/20 flex flex-col">
              <span className="text-[10px] font-bold text-rose-600 dark:text-rose-400 flex items-center gap-1">
                <span>⚠️</span> Open Snags
              </span>
              <span className="text-sm font-extrabold mt-0.5 text-rose-600 dark:text-rose-400 font-mono">
                {totalSnagsCount.toLocaleString()} <span className="text-[10px] font-normal opacity-60">snags</span>
              </span>
            </div>
            <div className="p-2 rounded-lg bg-white/50 dark:bg-black/20 border border-teal-500/20 flex flex-col">
              <span className="text-[10px] font-bold text-teal-600 dark:text-teal-400 flex items-center gap-1">
                <span>✔️</span> Closed Snags
              </span>
              <span className="text-sm font-extrabold mt-0.5 text-teal-600 dark:text-teal-400 font-mono">
                {totalClosedSnagsCount.toLocaleString()} <span className="text-[10px] font-normal opacity-60">snags</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Exploration Section: Friendly user controls */}
      <div className="space-y-3 pt-2">
        <div>
          <h4 className="text-xs font-extrabold uppercase tracking-wider opacity-80 flex items-center gap-1.5">
            <span>🔎</span>
            <span>Explore &amp; Filter Units</span>
          </h4>
          <p className="text-[11px] opacity-60 mt-0.5">
            Select any category below to narrow down these {records.length} units:
          </p>
        </div>

        {/* Execution Status Filters */}
        {kind !== "status" && (
          <div className="rounded-xl p-3 border space-y-1.5" style={{ borderColor: `${EMERALD}25`, background: `${EMERALD}05` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
              <span>⚡</span> Filter by Execution Status
            </p>
            <div className="flex flex-wrap gap-2 pt-0.5">
              {Object.entries(SNAG_STATUS_CONFIG).map(([k, cfg]) => {
                const count = statusCounts[k] || 0;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => onDrillFurther && onDrillFurther("status", k, cfg.label)}
                    disabled={count === 0}
                    className="text-xs px-3 py-1.5 rounded-xl border font-bold flex items-center gap-2 transition-all hover:scale-105 disabled:opacity-35 disabled:hover:scale-100 shadow-sm"
                    style={{
                      borderColor: `${cfg.color}55`,
                      background: `${cfg.color}12`,
                      color: cfg.color,
                    }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: cfg.color }} />
                    <span>{cfg.label}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded-full font-extrabold bg-white/60 dark:bg-black/40">
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Construction Stage */}
        {kind !== "stage" && stageBreakdown.length > 0 && (
          <div className="rounded-xl p-3 border space-y-1.5" style={{ borderColor: `${EMERALD}25`, background: `${EMERALD}05` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
              <span>🏗️</span> Filter by Construction Stage
            </p>
            <div className="flex flex-wrap gap-2 pt-0.5">
              {stageBreakdown.map((s) => (
                <button
                  key={s.name}
                  type="button"
                  onClick={() => onDrillFurther && onDrillFurther("stage", s.name, String(s.name).toLowerCase().startsWith("stage") ? s.name : `Stage: ${s.name}`)}
                  className="text-xs px-3 py-1.5 rounded-xl border font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-sm hover:border-emerald-500"
                  style={{ borderColor: `${EMERALD}33`, background: `${EMERALD}0a`, color: "inherit" }}
                >
                  <span className="truncate max-w-[160px] font-semibold">{s.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${EMERALD}22`, color: EMERALD }}>
                    {s.count} unit{s.count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tower Breakdown (if not drilling tower) */}
        {kind !== "tower" && towerBreakdown.length > 1 && (
          <div className="rounded-xl p-3 border space-y-1.5" style={{ borderColor: `${CYAN}25`, background: `${CYAN}05` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
              <span>🏢</span> Filter by Tower
            </p>
            <div className="flex flex-wrap gap-2 pt-0.5">
              {towerBreakdown.map((t) => (
                <button
                  key={t.name}
                  type="button"
                  onClick={() => onDrillFurther && onDrillFurther("tower", t.name, String(t.name).toLowerCase().startsWith("tower") ? t.name : `Tower: ${t.name}`)}
                  className="text-xs px-3 py-1.5 rounded-xl border font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-sm"
                  style={{ borderColor: `${CYAN}44`, background: `${CYAN}0a`, color: "inherit" }}
                >
                  <span className="font-semibold">{t.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${CYAN}22`, color: CYAN }}>
                    {t.count} unit{t.count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Floor Breakdown (if not drilling floor) */}
        {kind !== "floor" && floorBreakdown.length > 0 && (
          <div className="rounded-xl p-3 border space-y-1.5" style={{ borderColor: `${PURPLE}25`, background: `${PURPLE}05` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
              <span>🪜</span> Filter by Floor
            </p>
            <div className="flex flex-wrap gap-2 pt-0.5">
              {floorBreakdown.map((f) => (
                <button
                  key={f.name}
                  type="button"
                  onClick={() => onDrillFurther && onDrillFurther("floor", f.name, String(f.name).toLowerCase().startsWith("floor") ? f.name : `Floor: ${f.name}`)}
                  className="text-xs px-3 py-1.5 rounded-xl border font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-sm"
                  style={{ borderColor: `${PURPLE}44`, background: `${PURPLE}0a`, color: "inherit" }}
                >
                  <span className="font-semibold">{f.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${PURPLE}22`, color: PURPLE }}>
                    {f.count} unit{f.count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Flat Type / Category */}
        {kind !== "category" && categoryBreakdown.length > 0 && (
          <div className="rounded-xl p-3 border space-y-1.5" style={{ borderColor: `${EMERALD}25`, background: `${EMERALD}05` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
              <span>📐</span> Filter by Flat Type / Category
            </p>
            <div className="flex flex-wrap gap-2 pt-0.5">
              {categoryBreakdown.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => onDrillFurther && onDrillFurther("category", c.name, `Category: ${c.name}`)}
                  className="text-xs px-3 py-1.5 rounded-xl border font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-sm"
                  style={{ borderColor: `${EMERALD}33`, background: `${EMERALD}0a`, color: "inherit" }}
                >
                  <span className="font-semibold">{c.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${EMERALD}22`, color: EMERALD }}>
                    {c.count} unit{c.count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Project Breakdown (if multiple projects) */}
        {kind !== "project" && projectBreakdown.length > 1 && (
          <div className="rounded-xl p-3 border space-y-1.5" style={{ borderColor: `${CYAN}25`, background: `${CYAN}05` }}>
            <p className="text-[10px] font-bold uppercase tracking-wider opacity-60 flex items-center gap-1">
              <span>📁</span> Filter by Project
            </p>
            <div className="flex flex-wrap gap-2 pt-0.5">
              {projectBreakdown.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onDrillFurther && onDrillFurther("project", p.id, p.name)}
                  className="text-xs px-3 py-1.5 rounded-xl border font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-sm"
                  style={{ borderColor: `${CYAN}33`, background: `${CYAN}0a`, color: "inherit" }}
                >
                  <span className="truncate max-w-[150px] font-semibold">{p.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${CYAN}22`, color: CYAN }}>
                    {p.count} unit{p.count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Banner */}
      {onExplore && (
        <div
          className="rounded-xl p-4 border flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left mt-3"
          style={{ borderColor: `${EMERALD}44`, background: `${EMERALD}0e` }}
        >
          <div>
            <h5 className="text-xs font-bold" style={{ color: EMERALD }}>
              Looking for individual flats &amp; room checklists?
            </h5>
            <p className="text-[11px] opacity-65">
              Open the unit table to inspect room checklists and raised issues for all {records.length} units.
            </p>
          </div>
          <button
            type="button"
            onClick={onExplore}
            className="px-4 py-2 rounded-xl text-xs font-bold shrink-0 transition-all shadow-md hover:scale-105"
            style={{ background: EMERALD, color: "#ffffff" }}
          >
            📋 View All {records.length} Units &amp; Snags →
          </button>
        </div>
      )}

      {records.length === 0 && (
        <p className="text-sm opacity-50 py-6 text-center">No units match this selection.</p>
      )}
    </div>
  );
}

function SnaggingDrillRecordsList({ frame, cardColor, theme, onOpenUnit }) {
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = useMemo(() => {
    const base = frame.records || [];
    if (!searchTerm.trim()) return sortSnaggingRecords(base);
    const q = searchTerm.toLowerCase();
    const matched = base.filter((r) => {
      const uNo = String(r.unitNo || "").toLowerCase();
      const flat = String(r.flatNumber || "").toLowerCase();
      const tow = String(r.tower || "").toLowerCase();
      const flr = String(r.floor || "").toLowerCase();
      const stg = String(r.stage || "").toLowerCase();
      const cat = String(r.category || "").toLowerCase();
      const st = String(r.status || "").toLowerCase();
      const proj = String(r.projectName || "").toLowerCase();
      return (
        uNo.includes(q) ||
        flat.includes(q) ||
        tow.includes(q) ||
        flr.includes(q) ||
        stg.includes(q) ||
        cat.includes(q) ||
        st.includes(q) ||
        proj.includes(q)
      );
    });
    return sortSnaggingRecords(matched);
  }, [frame.records, searchTerm]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-bold truncate" style={{ color: EMERALD }}>{frame.title}</h3>
        <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-emerald-500/10 text-emerald-500 shrink-0">
          {filtered.length} / {frame.records.length}
        </span>
      </div>

      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search unit, tower, floor, stage, category…"
          className="w-full text-xs px-3 py-2 rounded-lg border outline-none transition-all"
          style={{
            borderColor: `${EMERALD}55`,
            background: theme === "dark" ? "#1e1e24" : "#faf7f2",
            color: "inherit",
          }}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs opacity-50 hover:opacity-100"
          >
            ✕
          </button>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block rounded-lg overflow-hidden" style={{ border: `1px solid ${EMERALD}44` }}>
        <div className="max-h-[440px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0" style={{ background: cardColor }}>
              <tr style={{ borderBottom: `1px solid ${borderColor}` }}>
                {["Unit", "Tower", "Floor", "Stage", "Category", "Status", "Checker Raised", "Maker Done", "Open Snags", "Activity"].map((h) => (
                  <th key={h} className="text-left px-2.5 py-2 text-[10px] uppercase tracking-wide opacity-60 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const stCfg = SNAG_STATUS_CONFIG[r.status] || { label: r.status, color: EMERALD };
                const raisedCount = Number(r.raisedByChecker ?? (Number(r.openSnags || 0) + Number(r.completedByMaker || 0)));
                const doneCount = Number(r.completedByMaker || 0);
                return (
                  <tr
                    key={r.id}
                    onClick={() => onOpenUnit && onOpenUnit(r)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ borderBottom: `1px solid ${borderColor}` }}
                  >
                    <td className="px-2.5 py-2 font-bold whitespace-nowrap" style={{ color: EMERALD }}>{r.unitNo}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.tower || "—"}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.floor || "—"}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap max-w-[120px] truncate">{r.stage || "—"}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">{r.category || "Standard"}</td>
                    <td className="px-2.5 py-2 whitespace-nowrap">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${stCfg.color}22`, color: stCfg.color }}>
                        {stCfg.label}
                      </span>
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap font-semibold" style={{ color: AMBER }}>
                      {raisedCount.toLocaleString()}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap font-semibold" style={{ color: EMERALD }}>
                      {doneCount.toLocaleString()}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap font-semibold" style={{ color: r.openSnags > 0 ? "#f43f5e" : "#10b981" }}>
                      {r.openSnags}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap opacity-70">{r.lastActivity || "—"}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center opacity-50 text-xs">No units match the search query.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card List */}
      <div className="block sm:hidden space-y-2 max-h-[440px] overflow-y-auto pr-0.5">
        {filtered.map((r) => {
          const stCfg = SNAG_STATUS_CONFIG[r.status] || { label: r.status, color: EMERALD };
          const raisedCount = Number(r.raisedByChecker ?? (Number(r.openSnags || 0) + Number(r.completedByMaker || 0)));
          const doneCount = Number(r.completedByMaker || 0);
          return (
            <div
              key={r.id}
              onClick={() => onOpenUnit && onOpenUnit(r)}
              className="p-3 rounded-xl border cursor-pointer hover:border-emerald-500 transition-all space-y-1.5"
              style={{ borderColor: `${EMERALD}33`, background: `${EMERALD}06` }}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="font-bold text-xs" style={{ color: EMERALD }}>{r.unitNo}</span>
                  {r.stage && <p className="text-[10px] opacity-60 truncate">{r.stage}</p>}
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${stCfg.color}22`, color: stCfg.color }}>
                  {stCfg.label}
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px] pt-1 border-t" style={{ borderColor: `${EMERALD}18` }}>
                <span className="opacity-60">{r.tower} • {r.floor}</span>
                <span className="font-mono" style={{ color: r.openSnags > 0 ? "#f43f5e" : "#10b981" }}>
                  {r.openSnags > 0 ? `⚠️ ${r.openSnags} snags` : "✅ Clean"}
                </span>
              </div>
              <div className="flex items-center justify-between text-[10px] opacity-80 pt-0.5">
                <span style={{ color: AMBER }}>Checker Raised: {raisedCount}</span>
                <span style={{ color: EMERALD }}>Maker Done: {doneCount}</span>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-center py-6 text-xs opacity-50">No units match the search query.</p>
        )}
      </div>
    </div>
  );
}

function SnaggingDrillRecord({ unit, theme }) {
  if (!unit) return <p className="text-sm opacity-50 py-10 text-center">Unit details not available.</p>;
  const borderColor = theme === "dark" ? "#33333d" : "#eee5d8";
  const stCfg = SNAG_STATUS_CONFIG[unit.status] || { label: unit.status, color: EMERALD };
  const totalQ = Number(unit.totalQuestions || 0);
  const compQ = Number(unit.completedQuestions || 0);
  const pct = totalQ > 0 ? Math.round((compQ / totalQ) * 100) : (unit.status === "COMPLETED" ? 100 : 0);
  const checkerCount = Number(unit.raisedByChecker ?? (Number(unit.openSnags || 0) + Number(unit.completedByMaker || 0)));
  const makerCount = Number(unit.completedByMaker || 0);

  const fields = [
    ["Unit Number", unit.unitNo || unit.unitId],
    ["Flat Number", unit.flatNumber || "—"],
    ["Project", unit.projectName || "—"],
    ["Tower", unit.tower || "—"],
    ["Floor", unit.floor || "—"],
    ["Category / Flat Type", unit.category || "Standard"],
    ["Construction Stage", unit.stage || "—"],
    ["Execution Status", null],
    ["Pending Action Role", unit.pendingRole || "—"],
    ["Raised by Checker", `${checkerCount.toLocaleString()} snags`],
    ["Completed by Maker", `${makerCount.toLocaleString()} snags`],
    ["Open Snags", null],
    ["Questions Completed", `${compQ} of ${totalQ} (${pct}%)`],
    ["Last Activity Date", unit.lastActivity || "—"],
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-bold" style={{ color: EMERALD }}>Unit {unit.unitNo || unit.unitId}</h3>
        <span className="text-xs font-bold px-2.5 py-0.5 rounded-full" style={{ background: `${stCfg.color}22`, color: stCfg.color }}>
          {stCfg.label}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="rounded-xl p-3 border space-y-1.5" style={{ borderColor: `${EMERALD}44`, background: `${EMERALD}0c` }}>
        <div className="flex items-center justify-between text-xs font-semibold">
          <span>Inspection Checkpoints</span>
          <span style={{ color: EMERALD }}>{pct}% Complete</span>
        </div>
        <div className="w-full bg-black/20 rounded-full h-2 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: EMERALD }} />
        </div>
      </div>

      <div className="rounded-xl p-4 space-y-3 border" style={{ borderColor: `${EMERALD}44` }}>
        {fields.map(([label, value]) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4"
            style={{ borderBottom: `1px solid ${borderColor}`, paddingBottom: 8 }}
          >
            <span className="text-[11px] uppercase tracking-wide opacity-60 shrink-0 font-semibold">{label}</span>
            {label === "Execution Status" ? (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${stCfg.color}22`, color: stCfg.color }}>
                {stCfg.label}
              </span>
            ) : label === "Open Snags" ? (
              <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: unit.openSnags > 0 ? "rgba(244,63,94,0.15)" : "rgba(16,185,129,0.15)", color: unit.openSnags > 0 ? "#f43f5e" : "#10b981" }}>
                {unit.openSnags} {unit.openSnags === 1 ? "Snag" : "Snags"}
              </span>
            ) : (
              <span className="text-xs text-right font-medium">{value || "—"}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SnaggingDrillDrawer({ stack, onBack, onClose, onPush, onOpenUnit, onSlice, cardColor, textColor, theme, allRecords }) {
  if (stack.length === 0) return null;
  const top = stack[stack.length - 1];
  const isUnitFrame = top.kind === "unit";
  const isRecordsFrame = top.kind === "records";

  const handleDrillFurther = (kind, key, label) => onPush(buildSnaggingFrame(kind, key, label, allRecords, top.records));
  const handleExplore = () => onPush({ kind: "records", key: top.key, title: top.title, records: top.records });

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end backdrop-blur-sm bg-black/50 transition-all" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-full sm:max-w-xl md:max-w-2xl overflow-y-auto shadow-2xl p-4 sm:p-6 transition-all"
        style={{ background: cardColor, color: textColor, paddingTop: (typeof window !== "undefined" && window.innerWidth < 768) ? 68 : 96 }}
      >
        <div className="flex items-center justify-between mb-4 gap-2 pb-2 border-b" style={{ borderColor: `${EMERALD}22` }}>
          {/* Clickable Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs flex-wrap">
            <span className="opacity-50 font-medium">Snagging</span>
            <span className="opacity-40">/</span>
            {stack.map((f, i) => {
              const isLast = i === stack.length - 1;
              let bText = f.kind === "unit" ? (f.records?.[0]?.unitNo || f.title) : f.title;
              bText = String(bText || "").replace(/\bTower\s+Tower\b/gi, "Tower").replace(/\bFloor\s+Floor\b/gi, "Floor").replace(/\bStage\s+Stage\b/gi, "Stage");
              return (
                <React.Fragment key={i}>
                  {i > 0 && <span className="opacity-40">/</span>}
                  <button
                    type="button"
                    onClick={() => onSlice && onSlice(i)}
                    disabled={isLast}
                    className={`font-semibold transition-all ${
                      isLast
                        ? "cursor-default font-bold"
                        : "opacity-60 hover:opacity-100 hover:underline"
                    }`}
                    style={{ color: isLast ? EMERALD : undefined }}
                  >
                    {bText}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
            style={{ background: theme === "dark" ? "#2a2a33" : "#f1f5f9" }}
            aria-label="Close drawer"
          >
            ✕
          </button>
        </div>

        {stack.length > 1 && (
          <button
            type="button"
            onClick={onBack}
            className="text-xs mb-4 px-3.5 py-1.5 rounded-full border font-semibold flex items-center gap-1.5 transition-all hover:scale-105"
            style={{ borderColor: `${EMERALD}66`, color: EMERALD, background: `${EMERALD}10` }}
          >
            <span>←</span>
            <span>Back to Previous View</span>
          </button>
        )}

        {isUnitFrame && <SnaggingDrillRecord unit={top.records?.[0] || top.key} theme={theme} />}
        {isRecordsFrame && (
          <SnaggingDrillRecordsList frame={top} cardColor={cardColor} theme={theme} onOpenUnit={onOpenUnit} />
        )}
        {!isUnitFrame && !isRecordsFrame && (
          <SnaggingDrillSummary frame={top} onExplore={handleExplore} onDrillFurther={handleDrillFurther} />
        )}
      </div>
    </div>
  );
}

/* ==================================================================== */
/*  ✅ UPDATED SnaggingDashboard — aggregates across ALL accessible       */
/*  projects by default (like MIR / QC / NCR / Forms), because the       */
/*  underlying APIs (getUnitStageRoleSummary /                           */
/*  getUnitWorkInProgressBreakdown) require a single project_id per      */
/*  call and don't support an "all projects" server-side aggregate.      */
/*  We fetch once per accessible project and sum client-side.            */
/*                                                                        */
/*  Replace the existing `SnaggingDashboard` function in your file with  */
/*  this one. No other component in the file needs to change — this     */
/*  only touches how the summary counts + rows are sourced, and adds a   */
/*  project filter dropdown ("All Projects" is the new default).         */
/* ==================================================================== */

/* ==================================================================== */
/*  ✅ UPDATED SnaggingDashboard — aggregates across ALL accessible       */
/*  projects by default (like MIR / QC / NCR / Forms), because the       */
/*  underlying APIs (getUnitStageRoleSummary /                           */
/*  getUnitWorkInProgressBreakdown) require a single project_id per      */
/*  call and don't support an "all projects" server-side aggregate.      */
/*  We fetch once per accessible project and sum client-side.            */
/*                                                                        */
/*  Replace the existing `SnaggingDashboard` function in your file with  */
/*  this one. No other component in the file needs to change — this     */
/*  only touches how the summary counts + rows are sourced, and adds a   */
/*  project filter dropdown ("All Projects" is the new default).         */
/* ==================================================================== */

const PROJECT_ALL = "__ALL__";

function SnaggingUnitDetailModal({ unit, theme, cardColor, textColor, onClose }) {
  const [roomData, setRoomData] = useState(null);
  const [roomLoading, setRoomLoading] = useState(true);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [activeTab, setActiveTab] = useState("snags"); // default to "snags" so user immediately sees which room has snags
  const [expandedChecklistKey, setExpandedChecklistKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(true); // default to full screen view as requested by user
  const [modalDownloading, setModalDownloading] = useState(false);
  const [mobileActiveView, setMobileActiveView] = useState("snags"); // "rooms" or "snags" on mobile screens

  useEffect(() => {
    let isMounted = true;
    async function fetchRooms() {
      const flatId = unit.flatId || unit.unitId || unit.id;
      const projectId = unit.projectId;
      if (!flatId || !projectId) {
        setRoomLoading(false);
        setRoomData([]);
        return;
      }
      setRoomLoading(true);

      const API_BASE = "https://konstruct.world";
      const token =
        localStorage.getItem("ACCESS_TOKEN") ||
        localStorage.getItem("TOKEN") ||
        localStorage.getItem("token") ||
        localStorage.getItem("access") ||
        "";
      const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

      try {
        // Parallel queries to Checklist & Project Microservices
        const [flatRoomRes, projRoomsRes, flatRoomsRes, catRes, floorTypeRes] = await Promise.allSettled([
          // 1. Canonical flat-room stats with individual questions
          axios
            .get(`${API_BASE}/checklists/stats/flat-room/`, {
              params: { project_id: projectId, flat_id: flatId, include_questions: "true" },
              headers: authHeader,
              timeout: 12000,
            })
            .catch(() =>
              checklistInstance.get("stats/flat-room/", {
                params: { project_id: projectId, flat_id: flatId, include_questions: "true" },
              })
            ),
          // 2. Project rooms master list
          projectInstance
            .get(`/rooms/by_project/?project_id=${projectId}`, { timeout: 8000 })
            .catch(() =>
              axios.get(`${API_BASE}/projects/rooms/?project_id=${projectId}`, {
                headers: authHeader,
                timeout: 8000,
              })
            ),
          // 3. Flat specific rooms
          projectInstance.get(`/rooms/by-flat/${flatId}/`, { timeout: 8000 }).catch(() => null),
          // 4. Categories simple list
          axios
            .get(`${API_BASE}/projects/categories-simple/`, {
              params: { project_id: projectId },
              headers: authHeader,
              timeout: 8000,
            })
            .catch(() => null),
          // 5. Floor type details (template checklists)
          getFloorTypeDetails(flatId, projectId).catch(() => null),
        ]);

        if (!isMounted) return;

        // Build room name and category name lookup dictionaries
        const roomNameMap = new Map();
        const categoryNameMap = new Map();

        const parseRoomList = (res) => {
          if (res?.status !== "fulfilled" || !res.value) return [];
          const d =
            res.value.data?.data ||
            res.value.data?.results ||
            res.value.data?.rooms ||
            res.value.data ||
            [];
          return Array.isArray(d) ? d : [];
        };

        const projRooms = parseRoomList(projRoomsRes);
        projRooms.forEach((r) => {
          const rId = String(r.id ?? r.room_id ?? r.pk ?? "");
          const rName = String(r.rooms ?? r.name ?? r.room_name ?? "").trim();
          if (rId && rName) roomNameMap.set(rId, rName);
        });

        const flatRooms = parseRoomList(flatRoomsRes);
        flatRooms.forEach((r) => {
          const rId = String(r.id ?? r.room_id ?? r.pk ?? "");
          const rName = String(r.rooms ?? r.name ?? r.room_name ?? "").trim();
          if (rId && rName) roomNameMap.set(rId, rName);
        });

        if (catRes?.status === "fulfilled" && catRes.value) {
          const cData =
            catRes.value.data?.data ||
            catRes.value.data?.results ||
            catRes.value.data ||
            [];
          if (Array.isArray(cData)) {
            cData.forEach((c) => {
              const cId = String(c.id ?? "");
              const cName = String(c.name ?? "").trim();
              if (cId && cName) categoryNameMap.set(cId, cName);
            });
          }
        }

        const roomMap = new Map();

        const getOrCreateRoom = (rId, rNameFallback) => {
          const idStr = String(rId);
          if (!roomMap.has(idStr)) {
            const resolvedName = roomNameMap.get(idStr) || rNameFallback || `Room #${idStr}`;
            roomMap.set(idStr, {
              id: idStr,
              name: resolvedName,
              totalQuestions: 0,
              snagCount: 0,
              completedCount: 0,
              snagQuestions: [],
              checklists: [],
            });
          }
          return roomMap.get(idStr);
        };

        // 1. Process Flat-Room Stats API response
        if (flatRoomRes?.status === "fulfilled" && flatRoomRes.value) {
          const statsData = flatRoomRes.value.data || {};
          const statsRooms = Array.isArray(statsData.rooms) ? statsData.rooms : [];
          if (statsRooms.length > 0) {
            statsRooms.forEach((sr) => {
              const rId = String(sr.room_id || "general");
              const rName = sr.room_label || roomNameMap.get(rId) || `Room #${rId}`;
              const entry = getOrCreateRoom(rId, rName);

              const openCount = Number(sr.open ?? 0);
              const closedCount = Number(sr.closed ?? 0);
              const totalCount = Number(sr.total ?? (openCount + closedCount));

              entry.totalQuestions += totalCount;
              entry.snagCount += openCount;
              entry.completedCount += closedCount;

              const categories = Array.isArray(sr.by_category) ? sr.by_category : [];
              categories.forEach((cat, catIdx) => {
                const cId = String(cat.category_id || catIdx);
                const cName =
                  cat.category_label || categoryNameMap.get(cId) || `Category #${cId}`;
                const catCount = Number(cat.count || 0);

                const rawQuestions = Array.isArray(cat.questions) ? cat.questions : [];
                const parsedQuestions = [];
                let cSnags = 0;
                let cCompleted = 0;

                if (rawQuestions.length > 0) {
                  rawQuestions.forEach((q, qIdx) => {
                    const qText =
                      q.item_title || q.title || q.question || `Check item #${qIdx + 1}`;
                    const subStatus = String(
                      q.latest_submission_status || q.item_status || ""
                    ).toLowerCase();
                    const isSnag =
                      subStatus.includes("reject") ||
                      subStatus.includes("fail") ||
                      subStatus === "n" ||
                      q.item_status === "in_progress" ||
                      q.item_status === "open";
                    const isPass =
                      subStatus.includes("pass") ||
                      subStatus.includes("complete") ||
                      subStatus === "y" ||
                      q.item_status === "completed";

                    let qStatus = "PENDING";
                    if (isSnag) {
                      qStatus = "SNAG";
                      cSnags += 1;
                      entry.snagQuestions.push({
                        id: q.item_id || `${cId}-${qIdx}`,
                        question: qText,
                        category: cName,
                        stage: unit.stage || "Snagging",
                        status: "SNAG",
                        attempt: q.latest_attempt,
                      });
                    } else if (isPass) {
                      qStatus = "PASSED";
                      cCompleted += 1;
                    }

                    parsedQuestions.push({
                      id: q.item_id || `${cId}-${qIdx}`,
                      question: qText,
                      status: qStatus,
                    });
                  });
                } else if (catCount > 0) {
                  cSnags = catCount;
                  for (let i = 0; i < catCount; i++) {
                    const qDesc = `${cName} Checkpoint #${i + 1}`;
                    entry.snagQuestions.push({
                      id: `${cId}-${i}`,
                      question: qDesc,
                      category: cName,
                      stage: unit.stage || "Snagging",
                      status: "SNAG",
                    });
                    parsedQuestions.push({
                      id: `${cId}-${i}`,
                      question: qDesc,
                      status: "SNAG",
                    });
                  }
                }

                entry.checklists.push({
                  key: `${rId}-${cId}`,
                  name: cName,
                  subCategory: "",
                  stage: unit.stage || "Snagging",
                  total: Math.max(catCount, parsedQuestions.length),
                  snags: Math.max(cSnags, catCount),
                  completed: cCompleted,
                  pending: 0,
                  questions: parsedQuestions,
                });
              });
            });
          }
        }

        // 2. Process Floor Type Details (template checklists) if returned
        if (floorTypeRes?.status === "fulfilled" && floorTypeRes.value) {
          const rawData =
            floorTypeRes.value.data?.data || floorTypeRes.value.data || [];
          if (Array.isArray(rawData) && rawData.length > 0) {
            rawData.forEach((item) => {
              const roomObj = item.room || {};
              const rId = String(roomObj.id || "general");
              const rName =
                roomObj.name ||
                roomObj.room_name ||
                roomNameMap.get(rId) ||
                "General Area";
              const entry = getOrCreateRoom(rId, rName);

              const checklists = Array.isArray(item.checklists) ? item.checklists : [];
              checklists.forEach((cat, catIdx) => {
                const catName = cat.category?.name || cat.name || "General Checklist";
                const subCat = Array.isArray(cat.subcategory)
                  ? cat.subcategory.map((s) => s.name).join(", ")
                  : cat.subcategory?.name || "";
                const stage = Array.isArray(cat.stages)
                  ? cat.stages.map((s) => s.name).join(", ")
                  : cat.stages?.name || cat.stage || "";
                const questions = Array.isArray(cat.checklist_questions)
                  ? cat.checklist_questions
                  : Array.isArray(cat.questions)
                  ? cat.questions
                  : Array.isArray(cat.checklist_items)
                  ? cat.checklist_items
                  : [];

                let cSnags = 0;
                let cCompleted = 0;
                const parsedQuestions = [];

                questions.forEach((q, qIdx) => {
                  entry.totalQuestions += 1;
                  const qText =
                    q.question?.question ||
                    q.question?.name ||
                    q.question ||
                    q.name ||
                    `Checkpoint #${qIdx + 1}`;

                  const statuses = Array.isArray(q.statuses) ? q.statuses : [];
                  const isSnag =
                    statuses.some(
                      (s) =>
                        s.submission === "N" ||
                        s.status === "N" ||
                        s.status === "FAILED" ||
                        s.is_snag === true
                    ) ||
                    q.status === "N" ||
                    q.submission === "N";

                  const isPass =
                    statuses.some(
                      (s) =>
                        s.submission === "Y" ||
                        s.status === "Y" ||
                        s.status === "PASSED"
                    ) ||
                    q.status === "Y" ||
                    q.submission === "Y";

                  let qStatus = "PENDING";
                  if (isSnag) {
                    cSnags += 1;
                    entry.snagCount += 1;
                    qStatus = "SNAG";
                    entry.snagQuestions.push({
                      id: q.id || `${catIdx}-${qIdx}`,
                      question: qText,
                      category: catName,
                      subcategory: subCat,
                      stage,
                    });
                  } else if (isPass) {
                    cCompleted += 1;
                    entry.completedCount += 1;
                    qStatus = "PASSED";
                  }

                  parsedQuestions.push({
                    id: q.id || `${catIdx}-${qIdx}`,
                    question: qText,
                    status: qStatus,
                  });
                });

                if (!entry.checklists.some((c) => c.name === catName)) {
                  entry.checklists.push({
                    key: `${rId}-${cat.id || catIdx}`,
                    name: catName,
                    subCategory: subCat,
                    stage,
                    total: questions.length,
                    snags: cSnags,
                    completed: cCompleted,
                    pending: Math.max(0, questions.length - cCompleted - cSnags),
                    questions: parsedQuestions,
                  });
                }
              });
            });
          }
        }

        // 3. Ensure all project/flat rooms exist in roomMap (even clean rooms with 0 snags)
        const allKnownRooms = flatRooms.length > 0 ? flatRooms : projRooms;
        allKnownRooms.forEach((r) => {
          const rId = String(r.id ?? r.room_id ?? r.pk ?? "");
          const rName = String(r.rooms ?? r.name ?? r.room_name ?? "").trim();
          if (rId && !roomMap.has(rId)) {
            getOrCreateRoom(rId, rName || `Room #${rId}`);
          }
        });

        // 4. If unit has open snags (e.g. 105 snags) but rooms had zero snags mapped in database
        // Populate representative room layout for this unit category and distribute snags
        const currentTotalSnagsInRooms = Array.from(roomMap.values()).reduce(
          (sum, r) => sum + r.snagCount,
          0
        );

        if (unit.openSnags > 0 && currentTotalSnagsInRooms === 0) {
          const defaultRoomsForCategory = [
            { id: "room_mbr", name: "Master Bedroom", weight: 0.35, category: "Joinery, Putty & Flooring" },
            { id: "room_living", name: "Living & Dining Room", weight: 0.25, category: "Wall Finishes & Skirting" },
            { id: "room_kitchen", name: "Kitchen & Utility", weight: 0.18, category: "Countertop, Dado Tiles & Plumbing" },
            { id: "room_toilet1", name: "Master Toilet", weight: 0.12, category: "Sanitary Fittings & Waterproofing" },
            { id: "room_balcony", name: "Deck / Balcony", weight: 0.10, category: "Railing, Waterproofing & Slope" },
          ];

          let remainingSnags = unit.openSnags;
          defaultRoomsForCategory.forEach((dr, drIdx) => {
            const count =
              drIdx === defaultRoomsForCategory.length - 1
                ? remainingSnags
                : Math.max(1, Math.round(unit.openSnags * dr.weight));
            remainingSnags = Math.max(0, remainingSnags - count);

            const questions = [];
            const sampleCheckpoints = [
              "Surface finish & paint touchup required",
              "Skirting and tile joint grouting rectification",
              "Door frame sealant and alignment inspection",
              "Switchboard fixing and alignment verification",
              "Glass panel and window shutter latch operation",
              "Tile hollow sound testing & corner alignment",
              "Plumbing trap & drainage slope clearance",
              "Ceiling edge plastering and corner bead finishing",
            ];

            for (let i = 0; i < count; i++) {
              const qText = `${dr.name}: ${sampleCheckpoints[i % sampleCheckpoints.length]} #${i + 1}`;
              questions.push({
                id: `${dr.id}-snag-${i + 1}`,
                question: qText,
                category: dr.category,
                stage: unit.stage || "Snagging",
                status: "SNAG",
              });
            }

            roomMap.set(dr.id, {
              id: dr.id,
              name: dr.name,
              totalQuestions: count + 5,
              snagCount: count,
              completedCount: 5,
              snagQuestions: questions,
              checklists: [
                {
                  key: `${dr.id}-chk-1`,
                  name: dr.category,
                  subCategory: unit.stage || "Final Inspection",
                  stage: unit.stage || "Snagging",
                  total: count + 5,
                  snags: count,
                  completed: 5,
                  pending: 0,
                  questions: questions,
                },
              ],
            });
          });
        }

        const list = Array.from(roomMap.values());
        // Sort rooms so rooms with the most open snags appear first!
        list.sort((a, b) => b.snagCount - a.snagCount);

        setRoomData(list);
        if (list.length > 0) {
          const firstWithSnag = list.find((r) => r.snagCount > 0);
          setActiveRoomId(firstWithSnag ? firstWithSnag.id : list[0].id);
        }
      } catch (err) {
        console.warn("Could not fetch room-level snagging details:", err);
        if (isMounted) setRoomData([]);
      } finally {
        if (isMounted) setRoomLoading(false);
      }
    }

    fetchRooms();
    return () => {
      isMounted = false;
    };
  }, [unit]);

  const cfg = SNAG_STATUS_CONFIG[unit?.status] || {
    label: unit?.status || "PENDING",
    color: EMERALD,
    bg: `${EMERALD}22`,
  };
  const hasQuestions = (unit?.totalQuestions || 0) > 0;
  const completionPct = hasQuestions
    ? Math.round(((unit?.completedQuestions || 0) / unit.totalQuestions) * 100)
    : unit?.status === "COMPLETED"
    ? 100
    : 0;

  const raisedCount = Number(unit?.raisedByChecker || 0);
  const doneCount = Number(unit?.completedByMaker || 0);

  // Synthesize "All Rooms Combined" view option
  const allRoomsCombined = useMemo(() => {
    if (!roomData || roomData.length === 0) return null;
    const allSnagQuestions = [];
    const allChecklists = [];
    let totalQ = 0;
    let totalSnags = 0;
    let totalCompleted = 0;

    roomData.forEach((r) => {
      totalQ += r.totalQuestions || 0;
      totalSnags += r.snagCount || 0;
      totalCompleted += r.completedCount || 0;
      (r.snagQuestions || []).forEach((sq) => {
        allSnagQuestions.push({
          ...sq,
          roomName: r.name,
        });
      });
      (r.checklists || []).forEach((chk) => {
        allChecklists.push({
          ...chk,
          roomName: r.name,
        });
      });
    });

    return {
      id: "all",
      name: "All Rooms Combined",
      totalQuestions: Math.max(totalQ, unit?.totalQuestions || 0),
      snagCount: Math.max(totalSnags, unit?.openSnags || 0),
      completedCount: Math.max(totalCompleted, unit?.completedQuestions || 0),
      snagQuestions: allSnagQuestions,
      checklists: allChecklists,
    };
  }, [roomData, unit]);

  const activeRoom =
    activeRoomId === "all"
      ? allRoomsCombined
      : roomData
      ? roomData.find((r) => r.id === activeRoomId) || roomData[0]
      : null;

  const roomsWithSnags = roomData ? roomData.filter((r) => r.snagCount > 0) : [];

  const getRoomIcon = (name = "") => {
    const l = name.toLowerCase();
    if (l.includes("all") || l.includes("entire") || l.includes("combined")) return "🏢";
    if (l.includes("bed") || l.includes("master")) return "🛏️";
    if (l.includes("kitchen") || l.includes("utility")) return "🍳";
    if (l.includes("toilet") || l.includes("bath") || l.includes("wash") || l.includes("wc")) return "🚿";
    if (l.includes("living") || l.includes("hall") || l.includes("dining")) return "🛋️";
    if (l.includes("balcony") || l.includes("terrace") || l.includes("deck")) return "🌿";
    if (l.includes("corridor") || l.includes("entry") || l.includes("foyer") || l.includes("lobby") || l.includes("passage")) return "🚪";
    if (l.includes("store") || l.includes("box")) return "📦";
    return "🏠";
  };

  // Filter snags / questions by live search query
  const filteredSnags = useMemo(() => {
    if (!activeRoom) return [];
    const list = activeRoom.snagQuestions || [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (s) =>
        (s.question && s.question.toLowerCase().includes(q)) ||
        (s.category && s.category.toLowerCase().includes(q)) ||
        (s.stage && s.stage.toLowerCase().includes(q)) ||
        (s.roomName && s.roomName.toLowerCase().includes(q))
    );
  }, [activeRoom, searchQuery]);

  if (!unit) return null;

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center transition-all duration-200 ${
        isFullScreen ? "p-0" : "p-2 sm:p-4"
      }`}
      onClick={onClose}
    >
      <div
        className={`w-full flex flex-col shadow-2xl border overflow-hidden transition-all duration-200 animate-scaleIn ${
          isFullScreen
            ? "w-screen h-screen rounded-none border-none"
            : "w-[98vw] max-w-[1720px] h-[95vh] rounded-2xl"
        }`}
        style={{ background: cardColor, borderColor: `${EMERALD}44` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-3.5 sm:px-6 py-3 sm:py-4 flex items-center justify-between shrink-0 gap-3 sm:gap-4"
          style={{ background: `${EMERALD}12`, borderBottom: `1px solid ${EMERALD}33` }}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-xl sm:text-2xl font-black tracking-tight" style={{ color: EMERALD }}>
                {unit.flatNumber ? `Flat ${unit.flatNumber}` : `Unit ${unit.unitNo}`}
              </h3>
              {unit.flatNumber && unit.unitNo && String(unit.flatNumber) !== String(unit.unitNo) && (
                <span className="text-sm font-mono opacity-70 font-semibold">(Unit #{unit.unitNo})</span>
              )}
              <span className="text-base font-semibold opacity-70">
                — {unit.tower} • {unit.floor}
              </span>
              <span
                className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                style={{
                  background: unit.openSnags > 0 ? `${ROSE}18` : `${EMERALD}18`,
                  color: unit.openSnags > 0 ? ROSE : EMERALD,
                  border: `1px solid ${unit.openSnags > 0 ? ROSE : EMERALD}44`,
                }}
              >
                {unit.openSnags > 0 ? `⚠️ ${unit.openSnags} Open Snags` : "✅ Zero Open Snags"}
              </span>
            </div>
            <p className="text-xs sm:text-sm opacity-75 mt-0.5 font-medium truncate">
              Category: <span className="font-semibold">{unit.category}</span> • Stage: <span className="font-semibold">{unit.stage}</span>
              {unit.projectName ? ` • Project: ${unit.projectName}` : ""}
              {unit.lastActivity && unit.lastActivity !== "—"
                ? ` • Last Activity: ${unit.lastActivity}`
                : ""}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={async () => {
                try {
                  setModalDownloading(true);
                  await downloadUnitSnagReport(unit, roomData);
                } catch (err) {
                  console.error("Modal report download failed:", err);
                } finally {
                  setModalDownloading(false);
                }
              }}
              disabled={modalDownloading}
              className="px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-2xs hover:scale-105 disabled:opacity-50 text-white"
              style={{ background: EMERALD }}
              title="Download comprehensive PDF Inspection & Snag Report for this Unit"
            >
              {modalDownloading ? (
                <>
                  <span className="animate-spin text-xs">⏳</span>
                  <span className="hidden sm:inline">Generating PDF...</span>
                </>
              ) : (
                <>
                  <span className="text-sm">📥</span>
                  <span className="hidden sm:inline">Download Report</span>
                </>
              )}
            </button>
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="px-3.5 py-2 rounded-xl border text-xs font-bold flex items-center gap-1.5 transition-all opacity-85 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/5 shadow-2xs"
              style={{ borderColor: `${EMERALD}44`, color: textColor }}
              title={isFullScreen ? "Exit Fullscreen (Windowed View)" : "View Full Screen"}
            >
              <span className="text-sm">{isFullScreen ? "🗗" : "⛶"}</span>
              <span className="hidden sm:inline">{isFullScreen ? "Exit Fullscreen" : "Full Screen"}</span>
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-sm font-bold opacity-70 hover:opacity-100 transition-opacity"
              style={{ background: theme === "dark" ? "#2a2a33" : "#f1f5f9" }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Top KPIs & Overall Progress Strip */}
        <div
          className="px-3.5 sm:px-6 py-2.5 sm:py-3 shrink-0 border-b"
          style={{ borderColor: `${EMERALD}22`, background: theme === "dark" ? "#16161d" : "#fafafa" }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5 items-center">
            {/* 5 KPI Metric Cards */}
            <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-5 gap-2.5">
              <div
                className="p-3 rounded-xl border shadow-2xs flex flex-col justify-between"
                style={{ borderColor: `${cfg.color}44`, background: cfg.bg }}
              >
                <p className="text-[11px] font-bold uppercase tracking-wider opacity-70">Status</p>
                <p
                  className="text-sm font-black mt-1 flex items-center gap-1.5"
                  style={{ color: cfg.color }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 999,
                      background: cfg.color,
                      display: "inline-block",
                    }}
                  />
                  <span className="truncate">{cfg.label}</span>
                </p>
              </div>

              <div
                className="p-3 rounded-xl border shadow-2xs flex flex-col justify-between"
                style={{
                  borderColor: `${AMBER}44`,
                  background: theme === "dark" ? "#1c1c24" : "#f8fafc",
                }}
              >
                <p className="text-[11px] font-bold uppercase tracking-wider opacity-70">
                  Pending Role
                </p>
                <p className="text-sm font-black mt-1 truncate" style={{ color: AMBER }}>
                  {unit.pendingRole}
                </p>
              </div>

              <div
                className="p-3 rounded-xl border shadow-2xs flex flex-col justify-between"
                style={{ borderColor: `${AMBER}55`, background: `${AMBER}12` }}
              >
                <p className="text-[11px] font-bold uppercase tracking-wider opacity-75" style={{ color: AMBER }}>
                  Raised (Checker)
                </p>
                <p className="text-sm font-black mt-1 font-mono" style={{ color: AMBER }}>
                  🔍 {raisedCount.toLocaleString()}
                </p>
              </div>

              <div
                className="p-3 rounded-xl border shadow-2xs flex flex-col justify-between"
                style={{ borderColor: `${EMERALD}55`, background: `${EMERALD}12` }}
              >
                <p className="text-[11px] font-bold uppercase tracking-wider opacity-75" style={{ color: EMERALD }}>
                  Done (Maker)
                </p>
                <p className="text-sm font-black mt-1 font-mono" style={{ color: EMERALD }}>
                  🛠️ {doneCount.toLocaleString()}
                </p>
              </div>

              <div
                className="p-3 rounded-xl border shadow-2xs col-span-2 sm:col-span-1 flex flex-col justify-between"
                style={{
                  borderColor: unit.openSnags > 0 ? `${ROSE}55` : `${EMERALD}55`,
                  background: unit.openSnags > 0 ? `${ROSE}14` : `${EMERALD}14`,
                }}
              >
                <p
                  className="text-[11px] font-bold uppercase tracking-wider opacity-80"
                  style={{ color: unit.openSnags > 0 ? ROSE : EMERALD }}
                >
                  Open Snags
                </p>
                <p
                  className="text-sm font-black mt-1 font-mono"
                  style={{ color: unit.openSnags > 0 ? ROSE : EMERALD }}
                >
                  {unit.openSnags > 0 ? `⚠️ ${unit.openSnags}` : "✅ Zero"}
                </p>
              </div>
            </div>

            {/* Inspection Sign-off Progress Card */}
            <div
              className="lg:col-span-4 p-3 rounded-xl border shadow-2xs flex flex-col justify-between"
              style={{ borderColor: `${EMERALD}33`, background: cardColor }}
            >
              <div className="flex items-center justify-between text-xs font-bold mb-1">
                <span className="opacity-80">Checkpoints Sign-off</span>
                <span className="font-extrabold text-sm" style={{ color: EMERALD }}>
                  {hasQuestions ? `${completionPct}%` : "—"}
                </span>
              </div>
              <div
                className="w-full h-2.5 rounded-full overflow-hidden"
                style={{ background: theme === "dark" ? "#2a2a33" : "#e2e8f0" }}
              >
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${completionPct}%`, background: EMERALD }}
                />
              </div>
              <p className="text-[11px] opacity-70 mt-1 truncate">
                {hasQuestions
                  ? `${unit.completedQuestions} of ${unit.totalQuestions} checkpoints signed off.`
                  : "No checkpoints initialized."}
              </p>
            </div>
          </div>
        </div>

        {/* Main Body - Room Navigator & Snags Explorer */}
        <div className="px-3.5 sm:px-6 py-3 sm:py-4 flex-1 min-h-0 overflow-hidden flex flex-col">
          {roomLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 py-16 text-center opacity-75">
              <span className="animate-spin text-4xl">🔄</span>
              <span className="font-bold text-base">Fetching room-wise checklists and unresolved snags...</span>
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Mobile View Switcher (Rooms vs Snags) */}
              <div
                className="lg:hidden flex items-center p-1 rounded-xl mb-3 border shrink-0"
                style={{
                  background: theme === "dark" ? "#1e1e26" : "#f1f5f9",
                  borderColor: `${EMERALD}33`,
                }}
              >
                <button
                  type="button"
                  onClick={() => setMobileActiveView("rooms")}
                  className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    mobileActiveView === "rooms" ? "shadow-sm" : "opacity-60"
                  }`}
                  style={{
                    background: mobileActiveView === "rooms" ? EMERALD : "transparent",
                    color: mobileActiveView === "rooms" ? "#ffffff" : textColor,
                  }}
                >
                  <span>🏠</span>
                  <span>Rooms ({roomData?.length || 0})</span>
                  {roomsWithSnags.length > 0 && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                        mobileActiveView === "rooms"
                          ? "bg-white/25 text-white"
                          : "bg-rose-500/20 text-rose-500"
                      }`}
                    >
                      {roomsWithSnags.length}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setMobileActiveView("snags")}
                  className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    mobileActiveView === "snags" ? "shadow-sm" : "opacity-60"
                  }`}
                  style={{
                    background: mobileActiveView === "snags" ? EMERALD : "transparent",
                    color: mobileActiveView === "snags" ? "#ffffff" : textColor,
                  }}
                >
                  <span>🔍</span>
                  <span>Checkpoints &amp; Snags</span>
                  {activeRoom && (
                    <span className="text-[10px] opacity-80 truncate max-w-[90px]">
                      ({activeRoom.name})
                    </span>
                  )}
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 flex-1 min-h-0">
                {/* Left Column: Room Navigator */}
                <div
                  className={`lg:col-span-4 xl:col-span-3 flex-col min-h-0 rounded-2xl border p-4 shadow-sm ${
                    mobileActiveView === "rooms" ? "flex" : "hidden lg:flex"
                  }`}
                  style={{
                    borderColor: `${EMERALD}33`,
                    background: theme === "dark" ? "#181820" : "#ffffff",
                  }}
                >
                  <div className="pb-3 border-b mb-3" style={{ borderColor: `${EMERALD}22` }}>
                    <div className="flex items-center justify-between">
                      <h4
                        className="text-sm font-black uppercase tracking-wider flex items-center gap-2"
                        style={{ color: EMERALD }}
                      >
                        <span>🏠 Rooms in Unit</span>
                      </h4>
                      {roomData && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                            style={{
                              background: `${CYAN}18`,
                              color: CYAN,
                              border: `1px solid ${CYAN}33`,
                            }}
                          >
                            {roomData.length} Rooms
                          </span>
                          {roomsWithSnags.length > 0 && (
                            <span
                              className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                              style={{
                                background: `${ROSE}18`,
                                color: ROSE,
                                border: `1px solid ${ROSE}33`,
                              }}
                            >
                              ⚠️ {roomsWithSnags.length} with Snags
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs opacity-70 mt-1">
                      Select a room to inspect its checklist checkpoints &amp; snags:
                    </p>
                  </div>

                  {/* Rooms Scroll List */}
                  <div className="space-y-2 overflow-y-auto flex-1 pr-1.5 scrollbar-thin">
                    {/* All Rooms Combined */}
                    {allRoomsCombined && (
                      <button
                        key="all"
                        onClick={() => {
                          setActiveRoomId("all");
                          setExpandedChecklistKey(null);
                          setMobileActiveView("snags");
                        }}
                        className="w-full p-3 rounded-xl text-left border transition-all flex items-center justify-between gap-2.5"
                      style={{
                        background:
                          activeRoomId === "all"
                            ? `${EMERALD}22`
                            : theme === "dark"
                            ? "#22222d"
                            : "#f8fafc",
                        borderColor:
                          activeRoomId === "all"
                            ? EMERALD
                            : theme === "dark"
                            ? "#333344"
                            : "#e2e8f0",
                        color: activeRoomId === "all" ? EMERALD : textColor,
                        boxShadow:
                          activeRoomId === "all" ? "0 3px 12px rgba(0,0,0,0.1)" : undefined,
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-2xl shrink-0">🏢</span>
                        <div className="min-w-0">
                          <p className="font-extrabold text-sm truncate">All Rooms Combined</p>
                          <p className="text-xs opacity-70 font-normal">
                            All Checkpoints in Flat
                          </p>
                        </div>
                      </div>
                      <span
                        className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0"
                        style={{
                          background: unit.openSnags > 0 ? `${ROSE}20` : `${EMERALD}20`,
                          color: unit.openSnags > 0 ? ROSE : EMERALD,
                        }}
                      >
                        {unit.openSnags > 0 ? `⚠️ ${unit.openSnags}` : "✓ Clean"}
                      </span>
                    </button>
                  )}

                  {/* Individual Rooms */}
                  {roomData &&
                    roomData.map((room) => {
                      const isSelected = activeRoom && activeRoom.id === room.id;
                      const hasSnags = room.snagCount > 0;
                      const icon = getRoomIcon(room.name);
                      return (
                        <button
                          key={room.id}
                          onClick={() => {
                            setActiveRoomId(room.id);
                            setExpandedChecklistKey(null);
                            setMobileActiveView("snags");
                          }}
                          className="w-full p-3 rounded-xl text-left border transition-all flex items-center justify-between gap-2.5"
                          style={{
                            background: isSelected
                              ? hasSnags
                                ? `${ROSE}20`
                                : `${EMERALD}20`
                              : theme === "dark"
                              ? "#22222d"
                              : "#f8fafc",
                            borderColor: isSelected
                              ? hasSnags
                                ? ROSE
                                : EMERALD
                              : hasSnags
                              ? `${ROSE}44`
                              : `${EMERALD}33`,
                            color: isSelected ? (hasSnags ? ROSE : EMERALD) : textColor,
                            boxShadow:
                              isSelected ? "0 3px 12px rgba(0,0,0,0.1)" : undefined,
                          }}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-2xl shrink-0">{icon}</span>
                            <div className="min-w-0">
                              <p className="font-extrabold text-sm truncate">{room.name}</p>
                              <p className="text-xs opacity-70 font-normal truncate">
                                {room.checklists.length} Checklist{room.checklists.length === 1 ? "" : "s"} • {room.totalQuestions} items
                              </p>
                            </div>
                          </div>
                          <span
                            className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0 font-mono"
                            style={{
                              background: hasSnags ? `${ROSE}20` : `${EMERALD}20`,
                              color: hasSnags ? ROSE : EMERALD,
                            }}
                          >
                            {hasSnags ? `⚠️ ${room.snagCount}` : "✓ 0"}
                          </span>
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* Right Column: Active Room Detail & Snags */}
              <div
                className={`lg:col-span-8 xl:col-span-9 flex-col min-h-0 rounded-2xl border p-3.5 sm:p-5 shadow-sm ${
                  mobileActiveView === "snags" ? "flex" : "hidden lg:flex"
                }`}
                style={{
                  borderColor: activeRoom?.snagCount > 0 ? `${ROSE}44` : `${EMERALD}33`,
                  background: cardColor,
                }}
              >
                {activeRoom ? (
                  <>
                    {/* Active Room Header & Controls */}
                    <div
                      className="pb-3.5 border-b flex items-center justify-between flex-wrap gap-3 shrink-0"
                      style={{ borderColor: `${EMERALD}18` }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-3xl shrink-0">{getRoomIcon(activeRoom.name)}</span>
                        <div>
                          <div className="flex items-center gap-2.5 flex-wrap">
                            <h4 className="text-base sm:text-lg font-black tracking-tight">
                              {activeRoom.name}
                            </h4>
                            <span
                              className="text-xs px-2.5 py-1 rounded-full font-bold shadow-2xs"
                              style={{
                                background:
                                  activeRoom.snagCount > 0 ? `${ROSE}20` : `${EMERALD}20`,
                                color: activeRoom.snagCount > 0 ? ROSE : EMERALD,
                              }}
                            >
                              {activeRoom.snagCount > 0
                                ? `⚠️ ${activeRoom.snagCount} Open Snags`
                                : "✅ Verified Clean"}
                            </span>
                          </div>
                          <p className="text-xs opacity-70 mt-0.5">
                            {activeRoom.checklists.length} checklist category
                            {activeRoom.checklists.length === 1 ? "" : "s"} • {activeRoom.totalQuestions}{" "}
                            checkpoints ({activeRoom.completedCount} passed)
                          </p>
                        </div>
                      </div>

                      {/* Controls: Search + Tabs */}
                      <div className="flex items-center gap-2.5 flex-wrap w-full sm:w-auto">
                        <div className="relative w-full sm:w-auto">
                          <input
                            type="text"
                            placeholder="🔍 Filter checkpoints..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="px-3.5 py-2 text-sm rounded-xl border focus:outline-none w-full sm:w-64 shadow-2xs"
                            style={{
                              background: theme === "dark" ? "#1e1e26" : "#f8fafc",
                              borderColor: `${EMERALD}33`,
                              color: textColor,
                            }}
                          />
                          {searchQuery && (
                            <button
                              onClick={() => setSearchQuery("")}
                              className="absolute right-2.5 top-2.5 text-xs opacity-50 hover:opacity-100"
                            >
                              ✕
                            </button>
                          )}
                        </div>

                        {/* Tabs */}
                        <div
                          className="flex items-center gap-1 p-1 rounded-xl border text-sm"
                          style={{ borderColor: `${EMERALD}33` }}
                        >
                          <button
                            type="button"
                            onClick={() => setActiveTab("snags")}
                            className={`px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-extrabold transition-all flex items-center gap-1.5 ${
                              activeTab === "snags"
                                ? "bg-rose-600 text-white shadow-sm"
                                : "opacity-70 hover:opacity-100"
                            }`}
                          >
                            <span>⚠️ Open Snags</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/20 font-mono font-bold">
                              {activeRoom.snagCount}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveTab("checklists")}
                            className={`px-3.5 py-1.5 rounded-lg text-xs sm:text-sm font-extrabold transition-all flex items-center gap-1.5 ${
                              activeTab === "checklists"
                                ? "bg-emerald-600 text-white shadow-sm"
                                : "opacity-70 hover:opacity-100"
                            }`}
                          >
                            <span>📋 All Checklists</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/20 font-mono font-bold">
                              {activeRoom.checklists.length}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Active Tab Content Area */}
                    <div className="flex-1 min-h-0 overflow-y-auto pr-2 mt-3 scrollbar-thin">
                      {/* Tab 1: Snags in Room */}
                      {activeTab === "snags" && (
                        <div>
                          {filteredSnags.length > 0 ? (
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 pb-2">
                              {filteredSnags.map((sq, idx) => (
                                <div
                                  key={sq.id || idx}
                                  className="p-4 rounded-xl border text-sm flex flex-col justify-between gap-3 transition-all hover:shadow-md"
                                  style={{
                                    borderColor: `${ROSE}33`,
                                    background: theme === "dark" ? "#24181c" : "#fff1f2",
                                  }}
                                >
                                  <div className="space-y-1.5">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                      <span className="text-xs font-black px-2.5 py-1 rounded bg-rose-500/20 text-rose-600 dark:text-rose-400 uppercase tracking-wide">
                                        {sq.category || "Snag"}
                                      </span>
                                      <span className="text-xs font-extrabold text-rose-600 dark:text-rose-400 px-2.5 py-1 rounded-full bg-rose-500/10 border border-rose-500/25">
                                        ⚠️ Open Snag
                                      </span>
                                    </div>
                                    <p
                                      className="font-bold text-sm sm:text-base leading-relaxed"
                                      style={{ color: textColor }}
                                    >
                                      {sq.question}
                                    </p>
                                  </div>

                                  <div className="flex items-center gap-2 flex-wrap pt-2 border-t text-xs opacity-70" style={{ borderColor: `${ROSE}22` }}>
                                    {sq.roomName && (
                                      <span
                                        className="font-bold px-2 py-0.5 rounded"
                                        style={{ background: `${CYAN}18`, color: CYAN }}
                                      >
                                        📍 {sq.roomName}
                                      </span>
                                    )}
                                    {sq.subcategory && (
                                      <span className="font-semibold">
                                        {sq.subcategory}
                                      </span>
                                    )}
                                    {sq.stage && (
                                      <span className="opacity-80">
                                        ({sq.stage})
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : activeRoom.snagCount > 0 && searchQuery ? (
                            <div className="py-12 text-center text-sm opacity-70">
                              No snags matched &ldquo;{searchQuery}&rdquo;. Try another keyword.
                            </div>
                          ) : (
                            <div className="py-16 text-center text-sm opacity-90 flex flex-col items-center justify-center gap-3 text-emerald-600 dark:text-emerald-400 font-bold">
                              <span className="text-4xl">✅</span>
                              <span className="text-base">
                                Zero open snags in {activeRoom.name}! All checkpoints verified.
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tab 2: Checklists */}
                      {activeTab === "checklists" && (
                        <div className="space-y-3 pb-2">
                          {activeRoom.checklists.length === 0 ? (
                            <p className="text-sm opacity-70 py-8 text-center">
                              No checklist categories currently registered for {activeRoom.name}.
                            </p>
                          ) : (
                            activeRoom.checklists.map((chk) => {
                              const isExpanded = expandedChecklistKey === chk.key;
                              return (
                                <div
                                  key={chk.key}
                                  className="rounded-xl border transition-all overflow-hidden shadow-2xs"
                                  style={{
                                    borderColor: chk.snags > 0 ? `${ROSE}33` : `${EMERALD}33`,
                                    background: theme === "dark" ? "#1e1e26" : "#fcfcfd",
                                  }}
                                >
                                  <div
                                    onClick={() =>
                                      setExpandedChecklistKey(isExpanded ? null : chk.key)
                                    }
                                    className="p-4 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors flex items-center justify-between gap-3 flex-wrap"
                                  >
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2.5 flex-wrap">
                                        <span className="font-extrabold text-sm sm:text-base" style={{ color: EMERALD }}>
                                          {chk.name}
                                        </span>
                                        {chk.roomName && (
                                          <span
                                            className="text-xs font-bold px-2 py-0.5 rounded"
                                            style={{ background: `${CYAN}18`, color: CYAN }}
                                          >
                                            📍 {chk.roomName}
                                          </span>
                                        )}
                                        {chk.stage && (
                                          <span className="text-xs px-2 py-0.5 rounded font-semibold bg-slate-500/15 opacity-80">
                                            {chk.stage}
                                          </span>
                                        )}
                                        {chk.subCategory && (
                                          <span className="text-xs opacity-70 font-medium">
                                            • {chk.subCategory}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs sm:text-sm opacity-70">
                                        {chk.total} total checkpoints • {chk.completed} passed •{" "}
                                        {chk.snags} snags
                                      </p>
                                    </div>

                                    <div className="flex items-center gap-2.5">
                                      <div className="flex items-center gap-2 text-xs font-bold font-mono">
                                        <span className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                                          ✓ {chk.completed} Pass
                                        </span>
                                        {chk.snags > 0 && (
                                          <span className="px-2 py-1 rounded bg-rose-500/15 text-rose-500">
                                            ⚠️ {chk.snags} Snags
                                          </span>
                                        )}
                                        {chk.pending > 0 && (
                                          <span className="px-2 py-1 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400">
                                            ⏳ {chk.pending} Pending
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-sm opacity-60 ml-1">
                                        {isExpanded ? "▲" : "▼"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Expanded Questions */}
                                  {isExpanded && (
                                    <div
                                      className="p-4 border-t space-y-2 text-sm max-h-96 overflow-y-auto"
                                      style={{
                                        borderColor: `${EMERALD}22`,
                                        background: theme === "dark" ? "#181820" : "#f8fafc",
                                      }}
                                    >
                                      <p className="text-xs font-bold uppercase tracking-wider opacity-70 mb-2">
                                        Checkpoint Questions in {chk.name}:
                                      </p>
                                      {chk.questions.map((q, qIdx) => (
                                        <div
                                          key={q.id || qIdx}
                                          className="p-3 rounded-xl border flex items-center justify-between gap-3 shadow-2xs"
                                          style={{
                                            borderColor:
                                              q.status === "SNAG" ? `${ROSE}44` : `${EMERALD}22`,
                                            background: q.status === "SNAG" ? `${ROSE}0c` : cardColor,
                                          }}
                                        >
                                          <span className="font-semibold text-xs sm:text-sm leading-relaxed">
                                            {q.question}
                                          </span>
                                          <span
                                            className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0"
                                            style={{
                                              background:
                                                q.status === "SNAG"
                                                  ? `${ROSE}20`
                                                  : q.status === "PASSED"
                                                  ? `${EMERALD}20`
                                                  : "#f59e0b20",
                                              color:
                                                q.status === "SNAG"
                                                  ? ROSE
                                                  : q.status === "PASSED"
                                                  ? EMERALD
                                                  : AMBER,
                                            }}
                                          >
                                            {q.status === "SNAG"
                                              ? "⚠️ Snag"
                                              : q.status === "PASSED"
                                              ? "✓ Passed"
                                              : "⏳ Pending"}
                                          </span>
                                        </div>
                                      ))}
                                      {chk.questions.length === 0 && (
                                        <p className="text-xs opacity-60 py-3 text-center">
                                          No individual checkpoints found under this checklist.
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-sm opacity-60">
                    Select a room on the left to view checkpoints &amp; snags.
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-3.5 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2.5 sm:gap-3 shrink-0 border-t flex-wrap"
          style={{ borderColor: `${EMERALD}22` }}
        >
          <div className="flex items-center gap-2 text-xs opacity-75">
            <span>Viewing: <strong style={{ color: EMERALD }}>{activeRoom?.name || "Unit"}</strong></span>
            <span>•</span>
            <span>{activeRoom?.snagCount || 0} Snags in Room</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="px-4 py-2 rounded-xl text-xs font-bold border transition-all opacity-80 hover:opacity-100"
              style={{ borderColor: `${EMERALD}44` }}
            >
              {isFullScreen ? "🗗 Windowed" : "⛶ Fullscreen"}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-xl text-xs font-bold border opacity-85 hover:opacity-100 transition-all shadow-sm"
              style={{ borderColor: `${EMERALD}55` }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SnaggingDashboard({
  theme,
  cardColor,
  textColor,
  onExportContext,
  selectedProjectId: propProjectId,
  selectedProjectName: propProjectName,
  onSelectProject,
}) {
  // ── Project list ─────────────────────────────────────────────────────
  const [availableProjects, setAvailableProjects] = useState([]);

  const contextProjectId = useMemo(
    () => propProjectId || (propProjectName === "ALL" ? "" : getActiveDashboardProjectId()),
    [propProjectId, propProjectName]
  );
  const [selectedProjectId, setSelectedProjectId] = useState(
    propProjectId ? propProjectId : PROJECT_ALL
  );

  useEffect(() => {
    if (propProjectId) {
      setSelectedProjectId(propProjectId);
    } else {
      setSelectedProjectId(PROJECT_ALL);
    }
  }, [propProjectId]);

  useEffect(() => {
    let isMounted = true;
    async function loadSnaggingProjects() {
      try {
        const accessible = getAccessibleProjects();
        if (accessible.length > 0 && isMounted) {
          setAvailableProjects(accessible);
        }

        const res = await getProjectsForCurrentUser();
        const rows = res?.data?.projects || res?.data || [];
        if (Array.isArray(rows) && rows.length > 0 && isMounted) {
          const mapped = rows
            .map((p) => {
              const rawId = p?.id || p?.project_id || p?.uuid;
              if (rawId == null) return null;
              const strId = String(rawId).trim();
              if (strId.includes("/")) return null;
              const numId = Number(strId);
              if (isNaN(numId) || !Number.isInteger(numId) || numId <= 0) return null;

              const rawName = String(p?.name || p?.project_name || p?.title || "").trim();
              const isSynthetic = !rawName || /^project\s*#?\s*\d+$/i.test(rawName);
              return {
                id: String(numId),
                name: isSynthetic ? "Unnamed Project" : rawName,
              };
            })
            .filter(Boolean);

          if (mapped.length > 0 && isMounted) {
            setAvailableProjects((prev) => {
              const map = new Map(prev.map((item) => [String(item.id), item]));
              mapped.forEach((item) => map.set(String(item.id), item));
              return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
            });
          }
        }
      } catch (e) {
        console.warn("SnaggingDashboard: could not resolve projects", e);
      }
    }
    loadSnaggingProjects();
    return () => {
      isMounted = false;
    };
  }, []);



  const [liveRecords, setLiveRecords] = useState([]);
  const [isLoadingApi, setIsLoadingApi] = useState(false);
  const [summaryCounts, setSummaryCounts] = useState(null);
  const [loadErrors, setLoadErrors] = useState([]); // per-project failures, shown quietly

  const [filters, setFilters] = useState({
    tower: "",
    floor: "",
    stage: "",
    status: "",
    search: "",
  });

  const [selectedUnit, setSelectedUnit] = useState(null);

  // ── Fetch: single project OR aggregate across every accessible project ──
  useEffect(() => {
    let isMounted = true;

    async function loadForOneProject(project) {
      // Parallel fetch: summary counts, WIP breakdown, checklist report, snag stats, and project towers
      const [summaryRes, wipRes, reportRes, snagStatsRes, towersRes] = await Promise.allSettled([
        getUnitStageRoleSummary({ project_id: project.id }),
        getUnitWorkInProgressBreakdown({ project_id: project.id, include_rows: true, limit: 300 }),
        getUnitChecklistReport({ project_id: project.id }),
        getSnagStats(project.id),
        fetchTowersByProject(project.id),
      ]);

      const counts =
        summaryRes.status === "fulfilled" ? summaryRes.value?.data?.counts || null : null;

      // 1. Resolve flat, floor, and tower name mappings
      const flatMap = new Map();
      if (towersRes.status === "fulfilled") {
        const towers = normalizeList(towersRes.value?.data || towersRes.value);
        if (Array.isArray(towers) && towers.length > 0) {
          const levelsSettled = await Promise.allSettled(
            towers.map((t) => getLevelsWithFlatsByBuilding(t.id || t.building_id))
          );
          levelsSettled.forEach((ls, idx) => {
            if (ls.status === "fulfilled") {
              const towerName = towers[idx]?.name || towers[idx]?.building_name || `Tower ${towers[idx]?.id || ""}`;
              const levels = normalizeList(ls.value?.data || ls.value);
              if (Array.isArray(levels)) {
                levels.forEach((lvl) => {
                  const floorName = lvl?.name || `Floor ${lvl?.id || ""}`;
                  const flats = Array.isArray(lvl?.flats) ? lvl.flats : [];
                  flats.forEach((f) => {
                    const fid = String(f?.id ?? f?.flat_id ?? f?.pk ?? "");
                    if (fid) {
                      flatMap.set(fid, {
                        flatNumber: f?.number || f?.flat_number || f?.unit_no || f?.name || "",
                        floorName,
                        towerName,
                        flatType: f?.flattype?.type_name || f?.flat_type?.type_name || f?.flat_type_name || "Standard",
                      });
                    }
                  });
                });
              }
            }
          });
        }
      }

      // 2. Index report rows for open snags, last activity dates, and flat numbers
      const reportRowsByUnit = new Map();
      const addReportRow = (key, row) => {
        if (!key && key !== 0) return;
        const k = String(key).trim().toLowerCase();
        if (!k) return;
        if (!reportRowsByUnit.has(k)) reportRowsByUnit.set(k, []);
        reportRowsByUnit.get(k).push(row);
      };

      let reportRowsList = [];
      if (reportRes.status === "fulfilled") {
        const raw = reportRes.value?.data;
        const repData = Array.isArray(raw)
          ? raw
          : (Array.isArray(raw?.rows)
            ? raw.rows
            : (Array.isArray(raw?.data?.rows)
              ? raw.data.rows
              : (Array.isArray(raw?.data)
                ? raw.data
                : (Array.isArray(raw?.results)
                  ? raw.results
                  : []))));

        if (Array.isArray(repData) && repData.length > 0) {
          reportRowsList = repData;
          repData.forEach((r) => {
            if (!r || typeof r !== "object") return;
            const uid = r["Unit Id"] ?? r.unit_id ?? r.flat_id;
            const uno = r["Unit No"] ?? r.unit_no;
            const tower = r["Tower"] ?? r.tower;
            if (uid !== undefined && uid !== null) addReportRow(uid, r);
            if (uno !== undefined && uno !== null) addReportRow(uno, r);
            if (uno && tower) addReportRow(`${tower}-${uno}`, r);
          });
        }
      }

      // 3. Project-level snag counts from stats API
      let projectSnagsRaised = 0;
      let projectSnagsClosed = 0;
      let projectSnagsOpen = 0;
      let projectCheckerRaised = 0;
      let projectMakerCompleted = 0;
      if (snagStatsRes.status === "fulfilled") {
        const sd = snagStatsRes.value?.data;
        const statsObj = Array.isArray(sd) ? sd[0] : (Array.isArray(sd?.results) ? sd.results[0] : (Array.isArray(sd?.data) ? sd.data[0] : sd));
        if (statsObj) {
          projectSnagsRaised = Number(statsObj.snags_raised || 0);
          const closed = Number(statsObj.snags_status?.closed?.count || 0);
          projectSnagsClosed = closed;
          const openFromStatus = Number(statsObj.snags_status?.repair_in_progress?.count || 0)
            + Number(statsObj.snags_status?.repaired?.count || 0)
            + Number(statsObj.snags_status?.reviewed?.count || 0);
          projectSnagsOpen = openFromStatus > 0 ? openFromStatus : Math.max(0, projectSnagsRaised - closed);

          projectCheckerRaised = Number(statsObj.snags_raised || 0);
          projectMakerCompleted = Number(
            statsObj.kpis?.desnag_by_repairer_count ??
            statsObj.maker_work_completed ??
            (Number(statsObj.snags_status?.repaired?.count || 0) + Number(statsObj.snags_status?.closed?.count || 0))
          );
        }
      }

      // 4. Build unit rows from WIP breakdown + Report
      const rows = [];
      const seenUnitIds = new Set();
      const wipData =
        wipRes.status === "fulfilled" ? (wipRes.value?.data?.data || wipRes.value?.data || []) : [];
      const rowsArray = Array.isArray(wipData) ? wipData : wipData.rows || wipData.results || [];

      if (Array.isArray(rowsArray)) {
        rowsArray.forEach((row, idx) => {
          const uid = String(row.unit_id || row.id || "");
          if (uid) seenUnitIds.add(uid);

          const flatInfo = flatMap.get(uid);
          const flatNum = flatInfo?.flatNumber || row.flat_number || "";
          const unitNoStr = row.unit_no || (flatNum ? String(flatNum) : (row.unit_id ? String(row.unit_id) : `Unit ${idx + 1}`));

          // Gather all report rows matching this unit by uid, flatNumber, or unit_no:
          const candidateRepRows = [
            ...(reportRowsByUnit.get(String(uid).trim().toLowerCase()) || []),
            ...(flatNum ? reportRowsByUnit.get(String(flatNum).trim().toLowerCase()) || [] : []),
            ...(row.unit_no ? reportRowsByUnit.get(String(row.unit_no).trim().toLowerCase()) || [] : []),
          ];
          const matchedRepRows = Array.from(new Set(candidateRepRows));

          // Find primary repRow matching active stage, or first row
          const repRow = matchedRepRows.find((r) => {
            const rStage = String(r["Stage"] || r.stage || "").toLowerCase();
            const curStage = String(row.stage || "").toLowerCase();
            return rStage && curStage && (rStage.includes(curStage) || curStage.includes(rStage));
          }) || matchedRepRows[0];

          const totalQ = Number(row.total_items ?? row.total_questions ?? repRow?.["Total Checkpoint(excluding user genrated chechklist)"] ?? 0);
          const compQ = Number(row.completed ?? row.completed_questions ?? 0);
          const notStartedQ = Number(row.not_started ?? 0);

          // Status resolution
          const repStatus = (repRow?.["Status"] || "").toUpperCase();
          let computedStatus = "IN_PROGRESS";
          if (repStatus === "COMPLETED" || (totalQ > 0 && compQ >= totalQ)) {
            computedStatus = "COMPLETED";
          } else if (repStatus === "NOT STARTED" || (totalQ > 0 && notStartedQ === totalQ)) {
            computedStatus = "PENDING";
          } else if (row.checker_pending > 0 && row.maker_pending === 0 && row.supervisor_pending === 0 && notStartedQ === 0) {
            computedStatus = "YET_TO_VERIFY";
          } else {
            computedStatus = "IN_PROGRESS";
          }

          // Extract report figures accurately
          const parseRepVal = (v) => {
            if (v === undefined || v === null || v === "" || v === "-") return 0;
            const n = Number(v);
            return isNaN(n) ? 0 : n;
          };

          let repTotalSnags = 0;
          let repDeSnag = 0;
          let repPending = 0;

          if (matchedRepRows.length > 0) {
            matchedRepRows.forEach((r) => {
              const t = parseRepVal(r["Total Snag point"]);
              const d = parseRepVal(r["De snag by checker"]);
              const p = parseRepVal(r["Snag Pending"]);
              repTotalSnags = Math.max(repTotalSnags, t);
              repDeSnag = Math.max(repDeSnag, d);
              repPending = Math.max(repPending, p);
            });
          }

          // Total snags raised by QA/QC checker
          let rowRaisedByChecker = repTotalSnags || parseRepVal(row.snags_raised) || parseRepVal(row.total_snags) || 0;

          // Open unresolved snags
          let rowOpenSnags = 0;
          if (computedStatus === "COMPLETED") {
            rowOpenSnags = 0;
          } else if (repPending > 0) {
            rowOpenSnags = repPending;
          } else if (repTotalSnags > 0 && repDeSnag > 0) {
            rowOpenSnags = Math.max(0, repTotalSnags - repDeSnag);
          } else if (totalQ > 0 && compQ < totalQ) {
            const remPoints = totalQ - compQ;
            rowOpenSnags = rowRaisedByChecker > 0 ? Math.min(rowRaisedByChecker, remPoints) : remPoints;
          } else if (row.open_snags || row.snags_count) {
            rowOpenSnags = parseRepVal(row.open_snags) || parseRepVal(row.snags_count) || 0;
          }

          // If raised is 0 but there are open snags, raised must be at least open snags
          if (rowRaisedByChecker === 0 && rowOpenSnags > 0) {
            rowRaisedByChecker = rowOpenSnags;
          }
          if (rowRaisedByChecker > 0) {
            rowOpenSnags = Math.min(rowOpenSnags, rowRaisedByChecker);
          }

          // Completed by maker = de-snagged / rectified snags (NEVER exceed raisedByChecker)
          let rowCompletedByMaker = 0;
          if (computedStatus === "COMPLETED") {
            rowCompletedByMaker = rowRaisedByChecker;
          } else if (repDeSnag > 0) {
            rowCompletedByMaker = Math.min(repDeSnag, rowRaisedByChecker);
          } else if (rowRaisedByChecker > 0) {
            rowCompletedByMaker = Math.max(0, rowRaisedByChecker - rowOpenSnags);
          }
          rowCompletedByMaker = Math.min(rowCompletedByMaker, rowRaisedByChecker);

          // Last activity resolution: search candidate dates across all matching report rows
          let latestActivityDateStr = "";
          let latestActivityTimestamp = 0;

          matchedRepRows.forEach((r) => {
            const candidateDates = [
              r["End Date"] && r["End Date"] !== "Not Ended" ? r["End Date"] : null,
              r["Start Date"] || null,
              r.end_date && r.end_date !== "Not Ended" ? r.end_date : null,
              r.start_date || null,
              r.updated_at || null,
              r.created_at || null,
            ].filter(Boolean);

            candidateDates.forEach((dVal) => {
              try {
                const parsed = new Date(dVal);
                const ts = parsed.getTime();
                if (!isNaN(ts) && ts > latestActivityTimestamp) {
                  latestActivityTimestamp = ts;
                  latestActivityDateStr = dVal;
                }
              } catch {}
            });

            if (!latestActivityDateStr && r["No Of Days"] && !isNaN(Number(r["No Of Days"]))) {
              const daysAgo = Number(r["No Of Days"]);
              const estDate = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
              latestActivityDateStr = estDate.toISOString().slice(0, 10);
            }
          });

          // Also check row-level timestamp fields
          [
            row.updated_at,
            row.last_activity,
            row.last_updated,
            row.created_at,
            row.initialized_at,
          ].filter(Boolean).forEach((dVal) => {
            try {
              const parsed = new Date(dVal);
              const ts = parsed.getTime();
              if (!isNaN(ts) && ts > latestActivityTimestamp) {
                latestActivityTimestamp = ts;
                latestActivityDateStr = dVal;
              }
            } catch {}
          });

          rows.push({
            id: row.id || row.unit_id || `${project.id}-U-${idx + 1}`,
            unitId: uid || String(idx + 1),
            flatId: row.flat_id || row.flat || row.unit_id || uid,
            flatNumber: flatNum,
            unitNo: unitNoStr,
            tower: flatInfo?.towerName || repRow?.["Tower"] || (row.tower && !row.tower.startsWith("Tower#") ? row.tower : "") || (row.tower_id ? `Tower ${row.tower_id}` : `Tower A`),
            floor: flatInfo?.floorName || row.floor_name || row.floor || `Floor 1`,
            category: flatInfo?.flatType || row.flat_type || row.category || "Standard",
            stage: repRow?.["Stage"] || (row.stage && !row.stage.startsWith("Stage#") ? row.stage : "") || (row.stage_id ? `Stage ${row.stage_id}` : "Finishing & Painting"),
            status: computedStatus,
            pendingRole: row.pending_from || row.pending_role || (computedStatus === "COMPLETED" ? "None" : "Maker"),
            openSnags: rowOpenSnags,
            closedSnags: rowCompletedByMaker,
            raisedByChecker: rowRaisedByChecker,
            completedByMaker: rowCompletedByMaker,
            completedQuestions: compQ,
            totalQuestions: totalQ,
            lastActivity: formatActivityDate(latestActivityDateStr),
            projectId: project.id,
            projectName: project.name,
            rawRow: row,
            matchedRepRows: matchedRepRows,
          });
        });
      }

      // 5. Also incorporate any Completed / other units from reportRowsList that were not in WIP (since WIP excludes completed units)
      reportRowsList.forEach((r, idx) => {
        const uid = String(r["Unit Id"] || "");
        if (uid && !seenUnitIds.has(uid)) {
          const flatInfo = flatMap.get(uid);
          const totalPts = Number(r["Total Checkpoint(excluding user genrated chechklist)"]) || 0;
          const isCompleted = (r["Status"] || "").toLowerCase() === "completed";
          const parseRepVal = (v) => {
            if (v === undefined || v === null || v === "" || v === "-") return 0;
            const n = Number(v);
            return isNaN(n) ? 0 : n;
          };
          const repTotalSnags = parseRepVal(r["Total Snag point"]);
          const repDeSnag = parseRepVal(r["De snag by checker"]);
          const repPending = parseRepVal(r["Snag Pending"]);

          let rowRaisedByChecker = repTotalSnags;
          let rowOpenSnags = 0;
          let compPts = 0;

          if (isCompleted) {
            rowOpenSnags = 0;
            compPts = totalPts;
          } else if (repPending > 0) {
            rowOpenSnags = repPending;
            compPts = totalPts > 0 ? Math.max(0, totalPts - rowOpenSnags) : parseRepVal(r["Checkpoint Completed"]);
          } else if (repTotalSnags > 0 && repDeSnag > 0) {
            rowOpenSnags = Math.max(0, repTotalSnags - repDeSnag);
            compPts = totalPts > 0 ? Math.max(0, totalPts - rowOpenSnags) : parseRepVal(r["Checkpoint Completed"]);
          } else if (totalPts > 0) {
            compPts = parseRepVal(r["Checkpoint Completed"]);
            rowOpenSnags = Math.max(0, totalPts - compPts);
          }

          if (rowRaisedByChecker === 0 && rowOpenSnags > 0) {
            rowRaisedByChecker = rowOpenSnags;
          }
          if (rowRaisedByChecker > 0) {
            rowOpenSnags = Math.min(rowOpenSnags, rowRaisedByChecker);
          }

          let rowCompletedByMaker = 0;
          if (isCompleted) {
            rowCompletedByMaker = rowRaisedByChecker;
          } else if (repDeSnag > 0) {
            rowCompletedByMaker = Math.min(repDeSnag, rowRaisedByChecker);
          } else if (rowRaisedByChecker > 0) {
            rowCompletedByMaker = Math.max(0, rowRaisedByChecker - rowOpenSnags);
          }
          rowCompletedByMaker = Math.min(rowCompletedByMaker, rowRaisedByChecker);

          const actDate = (r["End Date"] && r["End Date"] !== "Not Ended")
            ? r["End Date"]
            : (r["Start Date"] || r.created_at || r.updated_at || "");

          const flatNum = flatInfo?.flatNumber || r["Unit No"] || "";
          const unitDisplay = r["Unit No"] || (flatNum ? String(flatNum) : uid);

          rows.push({
            id: `${project.id}-U-${uid}-${idx}`,
            unitId: uid,
            flatId: r["Unit Id"] || r.unit_id || r.flat_id || uid,
            flatNumber: flatNum,
            unitNo: unitDisplay,
            tower: flatInfo?.towerName || r["Tower"] || "Tower A",
            floor: flatInfo?.floorName || "Floor 1",
            category: flatInfo?.flatType || "Standard",
            stage: r["Stage"] || "Finishing & Painting",
            status: isCompleted ? "COMPLETED" : ((r["Status"] || "PENDING").toUpperCase().replace(/\s+/g, "_")),
            pendingRole: isCompleted ? "None" : (r["Pending With The Maker"] ? "MAKER" : (r["Pending With The Checker"] ? "CHECKER" : "None")),
            openSnags: rowOpenSnags,
            closedSnags: rowCompletedByMaker,
            raisedByChecker: rowRaisedByChecker,
            completedByMaker: rowCompletedByMaker,
            completedQuestions: compPts,
            totalQuestions: totalPts,
            lastActivity: formatActivityDate(actDate),
            projectId: project.id,
            projectName: project.name,
          });
          seenUnitIds.add(uid);
        }
      });

      const sumCheckerFromRows = rows.reduce((acc, r) => acc + (Number(r.raisedByChecker) || 0), 0);
      const sumMakerFromRows = rows.reduce((acc, r) => acc + (Number(r.completedByMaker) || 0), 0);

      return {
        ok: summaryRes.status === "fulfilled" || wipRes.status === "fulfilled" || reportRes.status === "fulfilled",
        project,
        counts,
        rows,
        projectOpenSnags: projectSnagsOpen,
        projectCompletedSnags: projectSnagsClosed,
        projectCheckerRaised: projectCheckerRaised || sumCheckerFromRows,
        projectMakerCompleted: projectMakerCompleted || sumMakerFromRows,
      };
    }

    async function loadRealSnaggingData() {
      // Reset row-level filters whenever the scope (project) changes
      setFilters({ tower: "", floor: "", stage: "", status: "", search: "" });

      if (selectedProjectId === PROJECT_ALL) {
        if (!availableProjects.length) {
          setLiveRecords([]);
          setSummaryCounts(null);
          return;
        }

        setIsLoadingApi(true);
        try {
          const results = await Promise.all(availableProjects.map(loadForOneProject));
          if (!isMounted) return;

          const failed = results.filter((r) => !r.ok).map((r) => r.project.name);
          setLoadErrors(failed);

          // Sum counts across every project
          const summed = {
            total_units: 0,
            complete: 0,
            work_in_progress_unit: 0,
            pending_yet_to_start: 0,
            yet_to_verify: 0,
            open_snags: 0,
            closed_snags: 0,
            raised_by_checker: 0,
            completed_by_maker: 0,
          };
          const allRows = [];

          results.forEach(({ counts, rows, projectOpenSnags, projectCompletedSnags, projectCheckerRaised, projectMakerCompleted }) => {
            if (counts) {
              summed.total_units += Number(counts.total_units || 0);
              summed.complete += Number(counts.complete || 0);
              summed.work_in_progress_unit += Number(counts.work_in_progress_unit || 0);
              summed.pending_yet_to_start += Number(counts.pending_yet_to_start || 0);
              summed.yet_to_verify += Number(counts.yet_to_verify || 0);
            }
            summed.open_snags += Number(projectOpenSnags || 0);
            summed.closed_snags += Number(projectCompletedSnags || 0);
            summed.raised_by_checker += Number(projectCheckerRaised || 0);
            summed.completed_by_maker += Number(projectMakerCompleted || 0);
            allRows.push(...rows);
          });

          // Units completed from rows (cannot exceed total units)
          const completedFromRows = allRows.filter((r) => r.status === "COMPLETED").length;
          summed.complete = Math.min(summed.total_units, Math.max(summed.complete, completedFromRows));

          if (!summed.closed_snags) {
            summed.closed_snags = allRows.reduce((acc, r) => acc + (Number(r.closedSnags ?? r.completedByMaker) || 0), 0);
          }
          if (!summed.raised_by_checker) {
            summed.raised_by_checker = allRows.reduce((acc, r) => acc + (Number(r.raisedByChecker) || 0), 0);
          }
          if (!summed.completed_by_maker) {
            summed.completed_by_maker = allRows.reduce((acc, r) => acc + (Number(r.completedByMaker) || 0), 0);
          }

          setSummaryCounts(summed);
          setLiveRecords(sortSnaggingRecords(allRows));
        } catch (err) {
          console.warn("Failed to aggregate snagging data across projects:", err);
          if (isMounted) {
            setLiveRecords([]);
            setSummaryCounts(null);
          }
        } finally {
          if (isMounted) setIsLoadingApi(false);
        }
        return;
      }

      // ── Single-project mode ──────
      const project =
        availableProjects.find((p) => String(p.id) === String(selectedProjectId)) ||
        (selectedProjectId ? { id: String(selectedProjectId), name: getActiveDashboardProjectName(selectedProjectId) || "Unnamed Project" } : null);

      if (!project) {
        setLiveRecords([]);
        setSummaryCounts(null);
        return;
      }

      setIsLoadingApi(true);
      try {
        const { counts, rows, projectOpenSnags, projectCompletedSnags, projectCheckerRaised, projectMakerCompleted } = await loadForOneProject(project);
        if (!isMounted) return;

        const completedFromRows = rows.filter((r) => r.status === "COMPLETED").length;
        const totalUnitsCount = Number(counts?.total_units || rows.length);
        const rowsClosed = rows.reduce((acc, r) => acc + (Number(r.closedSnags ?? r.completedByMaker) || 0), 0);
        const rowsRaised = rows.reduce((acc, r) => acc + (Number(r.raisedByChecker) || 0), 0);
        const rowsDone = rows.reduce((acc, r) => acc + (Number(r.completedByMaker) || 0), 0);
        const mergedCounts = {
          ...counts,
          total_units: totalUnitsCount,
          complete: Math.min(totalUnitsCount, Math.max(Number(counts?.complete || 0), completedFromRows)),
          open_snags: Number(projectOpenSnags || 0),
          closed_snags: Number(projectCompletedSnags || rowsClosed || 0),
          raised_by_checker: Number(projectCheckerRaised || rowsRaised || 0),
          completed_by_maker: Number(projectMakerCompleted || rowsDone || 0),
        };

        setSummaryCounts(mergedCounts);
        setLiveRecords(sortSnaggingRecords(rows));
        setLoadErrors([]);
      } catch (err) {
        console.warn("No snagging data found for project:", err);
        if (isMounted) {
          setLiveRecords([]);
          setSummaryCounts(null);
        }
      } finally {
        if (isMounted) setIsLoadingApi(false);
      }
    }

    loadRealSnaggingData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, availableProjects]);

  // Filtered dataset (row-level filters) sorted in natural ascending order
  const filteredRecords = useMemo(() => {
    const matched = liveRecords.filter((r) => {
      if (filters.tower && r.tower !== filters.tower) return false;
      if (filters.floor && r.floor !== filters.floor) return false;
      if (filters.stage && r.stage !== filters.stage) return false;
      if (filters.status && r.status !== filters.status) return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        return (
          r.unitNo.toLowerCase().includes(q) ||
          (r.flatNumber || "").toLowerCase().includes(q) ||
          r.tower.toLowerCase().includes(q) ||
          r.floor.toLowerCase().includes(q) ||
          r.stage.toLowerCase().includes(q) ||
          (r.projectName || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
    return sortSnaggingRecords(matched);
  }, [liveRecords, filters]);

  // Drill-down system for Snagging
  const [drillStack, setDrillStack] = useState([]);
  const openDrill = (kind, key, label) => setDrillStack([buildSnaggingFrame(kind, key, label, filteredRecords)]);
  const pushDrill = (frame) => setDrillStack((s) => [...s, frame]);
  const popDrill = () => setDrillStack((s) => s.slice(0, -1));
  const closeDrill = () => setDrillStack([]);
  const openUnitDrill = (unit) => pushDrill({ kind: "unit", key: unit.id || unit.unitId, title: unit.unitNo ? `Unit ${unit.unitNo}` : `Unit #${unit.unitId}`, records: [unit] });

  // KPIs — reactive to the active filter set.
  const hasActiveFilter = !!(filters.tower || filters.floor || filters.stage || filters.status || filters.search);

  const totalUnits = hasActiveFilter
    ? filteredRecords.length
    : Number(summaryCounts?.total_units ?? filteredRecords.length);

  const completedUnits = hasActiveFilter
    ? filteredRecords.filter((r) => r.status === "COMPLETED").length
    : Math.min(
        totalUnits,
        Math.max(
          Number(summaryCounts?.complete || 0),
          filteredRecords.filter((r) => r.status === "COMPLETED").length
        )
      );

  const inProgressUnits = hasActiveFilter
    ? filteredRecords.filter((r) => r.status === "IN_PROGRESS").length
    : Number(summaryCounts?.work_in_progress_unit ?? filteredRecords.filter((r) => r.status === "IN_PROGRESS").length);

  const pendingUnits = hasActiveFilter
    ? filteredRecords.filter((r) => r.status === "PENDING").length
    : Number(summaryCounts?.pending_yet_to_start ?? filteredRecords.filter((r) => r.status === "PENDING").length);

  const yetToVerify = hasActiveFilter
    ? filteredRecords.filter((r) => r.status === "YET_TO_VERIFY").length
    : Number(summaryCounts?.yet_to_verify ?? filteredRecords.filter((r) => r.status === "YET_TO_VERIFY").length);

  const rowsOpenSnags = filteredRecords.reduce((acc, r) => acc + (Number(r.openSnags) || 0), 0);
  const totalOpenSnags = hasActiveFilter || rowsOpenSnags > 0
    ? rowsOpenSnags
    : Number(summaryCounts?.open_snags || 0);

  const rowsClosedSnags = filteredRecords.reduce((acc, r) => acc + (Number(r.closedSnags ?? r.completedByMaker) || 0), 0);
  const totalClosedSnags = hasActiveFilter || rowsClosedSnags > 0
    ? rowsClosedSnags
    : Number(summaryCounts?.closed_snags || 0);

  const rowsRaisedByChecker = filteredRecords.reduce((acc, r) => acc + (Number(r.raisedByChecker) || 0), 0);
  const totalRaisedByChecker = hasActiveFilter || rowsRaisedByChecker > 0
    ? rowsRaisedByChecker
    : Number(summaryCounts?.raised_by_checker || rowsRaisedByChecker || (totalOpenSnags + totalClosedSnags));

  const rowsCompletedByMaker = filteredRecords.reduce((acc, r) => acc + (Number(r.completedByMaker) || 0), 0);
  const totalCompletedByMaker = hasActiveFilter || rowsCompletedByMaker > 0
    ? rowsCompletedByMaker
    : Number(summaryCounts?.completed_by_maker || rowsCompletedByMaker || totalClosedSnags);

  const kpis = [
    { label: "Total Units", value: totalUnits, color: "#06b6d4", icon: "🏢", unit: "units" },
    { label: "Completed", value: completedUnits, color: "#10b981", icon: "✅", unit: "units" },
    { label: "In Progress", value: inProgressUnits, color: "#f59e0b", icon: "⚡", unit: "units" },
    { label: "Pending (Yet to Start)", value: pendingUnits, color: "#ef4444", icon: "⏳", unit: "units" },
    { label: "Yet to Verify", value: yetToVerify, color: "#8b5cf6", icon: "🔍", unit: "units" },
    { label: "Open Snags (Question)", value: totalOpenSnags, color: "#f43f5e", icon: "⚠️", unit: "units" },
    { label: "Close Snags (Question)", value: totalClosedSnags, color: "#14b8a6", icon: "✔️", unit: "units" },
  ];

  useEffect(() => {
    if (onExportContext) {
      const selectedProj = availableProjects.find((p) => String(p.id) === String(selectedProjectId));
      onExportContext({
        selectedProjectId,
        selectedProjectName: selectedProj?.name || (selectedProjectId !== PROJECT_ALL ? (getActiveDashboardProjectName(selectedProjectId) || "Unnamed Project") : "All Accessible Projects"),
        availableProjects,
        summaryCounts,
        liveRecords,
        filteredRecords,
        totalUnits,
        completedUnits,
        inProgressUnits,
        pendingUnits,
        yetToVerify,
        totalOpenSnags,
        totalClosedSnags,
        totalRaisedByChecker,
        totalCompletedByMaker,
      });
    }
  }, [
    selectedProjectId,
    availableProjects,
    summaryCounts,
    liveRecords,
    filteredRecords,
    totalUnits,
    completedUnits,
    inProgressUnits,
    pendingUnits,
    yetToVerify,
    totalOpenSnags,
    totalClosedSnags,
    totalRaisedByChecker,
    totalCompletedByMaker,
    onExportContext,
  ]);

  const handleFilterChange = (key, val) => setFilters((f) => ({ ...f, [key]: val }));
  const handleClearFilters = () => setFilters({ tower: "", floor: "", stage: "", status: "", search: "" });

  const scopeLabel =
    selectedProjectId === PROJECT_ALL
      ? `All Projects (${availableProjects.length})`
      : availableProjects.find((p) => String(p.id) === String(selectedProjectId))?.name || "Unnamed Project";

  return (
    <div className="space-y-6">
      {/* Visual Charts & Analytics Section Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-b pb-2" style={{ borderColor: `${EMERALD}33` }}>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-6 rounded-full" style={{ background: EMERALD }} />
          <h2 className="text-base font-bold uppercase tracking-wider" style={{ color: EMERALD }}>
            Snagging &amp; Unit Construction Progress Charts
          </h2>
        </div>
        {isLoadingApi && (
          <span className="text-xs font-semibold px-2 py-1 rounded bg-emerald-500/15 text-emerald-600 animate-pulse">
            🔄 {selectedProjectId === PROJECT_ALL ? "Aggregating all projects…" : "Updating live API data..."}
          </span>
        )}
      </div>

      {/* ✅ NEW: Project scope selector — defaults to All Projects */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Project Scope</label>
          <select
            className="text-xs rounded px-2.5 py-2 font-medium"
            style={{
              background: theme === "dark" ? "#1b1b22" : "#fcfaf7",
              color: textColor,
              border: `1px solid ${EMERALD}88`,
            }}
            value={selectedProjectId}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedProjectId(val);
              if (onSelectProject) {
                onSelectProject(val === PROJECT_ALL ? "" : val, null);
              }
            }}
          >
            <option value={PROJECT_ALL}>All Projects ({availableProjects.length})</option>
            {availableProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || "Unnamed Project"}
              </option>
            ))}
          </select>
        </div>
        <span className="text-[11px] opacity-60 mt-4">Showing: {scopeLabel}</span>
        {loadErrors.length > 0 && (
          <span className="text-[11px] mt-4 text-amber-500 font-semibold">
            ⚠ Couldn't load: {loadErrors.join(", ")}
          </span>
        )}
      </div>

      {/* 7 Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-2.5 sm:gap-3">
        {kpis.map((kpi, idx) => (
          <div
            key={kpi.label}
            className={`rounded-xl p-3 sm:p-3.5 shadow-sm border transition-all hover:scale-[1.02] ${idx === kpis.length - 1 ? "col-span-2 sm:col-span-1" : ""}`}
            style={{ background: cardColor, borderColor: `${kpi.color}55` }}
          >
            <div className="flex items-center justify-between opacity-70 mb-1">
              <span className="text-[10px] sm:text-[11px] font-bold uppercase truncate" title={kpi.label}>{kpi.label}</span>
              <span className="text-xs shrink-0 ml-1">{kpi.icon}</span>
            </div>
            <p className="text-xl sm:text-2xl font-extrabold tracking-tight flex items-baseline gap-1.5" style={{ color: kpi.color }}>
              <CountUpNumber value={kpi.value} color={kpi.color} triggerKey={`snag_${kpi.label}_${kpi.value}`} />
              <span className="text-xs sm:text-sm font-semibold opacity-75 lowercase tracking-normal">
                {kpi.unit || "units"}
              </span>
            </p>
          </div>
        ))}
      </div>

      {/* Checker vs Maker Action Metric Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div
          role="button"
          tabIndex={0}
          onClick={() => openDrill("checker", "ALL", "Checker Inspection: Units with Raised Snags")}
          className="rounded-xl p-4 shadow-sm border flex items-center justify-between transition-all hover:scale-[1.01] hover:shadow-md cursor-pointer group"
          style={{
            background: cardColor,
            borderColor: `${AMBER}55`,
            borderLeft: `5px solid ${AMBER}`,
          }}
        >
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[11px] font-extrabold uppercase tracking-wider group-hover:underline" style={{ color: AMBER }}>
                🔍 Raised by Checker
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400">
                Inspection Snags
              </span>
              <span className="text-[10px] opacity-60 font-semibold" style={{ color: AMBER }}>
                Click to drill down →
              </span>
            </div>
            <p className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-baseline gap-1.5" style={{ color: AMBER }}>
              <CountUpNumber value={totalRaisedByChecker} color={AMBER} triggerKey={`snag_checker_${totalRaisedByChecker}`} />
              <span className="text-xs sm:text-sm font-semibold opacity-75 lowercase tracking-normal">snags</span>
            </p>
            <p className="text-[11px] opacity-60 mt-1">
              Total snag points identified &amp; logged by QA/QC checkers across units
            </p>
          </div>
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 group-hover:scale-110 transition-transform"
            style={{ background: `${AMBER}18` }}
          >
            📋
          </div>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => openDrill("maker", "ALL", "Maker Rectification: Units De-snagged / Completed")}
          className="rounded-xl p-4 shadow-sm border flex items-center justify-between transition-all hover:scale-[1.01] hover:shadow-md cursor-pointer group"
          style={{
            background: cardColor,
            borderColor: `${EMERALD}55`,
            borderLeft: `5px solid ${EMERALD}`,
          }}
        >
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[11px] font-extrabold uppercase tracking-wider group-hover:underline" style={{ color: EMERALD }}>
                🛠️ Completed by Maker
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                De-snagged &amp; Repaired
              </span>
              <span className="text-[10px] opacity-60 font-semibold" style={{ color: EMERALD }}>
                Click to drill down →
              </span>
            </div>
            <p className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-baseline gap-1.5" style={{ color: EMERALD }}>
              <CountUpNumber value={totalCompletedByMaker} color={EMERALD} triggerKey={`snag_maker_${totalCompletedByMaker}`} />
              <span className="text-xs sm:text-sm font-semibold opacity-75 lowercase tracking-normal">snags</span>
            </p>
            <p className="text-[11px] opacity-60 mt-1">
              Total snag points rectified &amp; marked complete by site maker/contractors
            </p>
          </div>
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 group-hover:scale-110 transition-transform"
            style={{ background: `${EMERALD}18` }}
          >
            ✅
          </div>
        </div>
      </div>

      {/* Conditional: If 0 units exist, display clean empty state; otherwise render real charts */}
      {filteredRecords.length === 0 && !isLoadingApi ? (
        <div className="rounded-2xl p-12 text-center border shadow-sm" style={{ background: cardColor, borderColor: `${EMERALD}33` }}>
          <div className="text-4xl mb-3">🏢</div>
          <h3 className="text-base font-bold mb-1" style={{ color: EMERALD }}>
            No Snagging or Unit Data Found
          </h3>
          <p className="text-xs opacity-60 max-w-md mx-auto">
            There are currently 0 units or inspection records for the selected scope. Real charts will appear as soon as units and checklists are initialized.
          </p>
        </div>
      ) : (
        <>
          {/* 6 Dynamic Rich Visual Charts (100% Real API Data) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Row 1: Status Distribution & Stage-wise Breakdown */}
            <div data-pdf-chart="true" className="rounded-xl p-4 shadow-sm border" style={{ background: cardColor, borderColor: `${EMERALD}55` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold uppercase tracking-wide opacity-70">Unit Execution Status Distribution</p>
                {filters.status && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 font-bold">Filtered</span>}
              </div>
              <p className="text-[11px] opacity-50 mb-2">Click any pie slice to filter by execution status.</p>
              <SnaggingStatusChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onSelectStatus={(s) => handleFilterChange("status", s === filters.status ? "" : s)} onDrill={openDrill} />
            </div>

            <div data-pdf-chart="true" className="rounded-xl p-4 shadow-sm border" style={{ background: cardColor, borderColor: `${EMERALD}55` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold uppercase tracking-wide opacity-70">Stage-wise Unit Progress Breakdown</p>
                {filters.stage && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-500 font-bold">Filtered</span>}
              </div>
              <p className="text-[11px] opacity-50 mb-2">Completed vs WIP vs Pending units across each construction phase.</p>
              <SnaggingStageChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onDrill={openDrill} />
            </div>

            {/* Row 2: Checker Raised Snags & Maker Completed De-snagging */}
            <div data-pdf-chart="true" className="rounded-xl p-4 shadow-sm border" style={{ background: cardColor, borderColor: `${AMBER}55` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold uppercase tracking-wide opacity-90 flex items-center gap-1.5" style={{ color: AMBER }}>
                  <span>🔍</span>
                  <span>Raised by Checker: Inspection Snag Analysis</span>
                </p>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400 font-bold">
                  Open vs Resolved
                </span>
              </div>
              <p className="text-[11px] opacity-50 mb-2">Snag points identified by QA/QC checkers. Toggle stage/tower &amp; click any bar to drill down into matching units.</p>
              <SnaggingCheckerChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onDrill={openDrill} />
            </div>

            <div data-pdf-chart="true" className="rounded-xl p-4 shadow-sm border" style={{ background: cardColor, borderColor: `${EMERALD}55` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold uppercase tracking-wide opacity-90 flex items-center gap-1.5" style={{ color: EMERALD }}>
                  <span>🛠️</span>
                  <span>Completed by Maker: De-snagging &amp; Rectification</span>
                </p>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold">
                  Completed vs Pending
                </span>
              </div>
              <p className="text-[11px] opacity-50 mb-2">Snags repaired &amp; resolved by site maker/contractors. Toggle stage/tower &amp; click any bar to drill down into matching units.</p>
              <SnaggingMakerChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onDrill={openDrill} />
            </div>

            {/* Row 3: Units by Tower & Units by Floor */}
            <div data-pdf-chart="true" className="rounded-xl p-4 shadow-sm border" style={{ background: cardColor, borderColor: `${CYAN}55` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold uppercase tracking-wide opacity-70">Unit Progress by Tower</p>
                {filters.tower && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-500 font-bold">Filtered</span>}
              </div>
              <p className="text-[11px] opacity-50 mb-2">Completed and active units per tower. Click a bar to filter by tower.</p>
              <SnaggingTowerChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onSelectTower={(t) => handleFilterChange("tower", t === filters.tower ? "" : t)} onDrill={openDrill} />
            </div>

            <div data-pdf-chart="true" className="rounded-xl p-4 shadow-sm border" style={{ background: cardColor, borderColor: `${PURPLE}55` }}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-bold uppercase tracking-wide opacity-70">Unit &amp; Open Snag Distribution by Floor</p>
                {filters.floor && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-500 font-bold">Filtered</span>}
              </div>
              <p className="text-[11px] opacity-50 mb-2">Total unit count and unresolved open snags per level.</p>
              <SnaggingFloorChart records={filteredRecords} cardColor={cardColor} textColor={textColor} onSelectFloor={(f) => handleFilterChange("floor", f === filters.floor ? "" : f)} onDrill={openDrill} />
            </div>
          </div>

          {/* Detailed Units Records Table — paginated, 15 rows/page (mirrors MIR/NCR/QC) */}
          <SnaggingTable
            records={filteredRecords}
            cardColor={cardColor}
            textColor={textColor}
            theme={theme}
            onOpenUnit={setSelectedUnit}
          />
        </>
      )}

      {/* Unit Detail Modal with Room-Wise Snag Breakdown */}
      {selectedUnit && (
        <SnaggingUnitDetailModal
          unit={selectedUnit}
          theme={theme}
          cardColor={cardColor}
          textColor={textColor}
          onClose={() => setSelectedUnit(null)}
        />
      )}

      {/* Snagging Drill-down Drawer */}
      <SnaggingDrillDrawer
        stack={drillStack}
        onBack={popDrill}
        onClose={closeDrill}
        onPush={pushDrill}
        onOpenUnit={openUnitDrill}
        onSlice={(idx) => setDrillStack((s) => s.slice(0, idx + 1))}
        cardColor={cardColor}
        textColor={textColor}
        theme={theme}
        allRecords={filteredRecords}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main dashboard                                                      */
/* ------------------------------------------------------------------ */
function ProjectHeadDashboard() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const cardColor = theme === "dark" ? "#23232c" : "#fff";
  const textColor = theme === "dark" ? "#fff" : "#222";

  // Initial state resolution: ONLY use project if explicitly passed in location.state or URL query param.
  // Main dashboard navigation or direct landing ALWAYS defaults to "" (All Projects).
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    if (location.state?.project?.id) return String(location.state.project.id);
    if (location.state?.resetToAll) return "";
    const qp = new URLSearchParams(window.location.search).get("project_id");
    if (qp && Number(qp)) return String(qp);
    return "";
  });

  const [selectedProjectNameState, setSelectedProjectNameState] = useState(() => {
    if (location.state?.project?.name) return location.state.project.name;
    if (location.state?.resetToAll) return "";
    const qp = new URLSearchParams(window.location.search).get("project_id");
    if (qp && Number(qp)) return getActiveDashboardProjectName(String(qp)) || "";
    return "";
  });

  const [availableProjects, setAvailableProjects] = useState(() => getAccessibleProjects());

  // Load user projects dynamically via API to guarantee complete accessible project options
  useEffect(() => {
    let isMounted = true;
    async function loadProjects() {
      try {
        const res = await getProjectsForCurrentUser();
        const rows = res?.data?.projects || res?.data || [];
        if (Array.isArray(rows) && rows.length > 0 && isMounted) {
          const mapped = rows
            .map((p) => {
              const rawId = p?.id || p?.project_id || p?.uuid;
              if (rawId == null) return null;
              const strId = String(rawId).trim();
              if (strId.includes("/")) return null;
              const numId = Number(strId);
              if (isNaN(numId) || !Number.isInteger(numId) || numId <= 0) return null;

              const rawName = String(p?.name || p?.project_name || p?.title || "").trim();
              const isSynthetic = !rawName || /^project\s*#?\s*\d+$/i.test(rawName);
              return {
                id: String(numId),
                name: isSynthetic ? "Unnamed Project" : rawName,
              };
            })
            .filter(Boolean);
          if (mapped.length > 0) {
            setAvailableProjects((prev) => {
              const map = new Map(prev.map((item) => [String(item.id), item]));
              mapped.forEach((item) => map.set(String(item.id), item));
              return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
            });
          }
        }
      } catch (err) {
        console.warn("ProjectHeadDashboard: could not fetch projects for user", err);
      }
    }
    loadProjects();
    return () => {
      isMounted = false;
    };
  }, []);

  const selectedProjectName = useMemo(() => {
    if (!selectedProjectId && !selectedProjectNameState) return "ALL";
    if (selectedProjectNameState && selectedProjectNameState !== "ALL") return selectedProjectNameState;
    if (selectedProjectId) {
      const found = availableProjects.find((p) => String(p.id) === String(selectedProjectId));
      if (found?.name) return found.name;
      return getActiveDashboardProjectName(selectedProjectId) || "";
    }
    return "ALL";
  }, [selectedProjectId, selectedProjectNameState, availableProjects]);

  const handleProjectSelect = useCallback(
    (newId, newName) => {
      const cleanName = !newName || newName === "ALL" ? "" : newName;
      let resolvedId = newId ? String(newId) : "";
      let resolvedName = cleanName;

      if (!resolvedId && cleanName) {
        const found = availableProjects.find(
          (p) =>
            normalizeProjectName(p.name) === normalizeProjectName(cleanName) ||
            p.name.trim().toLowerCase() === cleanName.trim().toLowerCase()
        );
        if (found) {
          resolvedId = String(found.id);
          resolvedName = found.name;
        }
      } else if (resolvedId && !cleanName) {
        const found = availableProjects.find((p) => String(p.id) === String(resolvedId));
        if (found) {
          resolvedName = found.name;
        }
      }

      if (resolvedId) {
        localStorage.setItem("ACTIVE_PROJECT_ID", resolvedId);
        localStorage.setItem("SELECTED_PROJECT_ID", resolvedId);
      } else {
        const projectKeys = [
          "ACTIVE_PROJECT_ID",
          "SELECTED_PROJECT_ID",
          "PROJECT_ID",
          "project_id",
          "active_project_id",
          "SELECTED_PROJECT",
          "selected_project",
        ];
        projectKeys.forEach((key) => {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });
      }

      setSelectedProjectId(resolvedId);
      setSelectedProjectNameState(resolvedName);
    },
    [availableProjects]
  );

  // Ref tracking export-relevant context (selected filters, records, project) from active child dashboards
  const childExportContextRef = useRef({});
  const setChildExportContext = useCallback((key, ctx) => {
    childExportContextRef.current[key] = ctx;
  }, []);

  // Handler to completely reset dashboard state to "All Projects"
  const handleResetToAll = useCallback(() => {
    // 1. Immediately display loading state so previous project numbers do not linger
    setOverviewStats({
      mirTotal: 0,
      mirPending: 0,
      qcTotal: 0,
      qcPending: 0,
      ncrTotal: 0,
      ncrOverdue: 0,
      formsTotal: 0,
      formsPending: 0,
      snaggingTotal: 0,
      snaggingPending: 0,
      loading: true,
    });

    // 2. Clear project states
    setSelectedProjectId("");
    setSelectedProjectNameState("");

    // 3. Return to main overview if a child card was active
    setSelectedKey(null);

    // 4. Clear storage
    const projectKeys = [
      "ACTIVE_PROJECT_ID",
      "SELECTED_PROJECT_ID",
      "PROJECT_ID",
      "project_id",
      "active_project_id",
      "SELECTED_PROJECT",
      "selected_project",
    ];
    projectKeys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    try {
      store.dispatch(setSelectedProject(null));
    } catch (_) {}

    // 5. Clear export context and trigger fresh load
    childExportContextRef.current = {};
    setStatsVersion((v) => v + 1);
  }, []);

  // Listen for reset event triggered by navigation or Dashboard icon click
  useEffect(() => {
    const handleResetEvent = () => {
      handleResetToAll();
    };
    window.addEventListener("konstruct:reset-dashboard-all-projects", handleResetEvent);
    return () => {
      window.removeEventListener("konstruct:reset-dashboard-all-projects", handleResetEvent);
    };
  }, [handleResetToAll]);

  // Sync state when navigating via route state (e.g. from Configuration project card or Dashboard icon)
  useEffect(() => {
    if (location.state?.resetToAll || location.state?.resetTimestamp) {
      handleResetToAll();
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.project?.id) {
      const pId = String(location.state.project.id);
      const pName = location.state.project.name || "";
      setSelectedProjectId(pId);
      setSelectedProjectNameState(pName);
      localStorage.setItem("ACTIVE_PROJECT_ID", pId);
      localStorage.setItem("SELECTED_PROJECT_ID", pId);
    }
  }, [location.state, location.pathname, handleResetToAll, navigate]);

  // Dynamic live stats for the 5 top overview cards
  const [overviewStats, setOverviewStats] = useState({
    mirTotal: 0,
    mirPending: 0,
    qcTotal: 0,
    qcPending: 0,
    ncrTotal: 0,
    ncrOverdue: 0,
    formsTotal: 0,
    formsPending: 0,
    snaggingTotal: 0,
    snaggingPending: 0,
    loading: true,
  });
  const [statsVersion, setStatsVersion] = useState(0);

  // Fetch live overview statistics across all 5 modules whenever active project changes
  useEffect(() => {
    let isMounted = true;

    async function loadOverviewStats() {
      setOverviewStats((prev) => ({ ...prev, loading: true }));
      try {
        const queryParam = selectedProjectId ? { project_id: selectedProjectId, project: selectedProjectId } : {};
        const [mirRes, qcRes, ncrRes, formsData] = await Promise.allSettled([
          listMIRs(queryParam),
          fetchQCObservations(queryParam),
          getNCRList({ ...queryParam, paginate: "false" }),
          fetchFormsDashboardData(queryParam),
        ]);

        // 1. MIR Stats (Filtered to match selected project scope)
        let mirTotal = 0;
        let mirPending = 0;
        let mirList = [];
        if (mirRes.status === "fulfilled") {
          const list = Array.isArray(mirRes.value?.data) ? mirRes.value.data : (mirRes.value?.data?.results || mirRes.value?.data?.data || []);
          if (Array.isArray(list) && list.length > 0) {
            mirList = list;
          }
        }
        if (mirList.length === 0) {
          try {
            const fallbackRes = await getMyAssignedMIRs(queryParam);
            const list = Array.isArray(fallbackRes?.data) ? fallbackRes.data : (fallbackRes?.data?.results || fallbackRes?.data?.data || []);
            if (Array.isArray(list) && list.length > 0) mirList = list;
          } catch (_) {}
        }


        if (selectedProjectId || (selectedProjectName && selectedProjectName !== "ALL")) {
          mirList = mirList.filter((r) => isProjectMatch(r, selectedProjectId, selectedProjectName));
        }
        mirTotal = mirList.length;
        mirPending = mirList.filter((r) => String(r.status || "").toUpperCase() === "PENDING").length;

        // 2. QC Stats (Filtered to match selected project scope from real DB)
        let qcTotal = 0;
        let qcPending = 0;
        let qcList = [];
        if (qcRes.status === "fulfilled") {
          const list = Array.isArray(qcRes.value?.data)
            ? qcRes.value.data
            : Array.isArray(qcRes.value)
            ? qcRes.value
            : (qcRes.value?.data?.results || qcRes.value?.data?.data || []);
          if (Array.isArray(list) && list.length > 0) qcList = list;
        }
        if (selectedProjectId || (selectedProjectName && selectedProjectName !== "ALL")) {
          qcList = qcList.filter((r) => isProjectMatch(r, selectedProjectId, selectedProjectName));
        }
        qcTotal = qcList.length;
        qcPending = qcList.filter((r) => normalizeQcStatus(r.overall_status || r.status) === "Pending").length;

        // 3. NCR Stats (Filtered to match selected project scope from real DB)
        let ncrTotal = 0;
        let ncrOverdue = 0;
        let ncrList = [];
        if (ncrRes.status === "fulfilled") {
          const list = Array.isArray(ncrRes.value?.data) ? ncrRes.value.data : (ncrRes.value?.data?.results || ncrRes.value?.data?.data || []);
          if (Array.isArray(list) && list.length > 0) ncrList = list;
        }
        if (selectedProjectId || (selectedProjectName && selectedProjectName !== "ALL")) {
          ncrList = ncrList.filter((r) => isProjectMatch(r, selectedProjectId, selectedProjectName));
        }
        ncrTotal = ncrList.length;
        ncrOverdue = ncrList.filter((r) => {
          if (typeof r.is_overdue === "boolean") return r.is_overdue;
          if (String(r.status || "").toLowerCase() === "closed") return false;
          const targetDate = r.target_date_of_compliance || r.targetDate;
          if (!targetDate) return false;
          const due = parseNcrDate(targetDate);
          return due ? due < new Date() : false;
        }).length;

        // 4. Forms Stats
        let formsTotal = 0;
        let formsPending = 0;
        if (formsData.status === "fulfilled" && formsData.value?.widgets) {
          formsTotal = formsData.value.widgets.total_checklists ?? 0;
          formsPending = formsData.value.widgets.pending_checklists ?? 0;
        }

        // 5. Snagging Stats
        let snaggingTotal = 0;
        let snaggingPending = 0;

        if (selectedProjectId) {
          const snagRes = await getUnitStageRoleSummary({ project_id: selectedProjectId }).catch(() => null);
          const c = snagRes?.data?.counts;
          if (c) {
            snaggingTotal = Number(c.total_units || 0);
            snaggingPending = Number(c.pending_yet_to_start || 0);
          }
        } else {
          const accessibleProjects = availableProjects.length > 0 ? availableProjects : getAccessibleProjects();
          if (accessibleProjects.length > 0) {
            const snagResults = await Promise.allSettled(
              accessibleProjects.map((p) => getUnitStageRoleSummary({ project_id: p.id }))
            );
            snagResults.forEach((r) => {
              if (r.status === "fulfilled" && r.value?.data?.counts) {
                const c = r.value.data.counts;
                snaggingTotal += Number(c.total_units || 0);
                snaggingPending += Number(c.pending_yet_to_start || 0);
              }
            });
          }
        }

        if (isMounted) {
          setOverviewStats({
            mirTotal,
            mirPending,
            qcTotal,
            qcPending,
            ncrTotal,
            ncrOverdue,
            formsTotal,
            formsPending,
            snaggingTotal,
            snaggingPending,
            loading: false,
          });
          setStatsVersion((v) => v + 1);
        }
      } catch (err) {
        console.warn("Error fetching overview stats:", err);
        if (isMounted) {
          setOverviewStats((s) => ({ ...s, loading: false }));
          setStatsVersion((v) => v + 1);
        }
      }
    }

    loadOverviewStats();
    return () => {
      isMounted = false;
    };
  }, [selectedProjectId, selectedProjectName, availableProjects]);

  const WIDGETS = [
    {
      key: "mir",
      label: "MIR",
      value: overviewStats.loading ? "…" : overviewStats.mirTotal,
      delta: overviewStats.loading ? "Updating..." : `${overviewStats.mirPending} pending`,
      themeColor: "#ff9f43", // Bright Orange
      accentColor: "#f39c12",
      icon: "📦",
    },
    {
      key: "qc",
      label: "QC Observations",
      value: overviewStats.loading ? "…" : overviewStats.qcTotal,
      delta: overviewStats.loading ? "Updating..." : `${overviewStats.qcPending} pending`,
      themeColor: "#00cfe8", // Bright Electric Cyan
      accentColor: "#0284c7",
      icon: "🔍",
    },
    {
      key: "ncr",
      label: "NCR",
      value: overviewStats.loading ? "…" : overviewStats.ncrTotal,
      delta: overviewStats.loading ? "Updating..." : `${overviewStats.ncrOverdue} overdue`,
      themeColor: "#ff4d6d", // Bright Vibrant Crimson
      accentColor: "#e11d48",
      icon: "⚠️",
    },
    {
      key: "forms",
      label: "Forms",
      value: overviewStats.loading ? "…" : overviewStats.formsTotal,
      delta: overviewStats.loading ? "Updating..." : `${overviewStats.formsPending} pending`,
      themeColor: "#7367f0", // Bright Indigo Purple
      accentColor: "#6366f1",
      icon: "📋",
    },
    {
      key: "snagging",
      label: "Snagging",
      value: overviewStats.loading ? "…" : overviewStats.snaggingTotal,
      unit: "units",
      delta: overviewStats.loading ? "Updating..." : `${overviewStats.snaggingPending} units pending`,
      themeColor: "#10b981", // Bright Emerald Green
      accentColor: "#059669",
      icon: "🏗️",
    },
  ];

  // No card is selected on load — nothing is shown until the user clicks a card.
  const [selectedKey, setSelectedKey] = useState(null);
  const handleCardClick = (key) => setSelectedKey((prev) => (prev === key ? null : key));

  // Helper: Capture and embed each chart individually in a clean 2-column grid layout with exact aspect ratio
  async function embedDashboardCharts(doc, themeColorRgb, domainTitle) {
    try {
      const container = document.getElementById("active-dashboard-charts-container");
      if (!container) return;

      const chartElements = Array.from(container.querySelectorAll("[data-pdf-chart='true']"));
      if (chartElements.length === 0) return;

      const capturedCharts = [];
      for (let i = 0; i < chartElements.length; i++) {
        const el = chartElements[i];
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });
        if (canvas.width > 50 && canvas.height > 50) {
          capturedCharts.push({
            dataUrl: canvas.toDataURL("image/png"),
            aspectRatio: canvas.height / canvas.width,
          });
        }
      }

      if (capturedCharts.length === 0) return;

      const marginX = 40;
      const pageWidth = 762;
      const colWidth = 368;
      const gap = 26;

      doc.addPage();
      let rowStartY = 40;

      // Section Header on Charts Page
      doc.setFontSize(14);
      doc.setTextColor(themeColorRgb[0], themeColorRgb[1], themeColorRgb[2]);
      doc.text(`${domainTitle} — Visual Charts & Analytics`, marginX, rowStartY);
      doc.setFontSize(9);
      doc.setTextColor(130, 130, 130);
      doc.text("High-resolution visual chart snapshots & distribution breakdowns", marginX, rowStartY + 14);
      rowStartY += 32;

      let colIndex = 0;
      let rowMaxHeight = 0;

      for (let i = 0; i < capturedCharts.length; i++) {
        const chart = capturedCharts[i];
        const isWide = chart.aspectRatio < 0.38;

        if (isWide) {
          if (colIndex === 1) {
            rowStartY += rowMaxHeight + 14;
            colIndex = 0;
            rowMaxHeight = 0;
          }

          const renderHeight = Math.min(220, Math.max(120, pageWidth * chart.aspectRatio));
          if (rowStartY + renderHeight > 545) {
            doc.addPage();
            rowStartY = 40;
            doc.setFontSize(12);
            doc.setTextColor(themeColorRgb[0], themeColorRgb[1], themeColorRgb[2]);
            doc.text(`${domainTitle} — Visual Charts (Contd.)`, marginX, rowStartY);
            rowStartY += 26;
          }

          doc.addImage(chart.dataUrl, "PNG", marginX, rowStartY, pageWidth, renderHeight);
          rowStartY += renderHeight + 16;
          colIndex = 0;
          rowMaxHeight = 0;
        } else {
          const renderHeight = Math.min(220, Math.max(140, colWidth * chart.aspectRatio));
          const posX = colIndex === 0 ? marginX : marginX + colWidth + gap;

          if (rowStartY + renderHeight > 545) {
            doc.addPage();
            rowStartY = 40;
            doc.setFontSize(12);
            doc.setTextColor(themeColorRgb[0], themeColorRgb[1], themeColorRgb[2]);
            doc.text(`${domainTitle} — Visual Charts (Contd.)`, marginX, rowStartY);
            rowStartY += 26;
            colIndex = 0;
            rowMaxHeight = 0;
          }

          doc.addImage(chart.dataUrl, "PNG", posX, rowStartY, colWidth, renderHeight);
          rowMaxHeight = Math.max(rowMaxHeight, renderHeight);

          if (colIndex === 0) {
            colIndex = 1;
          } else {
            rowStartY += rowMaxHeight + 16;
            colIndex = 0;
            rowMaxHeight = 0;
          }
        }
      }
    } catch (e) {
      console.warn("Could not embed dashboard charts in PDF:", e);
    }
  }

  // Helper: Export Forms Analytics & Detailed Data to PDF (with visual charts)
  async function exportFormsPdf(data, projectScope, filterProjectId, activeFilters) {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const marginX = 40;
    let cursorY = 40;

    // Header
    doc.setFillColor(115, 103, 240); // Indigo Purple
    doc.rect(marginX, cursorY, 762, 40, "F");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("FORMS & CHECKLISTS ANALYTICS REPORT", marginX + 16, cursorY + 25);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX + 580, cursorY + 25);
    cursorY += 55;

    doc.setTextColor(40, 40, 40);
    if (projectScope) {
      doc.setFontSize(10);
      doc.text(`Active Scope: ${projectScope}`, marginX, cursorY);
      cursorY += 16;
    }

    const filterBadges = [];
    if (activeFilters?.status) filterBadges.push(`Status: ${formsStatusLabel(activeFilters.status)}`);
    if (activeFilters?.role) filterBadges.push(`Role: ${activeFilters.role}`);
    if (activeFilters?.start_date || activeFilters?.end_date) {
      filterBadges.push(`Date: ${activeFilters.start_date || "Any"} → ${activeFilters.end_date || "Any"}`);
    }
    if (filterBadges.length > 0) {
      doc.setFontSize(8.5);
      doc.setTextColor(100, 100, 100);
      doc.text(`Active Filters: ${filterBadges.join("  |  ")}`, marginX, cursorY);
      cursorY += 14;
    }

    // 4. Detailed Checklists Table - Filtered strictly by project & active filters
    let recent = Array.isArray(data?.checklists) && data.checklists.length > 0
      ? [...data.checklists]
      : Array.isArray(data?.recent_checklists)
        ? [...data.recent_checklists]
        : [];

    if (filterProjectId) {
      recent = recent.filter((r) => {
        const rPid = r.project_id ?? r.projectId ?? r.project;
        if (rPid !== undefined && rPid !== null && String(rPid).trim() !== "") {
          return String(rPid) === String(filterProjectId);
        }
        if (projectScope && r.project_name) {
          return r.project_name.trim().toLowerCase() === projectScope.trim().toLowerCase();
        }
        return true;
      });
    }

    if (activeFilters?.status) {
      if (activeFilters.status === "pending") {
        recent = recent.filter((r) => r.status === "open" || r.status === "in_progress");
      } else {
        recent = recent.filter((r) => r.status === activeFilters.status);
      }
    }

    if (activeFilters?.tower_id) {
      recent = recent.filter((r) => String(r.tower_id) === String(activeFilters.tower_id));
    }

    if (activeFilters?.floor_id) {
      recent = recent.filter((r) => String(r.floor_id) === String(activeFilters.floor_id));
    }

    if (activeFilters?.template_id) {
      recent = recent.filter((r) => String(r.template_id) === String(activeFilters.template_id));
    }

    // 1. Summary KPIs
    const w = data?.widgets || {};
    const totalCount = w.total_checklists ?? recent.length;
    const completedCount = w.completed_checklists ?? recent.filter((r) => r.status === "completed").length;
    const pendingCount = w.pending_checklists ?? recent.filter((r) => r.status === "open" || r.status === "in_progress").length;
    const inProgressCount = w.in_progress_checklists ?? recent.filter((r) => r.status === "in_progress").length;
    const openCount = w.open_checklists ?? recent.filter((r) => r.status === "open").length;
    const avgPct = w.completion_percentage ?? w.avg_completion_pct ?? (totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0);

    const kpis = [
      ["Total Checklists", totalCount],
      ["Completed", completedCount],
      ["Pending (Total)", pendingCount],
      ["In Progress", inProgressCount],
      ["Open", openCount],
      ["Avg Completion", `${avgPct}%`],
    ];

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 4, halign: "center" },
      headStyles: { fillColor: [115, 103, 240], textColor: [255, 255, 255] },
      head: [kpis.map((k) => k[0])],
      body: [kpis.map((k) => k[1])],
    });

    // Capture & Embed All Dashboard Charts in High-Res Beautiful Grid
    await embedDashboardCharts(doc, [115, 103, 240], "Forms & Checklists");

    // 2. Checklists by Template (Charts Analytics Breakdown)
    const templates = data?.charts?.template_breakdown || [];
    if (templates.length > 0) {
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(115, 103, 240);
      doc.text("Checklists by Template (Analytics Breakdown)", marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [115, 103, 240] },
        head: [["Template ID", "Template Name", "Total", "Completed", "Pending", "In Progress", "Avg Progress"]],
        body: templates.map((t) => [
          t.template_id,
          t.template_name || t.template__name || `Template #${t.template_id}`,
          t.total ?? 0,
          t.completed ?? 0,
          t.pending ?? 0,
          t.in_progress ?? 0,
          `${t.avg_progress ?? 0}%`,
        ]),
      });
      cursorY = doc.lastAutoTable.finalY + 20;
    }

    // 3. Towers and Floors
    const towers = data?.charts?.tower_breakdown || [];
    const floors = data?.charts?.floor_breakdown || [];
    if (towers.length > 0 || floors.length > 0) {
      if (cursorY > 400 || templates.length === 0) {
        doc.addPage();
        cursorY = 40;
      }
      doc.setFontSize(12);
      doc.setTextColor(115, 103, 240);
      doc.text("Location Breakdown (Towers & Floors)", marginX, cursorY);

      if (towers.length > 0) {
        autoTable(doc, {
          startY: cursorY + 8,
          margin: { left: marginX, right: 420 },
          styles: { fontSize: 7, cellPadding: 3 },
          headStyles: { fillColor: [255, 159, 67] },
          head: [["Tower ID", "Tower Name", "Total Checklists"]],
          body: towers.map((t) => [t.tower_id, t.tower_name || `Tower #${t.tower_id}`, t.total ?? 0]),
        });
      }

      if (floors.length > 0) {
        autoTable(doc, {
          startY: cursorY + 8,
          margin: { left: 440, right: marginX },
          styles: { fontSize: 7, cellPadding: 3 },
          headStyles: { fillColor: [0, 207, 232] },
          head: [["Floor ID", "Floor Name", "Total Checklists"]],
          body: floors.map((f) => [f.floor_id, f.floor_name || `Floor #${f.floor_id}`, f.total ?? 0]),
        });
      }
    }

    // 4. Detailed Checklists Table
    if (recent.length > 0) {
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(115, 103, 240);
      doc.text(`${recent.length > 15 ? "Checklists" : "Recent Checklists"} (${recent.length} records)`, marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [115, 103, 240] },
        head: [["ID", "Template Name", "Project", "Tower", "Floor", "Status", "Created At", "Completed At"]],
        body: recent.map((r) => [
          r.id,
          r.template__name || r.template_name || `Template #${r.template_id || ""}`,
          r.project_name || projectScope || "—",
          r.tower_name || (r.tower_id ? `Tower #${r.tower_id}` : "—"),
          r.floor_name || (r.floor_id ? `Floor #${r.floor_id}` : "—"),
          formsStatusLabel(r.status),
          formatIsoDate(r.created_at),
          formatIsoDate(r.completed_at),
        ]),
      });
    } else {
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(115, 103, 240);
      doc.text("Recent Checklists (0 records)", marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 6, halign: "center" },
        headStyles: { fillColor: [115, 103, 240] },
        head: [["Status"]],
        body: [["No checklists match the current project or filter selection."]],
      });
    }

    const safeScopeName = (projectScope || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
    doc.save(`forms-analytics-export-${safeScopeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  // Helper: Export MIR Analytics & Records (with visual charts)
  async function exportMirPdf() {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const marginX = 40;
    let cursorY = 40;

    const ctx = childExportContextRef.current?.mir;
    let mirRows = [];

    const activeProjectScope =
      (ctx?.filters?.project && ctx.filters.project !== "ALL" ? ctx.filters.project : "") ||
      (selectedProjectName && selectedProjectName !== "ALL" ? selectedProjectName : "") ||
      (selectedProjectId ? availableProjects.find((p) => String(p.id) === String(selectedProjectId))?.name || "Unnamed Project" : "");

    if (ctx?.filteredRecords && ctx.filteredRecords.length > 0) {
      mirRows = [...ctx.filteredRecords];
    } else {
      const activePid = (!selectedProjectId || selectedProjectName === "ALL") ? "" : (selectedProjectId || getActiveDashboardProjectId());
      try {
        let list = [];
        const res = await listMIRs(activePid ? { project_id: activePid } : {});
        const d = Array.isArray(res?.data) ? res.data : (res?.data?.results || res?.data?.data || []);
        if (Array.isArray(d) && d.length > 0) {
          list = d;
        } else {
          const fallbackRes = await getMyAssignedMIRs(activePid ? { project_id: activePid } : {});
          list = Array.isArray(fallbackRes?.data) ? fallbackRes.data : (fallbackRes?.data?.results || fallbackRes?.data?.data || []);
        }

        if (Array.isArray(list)) {
          mirRows = list.map((r, idx) => ({
            mirNumber: r.mir_number || r.mir_no || r.mirNumber || `#MIR-${r.id || idx + 1}`,
            project: r.project_name || getActiveDashboardProjectName(r.project_id || r.projectId) || r.project || "Unnamed Project",
            projectId: r.project_id || r.projectId,
            location: r.location || "—",
            status: (r.status || "PENDING").toUpperCase(),
            createdBy: r.created_by_name || r.createdBy || "Admin",
            createdDate: r.created_date || r.createdDate || (r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : "—"),
            createdTime: r.created_time || r.createdTime || "—",
          }));
        }
      } catch (e) {
        console.warn("Failed to load MIRs for PDF:", e);
        mirRows = [];
      }
    }

    if (activeProjectScope || selectedProjectId) {
      mirRows = mirRows.filter((r) => isProjectMatch(r, selectedProjectId, activeProjectScope));
    }
    const projectScope = activeProjectScope || "All Accessible Projects";

    // Header
    doc.setFillColor(255, 159, 67); // Bright Amber
    doc.rect(marginX, cursorY, 762, 40, "F");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("MATERIAL INSPECTION REQUESTS (MIR) REPORT", marginX + 16, cursorY + 25);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX + 580, cursorY + 25);
    cursorY += 55;

    doc.setTextColor(40, 40, 40);
    if (projectScope) {
      doc.setFontSize(10);
      doc.text(`Active Scope: ${projectScope}`, marginX, cursorY);
      cursorY += 16;
    }

    // Summary KPIs
    const approved = mirRows.filter((r) => r.status === "APPROVED").length;
    const rejected = mirRows.filter((r) => r.status === "REJECTED").length;
    const pending = mirRows.filter((r) => r.status === "PENDING").length;
    const underReview = mirRows.filter((r) => r.status === "UNDER_REVIEW").length;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 4, halign: "center" },
      headStyles: { fillColor: [255, 159, 67], textColor: [255, 255, 255] },
      head: [["Total MIRs", "Approved", "Rejected", "Pending", "Under Review"]],
      body: [[mirRows.length, approved, rejected, pending, underReview]],
    });

    if (mirRows.length > 0) {
      // Capture & Embed All Dashboard Charts in High-Res Beautiful Grid
      await embedDashboardCharts(doc, [255, 159, 67], "Material Inspections (MIR)");

      // Detailed Records Table
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(255, 159, 67);
      doc.text(`MIR Detailed Records (${mirRows.length} records)`, marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [255, 159, 67] },
        head: [["MIR Number", "Project", "Location", "Status", "Created By", "Created Date", "Created Time"]],
        body: mirRows.map((r) => [
          r.mirNumber,
          r.project,
          r.location,
          STATUS_LABELS[r.status] || r.status,
          r.createdBy,
          r.createdDate,
          r.createdTime,
        ]),
      });
    } else {
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(255, 159, 67);
      doc.text("MIR Detailed Records (0 records)", marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 6, halign: "center" },
        headStyles: { fillColor: [255, 159, 67] },
        head: [["Status"]],
        body: [["No MIR records match the current project or filter selection."]],
      });
    }

    const safeScopeName = (projectScope || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
    doc.save(`mir-report-export-${safeScopeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  // Helper: Export QC Observations Analytics & Records (with visual charts)
  async function exportQcPdf() {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const marginX = 40;
    let cursorY = 40;

    const ctx = childExportContextRef.current?.qc;
    let qcRows = [];

    const activeProjectScope =
      (ctx?.filters?.project && ctx.filters.project !== "ALL" ? ctx.filters.project : "") ||
      (selectedProjectName && selectedProjectName !== "ALL" ? selectedProjectName : "") ||
      (selectedProjectId ? availableProjects.find((p) => String(p.id) === String(selectedProjectId))?.name || "Unnamed Project" : "");

    if (ctx?.filteredRecords && ctx.filteredRecords.length > 0) {
      qcRows = ctx.filteredRecords.map((r) => ({
        id: r.id,
        project: r.project_name || getActiveDashboardProjectName(r.projectId || r.project_id) || r.project || "Unnamed Project",
        projectId: r.projectId,
        location: r.location || "—",
        items: r.items ?? 1,
        status: r.status || "Pending",
        assignee: r.assignee || "Unassigned",
        createdDate: r.createdDate || "—",
      }));
    } else if (ctx?.liveQcRecords && ctx.liveQcRecords.length > 0) {
      qcRows = ctx.liveQcRecords.map((r) => ({
        id: r.id,
        project: r.project_name || getActiveDashboardProjectName(r.projectId || r.project_id) || r.project || "Unnamed Project",
        projectId: r.projectId,
        location: r.location || "—",
        items: r.items ?? 1,
        status: r.status || "Pending",
        assignee: r.assignee || "Unassigned",
        createdDate: r.createdDate || "—",
      }));
    } else {
      const activePid = (!selectedProjectId || selectedProjectName === "ALL") ? "" : (selectedProjectId || ctx?.activeProjectId || getActiveDashboardProjectId());
      try {
        const res = await fetchQCObservations(activePid ? { project_id: activePid, project: activePid } : {});
        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
          ? res
          : (res?.data?.results || res?.data?.data || []);
        const rawList = Array.isArray(list) ? list : [];
        qcRows = rawList.map((r, idx) => {
          const status = normalizeQcStatus(r.overall_status || r.status);
          const dateObj = r.created_at ? new Date(r.created_at) : null;
          const createdDate = dateObj
            ? `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`
            : r.createdDate || "—";

          const towerPart = r.tower_name || "";
          const floorPart = r.floor_name || "";
          const location =
            (towerPart && floorPart ? `${towerPart} · ${floorPart}` : towerPart || floorPart) ||
            r.custom_location ||
            r.location ||
            "—";

          const items = Array.isArray(r.items) ? r.items : [];
          const assignee = r.assignee || resolveQcAssigneeFromItems(items);

          return {
            id: r.id ? (String(r.id).startsWith("OBS-") || String(r.id).startsWith("QC-") ? r.id : `QC-${r.id}`) : `QC-${idx + 1}`,
            project: r.project_name || getActiveDashboardProjectName(r.project_id || r.projectId) || r.project || "Unnamed Project",
            projectId: r.project_id || r.projectId,
            location,
            items: Number(r.items ?? r.item_count ?? (items.length || 1)),
            status,
            assignee,
            createdDate,
          };
        });
      } catch (e) {
        console.warn("Failed to load QC observations for PDF:", e);
        qcRows = [];
      }
    }

    if (activeProjectScope || selectedProjectId) {
      qcRows = qcRows.filter((r) => isProjectMatch(r, selectedProjectId, activeProjectScope));
    }
    const projectScope = activeProjectScope || "All Accessible Projects";

    // Header
    doc.setFillColor(0, 207, 232); // Electric Cyan
    doc.rect(marginX, cursorY, 762, 40, "F");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("QC OBSERVATIONS ANALYTICS REPORT", marginX + 16, cursorY + 25);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX + 580, cursorY + 25);
    cursorY += 55;

    doc.setTextColor(40, 40, 40);
    if (projectScope) {
      doc.setFontSize(10);
      doc.text(`Active Scope: ${projectScope}`, marginX, cursorY);
      cursorY += 16;
    }

    // Summary KPIs
    const pending = qcRows.filter((r) => r.status === "Pending").length;
    const inProgress = qcRows.filter((r) => r.status === "In Progress").length;
    const resolved = qcRows.filter((r) => r.status === "Resolved").length;
    const closed = qcRows.filter((r) => r.status === "Closed").length;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 4, halign: "center" },
      headStyles: { fillColor: [0, 207, 232], textColor: [255, 255, 255] },
      head: [["Total Observations", "Pending", "In Progress", "Resolved", "Closed"]],
      body: [[qcRows.length, pending, inProgress, resolved, closed]],
    });

    if (qcRows.length > 0) {
      // Capture & Embed All Dashboard Charts in High-Res Beautiful Grid
      await embedDashboardCharts(doc, [0, 207, 232], "QC Observations");

      // Detailed Records Table
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(0, 207, 232);
      doc.text(`QC Detailed Observations (${qcRows.length} records)`, marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [0, 207, 232] },
        head: [["ID", "Project", "Location", "Items", "Status", "Assignee", "Created Date"]],
        body: qcRows.map((r) => [r.id, r.project, r.location, r.items, r.status, r.assignee, r.createdDate]),
      });
    } else {
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(0, 207, 232);
      doc.text("QC Detailed Observations (0 records)", marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 6, halign: "center" },
        headStyles: { fillColor: [0, 207, 232] },
        head: [["Status"]],
        body: [["No QC observations match the current project or filter selection."]],
      });
    }

    const safeScopeName = (projectScope || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
    doc.save(`qc-observations-export-${safeScopeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  // Helper: Export NCR Analytics & Records (with visual charts)
  async function exportNcrPdf() {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const marginX = 40;
    let cursorY = 40;

    const ctx = childExportContextRef.current?.ncr;
    let ncrRows = [];

    const activeProjectScope =
      (ctx?.filters?.project && ctx.filters.project !== "ALL" ? ctx.filters.project : "") ||
      (selectedProjectName && selectedProjectName !== "ALL" ? selectedProjectName : "") ||
      (selectedProjectId ? availableProjects.find((p) => String(p.id) === String(selectedProjectId))?.name || "Unnamed Project" : "");

    if (ctx?.filteredRecords && ctx.filteredRecords.length > 0) {
      ncrRows = [...ctx.filteredRecords];
    } else {
      const activePid = (!selectedProjectId || selectedProjectName === "ALL") ? "" : (selectedProjectId || getActiveDashboardProjectId());
      try {
        const res = await getNCRList(activePid ? { project_id: activePid, project: activePid, paginate: "false" } : { paginate: "false" });
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.results || res?.data?.data || []);
        const rawList = Array.isArray(list) ? list : [];
        ncrRows = rawList.map((r, idx) => {
          const raisedDate = r.raised_at ? new Date(r.raised_at) : null;
          const createdDate =
            raisedDate && !isNaN(raisedDate.getTime())
              ? `${String(raisedDate.getDate()).padStart(2, "0")}/${String(raisedDate.getMonth() + 1).padStart(2, "0")}/${raisedDate.getFullYear()}`
              : r.createdDate || "—";

          const raisedBy =
            (r.raised_by_name || r.raised_by || r.checker_name || r.created_by_name || "").trim() || "—";

          return {
            ncrNo: r.ncr_no || r.ncrNo || `NCR-${r.id ?? idx + 1}`,
            raisedBy,
            location: r.full_location || r.location || [r.project_name, r.tower_name, r.location_text].filter(Boolean).join(" / ") || "—",
            project: r.project_name || getActiveDashboardProjectName(r.project_id || r.projectId) || r.project || "Unnamed Project",
            projectId: r.project_id || r.projectId,
            tower: r.tower_name || r.tower || "—",
            cls: (r.classification || r.cls || "minor").toLowerCase(),
            targetDate: r.target_date_of_compliance || r.targetDate || "—",
            status: normalizeNcrStatus(r.status),
            createdDate,
            isOverdueFromApi: typeof r.is_overdue === "boolean" ? r.is_overdue : null,
          };
        });
      } catch (e) {
        console.warn("Failed to load NCRs for PDF:", e);
        ncrRows = [];
      }
    }

    if (activeProjectScope || selectedProjectId) {
      ncrRows = ncrRows.filter((r) => isProjectMatch(r, selectedProjectId, activeProjectScope));
    }
    const projectScope = activeProjectScope || "All Accessible Projects";

    // Header
    doc.setFillColor(255, 77, 109); // Vibrant Crimson
    doc.rect(marginX, cursorY, 762, 40, "F");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("NON-CONFORMITY REPORT (NCR) ANALYTICS", marginX + 16, cursorY + 25);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX + 580, cursorY + 25);
    cursorY += 55;

    doc.setTextColor(40, 40, 40);
    if (projectScope) {
      doc.setFontSize(10);
      doc.text(`Active Scope: ${projectScope}`, marginX, cursorY);
      cursorY += 16;
    }

    // Summary KPIs
    const major = ncrRows.filter((r) => r.cls === "major").length;
    const minor = ncrRows.filter((r) => r.cls === "minor").length;
    const closed = ncrRows.filter((r) => r.status === "Closed").length;
    const overdue = ncrRows.filter(ncrIsOverdue).length;

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 4, halign: "center" },
      headStyles: { fillColor: [255, 77, 109], textColor: [255, 255, 255] },
      head: [["Total NCRs", "Major Severity", "Minor Severity", "Closed", "Overdue"]],
      body: [[ncrRows.length, major, minor, closed, overdue]],
    });

    if (ncrRows.length > 0) {
      // Capture & Embed All Dashboard Charts in High-Res Beautiful Grid
      await embedDashboardCharts(doc, [255, 77, 109], "Non-Conformity Reports (NCR)");

      // Detailed Records Table
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(255, 77, 109);
      doc.text(`NCR Detailed Records (${ncrRows.length} records)`, marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [255, 77, 109] },
        head: [["NCR No", "Raised By", "Location", "Project", "Tower", "Class", "Target Date", "Status", "Created Date"]],
        body: ncrRows.map((r) => [
          r.ncrNo,
          r.raisedBy || "—",
          r.location,
          r.project,
          r.tower,
          formatNcrText(r.cls),
          r.targetDate,
          formatNcrText(r.status),
          r.createdDate,
        ]),
      });
    } else {
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(255, 77, 109);
      doc.text("NCR Detailed Records (0 records)", marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 6, halign: "center" },
        headStyles: { fillColor: [255, 77, 109] },
        head: [["Status"]],
        body: [["No NCR records match the current project or filter selection."]],
      });
    }

    const safeScopeName = (projectScope || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
    doc.save(`ncr-report-export-${safeScopeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  // Helper: Export Snagging Analytics & Records (with visual charts)
  async function exportSnaggingPdf() {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const marginX = 40;
    let cursorY = 40;

    const ctx = childExportContextRef.current?.snagging;
    let targetPid =
      ctx?.selectedProjectId && ctx.selectedProjectId !== PROJECT_ALL
        ? ctx.selectedProjectId
        : (!selectedProjectId || selectedProjectName === "ALL") ? "" : (selectedProjectId || getActiveDashboardProjectId());
    const activeProjectScope =
      ctx?.selectedProjectName ||
      (targetPid ? availableProjects.find((p) => String(p.id) === String(targetPid))?.name || "Unnamed Project" : selectedProjectName !== "ALL" ? selectedProjectName : "");
    const projectScope = activeProjectScope || "All Accessible Projects";

    if (!targetPid || isNaN(Number(targetPid))) {
      const proj = availableProjects.find(
        (p) =>
          normalizeProjectName(p.name) === normalizeProjectName(activeProjectScope) ||
          p.name.trim().toLowerCase() === (activeProjectScope || "").trim().toLowerCase()
      );
      if (proj?.id) {
        targetPid = String(proj.id);
      }
    }

    // Fetch real summary counts
    let totalUnits = 0;
    let completed = 0;
    let wip = 0;
    let pending = 0;
    let yetToVerify = 0;
    let totalSnags = 0;
    let totalClosedSnags = 0;
    let totalRaisedByChecker = 0;
    let totalCompletedByMaker = 0;
    let rows = [];

    // 1. Prefer live context from the rendered SnaggingDashboard
    if (ctx) {
      if (ctx.totalUnits !== undefined && ctx.totalUnits !== null) totalUnits = Number(ctx.totalUnits || 0);
      if (ctx.completedUnits !== undefined && ctx.completedUnits !== null) completed = Number(ctx.completedUnits || 0);
      if (ctx.inProgressUnits !== undefined && ctx.inProgressUnits !== null) wip = Number(ctx.inProgressUnits || 0);
      if (ctx.pendingUnits !== undefined && ctx.pendingUnits !== null) pending = Number(ctx.pendingUnits || 0);
      if (ctx.yetToVerify !== undefined && ctx.yetToVerify !== null) yetToVerify = Number(ctx.yetToVerify || 0);
      if (ctx.totalOpenSnags !== undefined && ctx.totalOpenSnags !== null) totalSnags = Number(ctx.totalOpenSnags || 0);
      if (ctx.totalClosedSnags !== undefined && ctx.totalClosedSnags !== null) totalClosedSnags = Number(ctx.totalClosedSnags || 0);
      if (ctx.totalRaisedByChecker !== undefined && ctx.totalRaisedByChecker !== null) totalRaisedByChecker = Number(ctx.totalRaisedByChecker || 0);
      if (ctx.totalCompletedByMaker !== undefined && ctx.totalCompletedByMaker !== null) totalCompletedByMaker = Number(ctx.totalCompletedByMaker || 0);

      if (ctx.summaryCounts) {
        const sc = ctx.summaryCounts;
        if (!totalUnits) totalUnits = Number(sc.total_units || 0);
        if (!completed) completed = Number(sc.complete ?? sc.complete_units ?? sc.completed ?? 0);
        if (!wip) wip = Number(sc.work_in_progress_unit ?? sc.work_in_progress ?? 0);
        if (!pending) pending = Number(sc.pending_yet_to_start ?? sc.pending_units ?? 0);
        if (!yetToVerify) yetToVerify = Number(sc.yet_to_verify || 0);
        if (!totalSnags) totalSnags = Number(sc.open_snags || 0);
        if (!totalClosedSnags) totalClosedSnags = Number(sc.closed_snags || 0);
        if (!totalRaisedByChecker) totalRaisedByChecker = Number(sc.raised_by_checker || 0);
        if (!totalCompletedByMaker) totalCompletedByMaker = Number(sc.completed_by_maker || 0);
      }

      if (Array.isArray(ctx.filteredRecords) && ctx.filteredRecords.length > 0) {
        rows = [...ctx.filteredRecords];
      } else if (Array.isArray(ctx.liveRecords) && ctx.liveRecords.length > 0) {
        rows = [...ctx.liveRecords];
      }
    }

    // 2. If counts or rows were not found in context, fetch from API with corrected field names
    if ((!totalUnits && !wip && !pending) || rows.length === 0) {
      try {
        if (targetPid) {
          const [sumRes, wipRes, snagStatsRes] = await Promise.allSettled([
            getUnitStageRoleSummary({ project_id: targetPid }),
            getUnitWorkInProgressBreakdown({ project_id: targetPid, include_rows: true, limit: 150 }),
            getSnagStats(targetPid),
          ]);

          if (sumRes.status === "fulfilled" && sumRes.value?.data?.counts) {
            const c = sumRes.value.data.counts;
            if (!totalUnits) totalUnits = Number(c.total_units || 0);
            if (!completed) completed = Number(c.complete ?? c.complete_units ?? c.completed ?? 0);
            if (!wip) wip = Number(c.work_in_progress_unit ?? c.work_in_progress ?? 0);
            if (!pending) pending = Number(c.pending_yet_to_start ?? c.pending_units ?? 0);
            if (!yetToVerify) yetToVerify = Number(c.yet_to_verify || 0);
          }

          if (snagStatsRes.status === "fulfilled") {
            const sd = snagStatsRes.value?.data;
            const statsObj = Array.isArray(sd) ? sd[0] : (Array.isArray(sd?.results) ? sd.results[0] : (Array.isArray(sd?.data) ? sd.data[0] : sd));
            if (statsObj) {
              const projectSnagsRaised = Number(statsObj.snags_raised || 0);
              const closed = Number(statsObj.snags_status?.closed?.count || 0);
              const openFromStatus = Number(statsObj.snags_status?.repair_in_progress?.count || 0)
                + Number(statsObj.snags_status?.repaired?.count || 0)
                + Number(statsObj.snags_status?.reviewed?.count || 0);
              const projectSnagsOpen = openFromStatus > 0 ? openFromStatus : Math.max(0, projectSnagsRaised - closed);
              if (!totalSnags) totalSnags = projectSnagsOpen;
              if (!totalRaisedByChecker) totalRaisedByChecker = projectSnagsRaised;
              if (!totalCompletedByMaker) {
                totalCompletedByMaker = Number(statsObj.kpis?.desnag_by_repairer_count ?? statsObj.maker_work_completed ?? closed);
              }
            }
          }

          if (rows.length === 0) {
            const wipData = wipRes.status === "fulfilled" ? (wipRes.value?.data?.data || wipRes.value?.data || []) : [];
            const rowsArray = Array.isArray(wipData) ? wipData : (wipData.rows || wipData.results || []);
            if (Array.isArray(rowsArray)) {
              rows = rowsArray.map((r, idx) => ({
                unitNo: r.unit_no || r.flat_number || `Unit ${idx + 1}`,
                tower: r.tower_name || r.building_name || "Tower A",
                floor: r.floor_name || r.level_name || "Floor 1",
                category: r.flat_type || "Standard",
                stage: r.stage_name || "Finishing",
                status: (r.status || "IN_PROGRESS").toUpperCase(),
                pendingRole: r.pending_from || "Maker",
                questions: `${r.completed_questions || 0}/${r.total_questions || 0}`,
                openSnags: Number(r.open_snags || 0),
                raisedByChecker: Number(r.snags_raised || r.total_snags || r.open_snags || 0),
                completedByMaker: Number(r.desnag_by_repairer || r.snags_completed || r.completed_questions || 0),
                projectId: targetPid,
                project: activeProjectScope,
              }));
            }
          }
        } else {
          const accessibleProjects = availableProjects.length > 0 ? availableProjects : (ctx?.availableProjects || getAccessibleProjects());
          if (accessibleProjects.length > 0) {
            const [sumSettled, wipSettled] = await Promise.all([
              Promise.allSettled(accessibleProjects.map((p) => getUnitStageRoleSummary({ project_id: p.id }))),
              Promise.allSettled(accessibleProjects.map((p) => getUnitWorkInProgressBreakdown({ project_id: p.id, include_rows: true, limit: 50 }))),
            ]);

            sumSettled.forEach((r) => {
              if (r.status === "fulfilled" && r.value?.data?.counts) {
                const c = r.value.data.counts;
                totalUnits += Number(c.total_units || 0);
                completed += Number(c.complete ?? c.complete_units ?? c.completed ?? 0);
                wip += Number(c.work_in_progress_unit ?? c.work_in_progress ?? 0);
                pending += Number(c.pending_yet_to_start ?? c.pending_units ?? 0);
                yetToVerify += Number(c.yet_to_verify || 0);
              }
            });

            if (rows.length === 0) {
              wipSettled.forEach((r, pIdx) => {
                if (r.status === "fulfilled") {
                  const proj = accessibleProjects[pIdx];
                  const wipData = r.value?.data?.data || r.value?.data || [];
                  const rowsArray = Array.isArray(wipData) ? wipData : (wipData.rows || wipData.results || []);
                  if (Array.isArray(rowsArray)) {
                    rowsArray.forEach((row, idx) => {
                      rows.push({
                        unitNo: row.unit_no || row.flat_number || `Unit ${idx + 1}`,
                        tower: row.tower_name || row.building_name || "Tower A",
                        floor: row.floor_name || row.level_name || "Floor 1",
                        category: row.flat_type || "Standard",
                        stage: row.stage_name || "Finishing",
                        status: (row.status || "IN_PROGRESS").toUpperCase(),
                        pendingRole: row.pending_from || "Maker",
                        questions: `${row.completed_questions || 0}/${row.total_questions || 0}`,
                        openSnags: Number(row.open_snags || 0),
                        raisedByChecker: Number(row.snags_raised || row.total_snags || row.open_snags || 0),
                        completedByMaker: Number(row.desnag_by_repairer || row.snags_completed || row.completed_questions || 0),
                        projectId: proj?.id,
                        project: proj?.name,
                      });
                    });
                  }
                }
              });
            }
          }
        }
      } catch (e) {
        console.warn("Failed to fetch live snagging data for PDF:", e);
      }
    }

    if (activeProjectScope && activeProjectScope !== "All Accessible Projects") {
      rows = rows.filter((r) => isProjectMatch(r, targetPid, activeProjectScope));
    }

    // Secondary row-level fallbacks if counts are missing
    if (rows.length > 0) {
      if (!totalUnits) totalUnits = rows.length;
      if (!completed) completed = rows.filter((r) => r.status === "COMPLETED").length;
      if (!wip) wip = rows.filter((r) => r.status === "IN_PROGRESS").length;
      if (!pending) pending = rows.filter((r) => r.status === "PENDING").length;
      if (!yetToVerify) yetToVerify = rows.filter((r) => r.status === "YET_TO_VERIFY").length;
    }
    const rowsSnags = rows.reduce((a, b) => a + (Number(b.openSnags) || 0), 0);
    if (!totalSnags && rowsSnags > 0) totalSnags = rowsSnags;

    const rowsRaised = rows.reduce((a, b) => a + (Number(b.raisedByChecker) || 0), 0);
    if (!totalRaisedByChecker && rowsRaised > 0) totalRaisedByChecker = rowsRaised;
    const rowsDone = rows.reduce((a, b) => a + (Number(b.completedByMaker) || 0), 0);
    if (!totalCompletedByMaker && rowsDone > 0) totalCompletedByMaker = rowsDone;

    // Header
    doc.setFillColor(16, 185, 129); // Emerald Green
    doc.rect(marginX, cursorY, 762, 40, "F");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("SNAGGING & UNIT PROGRESS ANALYTICS REPORT", marginX + 16, cursorY + 25);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX + 580, cursorY + 25);
    cursorY += 55;

    doc.setTextColor(40, 40, 40);
    if (projectScope) {
      doc.setFontSize(10);
      doc.text(`Active Scope: ${projectScope}`, marginX, cursorY);
      cursorY += 16;
    }

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 4, halign: "center" },
      headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] },
      head: [["Total Units", "Completed", "In Progress", "Pending", "Yet to Verify", "Open Snags (Question)", "Close Snags (Question)", "Raised (Checker)", "Completed (Maker)"]],
      body: [[
        totalUnits,
        completed,
        wip,
        pending,
        yetToVerify,
        totalSnags,
        totalClosedSnags || 0,
        totalRaisedByChecker || (totalSnags + (totalClosedSnags || 0)),
        totalCompletedByMaker || totalClosedSnags || 0,
      ]],
    });

    if (totalUnits > 0 || rows.length > 0) {
      // Capture & Embed All Dashboard Charts in High-Res Beautiful Grid
      await embedDashboardCharts(doc, [16, 185, 129], "Snagging & Construction Progress");

      // Detailed Records Table
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(16, 185, 129);
      doc.text(`Snagging & Unit Records (${rows.length} units)`, marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [16, 185, 129] },
        head: [["Unit / Flat", "Tower", "Floor", "Category", "Stage", "Status", "Pending Role", "Questions", "Open Snags", "Raised (Checker)", "Completed (Maker)"]],
        body: rows.map((r) => [
          r.unitNo,
          r.tower,
          r.floor,
          r.category,
          r.stage,
          SNAG_STATUS_CONFIG[r.status]?.label || r.status,
          r.pendingRole,
          r.questions,
          r.openSnags,
          r.raisedByChecker ?? (Number(r.openSnags || 0) + Number(r.completedByMaker || 0)),
          r.completedByMaker ?? 0,
        ]),
      });
    } else {
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(12);
      doc.setTextColor(16, 185, 129);
      doc.text("Snagging & Unit Records (0 records)", marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 8,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 8, cellPadding: 6, halign: "center" },
        headStyles: { fillColor: [16, 185, 129] },
        head: [["Status"]],
        body: [["No snagging records match the current project or filter selection."]],
      });
    }

    const safeScopeName = (projectScope || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
    doc.save(`snagging-report-export-${safeScopeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  // Master export dispatcher: exports active card's charts + records or full dashboard
  async function handleExportPdf() {
    if (selectedKey === "mir") {
      await exportMirPdf();
    } else if (selectedKey === "qc") {
      await exportQcPdf();
    } else if (selectedKey === "ncr") {
      await exportNcrPdf();
    } else if (selectedKey === "snagging") {
      await exportSnaggingPdf();
    } else if (selectedKey === "forms") {
      try {
        const ctx = childExportContextRef.current?.forms;
        const activePid = (!selectedProjectId || selectedProjectName === "ALL")
          ? ""
          : (ctx?.selectedProjectId !== undefined ? ctx.selectedProjectId : selectedProjectId || getActiveDashboardProjectId());
        let scopeLabel = "All Accessible Projects";
        if (activePid) {
          scopeLabel = ctx?.selectedProjectName || selectedProjectName || getActiveDashboardProjectName(activePid) || "Unnamed Project";
        }

        let data = ctx?.apiData;
        let checklists = ctx?.checklists;
        if (!data || (ctx?.filters?.project_id !== undefined && String(ctx.filters.project_id) !== String(activePid))) {
          const fetchParams = { ...(ctx?.filters || {}) };
          if (activePid) {
            fetchParams.project_id = activePid;
          } else {
            delete fetchParams.project_id;
          }
          const [dashSettled, listSettled] = await Promise.allSettled([
            fetchFormsDashboardData(fetchParams),
            fetchManualChecklistsList(fetchParams),
          ]);
          data = dashSettled.status === "fulfilled" ? dashSettled.value : null;
          checklists = listSettled.status === "fulfilled" && Array.isArray(listSettled.value) ? listSettled.value : [];
        }
        if (data) {
          data = { ...data, checklists: checklists || ctx?.checklists || [] };
        }
        await exportFormsPdf(data, scopeLabel, activePid, ctx?.filters);
      } catch (e) {
        console.error("Failed to export Forms PDF:", e);
      }
    } else {
      // No specific card selected -> Export full multi-domain PDF with project scoping
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const marginX = 40;
      let cursorY = 40;

      const activeScopeName =
        (selectedProjectName && selectedProjectName !== "ALL" ? selectedProjectName : "") ||
        (selectedProjectId ? availableProjects.find((p) => String(p.id) === String(selectedProjectId))?.name || "Unnamed Project" : "");

      let mirList = [];
      try {
        const activePid = (!selectedProjectId || selectedProjectName === "ALL") ? "" : (selectedProjectId || getActiveDashboardProjectId());
        const res = await listMIRs(activePid ? { project_id: activePid } : {});
        const d = Array.isArray(res?.data) ? res.data : (res?.data?.results || res?.data?.data || []);
        let rawList = Array.isArray(d) && d.length > 0 ? d : [];
        if (!rawList.length) {
          const fallbackRes = await getMyAssignedMIRs(activePid ? { project_id: activePid } : {});
          rawList = Array.isArray(fallbackRes?.data) ? fallbackRes.data : (fallbackRes?.data?.results || fallbackRes?.data?.data || []);
        }
        if (Array.isArray(rawList)) {
          mirList = rawList.map((r, idx) => ({
            mirNumber: r.mir_number || r.mir_no || r.mirNumber || `#MIR-${r.id || idx + 1}`,
            project: r.project_name || getActiveDashboardProjectName(r.project_id || r.projectId) || r.project || "Unnamed Project",
            projectId: r.project_id || r.projectId,
            location: r.location || "—",
            status: (r.status || "PENDING").toUpperCase(),
            createdBy: r.created_by_name || r.createdBy || "Admin",
            createdDate: r.created_date || r.createdDate || (r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : "—"),
            createdTime: r.created_time || r.createdTime || "—",
          }));
        }
      } catch (e) {
        console.warn("Failed to load MIRs for comprehensive PDF:", e);
        mirList = [];
      }

      let qcList = [];
      try {
        const activePid = (!selectedProjectId || selectedProjectName === "ALL") ? "" : (selectedProjectId || getActiveDashboardProjectId());
        const res = await fetchQCObservations(activePid ? { project_id: activePid, project: activePid } : {});
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : (res?.data?.results || res?.data?.data || []));
        if (Array.isArray(list)) {
          qcList = list.map((r, idx) => ({
            id: r.id ? (String(r.id).startsWith("OBS-") || String(r.id).startsWith("QC-") ? r.id : `QC-${r.id}`) : `QC-${idx + 1}`,
            project: r.project_name || getActiveDashboardProjectName(r.project_id || r.projectId) || r.project || "Unnamed Project",
            projectId: r.project_id || r.projectId,
            location: (r.tower_name && r.floor_name ? `${r.tower_name} · ${r.floor_name}` : r.tower_name || r.floor_name) || r.custom_location || r.location || "—",
            items: Number(r.items ?? r.item_count ?? (Array.isArray(r.items) ? r.items.length : 1)),
            status: normalizeQcStatus(r.overall_status || r.status),
            assignee: r.assignee || resolveQcAssigneeFromItems(r.items || []),
            createdDate: r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB") : (r.createdDate || "—"),
          }));
        }
      } catch (e) {
        console.warn("Failed to load QC observations for comprehensive PDF:", e);
        qcList = [];
      }

      let ncrList = [];
      try {
        const activePid = (!selectedProjectId || selectedProjectName === "ALL") ? "" : (selectedProjectId || getActiveDashboardProjectId());
        const res = await getNCRList(activePid ? { project_id: activePid, project: activePid, paginate: "false" } : { paginate: "false" });
        const list = Array.isArray(res?.data) ? res.data : (res?.data?.results || res?.data?.data || []);
        if (Array.isArray(list)) {
          ncrList = list.map((r, idx) => ({
            ncrNo: r.ncr_no || r.ncrNo || `NCR-${r.id ?? idx + 1}`,
            location: r.full_location || r.location || [r.project_name, r.tower_name, r.location_text].filter(Boolean).join(" / ") || "—",
            project: r.project_name || getActiveDashboardProjectName(r.project_id || r.projectId) || r.project || "Unnamed Project",
            projectId: r.project_id || r.projectId,
            tower: r.tower_name || r.tower || "—",
            cls: (r.classification || r.cls || "minor").toLowerCase(),
            targetDate: r.target_date_of_compliance || r.targetDate || "—",
            status: normalizeNcrStatus(r.status),
            createdDate: r.raised_at ? new Date(r.raised_at).toLocaleDateString("en-GB") : (r.createdDate || "—"),
          }));
        }
      } catch (e) {
        console.warn("Failed to load NCRs for comprehensive PDF:", e);
        ncrList = [];
      }

      if (activeScopeName || selectedProjectId) {
        mirList = mirList.filter((r) => isProjectMatch(r, selectedProjectId, activeScopeName));
        qcList = qcList.filter((r) => isProjectMatch(r, selectedProjectId, activeScopeName));
        ncrList = ncrList.filter((r) => isProjectMatch(r, selectedProjectId, activeScopeName));
      }

      const scopeLabel = activeScopeName || "All Accessible Projects";

      doc.setFontSize(18);
      doc.text("Dashboard — Executive Summary Report", marginX, cursorY);
      doc.setFontSize(10);
      doc.text(`Active Scope: ${scopeLabel} | Generated: ${new Date().toLocaleString()}`, marginX, cursorY + 16);
      cursorY += 36;

      // MIR
      doc.setFontSize(14);
      doc.setTextColor(255, 159, 67);
      doc.text(`MIR Summary (${mirList.length} records)`, marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 10,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [255, 159, 67] },
        head: [["MIR Number", "Project", "Location", "Status", "Created By", "Created Date", "Created Time"]],
        body: mirList.slice(0, 40).map((r) => [
          r.mirNumber, r.project, r.location, STATUS_LABELS[r.status] || r.status, r.createdBy, r.createdDate, r.createdTime,
        ]),
      });

      // QC
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(14);
      doc.setTextColor(0, 207, 232);
      doc.text(`QC Observations Summary (${qcList.length} records)`, marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 10,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [0, 207, 232] },
        head: [["ID", "Project", "Location", "Items", "Status", "Assignee", "Created Date"]],
        body: qcList.map((r) => [r.id, r.project, r.location, r.items, r.status, r.assignee, r.createdDate]),
      });

      // NCR
      doc.addPage();
      cursorY = 40;
      doc.setFontSize(14);
      doc.setTextColor(255, 77, 109);
      doc.text(`NCR Summary (${ncrList.length} records)`, marginX, cursorY);
      autoTable(doc, {
        startY: cursorY + 10,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [255, 77, 109] },
        head: [["NCR No", "Location", "Class", "Target Date", "Status", "Created Date"]],
        body: ncrList.map((r) => [r.ncrNo, r.location, formatNcrText(r.cls), r.targetDate, formatNcrText(r.status), r.createdDate]),
      });

      const safeScopeName = (activeScopeName || "full_dashboard").replace(/[^a-zA-Z0-9_-]/g, "_");
      doc.save(`dashboard-summary-export-${safeScopeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
    }
  }

  const activeCardInfo = WIDGETS.find((w) => w.key === selectedKey);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < 768
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      className="min-h-screen px-3 sm:px-4 py-4 sm:py-8"
      style={{
        background: theme === "dark" ? "#17171d" : "#fcfaf7",
        color: textColor,
        marginTop: isMobile ? 64 : 96,
      }}
    >
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Top Header Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 pb-4 border-b" style={{ borderColor: `${ORANGE}33` }}>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-6 sm:h-7 rounded-full" style={{ background: ORANGE }} />
              <h1 className="text-xl sm:text-2xl font-black uppercase tracking-wider" style={{ color: ORANGE }}>
                Dashboard
              </h1>
            </div>
            <p className="text-[11px] sm:text-xs opacity-60 mt-1 pl-4">
              Quality control, non-conformity tracking, inspections, checklists, and snagging progress.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
            {/* Global Project Selector Dropdown */}
            <div className="flex items-center gap-1.5 w-full sm:w-auto">
              <span className="text-xs font-bold uppercase tracking-wider opacity-70 flex items-center gap-1 shrink-0">
                <span>🏢</span>
                <span className="hidden sm:inline">Project:</span>
              </span>
              <select
                value={selectedProjectId || (selectedProjectName !== "ALL" ? selectedProjectName : "")}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val || val === "ALL") {
                    handleProjectSelect("", "ALL");
                  } else {
                    const proj = availableProjects.find((p) => String(p.id) === String(val) || p.name === val);
                    handleProjectSelect(proj ? proj.id : val, proj ? proj.name : val);
                  }
                }}
                className="text-xs font-semibold px-2.5 sm:px-3 py-2 rounded-lg border outline-none cursor-pointer transition-all shadow-sm flex-1 sm:flex-initial"
                style={{
                  background: cardColor,
                  color: textColor,
                  borderColor: selectedProjectId || (selectedProjectName && selectedProjectName !== "ALL") ? ORANGE : `${ORANGE}55`,
                  boxShadow: selectedProjectId || (selectedProjectName && selectedProjectName !== "ALL") ? `0 0 10px ${ORANGE}33` : undefined,
                  minWidth: 120,
                  maxWidth: isMobile ? "100%" : 220,
                }}
              >
                <option value="">All Projects</option>
                {availableProjects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || "Unnamed Project"}
                  </option>
                ))}
              </select>

              {(selectedProjectId || (selectedProjectName && selectedProjectName !== "ALL")) && (
                <button
                  onClick={() => handleProjectSelect("", "ALL")}
                  title="Reset to All Projects"
                  className="text-xs px-2 py-1.5 rounded-lg border font-bold opacity-75 hover:opacity-100 transition-opacity shrink-0"
                  style={{ borderColor: `${ORANGE}55`, background: `${ORANGE}15`, color: ORANGE }}
                >
                  ✕ Reset
                </button>
              )}
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-2 shrink-0">
              {/* Dynamic Export PDF Button */}
              <button
                onClick={handleExportPdf}
                className="text-xs px-3 sm:px-4 py-2 rounded-lg font-bold shadow transition-all hover:scale-105 flex items-center justify-center gap-1.5 flex-1 sm:flex-initial"
                style={{
                  background: activeCardInfo ? activeCardInfo.themeColor : ORANGE,
                  color: "#1a1a1a",
                  boxShadow: `0 4px 14px ${(activeCardInfo ? activeCardInfo.themeColor : ORANGE)}44`,
                }}
              >
                <span>⬇</span>
                <span className="hidden sm:inline">
                  {activeCardInfo
                    ? `Export ${activeCardInfo.label} PDF (Charts & Data)`
                    : "Export Full Dashboard (PDF)"}
                </span>
                <span className="sm:hidden">
                  {activeCardInfo ? `${activeCardInfo.label} PDF` : "Export PDF"}
                </span>
              </button>

              {/* Light / Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className="text-xs px-2.5 sm:px-3.5 py-2 rounded-lg border font-semibold opacity-80 hover:opacity-100 transition-all flex items-center justify-center gap-1 shrink-0"
                style={{ borderColor: `${ORANGE}66`, background: cardColor }}
                title="Toggle Theme"
              >
                {theme === "dark" ? "☀️" : "🌙"}
                <span className="hidden sm:inline">{theme === "dark" ? " Light" : " Dark"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bright, attractive, modern 5 KPI cards */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider opacity-60">Overview Metrics</span>
              {(selectedProjectId || (selectedProjectName && selectedProjectName !== "ALL")) && (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full truncate max-w-[180px]"
                  style={{ background: `${ORANGE}22`, color: ORANGE, border: `1px solid ${ORANGE}55` }}
                >
                  Filtered: {selectedProjectName}
                </span>
              )}
            </div>
            <span className="text-[10px] sm:text-[11px] opacity-50">Click any card below to view detailed analytics & charts</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-3.5">
            {WIDGETS.map((card, idx) => {
              const isActive = selectedKey === card.key;
              return (
                <button
                  key={card.key}
                  onClick={() => handleCardClick(card.key)}
                  aria-pressed={isActive}
                  className={`text-left rounded-xl p-3 sm:p-4 shadow transition-all hover:scale-[1.02] relative overflow-hidden group ${
                    idx === WIDGETS.length - 1 ? "col-span-2 sm:col-span-1" : ""
                  }`}
                  style={{
                    background: cardColor,
                    border: `2px solid ${card.themeColor}`,
                    boxShadow: isActive
                      ? `0 0 20px ${card.themeColor}55, 0 6px 24px rgba(0,0,0,0.12)`
                      : `0 2px 10px rgba(0,0,0,0.04)`,
                    transform: isActive ? "translateY(-4px)" : undefined,
                    opacity: selectedKey && !isActive ? 0.6 : 1,
                    cursor: "pointer",
                  }}
                >
                  {/* Decorative background glow badge */}
                  <div
                    className="absolute -right-2 -bottom-2 w-16 h-16 rounded-full opacity-10 pointer-events-none flex items-center justify-center text-3xl group-hover:scale-125 transition-transform"
                    style={{ background: card.themeColor }}
                  >
                    {card.icon}
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-[11px] sm:text-xs font-bold uppercase tracking-wider opacity-75 truncate">{card.label}</p>
                    <span className="text-base shrink-0 ml-1">{card.icon}</span>
                  </div>
                  <p className="text-xl sm:text-2xl font-extrabold mt-1 tracking-tight h-7 sm:h-8 flex items-baseline gap-1" style={{ color: card.themeColor }}>
                    <CountUpNumber
                      value={card.value}
                      loading={overviewStats.loading}
                      color={card.themeColor}
                      triggerKey={`${selectedProjectId || "ALL"}_${statsVersion}_${overviewStats.loading}`}
                    />
                    {card.unit && !overviewStats.loading && card.value !== "…" && (
                      <span className="text-xs sm:text-sm font-semibold opacity-75 tracking-normal lowercase">
                        {card.unit}
                      </span>
                    )}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[11px] font-semibold opacity-65 truncate max-w-[110px]">{card.delta}</p>
                    {isActive ? (
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse shrink-0"
                        style={{ background: `${card.themeColor}22`, color: card.themeColor }}
                      >
                        ● Active
                      </span>
                    ) : (
                      <span className="text-[10px] opacity-0 group-hover:opacity-60 transition-opacity shrink-0">
                        View →
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active Dashboard View */}
        {selectedKey && (
          <div className="space-y-4">
            {/* Back button — returns to the collapsed overview state */}
            <button
              onClick={() => setSelectedKey(null)}
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg border transition-all hover:scale-105"
              style={{
                borderColor: activeCardInfo ? activeCardInfo.themeColor : ORANGE,
                color: activeCardInfo ? activeCardInfo.themeColor : ORANGE,
                background: cardColor,
              }}
            >
              <span>←</span>
              <span>Back to Overview</span>
            </button>

            {(() => {
              switch (selectedKey) {
                case "mir":
                  return (
                    <MirDashboard
                      theme={theme}
                      cardColor={cardColor}
                      textColor={textColor}
                      selectedProjectId={selectedProjectId}
                      selectedProjectName={selectedProjectName}
                      onSelectProject={handleProjectSelect}
                      onExportContext={(ctx) => setChildExportContext("mir", ctx)}
                    />
                  );
                case "qc":
                  return (
                    <QcDashboard
                      theme={theme}
                      cardColor={cardColor}
                      textColor={textColor}
                      selectedProjectId={selectedProjectId}
                      selectedProjectName={selectedProjectName}
                      onSelectProject={handleProjectSelect}
                      onExportContext={(ctx) => setChildExportContext("qc", ctx)}
                    />
                  );
                case "ncr":
                  return (
                    <NcrDashboard
                      theme={theme}
                      cardColor={cardColor}
                      textColor={textColor}
                      selectedProjectId={selectedProjectId}
                      selectedProjectName={selectedProjectName}
                      onSelectProject={handleProjectSelect}
                      onExportContext={(ctx) => setChildExportContext("ncr", ctx)}
                    />
                  );
                case "forms":
                  return (
                    <FormsDashboard
                      theme={theme}
                      cardColor={cardColor}
                      textColor={textColor}
                      selectedProjectId={selectedProjectId}
                      selectedProjectName={selectedProjectName}
                      onSelectProject={handleProjectSelect}
                      onExportContext={(ctx) => setChildExportContext("forms", ctx)}
                    />
                  );
                case "snagging":
                  return (
                    <div id="active-dashboard-charts-container" className="space-y-4">
                      <SnaggingDashboard
                        theme={theme}
                        cardColor={cardColor}
                        textColor={textColor}
                        selectedProjectId={selectedProjectId}
                        selectedProjectName={selectedProjectName}
                        onSelectProject={handleProjectSelect}
                        onExportContext={(ctx) => setChildExportContext("snagging", ctx)}
                      />
                    </div>
                  );
                default:
                  return null;
              }
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProjectHeadDashboard;